import {
  UPSTREAM_INBOUND_TYPES,
  UPSTREAM_OUTBOUND_TYPES,
  isParseFailure,
  parseInbound
} from "./chunk-OLBVUA2Y.js";

// src/alarm-regenerate.ts
var ALARM_REGENERATION_PREVIEW_CLASS = "alarm_regeneration_preview";
var ALARM_REGENERATE_COMMAND_VERSION = 1;
var ALARM_REGENERATE_ACTIONS = ["preview", "apply"];
var ALARM_REGENERATE_MODES = ["hardware", "decisions"];
var ALARM_REGENERATE_ID_MAX = 200;
var ALARM_REGENERATE_MAX_TICKS = 128;
var ALARM_REGENERATE_MAX_MODES = 16;
function parseAlarmRegenerateCommand(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== ALARM_REGENERATE_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported alarm regenerate command version (expected ${ALARM_REGENERATE_COMMAND_VERSION})`
    };
  }
  if (!isBoundedString(value.requestId)) {
    return {
      status: "rejected",
      reason: "requestId must be a non-empty string"
    };
  }
  if (!isAction(value.action)) {
    return { status: "rejected", reason: "missing or unknown action" };
  }
  if (!isRegenerateMode(value.mode)) {
    return { status: "rejected", reason: "missing or unknown mode" };
  }
  if (!isBoundedString(value.sourceUserId)) {
    return {
      status: "rejected",
      reason: "sourceUserId must be a non-empty string"
    };
  }
  let create;
  if (value.create !== void 0) {
    if (!Array.isArray(value.create) || value.create.length > ALARM_REGENERATE_MAX_TICKS) {
      return {
        status: "rejected",
        reason: `create must be an array of at most ${ALARM_REGENERATE_MAX_TICKS} recipe ids`
      };
    }
    if (!value.create.every(isBoundedString)) {
      return {
        status: "rejected",
        reason: "create entries must be non-empty strings"
      };
    }
    create = [...value.create];
  }
  let update;
  if (value.update !== void 0) {
    if (!Array.isArray(value.update) || value.update.length > ALARM_REGENERATE_MAX_TICKS) {
      return {
        status: "rejected",
        reason: `update must be an array of at most ${ALARM_REGENERATE_MAX_TICKS} entries`
      };
    }
    const ticks = [];
    for (const entry of value.update) {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
        return { status: "rejected", reason: "update entries must be objects" };
      }
      const tick = entry;
      if (!isBoundedString(tick.recipeId)) {
        return {
          status: "rejected",
          reason: "update.recipeId must be a non-empty string"
        };
      }
      if (tick.modes === void 0) {
        ticks.push({ recipeId: tick.recipeId });
        continue;
      }
      if (!Array.isArray(tick.modes) || tick.modes.length > ALARM_REGENERATE_MAX_MODES) {
        return {
          status: "rejected",
          reason: `update.modes must be an array of at most ${ALARM_REGENERATE_MAX_MODES} modes`
        };
      }
      if (!tick.modes.every(isBoundedString)) {
        return {
          status: "rejected",
          reason: "update.modes entries must be non-empty strings"
        };
      }
      ticks.push({
        recipeId: tick.recipeId,
        modes: [...tick.modes]
      });
    }
    update = ticks;
  }
  let retire;
  if (value.retire !== void 0) {
    if (!Array.isArray(value.retire) || value.retire.length > ALARM_REGENERATE_MAX_TICKS) {
      return {
        status: "rejected",
        reason: `retire must be an array of at most ${ALARM_REGENERATE_MAX_TICKS} recipe ids`
      };
    }
    if (!value.retire.every(isBoundedString)) {
      return {
        status: "rejected",
        reason: "retire entries must be non-empty strings"
      };
    }
    retire = [...value.retire];
  }
  if (value.expectedStoreDigest !== void 0 && !isBoundedString(value.expectedStoreDigest)) {
    return {
      status: "rejected",
      reason: "expectedStoreDigest must be a non-empty string when present"
    };
  }
  let itemDigests;
  if (value.expectedItemDigests !== void 0) {
    const raw2 = value.expectedItemDigests;
    if (typeof raw2 !== "object" || raw2 === null || Array.isArray(raw2)) {
      return {
        status: "rejected",
        reason: "expectedItemDigests must be an object of recipeId \u2192 digest"
      };
    }
    const entries = Object.entries(raw2);
    if (entries.length > ALARM_REGENERATE_MAX_TICKS) {
      return {
        status: "rejected",
        reason: `expectedItemDigests must name at most ${ALARM_REGENERATE_MAX_TICKS} recipes`
      };
    }
    const map = {};
    for (const [recipeId, digest] of entries) {
      if (!isBoundedString(recipeId) || !isBoundedString(digest)) {
        return {
          status: "rejected",
          reason: "expectedItemDigests entries must be non-empty strings"
        };
      }
      map[recipeId] = digest;
    }
    itemDigests = map;
  }
  return {
    status: "accepted",
    payload: {
      v: ALARM_REGENERATE_COMMAND_VERSION,
      requestId: value.requestId,
      action: value.action,
      mode: value.mode,
      ...create !== void 0 ? { create } : {},
      ...update !== void 0 ? { update } : {},
      ...retire !== void 0 ? { retire } : {},
      ...value.expectedStoreDigest !== void 0 ? { expectedStoreDigest: value.expectedStoreDigest } : {},
      ...itemDigests !== void 0 ? { expectedItemDigests: itemDigests } : {},
      sourceUserId: value.sourceUserId
    }
  };
}
function isBoundedString(value) {
  return typeof value === "string" && value.length > 0 && value.length <= ALARM_REGENERATE_ID_MAX;
}
function isAction(value) {
  return ALARM_REGENERATE_ACTIONS.includes(value);
}
function isRegenerateMode(value) {
  return ALARM_REGENERATE_MODES.includes(value);
}

// src/alarm-view.ts
var ALARM_SEVERITIES = [
  "emergency",
  "alarm",
  "warning",
  "caution"
];
function severityRank(severity) {
  const index = ALARM_SEVERITIES.indexOf(severity);
  return index === -1 ? ALARM_SEVERITIES.length : index;
}
function alarmSeverityLabel(severity) {
  switch (severity) {
    case "emergency":
      return "Emergency";
    case "alarm":
      return "Alarm";
    case "warning":
      return "Warning";
    case "caution":
      return "Caution";
    default:
      return "Alarm";
  }
}
var CANONICAL_MODES = [
  "Underway",
  "Anchored",
  "Moored",
  "Docked",
  "Maintenance",
  "Sailing"
];
var ALERT_STATE_META = {
  Disabled: { label: "Disabled", tone: "off", needsAttention: false },
  Normal: { label: "Normal", tone: "ok", needsAttention: false },
  Active: { label: "Active", tone: "danger", needsAttention: true },
  Silenced: { label: "Silenced", tone: "hushed", needsAttention: true },
  Acknowledged: {
    label: "Acknowledged",
    tone: "hushed",
    needsAttention: false
  },
  AwaitingAcknowledge: {
    label: "Back to normal \u2014 tap to acknowledge",
    tone: "attention",
    needsAttention: true
  }
};
function canAck(state) {
  return state === "Active" || state === "Silenced" || state === "AwaitingAcknowledge";
}
function canSilence(state) {
  return state === "Active";
}
function isSurfacedState(state) {
  return state !== "Normal" && state !== "Disabled";
}
function sortActiveForDisplay(rows) {
  return [...rows].sort((a, b) => {
    const rankA = a.severity ? severityRank(a.severity) : ALARM_SEVERITIES.length;
    const rankB = b.severity ? severityRank(b.severity) : ALARM_SEVERITIES.length;
    if (rankA !== rankB) return rankA - rankB;
    return parseTimeMs(b.lastTransitionAt) - parseTimeMs(a.lastTransitionAt);
  });
}
function parseTimeMs(iso2) {
  if (!iso2) return 0;
  const t = Date.parse(iso2);
  return Number.isFinite(t) ? t : 0;
}
function formatCountdown(deadlineIso, nowMs) {
  if (!deadlineIso) return "\u2014";
  const deadline = Date.parse(deadlineIso);
  if (!Number.isFinite(deadline)) return "\u2014";
  const remainingSec = Math.max(0, Math.ceil((deadline - nowMs) / 1e3));
  const hours = Math.floor(remainingSec / 3600);
  const minutes = Math.floor(remainingSec % 3600 / 60);
  const seconds = remainingSec % 60;
  if (hours > 0) return `${hours} h ${minutes} m`;
  if (minutes > 0) return `${minutes} m ${seconds} s`;
  return `${seconds} s`;
}
function numericThresholdFields(thresholds) {
  const fields = [];
  for (const [key, raw] of Object.entries(thresholds)) {
    if (typeof raw === "number" && Number.isFinite(raw)) {
      fields.push({ key, value: raw });
    }
  }
  fields.sort((a, b) => a.key.localeCompare(b.key));
  return fields;
}

// src/alarms-paused.ts
var ALARMS_PAUSED_RUNTIME_REASON = "paused_by_owner";
var RUNTIME_DISABLED_REASON_COPY = {
  [ALARMS_PAUSED_RUNTIME_REASON]: "paused by you"
};
function describeRuntimeDisabledReason(reason) {
  const trimmed = reason?.trim();
  if (trimmed === void 0 || trimmed.length === 0) return void 0;
  return RUNTIME_DISABLED_REASON_COPY[trimmed] ?? trimmed;
}
function isAlarmsPausedReason(reason) {
  return reason?.trim() === ALARMS_PAUSED_RUNTIME_REASON;
}
var ALARMS_PAUSE_COMMAND_VERSION = 1;
var ALARMS_PAUSE_COMMAND_ID_MAX = 200;
var ALARMS_PAUSE_COMMAND_USER_LABEL_MAX = 64;
function parseAlarmsPauseCommandPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== ALARMS_PAUSE_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported alarms pause command version (expected ${ALARMS_PAUSE_COMMAND_VERSION})`
    };
  }
  if (typeof value.paused !== "boolean") {
    return { status: "rejected", reason: "paused must be a boolean" };
  }
  if (typeof value.sourceUserId !== "string" || value.sourceUserId.length === 0 || value.sourceUserId.length > ALARMS_PAUSE_COMMAND_ID_MAX) {
    return {
      status: "rejected",
      reason: "sourceUserId must be a non-empty string"
    };
  }
  const label2 = typeof value.sourceUserLabel === "string" && value.sourceUserLabel.length > 0 ? value.sourceUserLabel.slice(0, ALARMS_PAUSE_COMMAND_USER_LABEL_MAX) : void 0;
  return {
    status: "accepted",
    payload: {
      v: ALARMS_PAUSE_COMMAND_VERSION,
      paused: value.paused,
      sourceUserId: value.sourceUserId,
      ...label2 !== void 0 ? { sourceUserLabel: label2 } : {}
    }
  };
}

// src/alert-command.ts
var ALERT_COMMAND_VERSION = 1;
var ALERT_COMMAND_ACTIONS = ["ack", "silence"];
var ALERT_COMMAND_ID_MAX = 200;
var ALERT_COMMAND_USER_LABEL_MAX = 64;
function parseAlertCommandPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== ALERT_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported alert command version (expected ${ALERT_COMMAND_VERSION})`
    };
  }
  if (!isAlertCommandAction(value.action)) {
    return { status: "rejected", reason: "missing or unknown action" };
  }
  if (typeof value.alertId !== "string" || value.alertId.length === 0 || value.alertId.length > ALERT_COMMAND_ID_MAX) {
    return {
      status: "rejected",
      action: value.action,
      reason: "alertId must be a non-empty string"
    };
  }
  if (typeof value.sourceUserId !== "string" || value.sourceUserId.length === 0 || value.sourceUserId.length > ALERT_COMMAND_ID_MAX) {
    return {
      status: "rejected",
      action: value.action,
      reason: "sourceUserId must be a non-empty string"
    };
  }
  const label2 = typeof value.sourceUserLabel === "string" && value.sourceUserLabel.length > 0 ? value.sourceUserLabel.slice(0, ALERT_COMMAND_USER_LABEL_MAX) : void 0;
  return {
    status: "accepted",
    payload: {
      v: ALERT_COMMAND_VERSION,
      action: value.action,
      alertId: value.alertId,
      sourceUserId: value.sourceUserId,
      ...label2 !== void 0 ? { sourceUserLabel: label2 } : {}
    }
  };
}
function isAlertCommandAction(value) {
  return ALERT_COMMAND_ACTIONS.includes(value);
}

// src/anchor-watch.ts
var ANCHOR_WATCH_COMMAND_VERSION = 1;
var ANCHOR_WATCH_COMMAND_ACTIONS = [
  "set",
  "radius",
  "clear",
  "move",
  "rode",
  "auto_arm"
];
var ANCHOR_WATCH_RADIUS_MIN_M = 5;
var ANCHOR_WATCH_RADIUS_MAX_M = 4e3;
var ANCHOR_WATCH_RODE_MIN_M = 1;
var ANCHOR_WATCH_RODE_MAX_M = 500;
var ANCHOR_AUTO_ARM_MODES = ["ask", "always", "never"];
var ANCHOR_COMMAND_STATUS_PHASES = [
  "accepted",
  "complete",
  "rejected"
];
function parseAnchorWatchCommandPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== ANCHOR_WATCH_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported anchor-watch command version (expected ${ANCHOR_WATCH_COMMAND_VERSION})`
    };
  }
  if (!isAnchorWatchCommandAction(value.action)) {
    return { status: "rejected", reason: "missing or unknown action" };
  }
  const radiusRequired = value.action === "radius";
  const radiusPresent = value.radiusM !== void 0;
  if (radiusRequired || radiusPresent) {
    if (!isFiniteInRange(
      value.radiusM,
      ANCHOR_WATCH_RADIUS_MIN_M,
      ANCHOR_WATCH_RADIUS_MAX_M
    )) {
      return {
        status: "rejected",
        action: value.action,
        reason: radiusRequired && !radiusPresent ? "radiusM is required for action=radius" : `radiusM must be a number in [${ANCHOR_WATCH_RADIUS_MIN_M}, ${ANCHOR_WATCH_RADIUS_MAX_M}] meters`
      };
    }
  }
  const fixRequired = value.action === "move";
  const latPresent = value.lat !== void 0;
  const lonPresent = value.lon !== void 0;
  if (fixRequired || latPresent || lonPresent) {
    if (!latPresent || !lonPresent) {
      return {
        status: "rejected",
        action: value.action,
        reason: fixRequired ? "lat and lon are required for action=move" : "lat and lon must be given together"
      };
    }
    if (!isFiniteInRange(value.lat, -90, 90) || !isFiniteInRange(value.lon, -180, 180)) {
      return {
        status: "rejected",
        action: value.action,
        reason: "lat must be a number in [-90, 90] and lon a number in [-180, 180] degrees"
      };
    }
  }
  const rodeRequired = value.action === "rode";
  const rodePresent = value.rodeM !== void 0;
  if (rodeRequired || rodePresent) {
    if (!isFiniteInRange(
      value.rodeM,
      ANCHOR_WATCH_RODE_MIN_M,
      ANCHOR_WATCH_RODE_MAX_M
    )) {
      return {
        status: "rejected",
        action: value.action,
        reason: rodeRequired && !rodePresent ? "rodeM is required for action=rode" : `rodeM must be a number in [${ANCHOR_WATCH_RODE_MIN_M}, ${ANCHOR_WATCH_RODE_MAX_M}] meters`
      };
    }
  }
  const autoArmRequired = value.action === "auto_arm";
  const autoArmPresent = value.autoArm !== void 0;
  if (autoArmRequired || autoArmPresent) {
    if (!isAnchorAutoArmMode(value.autoArm)) {
      return {
        status: "rejected",
        action: value.action,
        reason: autoArmRequired && !autoArmPresent ? "autoArm is required for action=auto_arm" : `autoArm must be one of ${ANCHOR_AUTO_ARM_MODES.join(", ")}`
      };
    }
  }
  return {
    status: "accepted",
    payload: {
      v: ANCHOR_WATCH_COMMAND_VERSION,
      action: value.action,
      ...typeof value.radiusM === "number" ? { radiusM: value.radiusM } : {},
      ...typeof value.lat === "number" && typeof value.lon === "number" ? { lat: value.lat, lon: value.lon } : {},
      ...typeof value.rodeM === "number" ? { rodeM: value.rodeM } : {},
      ...isAnchorAutoArmMode(value.autoArm) ? { autoArm: value.autoArm } : {}
    }
  };
}
function isAnchorWatchCommandAction(value) {
  return ANCHOR_WATCH_COMMAND_ACTIONS.includes(value);
}
function isAnchorAutoArmMode(value) {
  return ANCHOR_AUTO_ARM_MODES.includes(value);
}
function isFiniteInRange(value, min, max) {
  return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
}

// src/map-location-context.ts
var MAP_LOCATION_SUBJECT_KINDS = [
  "boat",
  "anchor",
  "friend",
  "recorded"
];
var MAP_LOCATION_LOOKUPS = [
  "found",
  "partial",
  "none_within_radius",
  "unavailable"
];
var MAP_PLACE_GROUPS = ["marina", "water", "island"];
var MAP_PLACE_GROUP_KINDS = {
  marina: ["marina", "small_craft_facility"],
  water: [
    "anchorage",
    "cove",
    "bay",
    "harbor",
    "basin",
    "lagoon",
    "inlet",
    "fjord"
  ],
  island: ["island", "archipelago"]
};
var MAP_PLACE_KIND_LABELS = {
  marina: "Marina",
  small_craft_facility: "Small-craft facility",
  anchorage: "Anchorage",
  cove: "Cove",
  bay: "Bay",
  harbor: "Harbour",
  basin: "Basin",
  lagoon: "Lagoon",
  inlet: "Inlet",
  fjord: "Fjord",
  island: "Island",
  archipelago: "Islands",
  port: "Port",
  populated_place: "Town"
};
var MAP_PLACE_NAME_MAX = 160;
var MAP_LOCATION_ATTRIBUTIONS_MAX = 8;
var MAP_LOCATION_ATTRIBUTION_TEXT_MAX = 200;
var MAP_LOCATION_ATTRIBUTION_URL_MAX = 300;
var MAP_CONTEXT_REUSE_RADIUS_M = 50;
var MAP_CONTEXT_REFRESH_AGE_MS = 6e4;
var MAP_CONTEXT_MIN_REQUEST_INTERVAL_MS = 1e4;
function isObj(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function finite(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function isIso(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}
function isLat(value) {
  return finite(value) && value >= -90 && value <= 90;
}
function isLon(value) {
  return finite(value) && value >= -180 && value <= 180;
}
var UNSAFE_TEXT = new RegExp(
  "[\\u0000-\\u001f\\u007f-\\u009f\\u2028\\u2029\\u200e\\u200f\\u202a-\\u202e\\u2066-\\u2069]"
);
function isMapDisplayText(value, max) {
  return typeof value === "string" && value.length > 0 && value.length <= max && value.trim() === value && !UNSAFE_TEXT.test(value);
}
function parseNamedPlace(value, group) {
  if (value === null) return null;
  if (!isObj(value)) return void 0;
  const { name, nameNorm, kind, lat, lon, distanceM } = value;
  if (!isMapDisplayText(name, MAP_PLACE_NAME_MAX) || !isMapDisplayText(nameNorm, MAP_PLACE_NAME_MAX) || typeof kind !== "string" || !MAP_PLACE_GROUP_KINDS[group].includes(kind) || !isLat(lat) || !isLon(lon) || !finite(distanceM) || distanceM < 0) {
    return void 0;
  }
  return { name, nameNorm, kind, lat, lon, distanceM };
}
function parsePortOrTown(value) {
  if (value === null) return null;
  if (!isObj(value)) return void 0;
  const { name, label: label2, kind, lat, lon, distanceM, at } = value;
  if (!isMapDisplayText(name, MAP_PLACE_NAME_MAX) || !isMapDisplayText(label2, MAP_PLACE_NAME_MAX) || !isMapDisplayText(kind, MAP_PLACE_NAME_MAX) || !isLat(lat) || !isLon(lon) || !finite(distanceM) || distanceM < 0 || typeof at !== "boolean") {
    return void 0;
  }
  return { name, label: label2, kind, lat, lon, distanceM, at };
}
function isSafeUrl(value) {
  if (typeof value !== "string" || value.length > MAP_LOCATION_ATTRIBUTION_URL_MAX) {
    return false;
  }
  if (!/^https:\/\/[^\s"'<>\\]+$/.test(value)) return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}
function parseAttributions(value) {
  if (!Array.isArray(value) || value.length > MAP_LOCATION_ATTRIBUTIONS_MAX) {
    return void 0;
  }
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const raw of value) {
    if (!isObj(raw)) return void 0;
    if (!isMapDisplayText(raw.text, MAP_LOCATION_ATTRIBUTION_TEXT_MAX)) {
      return void 0;
    }
    if (raw.url !== null && !isSafeUrl(raw.url)) return void 0;
    if (seen.has(raw.text)) return void 0;
    seen.add(raw.text);
    out.push({ text: raw.text, url: raw.url });
  }
  return out;
}
function parseMapLocationContext(value) {
  if (!isObj(value) || !isObj(value.subject)) return null;
  const s = value.subject;
  const kind = MAP_LOCATION_SUBJECT_KINDS.find((k) => k === s.kind);
  if (kind === void 0 || !isLat(s.lat) || !isLon(s.lon) || !isIso(s.at)) {
    return null;
  }
  if (!isIso(value.resolvedAt)) return null;
  const lookup = MAP_LOCATION_LOOKUPS.find((l) => l === value.lookup);
  if (lookup === void 0) return null;
  const marina = parseNamedPlace(value.marina, "marina");
  const water = parseNamedPlace(value.water, "water");
  const island = parseNamedPlace(value.island, "island");
  const portOrTown = parsePortOrTown(value.portOrTown);
  const attributions = parseAttributions(value.attributions);
  if (marina === void 0 || water === void 0 || island === void 0 || portOrTown === void 0 || attributions === void 0) {
    return null;
  }
  const named = marina !== null || water !== null || island !== null || portOrTown !== null;
  const consistent = lookup === "found" || lookup === "partial" ? named : !named;
  if (!consistent) return null;
  return {
    subject: { kind, lat: s.lat, lon: s.lon, at: s.at },
    resolvedAt: value.resolvedAt,
    lookup,
    marina,
    water,
    island,
    portOrTown,
    attributions
  };
}
var MAP_CAPTION_NEAR = "Near";
var MAP_CAPTION_SEPARATOR = " \xB7 ";
function mapCaption(context, legacyPlace) {
  if (context === null || context === void 0) {
    if (legacyPlace === null || legacyPlace === void 0) {
      return { source: "none", names: [], lookup: null };
    }
    return {
      source: "portOrTown",
      names: [portOrTownName(legacyPlace)],
      lookup: null
    };
  }
  const names = [];
  const seen = /* @__PURE__ */ new Set();
  for (const group of MAP_PLACE_GROUPS) {
    const place = context[group];
    if (place === null) continue;
    if (seen.has(place.nameNorm)) continue;
    seen.add(place.nameNorm);
    names.push({
      group,
      name: place.name,
      kind: place.kind,
      lat: place.lat,
      lon: place.lon,
      distanceM: place.distanceM
    });
  }
  if (names.length > 0) {
    return { source: "marine", names, lookup: context.lookup };
  }
  if (context.portOrTown !== null) {
    return {
      source: "portOrTown",
      names: [portOrTownName(context.portOrTown)],
      lookup: context.lookup
    };
  }
  return { source: "none", names: [], lookup: context.lookup };
}
function portOrTownName(place) {
  return {
    group: "portOrTown",
    name: place.name,
    kind: place.kind,
    lat: place.lat,
    lon: place.lon,
    distanceM: place.distanceM
  };
}
function mapMarineCaptionText(caption) {
  if (caption.source !== "marine") return null;
  return `${MAP_CAPTION_NEAR} ${caption.names.map((n) => n.name).join(MAP_CAPTION_SEPARATOR)}`;
}
function mapMarineCaptionSpoken(caption) {
  if (caption.source !== "marine") return null;
  return `${MAP_CAPTION_NEAR} ${caption.names.map((n) => n.name).join(", ")}`;
}
var EARTH_RADIUS_M = 63710088e-1;
function mapDistanceM(a, b) {
  const rad = (d) => d * Math.PI / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
function mapContextRefresh(input) {
  const { held, fix, nowMs } = input;
  const mayAsk = !input.inFlight && (input.lastRequestAtMs === null || nowMs - input.lastRequestAtMs >= MAP_CONTEXT_MIN_REQUEST_INTERVAL_MS);
  if (held === null) {
    return {
      current: false,
      displacementM: null,
      request: mayAsk,
      why: "no_context"
    };
  }
  if (held.subjectKey !== input.subjectKey) {
    return {
      current: false,
      displacementM: null,
      request: mayAsk,
      why: "other_subject"
    };
  }
  const age = nowMs - Date.parse(held.context.resolvedAt);
  const aged = !(age < MAP_CONTEXT_REFRESH_AGE_MS);
  if (fix === null) {
    return {
      current: true,
      displacementM: null,
      request: false,
      why: "no_fix"
    };
  }
  const displacementM = mapDistanceM(held.context.subject, fix);
  if (displacementM > MAP_CONTEXT_REUSE_RADIUS_M) {
    return { current: false, displacementM, request: mayAsk, why: "moved" };
  }
  return {
    current: true,
    displacementM,
    request: aged && mayAsk,
    why: aged ? "aged" : "fresh"
  };
}
function acceptMapContext(input) {
  const { incoming, held, fix } = input;
  if (incoming.subjectKey !== input.subjectKey) return false;
  if (fix !== null && mapDistanceM(incoming.context.subject, fix) > MAP_CONTEXT_REUSE_RADIUS_M) {
    return false;
  }
  if (held !== null && held.subjectKey === input.subjectKey) {
    const heldAt = Date.parse(held.context.subject.at);
    const incomingAt = Date.parse(incoming.context.subject.at);
    if (incomingAt < heldAt) return false;
    if (incomingAt === heldAt && Date.parse(incoming.context.resolvedAt) < Date.parse(held.context.resolvedAt)) {
      return false;
    }
  }
  return true;
}

// src/anchor-watch-status.ts
var ANCHOR_WATCH_ORIGINS = [
  "user",
  "cloud",
  "maretron",
  "signalk",
  "timezero",
  "auto"
];
var ANCHOR_WATCH_STATUS_CODES = [
  "off",
  "holding",
  "drag_suspected",
  "outside",
  "alarm",
  "unknown"
];
var ANCHOR_WATCH_REASONS = [
  /** The boat's `anchor_drift` alarm is sounding (Active or Silenced). */
  "anchor_alarm",
  /** The distance is at or past the radius. */
  "outside_radius",
  /** The distance is at or past 85% of the radius. */
  "near_radius",
  /** The boat's own swing fit flags a possible drag. */
  "estimate_drag_suspected",
  /** …because the swing is wider than the rode's band allows (`beyond_r_max`). */
  "estimate_beyond_swing_band",
  /** …because the centre of the swing is moving (`centre_migrating`). */
  "estimate_centre_migrating",
  /** The cloud has not heard from the boat inside the contact window. */
  "boat_offline",
  /** The boat is talking but its distance from the anchor is too old. */
  "distance_stale",
  /** A watch is armed but the boat reports no distance from it. */
  "no_distance",
  /** A watch is armed but the boat reports no radius for it. */
  "no_radius"
];
function isObj2(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function nullableNum(value) {
  if (value === null) return null;
  return typeof value === "number" && Number.isFinite(value) ? value : void 0;
}
function nullableIso(value) {
  if (value === null) return null;
  return typeof value === "string" && Number.isFinite(Date.parse(value)) ? value : void 0;
}
function oneOf(list2, value) {
  return list2.find((item) => item === value);
}
function parseAnchorWatchStatus(value) {
  if (!isObj2(value)) return null;
  const status = oneOf(ANCHOR_WATCH_STATUS_CODES, value.status);
  if (status === void 0 || typeof value.enabled !== "boolean") return null;
  if (typeof value.stale !== "boolean" || !Array.isArray(value.reasons)) {
    return null;
  }
  let anchor;
  if (value.anchor === null) anchor = null;
  else if (isObj2(value.anchor)) {
    const lat = nullableNum(value.anchor.lat);
    const lon = nullableNum(value.anchor.lon);
    anchor = typeof lat === "number" && typeof lon === "number" && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180 ? { lat, lon } : void 0;
  }
  let estimate;
  if (value.estimate === null) estimate = null;
  else if (isObj2(value.estimate)) {
    const confidence = oneOf(
      ["high", "medium", "low"],
      value.estimate.confidence
    );
    estimate = typeof value.estimate.dragSuspected === "boolean" && confidence !== void 0 ? { dragSuspected: value.estimate.dragSuspected, confidence } : void 0;
  }
  let depthBand;
  if (value.depthBand === null) depthBand = null;
  else if (isObj2(value.depthBand)) {
    const lowM = nullableNum(value.depthBand.lowM);
    const highM = nullableNum(value.depthBand.highM);
    depthBand = typeof lowM === "number" && typeof highM === "number" && highM >= lowM ? { lowM, highM } : void 0;
  }
  const origin = value.origin === null ? null : oneOf(ANCHOR_WATCH_ORIGINS, value.origin);
  const radiusM2 = nullableNum(value.radiusM);
  const distanceM = nullableNum(value.distanceM);
  const bearingDeg = nullableNum(value.bearingDeg);
  const enabledAt = nullableIso(value.enabledAt);
  const sampledAt = nullableIso(value.sampledAt);
  if (anchor === void 0 || estimate === void 0 || depthBand === void 0 || origin === void 0 || radiusM2 === void 0 || distanceM === void 0 || bearingDeg === void 0 || enabledAt === void 0 || sampledAt === void 0) {
    return null;
  }
  const reasons = [];
  for (const raw of value.reasons) {
    const reason = oneOf(ANCHOR_WATCH_REASONS, raw);
    if (reason !== void 0 && !reasons.includes(reason)) reasons.push(reason);
  }
  const parsed = {
    enabled: value.enabled,
    anchor,
    radiusM: radiusM2,
    enabledAt,
    origin,
    distanceM,
    bearingDeg,
    status,
    reasons,
    estimate,
    depthBand,
    sampledAt,
    stale: value.stale
  };
  if (value.locationContext !== void 0) {
    parsed.locationContext = anchor === null ? null : parseMapLocationContext(value.locationContext);
  }
  return parsed;
}

// src/battery-chemistry.ts
var BATTERY_CHEMISTRIES = ["lithium", "lead_acid"];
function isBatteryChemistry(raw) {
  return BATTERY_CHEMISTRIES.includes(raw);
}
var BATTERY_CHEMISTRY_SOURCES = ["owner", "reported", "bms"];
var BATTERY_CHEMISTRY_SETTINGS_KEY = "batteryChemistry";
var BANK_PATH = /^electrical\.batteries\.[A-Za-z0-9_-]{1,24}\.voltage$/;
function isBatteryBankPath(raw) {
  return typeof raw === "string" && BANK_PATH.test(raw);
}
var BATTERY_CHEMISTRY_COMMAND_VERSION = 1;
var BATTERY_CHEMISTRY_COMMAND_ID_MAX = 200;
var BATTERY_CHEMISTRY_COMMAND_USER_LABEL_MAX = 64;
function parseBatteryChemistryCommandPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== BATTERY_CHEMISTRY_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported battery chemistry command version (expected ${BATTERY_CHEMISTRY_COMMAND_VERSION})`
    };
  }
  if (!isBatteryBankPath(value.bank)) {
    return {
      status: "rejected",
      reason: "bank must be an electrical.batteries.<instance>.voltage path"
    };
  }
  if (value.chemistry !== null && !isBatteryChemistry(value.chemistry)) {
    return {
      status: "rejected",
      reason: `chemistry must be one of ${BATTERY_CHEMISTRIES.join(", ")}, or null`
    };
  }
  if (typeof value.sourceUserId !== "string" || value.sourceUserId.length === 0 || value.sourceUserId.length > BATTERY_CHEMISTRY_COMMAND_ID_MAX) {
    return {
      status: "rejected",
      reason: "sourceUserId must be a non-empty string"
    };
  }
  const label2 = typeof value.sourceUserLabel === "string" && value.sourceUserLabel.length > 0 ? value.sourceUserLabel.slice(0, BATTERY_CHEMISTRY_COMMAND_USER_LABEL_MAX) : void 0;
  return {
    status: "accepted",
    payload: {
      v: BATTERY_CHEMISTRY_COMMAND_VERSION,
      bank: value.bank,
      chemistry: value.chemistry,
      sourceUserId: value.sourceUserId,
      ...label2 !== void 0 ? { sourceUserLabel: label2 } : {}
    }
  };
}
var BATTERY_CHEMISTRY_SNAPSHOT_VERSION = 1;
var BATTERY_CHEMISTRY_SNAPSHOT_MAX_BANKS = 32;
var BATTERY_CHEMISTRY_BANK_NAME_MAX = 64;
function effectiveBatteryChemistry(bank) {
  return bank.override ?? bank.detected?.chemistry ?? null;
}
function parseDetected(raw) {
  if (raw === null) return null;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return void 0;
  const d = raw;
  if (!isBatteryChemistry(d.chemistry)) return void 0;
  if (d.source !== "reported" && d.source !== "bms") return void 0;
  if (typeof d.path !== "string" || d.path.length === 0 || d.path.length > 200) {
    return void 0;
  }
  return { chemistry: d.chemistry, source: d.source, path: d.path };
}
function parseBatteryChemistrySnapshot(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "snapshot must be an object" };
  }
  const value = raw;
  if (value.v !== BATTERY_CHEMISTRY_SNAPSHOT_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported battery chemistry snapshot version (expected ${BATTERY_CHEMISTRY_SNAPSHOT_VERSION})`
    };
  }
  if (!Array.isArray(value.banks) || value.banks.length > BATTERY_CHEMISTRY_SNAPSHOT_MAX_BANKS) {
    return {
      status: "rejected",
      reason: `banks must be an array of at most ${BATTERY_CHEMISTRY_SNAPSHOT_MAX_BANKS}`
    };
  }
  const banks = [];
  const seen = /* @__PURE__ */ new Set();
  for (const entry of value.banks) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return { status: "rejected", reason: "each bank must be an object" };
    }
    const b = entry;
    if (!isBatteryBankPath(b.bank) || seen.has(b.bank)) {
      return { status: "rejected", reason: "bank must be a unique bank path" };
    }
    if (b.override !== null && !isBatteryChemistry(b.override)) {
      return { status: "rejected", reason: `override is invalid on ${b.bank}` };
    }
    const detected = parseDetected(b.detected);
    if (detected === void 0) {
      return { status: "rejected", reason: `detected is invalid on ${b.bank}` };
    }
    const nominalVolts = b.nominalVolts === 12 || b.nominalVolts === 24 || b.nominalVolts === 48 ? b.nominalVolts : null;
    const name = typeof b.name === "string" && b.name.trim().length > 0 ? b.name.trim().slice(0, BATTERY_CHEMISTRY_BANK_NAME_MAX) : null;
    seen.add(b.bank);
    banks.push({
      bank: b.bank,
      nominalVolts,
      name,
      override: b.override,
      detected
    });
  }
  banks.sort((a, b) => a.bank.localeCompare(b.bank));
  return {
    status: "accepted",
    snapshot: { v: BATTERY_CHEMISTRY_SNAPSHOT_VERSION, banks }
  };
}

// src/digest.ts
function sortKeysDeep(value) {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([k, v]) => [k, sortKeysDeep(v)])
    );
  }
  return value;
}
function canonicalJson(value) {
  return JSON.stringify(sortKeysDeep(value));
}
var K = new Uint32Array([
  1116352408,
  1899447441,
  3049323471,
  3921009573,
  961987163,
  1508970993,
  2453635748,
  2870763221,
  3624381080,
  310598401,
  607225278,
  1426881987,
  1925078388,
  2162078206,
  2614888103,
  3248222580,
  3835390401,
  4022224774,
  264347078,
  604807628,
  770255983,
  1249150122,
  1555081692,
  1996064986,
  2554220882,
  2821834349,
  2952996808,
  3210313671,
  3336571891,
  3584528711,
  113926993,
  338241895,
  666307205,
  773529912,
  1294757372,
  1396182291,
  1695183700,
  1986661051,
  2177026350,
  2456956037,
  2730485921,
  2820302411,
  3259730800,
  3345764771,
  3516065817,
  3600352804,
  4094571909,
  275423344,
  430227734,
  506948616,
  659060556,
  883997877,
  958139571,
  1322822218,
  1537002063,
  1747873779,
  1955562222,
  2024104815,
  2227730452,
  2361852424,
  2428436474,
  2756734187,
  3204031479,
  3329325298
]);
function rotr(x, n) {
  return (x >>> n | x << 32 - n) >>> 0;
}
function toHex8(x) {
  return (x >>> 0).toString(16).padStart(8, "0");
}
function sha256Hex(message) {
  const msg = new TextEncoder().encode(message);
  const bitLen = msg.length * 8;
  const withOne = msg.length + 1;
  const pad = (56 - withOne % 64 + 64) % 64;
  const total = withOne + pad + 8;
  const buf = new Uint8Array(total);
  buf.set(msg);
  buf[msg.length] = 128;
  const dv = new DataView(buf.buffer);
  dv.setUint32(total - 4, bitLen >>> 0, false);
  dv.setUint32(total - 8, Math.floor(bitLen / 4294967296), false);
  let h0 = 1779033703, h1 = 3144134277, h2 = 1013904242, h3 = 2773480762;
  let h4 = 1359893119, h5 = 2600822924, h6 = 528734635, h7 = 1541459225;
  const w = new Uint32Array(64);
  for (let i = 0; i < total; i += 64) {
    for (let t = 0; t < 16; t++) w[t] = dv.getUint32(i + t * 4, false);
    for (let t = 16; t < 64; t++) {
      const s0 = rotr(w[t - 15], 7) ^ rotr(w[t - 15], 18) ^ w[t - 15] >>> 3;
      const s1 = rotr(w[t - 2], 17) ^ rotr(w[t - 2], 19) ^ w[t - 2] >>> 10;
      w[t] = w[t - 16] + s0 + w[t - 7] + s1 >>> 0;
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let t = 0; t < 64; t++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = e & f ^ ~e & g;
      const temp1 = h + S1 + ch + K[t] + w[t] >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = a & b ^ a & c ^ b & c;
      const temp2 = S0 + maj >>> 0;
      h = g;
      g = f;
      f = e;
      e = d + temp1 >>> 0;
      d = c;
      c = b;
      b = a;
      a = temp1 + temp2 >>> 0;
    }
    h0 = h0 + a >>> 0;
    h1 = h1 + b >>> 0;
    h2 = h2 + c >>> 0;
    h3 = h3 + d >>> 0;
    h4 = h4 + e >>> 0;
    h5 = h5 + f >>> 0;
    h6 = h6 + g >>> 0;
    h7 = h7 + h >>> 0;
  }
  return toHex8(h0) + toHex8(h1) + toHex8(h2) + toHex8(h3) + toHex8(h4) + toHex8(h5) + toHex8(h6) + toHex8(h7);
}

// src/equipment.ts
var EQUIPMENT_FAMILY_ORDER = [
  "engine",
  "gearbox",
  "tank",
  "battery"
];
var EQUIPMENT_FAMILIES = [
  {
    family: "engine",
    key: "propulsion.<i>",
    prefixes: ["propulsion.<i>.", "notifications.propulsion.<i>."]
  },
  {
    family: "gearbox",
    key: "propulsion.<i>.transmission",
    prefixes: [
      "propulsion.<i>.transmission.",
      "notifications.propulsion.<i>.transmission."
    ]
  },
  {
    family: "tank",
    key: "tanks.<fluid>.<i>",
    prefixes: ["tanks.<fluid>.<i>."]
  },
  {
    family: "battery",
    key: "electrical.batteries.<i>",
    prefixes: ["electrical.batteries.<i>."]
  }
];
function equipmentInstanceOf(path) {
  let seg = path.split(".");
  if (seg.some((s) => s.length === 0)) return null;
  if (seg[0] === "notifications" && seg[1] === "propulsion") seg = seg.slice(1);
  switch (seg[0]) {
    case "propulsion": {
      if (seg.length < 3) return null;
      const engine = `propulsion.${seg[1]}`;
      if (seg[2] === "transmission" && seg.length >= 4) {
        return { family: "gearbox", key: `${engine}.transmission` };
      }
      return { family: "engine", key: engine };
    }
    case "tanks": {
      if (seg.length < 4 || path.startsWith("notifications.")) return null;
      return { family: "tank", key: `tanks.${seg[1]}.${seg[2]}` };
    }
    case "electrical": {
      if (seg[1] !== "batteries" || seg.length < 4) return null;
      if (path.startsWith("notifications.")) return null;
      return { family: "battery", key: `electrical.batteries.${seg[2]}` };
    }
    default:
      return null;
  }
}
var CHARGING_SOURCE_FAMILIES = {
  chargers: "charger",
  solar: "solar",
  inverters: "inverter",
  alternator: "alternator"
};
function namedInstanceOf(path) {
  const equipment = equipmentInstanceOf(path);
  if (equipment !== null) return equipment;
  const seg = path.split(".");
  if (seg[0] !== "electrical" || seg.length < 4) return null;
  if (seg.some((s) => s.length === 0)) return null;
  if (seg[1] === "ac") {
    return { family: "acBus", key: `electrical.ac.${seg[2]}` };
  }
  const source = CHARGING_SOURCE_FAMILIES[seg[1]];
  if (source !== void 0) {
    return { family: source, key: `electrical.${seg[1]}.${seg[2]}` };
  }
  return null;
}
function equipmentFamilyOfKey(key) {
  const ref = equipmentInstanceOf(`${key}.x`);
  return ref !== null && ref.key === key ? ref.family : null;
}
function pathBelongsToInstance(path, key) {
  return equipmentInstanceOf(path)?.key === key;
}
function isUndefinedEquipmentKey(key) {
  return key.split(".").includes("undefined");
}
var DATA_ANSWER_KINDS = [
  "presence",
  "make_model"
];
var PRESENCE_ANSWERS = [
  "not_on_boat",
  "real"
];
var GEAR_FAMILY_ORDER = [
  "engine",
  "gearbox",
  "generator",
  "battery",
  "charger",
  "inverter"
];
var GEAR_ELECTRICAL_FAMILIES = {
  generators: "generator",
  chargers: "charger",
  inverters: "inverter"
};
var GEAR_SEGMENT = /^[A-Za-z0-9_-]{1,40}$/;
function gearFamilyOfKey(key) {
  const seg = key.split(".");
  if (seg.length === 3 && seg[0] === "electrical") {
    const family = GEAR_ELECTRICAL_FAMILIES[seg[1]];
    if (family !== void 0 && GEAR_SEGMENT.test(seg[2]) && seg[2] !== "undefined") {
      return family;
    }
  }
  const equipment = equipmentFamilyOfKey(key);
  if (equipment === null || equipment === "tank") return null;
  if (isUndefinedEquipmentKey(key)) return null;
  const instance = seg[equipment === "battery" ? 2 : 1];
  return instance !== void 0 && GEAR_SEGMENT.test(instance) ? equipment : null;
}
var GEAR_MAKE_MODEL_MAX = 60;
var CONTROL = /[\u0000-\u001f\u007f-\u009f]/u;
function gearWords(value) {
  if (typeof value !== "string") return null;
  const words = value.trim().replace(/\s+/gu, " ");
  if (words.length < 1 || words.length > GEAR_MAKE_MODEL_MAX) return null;
  return CONTROL.test(words) ? null : words;
}
function gearMakeModelFrom(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const { make, model } = value;
  const m = gearWords(make);
  const n = gearWords(model);
  return m === null || n === null ? null : { make: m, model: n };
}
function sameGearMakeModel(a, b) {
  if (a === null || b === null) return a === b;
  return a.make === b.make && a.model === b.model;
}
var EXAMPLE_PATHS = [
  "propulsion.port.revolutions",
  "propulsion.17.revolutions",
  "propulsion.1.revolutions",
  "propulsion.port.transmission.oilPressure",
  "propulsion.port.transmission",
  "propulsion.port",
  "notifications.propulsion.port.overTemperature",
  "notifications.propulsion.port.transmission.overTemperature",
  "tanks.fuel.0.currentLevel",
  "tanks.undefined.undefined.currentLevel",
  "tanks.fuel.0",
  "electrical.batteries.house.voltage",
  "electrical.batteries.200.voltage",
  "electrical.batteries.1.capacity.stateOfCharge",
  "electrical.solar.278.panelPower",
  "environment.wind.speedApparent",
  "notifications.nmea.alarm.engine.1.42"
];
function equipmentFixture() {
  return {
    version: 1,
    families: EQUIPMENT_FAMILIES.map((f) => ({
      ...f,
      prefixes: [...f.prefixes]
    })),
    examples: EXAMPLE_PATHS.map((path) => ({
      path,
      instance: equipmentInstanceOf(path)
    }))
  };
}

// src/boat-names.ts
var BOAT_NAME_MAX = 80;
var BOAT_NAME_SUBJECT_MAX = 200;
var BOAT_NAMES_VERSION = 1;
var BOAT_NAMES_MAX_ENTRIES = 16384;
function object(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function boatNameFrom(value) {
  if (typeof value !== "string") return null;
  const name = value.normalize("NFC").replace(/\s+/g, " ").trim();
  if ([...name].length < 1 || [...name].length > BOAT_NAME_MAX) return null;
  if (/[\u0000-\u001f\u007f-\u009f]/.test(name)) return null;
  return name;
}
function parseBoatNameTarget(value) {
  if (!object(value)) return null;
  const { kind, subject: subject2, channel } = value;
  if (typeof subject2 !== "string" || subject2.length < 1 || subject2.length > BOAT_NAME_SUBJECT_MAX || subject2.trim() !== subject2)
    return null;
  if (kind === "equipment") {
    if (Object.keys(value).some((k) => k !== "kind" && k !== "subject"))
      return null;
    return equipmentFamilyOfKey(subject2) === null ? null : { kind, subject: subject2 };
  }
  if (kind === "device") {
    if (Object.keys(value).some((k) => k !== "kind" && k !== "subject"))
      return null;
    return { kind, subject: subject2 };
  }
  if (kind !== "channel" || Object.keys(value).some((k) => !["kind", "subject", "channel"].includes(k)))
    return null;
  if (typeof channel !== "number" || !Number.isInteger(channel) || channel < 1 || channel > 256)
    return null;
  return { kind, subject: subject2, channel };
}
function boatNameTargetKey(target) {
  return target.kind === "channel" ? `channel:${target.channel}:${target.subject}` : `${target.kind}:${target.subject}`;
}
function boatNameAnswerSubject(target) {
  return target.kind === "equipment" ? target.subject : boatNameTargetKey(target);
}
function boatNameTargetFromAnswerSubject(subject2) {
  const channel = /^channel:(\d+):(.+)$/.exec(subject2);
  return channel ? parseBoatNameTarget({
    kind: "channel",
    subject: channel[2],
    channel: Number(channel[1])
  }) : parseBoatNameTarget({ kind: "equipment", subject: subject2 });
}
function boatNamesDigest(names) {
  return sha256Hex(
    canonicalJson({
      v: BOAT_NAMES_VERSION,
      equipment: names.equipment,
      devices: names.devices,
      channels: names.channels
    })
  );
}
function parseBoatNamesSnapshot(value) {
  if (!object(value)) return null;
  const { v, boatId, revision, digest, equipment, devices, channels } = value;
  if (v !== BOAT_NAMES_VERSION || typeof boatId !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    boatId
  ) || typeof revision !== "number" || !Number.isSafeInteger(revision) || revision < 0 || typeof digest !== "string" || !/^[0-9a-f]{64}$/.test(digest) || !object(equipment) || !object(devices) || !object(channels) || Object.keys(value).some(
    (k) => ![
      "v",
      "boatId",
      "revision",
      "digest",
      "equipment",
      "devices",
      "channels"
    ].includes(k)
  ))
    return null;
  let count2 = 0;
  for (const [kind, names] of [
    ["equipment", equipment],
    ["device", devices]
  ]) {
    for (const [subject2, name] of Object.entries(names)) {
      if (++count2 > BOAT_NAMES_MAX_ENTRIES || parseBoatNameTarget({ kind, subject: subject2 }) === null || boatNameFrom(name) !== name)
        return null;
    }
  }
  for (const [subject2, rows] of Object.entries(channels)) {
    if (!object(rows)) return null;
    for (const [channel, name] of Object.entries(rows)) {
      if (++count2 > BOAT_NAMES_MAX_ENTRIES || !/^[1-9]\d*$/.test(channel) || parseBoatNameTarget({
        kind: "channel",
        subject: subject2,
        channel: Number(channel)
      }) === null || boatNameFrom(name) !== name)
        return null;
    }
  }
  const result = {
    v,
    boatId,
    revision,
    digest,
    equipment,
    devices,
    channels
  };
  return boatNamesDigest({
    equipment: result.equipment,
    devices: result.devices,
    channels: result.channels
  }) === digest ? result : null;
}

// src/boat-systems.ts
var BOAT_SYSTEMS = [
  "electrical",
  "electronics",
  "mechanical",
  "plumbing"
];
var systemSet = (primary, ...secondary) => Object.freeze({ primary, secondary: Object.freeze(secondary) });
var NEO = systemSet(null);
var BOAT_SYSTEM_PATH_RULES = [
  // ---- Electrical --------------------------------------------------------------
  // The alternator sits on the engine, but it is a charging source.
  {
    prefix: "electrical.alternator",
    systems: systemSet("electrical", "mechanical")
  },
  // A switch channel is electrical unless its label names what it senses
  // (`SWITCH_CHANNEL_LABEL_RULES`).
  {
    prefix: "electrical.switches",
    systems: systemSet("electrical"),
    switchChannel: true
  },
  // Batteries, chargers, inverters, solar, shore power, AC and DC, the GX.
  { prefix: "electrical", systems: systemSet("electrical") },
  // ---- Mechanical --------------------------------------------------------------
  // The engine's own alternator reading (PGN 127489) is still the alternator.
  {
    prefix: "propulsion.*.alternatorVoltage",
    systems: systemSet("electrical", "mechanical")
  },
  // Engine fuel: mechanical first, plumbing second.
  { prefix: "propulsion.*.fuel", systems: systemSet("mechanical", "plumbing") },
  // Engines and gearboxes.
  { prefix: "propulsion", systems: systemSet("mechanical") },
  // An engine's and a gearbox's status lamps, raw-water and coolant flow
  // among them. The charge lamp is the alternator's; the fuel lamps are
  // engine fuel; a low system voltage is the electrical system's to explain.
  {
    prefix: "notifications.propulsion.*.chargeIndicator",
    systems: systemSet("electrical", "mechanical")
  },
  {
    prefix: "notifications.propulsion.*.lowFuelPressure",
    systems: systemSet("mechanical", "plumbing")
  },
  {
    prefix: "notifications.propulsion.*.waterInFuel",
    systems: systemSet("mechanical", "plumbing")
  },
  {
    prefix: "notifications.propulsion.*.lowSystemVoltage",
    systems: systemSet("mechanical", "electrical")
  },
  { prefix: "notifications.propulsion", systems: systemSet("mechanical") },
  // ---- Plumbing ----------------------------------------------------------------
  // A fuel tank: plumbing first, mechanical second.
  { prefix: "tanks.<fuel>", systems: systemSet("plumbing", "mechanical") },
  // Water, waste, black water, live well, and a tank of unknown fluid.
  { prefix: "tanks", systems: systemSet("plumbing") },
  // A watermaker makes fresh water from sea water (TOM-864).
  { prefix: "watermakers", systems: systemSet("plumbing") },
  // ---- Electronics -------------------------------------------------------------
  // Autopilot, rudder sensor, steering control.
  { prefix: "steering", systems: systemSet("electronics", "mechanical") },
  // The instruments' readings. Neo still answers "where are we" itself.
  { prefix: "navigation", systems: systemSet("electronics") },
  // Cabin temperature and humidity, the fridge among them: Neo's until data
  // exists.
  { prefix: "environment.inside", systems: NEO },
  // A GX's temperature probes. Only the owner's `.name` says what one
  // measures, and that is never a safety semantic. The GX's own class says
  // nothing about a probe, so the rule is final, not open.
  { prefix: "environment.venus", systems: NEO },
  // Depth, wind, water, current and weather.
  { prefix: "environment", systems: systemSet("electronics") },
  { prefix: "sensors", systems: systemSet("electronics") },
  // Starlink and the boat's network.
  { prefix: "network", systems: systemSet("electronics") },
  // Neomaris's derived values: the anchor watch, gusts, the source guards.
  { prefix: "computed", systems: systemSet("electronics") },
  { prefix: "entertainment", systems: systemSet("electronics") },
  // ---- Security cameras (TOM-824) ----------------------------------------------
  // Whether a camera is up is the boat network's; what it saw (a person, an
  // alarm sound) is security, which is Neo's, like `hatch_security`.
  { prefix: "security.cameras.*.connected", systems: systemSet("electronics") },
  { prefix: "security", systems: NEO },
  // ---- Neo's -------------------------------------------------------------------
  // The boat's dimensions and AIS type.
  { prefix: "design", systems: NEO },
  // Temperature sources a mapper could not name: the device may say.
  { prefix: "generic", systems: NEO, open: true }
];
var FUEL_FLUID = /fuel|diesel|petrol|gasoline/i;
var SWITCH_CHANNEL_LABEL_RULES = [
  // Life safety is Neo's, always first, as `smoke_co_gas` and
  // `hatch_security` are: smoke, fire, CO, gas, a hatch, door or intrusion
  // sensor, man overboard and the EPIRB. It comes before the bilge words, so
  // "Tank Room Heat/Smoke" or a smoke detector in the bilge is Neo's. `co`,
  // `gas`, `mob` and `fire` stand alone and `door` starts a word, so
  // `cockpit`, `gasket`, `mobile`, `Firefly` and `outdoor` are not life
  // safety.
  [
    /smoke|carbon[\s_-]?monoxide|propane|lpg|gas[\s_-]?detect|hatch|security|intrusion|motion|man[\s_-]?overboard|epirb|(?<![a-z])(?:co|gas|mob|fire)(?![a-z])|(?<![a-z])door/i,
    NEO
  ],
  // A bilge pump, a float or high water: plumbing, with electrical second, as
  // the bilge recipes are. A deck floodlight is not a flood.
  [
    /bilge|high[\s_-]?water|hi[\s_-]?water|float\s*switch|flood(?![\s_-]?(?:light|lamp))/i,
    systemSet("plumbing", "electrical")
  ],
  // A fuel/water separator's sensor: engine fuel, as `water_in_fuel` is.
  [
    /fuel[\s/_-]?(?:water|sep)|water[\s_-]?in[\s_-]?fuel/i,
    systemSet("mechanical", "plumbing")
  ],
  // A fridge or freezer alarm: refrigeration is Neo's until data exists, as
  // `fridge_freezer_high_temp` is.
  [/fridge|freezer|reefer|refriger/i, NEO]
];
var RULE_SEGMENTS = BOAT_SYSTEM_PATH_RULES.map((rule) => rule.prefix.split("."));
function segmentMatches(rule, seg) {
  if (rule === "*") return true;
  if (rule === "<fuel>") return FUEL_FLUID.test(seg);
  return rule === seg;
}
function boatSystemPathRule(path) {
  const seg = path.split(".");
  if (seg.some((s) => s.length === 0)) return null;
  for (let i = 0; i < RULE_SEGMENTS.length; i++) {
    const rule = RULE_SEGMENTS[i];
    if (rule.length > seg.length) continue;
    if (rule.every((r, j) => segmentMatches(r, seg[j]))) {
      return BOAT_SYSTEM_PATH_RULES[i];
    }
  }
  return null;
}
function systemsOfPath(path, channelLabel) {
  const rule = boatSystemPathRule(path);
  if (rule === null) return NEO;
  if (rule.switchChannel === true && typeof channelLabel === "string") {
    for (const [label2, systems] of SWITCH_CHANNEL_LABEL_RULES) {
      if (label2.test(channelLabel)) return systems;
    }
  }
  return rule.systems;
}
function pathSystemIsOpen(path) {
  const rule = boatSystemPathRule(path);
  return rule === null || rule.open === true;
}
var N2K_DEVICE_CLASS_CODES = [
  0,
  10,
  20,
  25,
  30,
  35,
  40,
  50,
  60,
  70,
  75,
  80,
  85,
  90,
  100,
  110,
  120,
  125
];
var N2K_DEVICE_CLASS_SYSTEMS = {
  // Reserved for 2000 Use: it says nothing about the device.
  0: { systems: NEO },
  // System tools: diagnostic tools and bus traffic loggers.
  10: { systems: systemSet("electronics") },
  // Safety systems: alarm enunciators, EPIRBs, man overboard, voyage data
  // recorders, cameras. Life safety is Neo's, always first.
  20: { systems: NEO },
  // Internetwork devices: gateways, routers, bridges, repeaters.
  25: { systems: systemSet("electronics") },
  // Electrical distribution: switch and indicator modules, load controllers.
  // What a channel drives is its own label's to say.
  30: { systems: systemSet("electrical") },
  // Electrical generation. Engines, engine gateways and gensets announce
  // themselves here too.
  35: {
    systems: systemSet("electrical"),
    functions: {
      140: systemSet("mechanical", "electrical"),
      // Engine
      141: systemSet("electrical", "mechanical"),
      // DC Generator/Alternator
      151: systemSet("mechanical", "electrical"),
      // AC Generator: a genset
      180: systemSet("mechanical")
      // Engine Gateway
    }
  },
  // Steering and control surfaces: autopilots, rudders, heading sensors, trim.
  40: { systems: systemSet("electronics", "mechanical") },
  // Propulsion: engines, transmissions, engine gateways, throttles, gauges.
  50: {
    systems: systemSet("mechanical"),
    functions: {
      141: systemSet("electrical", "mechanical"),
      // DC Generator/Alternator
      151: systemSet("mechanical", "electrical")
      // AC Generator: a genset
    }
  },
  // Navigation: depth, GNSS, speed, heading, AIS, radar.
  60: { systems: systemSet("electronics") },
  // Communication: radios, AIS, DSC, satellite. An EPIRB is life safety.
  70: { systems: systemSet("electronics"), functions: { 130: NEO } },
  // Sensor communication interfaces go by function. A temperature, pressure or
  // humidity interface can measure anything, so its paths decide.
  75: {
    systems: NEO,
    functions: {
      150: systemSet("plumbing"),
      // Fluid Level: a tank sender
      160: systemSet("plumbing", "mechanical")
      // Flow: water or engine fuel
    }
  },
  // Instrumentation and general systems: instruments, weather, transducers.
  80: { systems: systemSet("electronics") },
  // External environment: atmospheric and aquatic sensors.
  85: { systems: systemSet("electronics") },
  // Internal environment: HVAC is Neo's until data exists.
  90: { systems: NEO },
  // Deck, cargo and fishing equipment: Neo's until data exists.
  100: { systems: NEO },
  // Human interface: buttons, switch and analog interfaces.
  110: { systems: systemSet("electronics") },
  // Displays and alarm enunciators.
  120: { systems: systemSet("electronics") },
  // Entertainment: multimedia players and controllers.
  125: { systems: systemSet("electronics") }
};
function asBusNode(systems) {
  if (systems.primary === "electronics" || systems.secondary.includes("electronics")) {
    return systems;
  }
  return systemSet(systems.primary, ...systems.secondary, "electronics");
}
var DEVICE_CLASSES = new Map(
  N2K_DEVICE_CLASS_CODES.map((code) => [code, N2K_DEVICE_CLASS_SYSTEMS[code]])
);
function systemsOfDevice(device) {
  if (device.classCode === null) return NEO;
  const cls = DEVICE_CLASSES.get(device.classCode);
  if (cls === void 0) return asBusNode(NEO);
  const byFunction = device.functionCode === null ? void 0 : cls.functions?.[device.functionCode];
  return asBusNode(byFunction ?? cls.systems);
}
var RECIPE_SYSTEM_TABLE = {
  // The depth sounder backing the anchor watch.
  anchor_depth_low: systemSet("electronics"),
  // `computed.distanceFromAnchor`: the GPS and the anchor watch.
  anchor_drift: systemSet("electronics"),
  battery_critical: systemSet("electrical"),
  battery_low: systemSet("electrical"),
  battery_soc_critical: systemSet("electrical"),
  battery_soc_low: systemSet("electrical"),
  // A bilge float or pump on a switch channel.
  bilge_high: systemSet("plumbing", "electrical"),
  bilge_pump_cycling: systemSet("plumbing", "electrical"),
  bilge_pump_too_long: systemSet("plumbing", "electrical"),
  // A UniFi Protect camera (TOM-824): what it saw is security, Neo's like
  // `hatch_security`; whether it is up is the boat network's.
  camera_audio_alarm: NEO,
  camera_offline: systemSet("electronics"),
  camera_person: NEO,
  charger_alternator_failure: systemSet("electrical", "mechanical"),
  deep_depth: systemSet("electronics"),
  engine_overtemp: systemSet("mechanical"),
  // Refrigeration is Neo's until data exists.
  fridge_freezer_high_temp: NEO,
  gateway_offline: systemSet("electronics"),
  gps_loss: systemSet("electronics"),
  gps_source_mismatch: systemSet("electronics"),
  // Life safety is Neo's, always first. A detector or sensor on a switch
  // channel reads the same by its label.
  hatch_security: NEO,
  house_battery_critical: systemSet("electrical"),
  house_battery_low: systemSet("electrical"),
  low_oil_pressure: systemSet("mechanical"),
  sensor_stale: systemSet("electronics"),
  shallow_depth: systemSet("electronics"),
  shore_power_lost: systemSet("electrical"),
  smoke_co_gas: NEO,
  tank_fuel_low: systemSet("plumbing", "mechanical"),
  tank_high: systemSet("plumbing"),
  tank_waste_high: systemSet("plumbing"),
  tank_water_low: systemSet("plumbing"),
  water_in_fuel: systemSet("mechanical", "plumbing")
};
var RECIPE_SYSTEMS = RECIPE_SYSTEM_TABLE;
var RECIPES = new Map(
  Object.entries(RECIPE_SYSTEMS)
);
function systemOfRecipe(recipeBase) {
  return RECIPES.get(recipeBase);
}

// src/camera-events.ts
var CAMERA_EVENTS_CLASS = "camera_events";
var CAMERA_SNAPSHOTS_CLASS = "camera_snapshots";
var CAMERA_INVENTORY_CLASS = "camera_inventory";
var CAMERA_EVENTS_VERSION = 1;
var CAMERA_EVENTS_MAX_PER_FRAME = 200;
var CAMERA_ID_MAX = 128;
var CAMERA_NAME_MAX = 64;
var CAMERA_MODEL_MAX = 64;
var CAMERA_SMART_DETECT_TYPES_MAX = 8;
var CAMERA_SMART_DETECT_TYPE_MAX = 32;
var CAMERA_INVENTORY_MAX = 64;
var CAMERA_SNAPSHOT_MAX_BYTES = 1024 * 1024;
var CAMERA_EVENT_TYPES = [
  "person",
  "smart",
  "audio",
  "motion",
  "offline",
  "gap"
];
var CAMERA_AUDIO_KINDS = [
  "smoke",
  "co",
  "siren",
  "glass_break"
];
var CAMERA_EVENT_SOURCES = ["unifi_protect"];
var CAMERA_SNAPSHOT_ID_PATTERN = /^snap_[0-9A-HJKMNP-TV-Z]{26}$/;
var CAMERA_STREAM_QUALITIES = ["high", "medium", "low"];
var CAMERA_STATES = ["connected", "disconnected", "unknown"];
var CAMERA_PTZ_SLOT_MAX = 63;
var CAMERA_PTZ_LIST_MAX = 64;
function parseCameraEventsPayload(raw) {
  const value = asObject(raw);
  if (!value) return rejected("payload must be an object");
  if (value.v !== CAMERA_EVENTS_VERSION) {
    return rejected(
      `unsupported camera events version (expected ${CAMERA_EVENTS_VERSION})`
    );
  }
  if (!Array.isArray(value.events)) return rejected("events must be an array");
  if (value.events.length > CAMERA_EVENTS_MAX_PER_FRAME) {
    return rejected(
      `at most ${CAMERA_EVENTS_MAX_PER_FRAME} events per frame (got ${value.events.length})`
    );
  }
  const events = [];
  for (const [i, entry] of value.events.entries()) {
    const parsed = parseEventRecord(entry);
    if (typeof parsed === "string") return rejected(`events[${i}]: ${parsed}`);
    events.push(parsed);
  }
  return { status: "accepted", payload: { v: CAMERA_EVENTS_VERSION, events } };
}
function parseEventRecord(raw) {
  const value = asObject(raw);
  if (!value) return "must be an object";
  if (!isOneOf(CAMERA_EVENT_SOURCES, value.source)) return "unknown source";
  if (!isId(value.eventId)) return "eventId must be a non-empty id";
  if (!isOneOf(CAMERA_EVENT_TYPES, value.type)) return "unknown type";
  const type = value.type;
  if (value.cameraId === null) {
    if (type !== "gap") return "cameraId may be null only on a gap";
  } else if (!isId(value.cameraId)) {
    return "cameraId must be a non-empty id";
  }
  if (!Array.isArray(value.smartDetectTypes) || value.smartDetectTypes.length > CAMERA_SMART_DETECT_TYPES_MAX || !value.smartDetectTypes.every(
    (t) => typeof t === "string" && t.length > 0 && t.length <= CAMERA_SMART_DETECT_TYPE_MAX
  )) {
    return `smartDetectTypes must be at most ${CAMERA_SMART_DETECT_TYPES_MAX} short strings`;
  }
  if (type === "audio") {
    if (!isOneOf(CAMERA_AUDIO_KINDS, value.audioKind)) {
      return "an audio event needs an audioKind";
    }
  } else if (value.audioKind !== null) {
    return "audioKind must be null unless type is audio";
  }
  if (!isTimestamp(value.start)) return "start must be an ISO-8601 timestamp";
  if (value.end !== null && !isTimestamp(value.end)) {
    return "end must be an ISO-8601 timestamp or null";
  }
  if (value.end !== null && Date.parse(value.end) < Date.parse(value.start)) {
    return "end is before start";
  }
  if (!isTimestamp(value.lastHeardAt)) {
    return "lastHeardAt must be an ISO-8601 timestamp";
  }
  if (value.snapshotId !== null && !isSnapshotId(value.snapshotId)) {
    return "snapshotId must be a snap_ ULID or null";
  }
  const name = clampName(value.cameraName, CAMERA_NAME_MAX);
  return {
    source: value.source,
    eventId: value.eventId,
    cameraId: value.cameraId,
    ...name !== void 0 ? { cameraName: name } : {},
    type,
    smartDetectTypes: [...value.smartDetectTypes],
    audioKind: value.audioKind,
    start: value.start,
    end: value.end,
    lastHeardAt: value.lastHeardAt,
    snapshotId: value.snapshotId
  };
}
function parseCameraSnapshotPayload(raw) {
  const value = asObject(raw);
  if (!value) return rejected("payload must be an object");
  if (value.v !== CAMERA_EVENTS_VERSION) {
    return rejected(
      `unsupported camera snapshot version (expected ${CAMERA_EVENTS_VERSION})`
    );
  }
  if (!isSnapshotId(value.snapshotId)) {
    return rejected("snapshotId must be a snap_ ULID");
  }
  if (value.eventId !== null && !isId(value.eventId)) {
    return rejected("eventId must be a non-empty id or null");
  }
  if (!isId(value.cameraId)) return rejected("cameraId must be a non-empty id");
  if (!isTimestamp(value.takenAt)) {
    return rejected("takenAt must be an ISO-8601 timestamp");
  }
  if (value.contentType !== "image/jpeg") {
    return rejected("contentType must be image/jpeg");
  }
  if (typeof value.jpegBase64 !== "string" || value.jpegBase64.length === 0 || !BASE64.test(value.jpegBase64)) {
    return rejected("jpegBase64 must be non-empty base64");
  }
  if (value.requestId !== void 0 && (typeof value.requestId !== "string" || value.requestId.length === 0 || value.requestId.length > 128)) {
    return rejected(
      "requestId must be a non-empty id of at most 128 characters"
    );
  }
  const bytes = base64DecodedLength(value.jpegBase64);
  if (bytes > CAMERA_SNAPSHOT_MAX_BYTES) {
    return rejected(
      `snapshot is ${bytes} bytes, over the ${CAMERA_SNAPSHOT_MAX_BYTES}-byte cap`
    );
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_EVENTS_VERSION,
      snapshotId: value.snapshotId,
      eventId: value.eventId,
      ...typeof value.requestId === "string" ? { requestId: value.requestId } : {},
      cameraId: value.cameraId,
      takenAt: value.takenAt,
      contentType: "image/jpeg",
      jpegBase64: value.jpegBase64
    }
  };
}
function parseCameraInventoryPayload(raw) {
  const value = asObject(raw);
  if (!value) return rejected("payload must be an object");
  if (value.v !== CAMERA_EVENTS_VERSION) {
    return rejected(
      `unsupported camera inventory version (expected ${CAMERA_EVENTS_VERSION})`
    );
  }
  if (!isOneOf(CAMERA_EVENT_SOURCES, value.source)) {
    return rejected("unknown source");
  }
  const nvr = asObject(value.nvr);
  const nvrName = nvr ? clampName(nvr.name, CAMERA_NAME_MAX) : void 0;
  if (!nvr || nvrName === void 0) return rejected("nvr.name is required");
  if (!Array.isArray(value.cameras)) {
    return rejected("cameras must be an array");
  }
  if (value.cameras.length > CAMERA_INVENTORY_MAX) {
    return rejected(`at most ${CAMERA_INVENTORY_MAX} cameras`);
  }
  if (!isTimestamp(value.observedAt)) {
    return rejected("observedAt must be an ISO-8601 timestamp");
  }
  const cameras = [];
  for (const [i, entry] of value.cameras.entries()) {
    const camera = parseInventoryCamera(entry);
    if (typeof camera === "string") return rejected(`cameras[${i}]: ${camera}`);
    cameras.push(camera);
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_EVENTS_VERSION,
      source: value.source,
      nvr: {
        name: nvrName,
        version: clampName(nvr.version, CAMERA_MODEL_MAX) ?? null
      },
      cameras,
      observedAt: value.observedAt
    }
  };
}
function parseInventoryCamera(raw) {
  const value = asObject(raw);
  if (!value) return "must be an object";
  if (!isId(value.cameraId)) return "cameraId must be a non-empty id";
  const name = clampName(value.name, CAMERA_NAME_MAX);
  if (name === void 0) return "name is required";
  if (!isOneOf(CAMERA_STATES, value.state)) return "unknown state";
  if (!Array.isArray(value.streams)) return "streams must be an array";
  const streams = [];
  const seen = /* @__PURE__ */ new Set();
  for (const entry of value.streams) {
    const stream = asObject(entry);
    if (!stream || !isOneOf(CAMERA_STREAM_QUALITIES, stream.quality)) {
      return "each stream needs a quality of high, medium or low";
    }
    if (seen.has(stream.quality))
      return `stream ${stream.quality} listed twice`;
    seen.add(stream.quality);
    const bitrateBps = optionalCount(stream.bitrateBps);
    const width = optionalCount(stream.width);
    const height = optionalCount(stream.height);
    const fps = optionalCount(stream.fps);
    if ([bitrateBps, width, height, fps].includes(void 0)) {
      return `stream ${stream.quality}: bitrateBps, width, height and fps must be positive numbers or null`;
    }
    streams.push({
      quality: stream.quality,
      bitrateBps,
      width,
      height,
      fps
    });
  }
  let ptz;
  if (value.ptz !== void 0) {
    const parsed = parseInventoryPtz(value.ptz);
    if (typeof parsed === "string") return `ptz: ${parsed}`;
    ptz = parsed;
  }
  return {
    cameraId: value.cameraId,
    name,
    model: clampName(value.model, CAMERA_MODEL_MAX) ?? null,
    state: value.state,
    streams,
    ...ptz ? { ptz } : {}
  };
}
function parseInventoryPtz(raw) {
  const value = asObject(raw);
  if (!value) return "must be an object";
  const presets = parsePtzSlots(value.presets, -1);
  if (typeof presets === "string") return `presets ${presets}`;
  const patrols = parsePtzSlots(value.patrols, 0);
  if (typeof patrols === "string") return `patrols ${patrols}`;
  const active = value.activePatrolSlot;
  if (active !== null && !isPtzSlot(active, 0)) {
    return "activePatrolSlot must be a slot or null";
  }
  return { presets, patrols, activePatrolSlot: active };
}
function parsePtzSlots(raw, min) {
  if (raw === null) return null;
  if (!Array.isArray(raw)) return "must be an array or null";
  if (raw.length > CAMERA_PTZ_LIST_MAX) {
    return `at most ${CAMERA_PTZ_LIST_MAX}`;
  }
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const entry of raw) {
    const item = asObject(entry);
    if (!item || !isPtzSlot(item.slot, min)) {
      return `need slots from ${min} to ${CAMERA_PTZ_SLOT_MAX}`;
    }
    if (seen.has(item.slot)) return `list slot ${item.slot} twice`;
    seen.add(item.slot);
    const name = clampName(item.name, CAMERA_NAME_MAX);
    out.push({ slot: item.slot, name: name || `Slot ${item.slot}` });
  }
  return out;
}
function isPtzSlot(value, min) {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= CAMERA_PTZ_SLOT_MAX;
}
function neomarisCameraEventId(type, cameraId, startMs) {
  return `tombolo:${type}:${cameraId ?? "all"}:${Math.trunc(startMs)}`.slice(
    0,
    CAMERA_ID_MAX
  );
}
var BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;
function base64DecodedLength(b64) {
  const padding = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.floor(b64.length * 3 / 4) - padding;
}
function asObject(raw) {
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
}
function rejected(reason) {
  return { status: "rejected", reason };
}
function isOneOf(values, value) {
  return values.includes(value);
}
function isId(value) {
  return typeof value === "string" && value.length > 0 && value.length <= CAMERA_ID_MAX;
}
function isSnapshotId(value) {
  return typeof value === "string" && CAMERA_SNAPSHOT_ID_PATTERN.test(value);
}
function isTimestamp(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}
function clampName(value, max) {
  return typeof value === "string" && value.trim().length > 0 ? value.trim().slice(0, max) : void 0;
}
function optionalCount(value) {
  if (value === null || value === void 0) return null;
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : void 0;
}

// src/camera-lan.ts
var CAMERA_LAN_VERSION = 1;
var CAMERA_LAN_CONFIG_COMMAND = "camera_lan_config";
var CAMERA_LAN_ACK_CLASS = "camera_lan_ack";
var CAMERA_LAN_STATE_CLASS = "camera_lan_state";
var CAMERA_LAN_DEFAULT_PORT = 1443;
var CAMERA_LAN_RENEW_BEFORE_DAYS = 30;
var CAMERA_LAN_TOKEN_TYPE = "lan_view";
var CAMERA_LAN_LABEL_PATTERN = /^b[0-9a-f]{16}$/;
var CAMERA_LAN_HOSTNAME_MAX = 253;
var HOSTNAME_PATTERN = /^b[0-9a-f]{16}(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;
var CAMERA_LAN_VIEW_KEY_PATTERN = /^[A-Za-z0-9_-]{43}$/;
var CAMERA_LAN_CSR_MAX = 8 * 1024;
var CAMERA_LAN_CHAIN_MAX = 16 * 1024;
var CAMERA_LAN_COMMAND_KEY_MAX = 200;
var CAMERA_LAN_DETAIL_MAX = 500;
var SHA256_HEX = /^[0-9a-f]{64}$/;
var IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
function parseCameraLanStatePayload(raw) {
  const value = asObject2(raw);
  if (!value) return rejected2("payload must be an object");
  if (value.v !== CAMERA_LAN_VERSION) {
    return rejected2("unsupported camera lan state version");
  }
  if (!nullOr(value.lanIp, isCameraLanAddress)) {
    return rejected2("lanIp must be a private IPv4 address or null");
  }
  if (!nullOr(value.httpsPort, isPort)) {
    return rejected2("httpsPort must be a port number or null");
  }
  if (!nullOr(value.hostname, isHostname)) {
    return rejected2("hostname must be a boat lan hostname or null");
  }
  let certificate = null;
  if (value.certificate !== null) {
    const c = asObject2(value.certificate);
    if (!c || !isHostname(c.hostname) || !isTimestamp2(c.validTo) || !isSha256(c.sha256)) {
      return rejected2(
        "certificate must carry a hostname, validTo and sha256, or be null"
      );
    }
    certificate = {
      hostname: c.hostname,
      validTo: c.validTo,
      sha256: c.sha256
    };
  }
  let certificateRequest = null;
  if (value.certificateRequest !== null) {
    const r = asObject2(value.certificateRequest);
    if (!r || typeof r.csrPem !== "string" || r.csrPem.length > CAMERA_LAN_CSR_MAX || !r.csrPem.includes("-----BEGIN CERTIFICATE REQUEST-----") || !isSha256(r.sha256)) {
      return rejected2(
        "certificateRequest must carry a PEM request and its sha256, or be null"
      );
    }
    certificateRequest = { csrPem: r.csrPem, sha256: r.sha256 };
  }
  if (!nullOr(value.keyVersion, isPositiveInt)) {
    return rejected2("keyVersion must be a positive integer or null");
  }
  if (!isTimestamp2(value.at))
    return rejected2("at must be an ISO-8601 timestamp");
  return {
    status: "accepted",
    payload: {
      v: CAMERA_LAN_VERSION,
      lanIp: value.lanIp,
      httpsPort: value.httpsPort,
      hostname: value.hostname,
      certificate,
      certificateRequest,
      keyVersion: value.keyVersion,
      at: value.at
    }
  };
}
function parseCameraLanConfigPayload(raw) {
  const value = asObject2(raw);
  if (!value) return rejected2("payload must be an object");
  if (value.v !== CAMERA_LAN_VERSION) {
    return rejected2(
      `unsupported camera lan config version (expected ${CAMERA_LAN_VERSION})`
    );
  }
  if (!isHostname(value.hostname)) {
    return rejected2("hostname must be a boat lan hostname");
  }
  if (!isTimestamp2(value.issuedAt)) {
    return rejected2("issuedAt must be an ISO-8601 timestamp");
  }
  const hasChain = value.certificateChainPem !== void 0;
  if (hasChain !== (value.csrSha256 !== void 0)) {
    return rejected2("certificateChainPem and csrSha256 come together");
  }
  if (hasChain) {
    if (typeof value.certificateChainPem !== "string" || value.certificateChainPem.length > CAMERA_LAN_CHAIN_MAX || !value.certificateChainPem.includes("-----BEGIN CERTIFICATE-----")) {
      return rejected2("certificateChainPem must be a PEM certificate chain");
    }
    if (!isSha256(value.csrSha256)) {
      return rejected2("csrSha256 must be a SHA-256");
    }
  }
  const hasKey = value.viewKey !== void 0;
  if (hasKey !== (value.keyVersion !== void 0)) {
    return rejected2("viewKey and keyVersion come together");
  }
  if (hasKey) {
    if (typeof value.viewKey !== "string" || !CAMERA_LAN_VIEW_KEY_PATTERN.test(value.viewKey)) {
      return rejected2("viewKey must be 32 bytes of base64url");
    }
    if (!isPositiveInt(value.keyVersion)) {
      return rejected2("keyVersion must be a positive integer");
    }
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_LAN_VERSION,
      hostname: value.hostname,
      ...hasChain ? {
        certificateChainPem: value.certificateChainPem,
        csrSha256: value.csrSha256
      } : {},
      ...hasKey ? {
        viewKey: value.viewKey,
        keyVersion: value.keyVersion
      } : {},
      issuedAt: value.issuedAt
    }
  };
}
var CAMERA_LAN_REJECT_REASONS = [
  "invalid_command",
  // The chain answers a request the boat no longer holds (it made a new key).
  "stale_request",
  // The leaf's public key is not the boat's, or it does not name the hostname.
  "certificate_mismatch",
  // The chain does not parse, is not yet valid or has ended.
  "certificate_invalid",
  // A key version lower than the one held.
  "stale_key",
  "secret_store_unavailable"
];
function parseCameraLanAckPayload(raw) {
  const value = asObject2(raw);
  if (!value) return rejected2("payload must be an object");
  if (value.v !== CAMERA_LAN_VERSION) {
    return rejected2("unsupported camera lan ack version");
  }
  if (!isBounded(value.commandKey, CAMERA_LAN_COMMAND_KEY_MAX)) {
    return rejected2("commandKey must be a non-empty string");
  }
  if (value.phase !== "applied" && value.phase !== "rejected") {
    return rejected2("phase must be applied or rejected");
  }
  if (value.phase === "rejected") {
    if (!isOneOf2(CAMERA_LAN_REJECT_REASONS, value.reason)) {
      return rejected2("a rejected ack needs a known reason");
    }
  } else if (value.reason !== void 0) {
    return rejected2("reason is only for a rejected ack");
  }
  if (!isTimestamp2(value.at))
    return rejected2("at must be an ISO-8601 timestamp");
  if (value.detail !== void 0 && (typeof value.detail !== "string" || value.detail.length > CAMERA_LAN_DETAIL_MAX)) {
    return rejected2(
      `detail must be at most ${CAMERA_LAN_DETAIL_MAX} characters`
    );
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_LAN_VERSION,
      commandKey: value.commandKey,
      phase: value.phase,
      ...value.reason !== void 0 ? { reason: value.reason } : {},
      at: value.at,
      ...typeof value.detail === "string" && value.detail.length > 0 ? { detail: value.detail } : {}
    }
  };
}
var CAMERA_LAN_TOKEN_GOLDEN = {
  viewKey: "AAECAwQFBgcICQoLDA0ODxAREhMUFRYXGBkaGxwdHh8",
  claims: {
    typ: "lan_view",
    boatId: "boat_golden",
    cameraId: "0123456789abcdef01234567",
    sessionId: "lan_golden",
    exp: 18e8
  },
  nowS: 1799999e3,
  token: "v1.eyJ0eXAiOiJsYW5fdmlldyIsImJvYXRJZCI6ImJvYXRfZ29sZGVuIiwiY2FtZXJhSWQiOiIwMTIzNDU2Nzg5YWJjZGVmMDEyMzQ1NjciLCJzZXNzaW9uSWQiOiJsYW5fZ29sZGVuIiwiZXhwIjoxODAwMDAwMDAwfQ.1sMxXr4MAc2Cro9innTi08xQVvmbJB_r11WXOy1jrOo"
};
function isCameraLanAddress(value) {
  if (typeof value !== "string" || !IPV4.test(value)) return false;
  const [a, b] = value.split(".").map(Number);
  return a === 10 || a === 172 && b >= 16 && b <= 31 || a === 192 && b === 168;
}
function isHostname(value) {
  return typeof value === "string" && value.length <= CAMERA_LAN_HOSTNAME_MAX && HOSTNAME_PATTERN.test(value);
}
function isPort(value) {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 65535;
}
function isPositiveInt(value) {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1;
}
function asObject2(raw) {
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
}
function rejected2(reason) {
  return { status: "rejected", reason };
}
function isOneOf2(values, value) {
  return values.includes(value);
}
function isBounded(value, max) {
  return typeof value === "string" && value.length > 0 && value.length <= max;
}
function isTimestamp2(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}
function isSha256(value) {
  return typeof value === "string" && SHA256_HEX.test(value);
}
function nullOr(value, ok) {
  return value === null || ok(value);
}

// src/camera-ptz.ts
var CAMERA_PTZ_COMMAND = "camera_ptz";
var CAMERA_PTZ_VERSION = 1;
var CAMERA_PTZ_ACK_CLASS = "camera_ptz_ack";
var CAMERA_PTZ_ACTIONS = [
  "goto_preset",
  "patrol_start",
  "patrol_stop"
];
var CAMERA_PTZ_HOME_SLOT = -1;
var CAMERA_PTZ_ID_MAX = 128;
var CAMERA_PTZ_USER_ID_MAX = 200;
var CAMERA_PTZ_COMMAND_KEY_MAX = 200;
function parseCameraPtzCommandPayload(raw) {
  const reject = (reason) => ({ status: "rejected", reason });
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return reject("payload must be an object");
  }
  const value = raw;
  if (value.v !== CAMERA_PTZ_VERSION) {
    return reject(
      `unsupported camera ptz version (expected ${CAMERA_PTZ_VERSION})`
    );
  }
  if (!isBounded2(value.cameraId, CAMERA_ID_MAX)) {
    return reject("cameraId must be a non-empty id");
  }
  if (!isOneOf3(CAMERA_PTZ_ACTIONS, value.action)) {
    return reject("action must be goto_preset, patrol_start or patrol_stop");
  }
  if (!isBounded2(value.commandId, CAMERA_PTZ_ID_MAX)) {
    return reject("commandId must be a non-empty id");
  }
  if (!isBounded2(value.sourceUserId, CAMERA_PTZ_USER_ID_MAX)) {
    return reject("sourceUserId must be a non-empty string");
  }
  const action = value.action;
  if (action === "patrol_stop") {
    if (value.slot !== void 0) return reject("patrol_stop takes no slot");
  } else {
    const min = action === "goto_preset" ? CAMERA_PTZ_HOME_SLOT : 0;
    if (!isSlot(value.slot, min)) {
      return reject(
        `${action} needs a whole-number slot from ${min} to ${CAMERA_PTZ_SLOT_MAX}`
      );
    }
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_PTZ_VERSION,
      cameraId: value.cameraId,
      action,
      ...action === "patrol_stop" ? {} : { slot: value.slot },
      commandId: value.commandId,
      sourceUserId: value.sourceUserId
    }
  };
}
var CAMERA_PTZ_ACK_RESULTS = ["ok", "failed"];
var CAMERA_PTZ_FAILURE_REASONS = [
  "invalid_command",
  /** No Protect console is set up, or the camera is not in its list. */
  "not_configured",
  "camera_offline",
  /** The camera has no pan, tilt and zoom. */
  "not_ptz",
  /** Protect has no preset or patrol in that slot. */
  "no_such_slot",
  /** Protect refused the key for this call. */
  "forbidden",
  "rate_limited",
  "unreachable"
];
function parseCameraPtzAckPayload(raw) {
  const reject = (reason) => ({ status: "rejected", reason });
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return reject("payload must be an object");
  }
  const value = raw;
  if (value.v !== CAMERA_PTZ_VERSION) {
    return reject(
      `unsupported camera ptz ack version (expected ${CAMERA_PTZ_VERSION})`
    );
  }
  if (!isBounded2(value.commandKey, CAMERA_PTZ_COMMAND_KEY_MAX)) {
    return reject("commandKey must be a non-empty string");
  }
  if (!isBounded2(value.commandId, CAMERA_PTZ_ID_MAX)) {
    return reject("commandId must be a non-empty id");
  }
  if (!isBounded2(value.cameraId, CAMERA_ID_MAX)) {
    return reject("cameraId must be a non-empty id");
  }
  if (!isOneOf3(CAMERA_PTZ_ACTIONS, value.action)) {
    return reject("unknown action");
  }
  if (value.slot !== void 0 && !isSlot(value.slot, CAMERA_PTZ_HOME_SLOT)) {
    return reject("slot must be a whole number in range");
  }
  if (!isOneOf3(CAMERA_PTZ_ACK_RESULTS, value.result)) {
    return reject("result must be ok or failed");
  }
  if (typeof value.at !== "string" || !Number.isFinite(Date.parse(value.at))) {
    return reject("at must be an ISO-8601 timestamp");
  }
  const result = value.result;
  if (result === "ok") {
    if (value.reason !== void 0 || value.retryAfterMs !== void 0) {
      return reject("an ok ack carries no reason or retryAfterMs");
    }
  } else {
    if (!isOneOf3(CAMERA_PTZ_FAILURE_REASONS, value.reason)) {
      return reject("a failed ack needs a known reason");
    }
    if (value.retryAfterMs !== void 0) {
      if (value.reason !== "rate_limited") {
        return reject("retryAfterMs is only for rate_limited");
      }
      if (typeof value.retryAfterMs !== "number" || !Number.isInteger(value.retryAfterMs) || value.retryAfterMs < 0 || value.retryAfterMs > 10 * 60 * 1e3) {
        return reject(
          "retryAfterMs must be a whole number of ms up to 10 minutes"
        );
      }
    }
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_PTZ_VERSION,
      commandKey: value.commandKey,
      commandId: value.commandId,
      cameraId: value.cameraId,
      action: value.action,
      ...typeof value.slot === "number" ? { slot: value.slot } : {},
      result,
      ...result === "failed" ? { reason: value.reason } : {},
      ...typeof value.retryAfterMs === "number" ? { retryAfterMs: value.retryAfterMs } : {},
      at: value.at
    }
  };
}
function isSlot(v, min) {
  return typeof v === "number" && Number.isInteger(v) && v >= min && v <= CAMERA_PTZ_SLOT_MAX;
}
function isBounded2(v, max) {
  return typeof v === "string" && v.length > 0 && v.length <= max;
}
function isOneOf3(list2, v) {
  return typeof v === "string" && list2.includes(v);
}

// src/camera-snapshot-request.ts
var CAMERA_SNAPSHOT_REQUEST_COMMAND = "camera_snapshot_request";
var CAMERA_SNAPSHOT_REQUEST_VERSION = 1;
var CAMERA_SNAPSHOT_REQUEST_ID_MAX = 128;
var CAMERA_SNAPSHOT_USER_ID_MAX = 200;
function parseCameraSnapshotRequestPayload(raw) {
  const reject = (reason) => ({ status: "rejected", reason });
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return reject("payload must be an object");
  }
  const value = raw;
  if (value.v !== CAMERA_SNAPSHOT_REQUEST_VERSION) {
    return reject(
      `unsupported camera snapshot request version (expected ${CAMERA_SNAPSHOT_REQUEST_VERSION})`
    );
  }
  if (!isBounded3(value.cameraId, CAMERA_ID_MAX)) {
    return reject("cameraId must be a non-empty id");
  }
  if (!isBounded3(value.requestId, CAMERA_SNAPSHOT_REQUEST_ID_MAX)) {
    return reject("requestId must be a non-empty id");
  }
  if (!isBounded3(value.sourceUserId, CAMERA_SNAPSHOT_USER_ID_MAX)) {
    return reject("sourceUserId must be a non-empty string");
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_SNAPSHOT_REQUEST_VERSION,
      cameraId: value.cameraId,
      requestId: value.requestId,
      sourceUserId: value.sourceUserId
    }
  };
}
var CAMERA_SNAPSHOT_ACK_CLASS = "camera_snapshot_ack";
var CAMERA_SNAPSHOT_ACK_RESULTS = ["sent", "failed"];
var CAMERA_SNAPSHOT_FAILURE_REASONS = [
  "invalid_command",
  "not_configured",
  "camera_offline",
  "rate_limited",
  "too_large",
  "unreachable"
];
var CAMERA_SNAPSHOT_COMMAND_KEY_MAX = 200;
var SNAPSHOT_ID = /^snap_[0-9A-HJKMNP-TV-Z]{26}$/;
function parseCameraSnapshotAckPayload(raw) {
  const reject = (reason) => ({ status: "rejected", reason });
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return reject("payload must be an object");
  }
  const value = raw;
  if (value.v !== CAMERA_SNAPSHOT_REQUEST_VERSION) {
    return reject(
      `unsupported camera snapshot ack version (expected ${CAMERA_SNAPSHOT_REQUEST_VERSION})`
    );
  }
  if (!isBounded3(value.commandKey, CAMERA_SNAPSHOT_COMMAND_KEY_MAX)) {
    return reject("commandKey must be a non-empty string");
  }
  if (!isBounded3(value.requestId, CAMERA_SNAPSHOT_REQUEST_ID_MAX)) {
    return reject("requestId must be a non-empty id");
  }
  if (!isBounded3(value.cameraId, CAMERA_ID_MAX)) {
    return reject("cameraId must be a non-empty id");
  }
  if (!isOneOf4(CAMERA_SNAPSHOT_ACK_RESULTS, value.result)) {
    return reject("result must be sent or failed");
  }
  if (typeof value.at !== "string" || !Number.isFinite(Date.parse(value.at))) {
    return reject("at must be an ISO-8601 timestamp");
  }
  const result = value.result;
  if (result === "sent") {
    if (typeof value.snapshotId !== "string" || !SNAPSHOT_ID.test(value.snapshotId)) {
      return reject("a sent ack needs a snap_ snapshotId");
    }
    if (value.reason !== void 0 || value.retryAfterMs !== void 0) {
      return reject("a sent ack carries no reason or retryAfterMs");
    }
  } else {
    if (!isOneOf4(CAMERA_SNAPSHOT_FAILURE_REASONS, value.reason)) {
      return reject("a failed ack needs a known reason");
    }
    if (value.snapshotId !== void 0) {
      return reject("a failed ack carries no snapshotId");
    }
    if (value.retryAfterMs !== void 0) {
      if (value.reason !== "rate_limited") {
        return reject("retryAfterMs is only for rate_limited");
      }
      if (typeof value.retryAfterMs !== "number" || !Number.isInteger(value.retryAfterMs) || value.retryAfterMs < 0 || value.retryAfterMs > 10 * 60 * 1e3) {
        return reject(
          "retryAfterMs must be a whole number of ms up to 10 minutes"
        );
      }
    }
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_SNAPSHOT_REQUEST_VERSION,
      commandKey: value.commandKey,
      requestId: value.requestId,
      cameraId: value.cameraId,
      result,
      ...result === "sent" ? { snapshotId: value.snapshotId } : {},
      ...result === "failed" ? { reason: value.reason } : {},
      ...typeof value.retryAfterMs === "number" ? { retryAfterMs: value.retryAfterMs } : {},
      at: value.at
    }
  };
}
function isBounded3(v, max) {
  return typeof v === "string" && v.length > 0 && v.length <= max;
}
function isOneOf4(list2, v) {
  return typeof v === "string" && list2.includes(v);
}

// src/camera-stream.ts
var CAMERA_STREAM_VERSION = 1;
var CAMERA_STREAM_ACTIONS = ["start", "stop"];
var CAMERA_STREAM_SESSION_ID_MAX = 128;
var CAMERA_STREAM_TOKEN_MAX = 2048;
var CAMERA_STREAM_USER_ID_MAX = 200;
var CAMERA_STREAM_MAX_TTL_MS = 15 * 60 * 1e3;
function parseCameraStreamCommandPayload(raw, now = Date.now()) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== CAMERA_STREAM_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported camera stream command version (expected ${CAMERA_STREAM_VERSION})`
    };
  }
  if (!isOneOf5(CAMERA_STREAM_ACTIONS, value.action)) {
    return { status: "rejected", reason: "missing or unknown action" };
  }
  const action = value.action;
  const reject = (reason) => ({
    status: "rejected",
    action,
    reason
  });
  if (!isBounded4(value.cameraId, CAMERA_ID_MAX)) {
    return reject("cameraId must be a non-empty id");
  }
  if (!isBounded4(value.sessionId, CAMERA_STREAM_SESSION_ID_MAX)) {
    return reject("sessionId must be a non-empty id");
  }
  if (!isBounded4(value.sourceUserId, CAMERA_STREAM_USER_ID_MAX)) {
    return reject("sourceUserId must be a non-empty string");
  }
  if (action === "stop") {
    for (const field of ["quality", "uploadToken", "expiresAt"]) {
      if (value[field] !== void 0) {
        return reject(`${field} is not allowed for action=stop`);
      }
    }
    return {
      status: "accepted",
      payload: {
        v: CAMERA_STREAM_VERSION,
        action,
        cameraId: value.cameraId,
        sessionId: value.sessionId,
        sourceUserId: value.sourceUserId
      }
    };
  }
  if (!isOneOf5(CAMERA_STREAM_QUALITIES, value.quality)) {
    return reject("quality must be one of high, medium, low");
  }
  if (!isBounded4(value.uploadToken, CAMERA_STREAM_TOKEN_MAX)) {
    return reject("uploadToken is required for action=start");
  }
  if (typeof value.expiresAt !== "string") {
    return reject("expiresAt is required for action=start");
  }
  const expiresAt = Date.parse(value.expiresAt);
  if (!Number.isFinite(expiresAt)) {
    return reject("expiresAt must be an ISO-8601 timestamp");
  }
  if (expiresAt <= now) return reject("the upload token has expired");
  if (expiresAt - now > CAMERA_STREAM_MAX_TTL_MS) {
    return reject("expiresAt is too far in the future");
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_STREAM_VERSION,
      action,
      cameraId: value.cameraId,
      quality: value.quality,
      sessionId: value.sessionId,
      uploadToken: value.uploadToken,
      expiresAt: value.expiresAt,
      sourceUserId: value.sourceUserId
    }
  };
}
var CAMERA_STREAM_STATUS_CLASS = "camera_stream_status";
var CAMERA_STREAM_STATUS_PHASES = [
  "accepted",
  "streaming",
  "stopped",
  "failed"
];
var CAMERA_STREAM_FAILURE_REASONS = [
  "invalid_command",
  "not_configured",
  "camera_offline",
  "no_such_quality",
  "helper_unavailable",
  "upload_unreachable",
  "token_rejected",
  "link_paused_starlink_outage"
];
var CAMERA_STREAM_KEY_MAX = 200;
var CAMERA_STREAM_STATUS_DETAIL_MAX = 500;
function parseCameraStreamStatusPayload(raw) {
  const reject = (reason) => ({ status: "rejected", reason });
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return reject("payload must be an object");
  }
  const value = raw;
  if (value.v !== CAMERA_STREAM_VERSION) {
    return reject(
      `unsupported camera stream status version (expected ${CAMERA_STREAM_VERSION})`
    );
  }
  if (!isBounded4(value.commandKey, CAMERA_STREAM_KEY_MAX)) {
    return reject("commandKey must be a non-empty string");
  }
  if (!isBounded4(value.sessionId, CAMERA_STREAM_SESSION_ID_MAX)) {
    return reject("sessionId must be a non-empty id");
  }
  if (!isBounded4(value.cameraId, CAMERA_ID_MAX)) {
    return reject("cameraId must be a non-empty id");
  }
  if (!isOneOf5(CAMERA_STREAM_STATUS_PHASES, value.phase)) {
    return reject("missing or unknown phase");
  }
  if (value.quality !== void 0 && !isOneOf5(CAMERA_STREAM_QUALITIES, value.quality)) {
    return reject("quality must be one of high, medium, low");
  }
  if (value.phase === "failed") {
    if (!isOneOf5(CAMERA_STREAM_FAILURE_REASONS, value.reason)) {
      return reject("a failed beat needs a known reason");
    }
  } else if (value.reason !== void 0) {
    return reject("reason is only allowed on a failed beat");
  }
  if (typeof value.at !== "string" || !Number.isFinite(Date.parse(value.at))) {
    return reject("at must be an ISO-8601 timestamp");
  }
  if (value.detail !== void 0 && (typeof value.detail !== "string" || value.detail.length > CAMERA_STREAM_STATUS_DETAIL_MAX)) {
    return reject(
      `detail must be a string of at most ${CAMERA_STREAM_STATUS_DETAIL_MAX} characters`
    );
  }
  return {
    status: "accepted",
    payload: {
      v: CAMERA_STREAM_VERSION,
      commandKey: value.commandKey,
      sessionId: value.sessionId,
      cameraId: value.cameraId,
      phase: value.phase,
      ...value.quality !== void 0 ? { quality: value.quality } : {},
      ...value.reason !== void 0 ? { reason: value.reason } : {},
      at: value.at,
      ...typeof value.detail === "string" && value.detail.length > 0 ? { detail: value.detail } : {}
    }
  };
}
var CAMERA_UPLOAD_PATH = "/v1/boat/cameras/upload";
var CAMERA_UPLOAD_CLOSE_NO_VIEWERS = 4e3;
var CAMERA_UPLOAD_CLOSE_TOKEN_REJECTED = 4001;
var CAMERA_UPLOAD_CLOSE_REPLACED = 4002;
var CAMERA_UPLOAD_CLOSE_UNAVAILABLE = 1013;
var CAMERA_UPLOAD_FRAME_VERSION = 1;
var CAMERA_UPLOAD_HEADER_BYTES = 20;
var CAMERA_UPLOAD_FRAME_KINDS = ["init", "segment", "still"];
function encodeCameraUploadHeader(header2) {
  const bytes = new Uint8Array(CAMERA_UPLOAD_HEADER_BYTES);
  const view = new DataView(bytes.buffer);
  view.setUint8(0, CAMERA_UPLOAD_FRAME_VERSION);
  view.setUint8(1, CAMERA_UPLOAD_FRAME_KINDS.indexOf(header2.kind) + 1);
  view.setUint8(2, CAMERA_STREAM_QUALITIES.indexOf(header2.quality));
  view.setUint8(3, 0);
  view.setUint32(4, header2.seq >>> 0);
  view.setBigUint64(8, BigInt(Math.trunc(header2.startMs)));
  view.setUint32(16, Math.trunc(header2.durationMs) >>> 0);
  return bytes;
}
function decodeCameraUploadFrame(message) {
  if (message.byteLength < CAMERA_UPLOAD_HEADER_BYTES) {
    return { status: "rejected", reason: "frame shorter than its header" };
  }
  const view = new DataView(
    message.buffer,
    message.byteOffset,
    message.byteLength
  );
  if (view.getUint8(0) !== CAMERA_UPLOAD_FRAME_VERSION) {
    return { status: "rejected", reason: "unsupported frame version" };
  }
  const kind = CAMERA_UPLOAD_FRAME_KINDS[view.getUint8(1) - 1];
  if (!kind) return { status: "rejected", reason: "unknown frame kind" };
  const quality = CAMERA_STREAM_QUALITIES[view.getUint8(2)];
  if (!quality) return { status: "rejected", reason: "unknown quality" };
  const payload = message.subarray(CAMERA_UPLOAD_HEADER_BYTES);
  if (payload.byteLength === 0) {
    return { status: "rejected", reason: "frame has no payload" };
  }
  return {
    status: "accepted",
    header: {
      kind,
      quality,
      seq: view.getUint32(4),
      startMs: Number(view.getBigUint64(8)),
      durationMs: view.getUint32(16)
    },
    payload
  };
}
function isOneOf5(values, value) {
  return values.includes(value);
}
function isBounded4(value, max) {
  return typeof value === "string" && value.length > 0 && value.length <= max;
}

// src/dashboard-kinds.ts
var WIDGET_TYPES = [
  "gauge",
  "dial",
  "level",
  "tile",
  "sparkline",
  "chart"
];
var OVERVIEW_MODULE_KINDS = [
  // At Anchor.
  "anchor-watch",
  "anchor-details",
  "environment-outlook",
  "forecast",
  "tide",
  "pressure",
  "power-budget",
  "tanks-budget",
  "nearby-vessels",
  "stereo",
  // Underway.
  "helm",
  "depth-safety",
  "changes",
  "engine-vitals",
  "reserves",
  "traffic",
  "passage",
  "set-drift",
  "route-map",
  // Any mode: a boat camera's still (TOM-828, cloud ADR 0411).
  "camera"
];
function isOverviewModuleKind(value) {
  return OVERVIEW_MODULE_KINDS.includes(value);
}
var OVERVIEW_MODE_KEYS = ["anchored", "underway", "docked"];
var OVERVIEW_MODE_MODULE_LIMITS = {
  anchored: 12,
  underway: 6,
  docked: 12
};
var OVERVIEW_MODULE_TITLES = {
  "anchor-watch": "Anchor watch",
  "anchor-details": "Anchor details",
  "environment-outlook": "Environment",
  forecast: "Forecast",
  tide: "Tide",
  pressure: "Pressure",
  "power-budget": "Power",
  "tanks-budget": "Tanks",
  "nearby-vessels": "Nearby vessels",
  stereo: "Stereo",
  helm: "Helm",
  "depth-safety": "Depth safety",
  changes: "Changes",
  "engine-vitals": "Engine vitals",
  reserves: "Reserves",
  traffic: "Traffic",
  passage: "Passage",
  "set-drift": "Set and drift",
  "route-map": "Route map",
  camera: "Camera"
};

// src/demo-replay.ts
var DEMO_REPLAY_JUMP_COMMAND_TYPE = "demo_replay_jump";
var DEMO_REPLAY_STATUS_CLASS = "demo_replay_status";
var DEMO_REPLAY_COMMAND_VERSION = 1;
var DEMO_REPLAY_REQUEST_ID_MAX = 200;
var DEMO_REPLAY_MAX_DAYS = 31;
var DEMO_REPLAY_TIME_ZONE_MAX = 64;
var DEMO_REPLAY_REFUSAL_REASONS = [
  /** The core was not started by the demo fleet. */
  "not_demo_core",
  /** The core has no clock-pinned replay source. */
  "no_clock_replay",
  /** `day` is past the end of the recording. */
  "day_out_of_range",
  /** The replay source is not connected, so there is no file loaded to jump in. */
  "not_connected"
];
function isObject(raw) {
  return raw !== null && typeof raw === "object" && !Array.isArray(raw);
}
function isRequestId(value) {
  return typeof value === "string" && value.length > 0 && value.length <= DEMO_REPLAY_REQUEST_ID_MAX;
}
function isDay(value) {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= DEMO_REPLAY_MAX_DAYS;
}
function isTimeZoneName(value) {
  return typeof value === "string" && value.length > 0 && value.length <= DEMO_REPLAY_TIME_ZONE_MAX;
}
function isIsoInstant(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}
function parseDemoReplayJumpPayload(raw) {
  if (!isObject(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  if (raw.v !== DEMO_REPLAY_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported demo replay command version (expected ${DEMO_REPLAY_COMMAND_VERSION})`
    };
  }
  if (!isRequestId(raw.requestId)) {
    return {
      status: "rejected",
      reason: `requestId must be a string of 1 to ${DEMO_REPLAY_REQUEST_ID_MAX} characters`
    };
  }
  if (!isDay(raw.day)) {
    return {
      status: "rejected",
      reason: `day must be a whole number from 1 to ${DEMO_REPLAY_MAX_DAYS}`
    };
  }
  if (raw.timeZone !== void 0 && !isTimeZoneName(raw.timeZone)) {
    return {
      status: "rejected",
      reason: `timeZone must be a string of 1 to ${DEMO_REPLAY_TIME_ZONE_MAX} characters`
    };
  }
  return {
    status: "accepted",
    payload: {
      v: DEMO_REPLAY_COMMAND_VERSION,
      requestId: raw.requestId,
      day: raw.day,
      ...raw.timeZone === void 0 ? {} : { timeZone: raw.timeZone }
    }
  };
}
function isFileHash(value) {
  return typeof value === "string" && /^[0-9a-f]{16}$/.test(value);
}
function parsePosition(raw) {
  if (!isDay(raw.days) || !isDay(raw.day) || raw.day > raw.days) {
    return "day and days must be whole numbers with 1 <= day <= days";
  }
  if (!isIsoInstant(raw.fileTime) || !isIsoInstant(raw.playingAt) || !isIsoInstant(raw.dayStartedAt) || !isIsoInstant(raw.nextDayAt)) {
    return "fileTime, playingAt, dayStartedAt and nextDayAt must be ISO 8601 instants";
  }
  if (Date.parse(raw.nextDayAt) <= Date.parse(raw.dayStartedAt)) {
    return "nextDayAt must be after dayStartedAt";
  }
  if (!isFileHash(raw.file)) {
    return "file must be 16 lowercase hex digits";
  }
  return {
    day: raw.day,
    days: raw.days,
    fileTime: raw.fileTime,
    playingAt: raw.playingAt,
    dayStartedAt: raw.dayStartedAt,
    nextDayAt: raw.nextDayAt,
    file: raw.file,
    // A zone of the wrong shape is dropped, not refused: the position is
    // still good, and the reader falls back to the fleet's zone.
    ...isTimeZoneName(raw.timeZone) ? { timeZone: raw.timeZone } : {}
  };
}
function parseDemoReplayStatusPayload(raw) {
  if (!isObject(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  if (raw.v !== DEMO_REPLAY_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported demo replay status version (expected ${DEMO_REPLAY_COMMAND_VERSION})`
    };
  }
  if (raw.outcome === "playing") {
    const position2 = parsePosition(raw);
    if (typeof position2 === "string") {
      return { status: "rejected", reason: position2 };
    }
    return {
      status: "accepted",
      payload: {
        v: DEMO_REPLAY_COMMAND_VERSION,
        outcome: "playing",
        ...position2
      }
    };
  }
  if (!isRequestId(raw.requestId)) {
    return {
      status: "rejected",
      reason: `requestId must be a string of 1 to ${DEMO_REPLAY_REQUEST_ID_MAX} characters`
    };
  }
  if (raw.outcome === "refused") {
    if (typeof raw.reason !== "string" || !DEMO_REPLAY_REFUSAL_REASONS.includes(raw.reason)) {
      return { status: "rejected", reason: "unknown refusal reason" };
    }
    return {
      status: "accepted",
      payload: {
        v: DEMO_REPLAY_COMMAND_VERSION,
        requestId: raw.requestId,
        outcome: "refused",
        reason: raw.reason
      }
    };
  }
  if (raw.outcome !== "jumped") {
    return {
      status: "rejected",
      reason: "outcome must be jumped, playing or refused"
    };
  }
  const position = parsePosition(raw);
  if (typeof position === "string") {
    return { status: "rejected", reason: position };
  }
  return {
    status: "accepted",
    payload: {
      v: DEMO_REPLAY_COMMAND_VERSION,
      requestId: raw.requestId,
      outcome: "jumped",
      ...position
    }
  };
}

// src/device-alerts.ts
var DEVICE_ALERTS_CLASS = "device_alerts";
var DEVICE_ALERTS_MAX_PER_FRAME = 200;
var DEVICE_ALERT_KEY_MAX = 160;
var DEVICE_ALERT_MESSAGE_MAX = 200;
var DEVICE_ALERT_SOURCES = ["n2k_126983", "victron_venus"];
var DEVICE_ALERT_STATES = [
  "active",
  "silenced",
  "acknowledged",
  "cleared"
];
var DEVICE_ALERT_SEVERITIES = [
  "emergency",
  "alarm",
  "warning",
  "caution"
];
var DEVICE_ALERT_PATH_PREFIX = "notifications.nmea.";
var VICTRON_ALERT_PATH_PREFIX = "notifications.victron.";
function deviceAlertPathSource(path) {
  if (path.startsWith(DEVICE_ALERT_PATH_PREFIX)) {
    return { source: "n2k_126983", prefix: DEVICE_ALERT_PATH_PREFIX };
  }
  if (path.startsWith(VICTRON_ALERT_PATH_PREFIX)) {
    return { source: "victron_venus", prefix: VICTRON_ALERT_PATH_PREFIX };
  }
  return null;
}
function isDeviceAlertPath(path) {
  return deviceAlertPathSource(path) !== null;
}
var N2K_DEVICE_ID = /^(n2k:.+:\d+):[^:]+$/;
function deviceAlertDeviceKey(deviceId) {
  if (typeof deviceId !== "string" || deviceId.length === 0) return null;
  const match = N2K_DEVICE_ID.exec(deviceId);
  return (match ? match[1] : deviceId).slice(0, DEVICE_ALERT_KEY_MAX);
}
function deviceAlertsIdempotencyKey(payload) {
  return `device_alerts:${sha256Hex(canonicalJson(payload)).slice(0, 32)}`;
}

// src/duration.ts
var SECOND_UNITS = /* @__PURE__ */ new Set(["s", "sec", "secs", "second", "seconds"]);
function isSecondsUnit(unit) {
  return unit !== null && unit !== void 0 && SECOND_UNITS.has(unit.toLowerCase());
}
var HOUR_METER_PATH = /^propulsion\.[^.]+\.runTime$/;
function isHourMeter(path, unit) {
  return isSecondsUnit(unit) && typeof path === "string" && HOUR_METER_PATH.test(path);
}
function hourMeterText(seconds) {
  return Math.round(seconds / 3600).toLocaleString("en-US");
}
var MINUTE = 60;
var HOUR = 3600;
var DAY = 86400;
function pair(big, bigUnit, small, smallUnit) {
  return small === 0 ? `${big} ${bigUnit}` : `${big} ${bigUnit} ${small} ${smallUnit}`;
}
function formatDurationSeconds(seconds) {
  if (!Number.isFinite(seconds)) return "\u2014";
  if (seconds < 0) return `\u2212${formatDurationSeconds(-seconds)}`;
  if (seconds === 0) return "0 s";
  if (seconds < 1) return "<1 s";
  const wholeSeconds = Math.round(seconds);
  if (wholeSeconds < MINUTE) return `${wholeSeconds} s`;
  if (wholeSeconds < HOUR) {
    const min = Math.floor(wholeSeconds / MINUTE);
    return pair(min, "min", wholeSeconds - min * MINUTE, "s");
  }
  const wholeMinutes = Math.round(seconds / MINUTE);
  if (wholeMinutes < DAY / MINUTE) {
    const h = Math.floor(wholeMinutes / 60);
    return pair(h, "h", wholeMinutes - h * 60, "min");
  }
  const wholeHours = Math.round(seconds / HOUR);
  const d = Math.floor(wholeHours / 24);
  return pair(d, "d", wholeHours - d * 24, "h");
}
function durationScale(maxAbsSeconds) {
  const m = Number.isFinite(maxAbsSeconds) ? Math.abs(maxAbsSeconds) : 0;
  if (m < MINUTE) return { unit: "s", seconds: 1 };
  if (m < HOUR) return { unit: "min", seconds: MINUTE };
  if (m < 2 * DAY) return { unit: "h", seconds: HOUR };
  return { unit: "d", seconds: DAY };
}

// src/display-units.ts
var STORE_UNIT = {
  distance: "m",
  shortDistance: "m",
  speed: "kn",
  depth: "m",
  temperature: "C",
  pressure: "hPa",
  volume: "L"
};
var same = (value) => value;
var always = (decimals) => () => decimals;
var readsBelow = (abs, decimals, cutoff) => Number(abs.toFixed(decimals)) < cutoff;
var coarserAtTen = (below, atOrAbove) => (abs) => readsBelow(abs, below, 10) ? below : atOrAbove;
var DISPLAY_RULES = {
  distance: {
    nm: {
      label: "NM",
      convert: (v) => v / 1852,
      invert: (v) => v * 1852,
      decimals: coarserAtTen(2, 1)
    },
    mi: {
      label: "mi",
      convert: (v) => v / 1609.344,
      invert: (v) => v * 1609.344,
      decimals: coarserAtTen(2, 1)
    },
    km: {
      label: "km",
      convert: (v) => v / 1e3,
      invert: (v) => v * 1e3,
      decimals: coarserAtTen(2, 1)
    }
  },
  // Whole metres/feet for ranges and rode; a decimal only for small readouts.
  shortDistance: {
    m: {
      label: "m",
      convert: same,
      invert: same,
      decimals: coarserAtTen(1, 0)
    },
    ft: {
      label: "ft",
      convert: (v) => v / 0.3048,
      invert: (v) => v * 0.3048,
      decimals: coarserAtTen(1, 0)
    }
  },
  speed: {
    kn: {
      label: "kn",
      convert: same,
      invert: same,
      decimals: coarserAtTen(1, 0)
    },
    mph: {
      label: "mph",
      convert: (v) => v * 1.15078,
      invert: (v) => v / 1.15078,
      decimals: coarserAtTen(1, 0)
    },
    kmh: {
      label: "km/h",
      convert: (v) => v * 1.852,
      invert: (v) => v / 1.852,
      decimals: coarserAtTen(1, 0)
    }
  },
  // Depth keeps a decimal in metres and fathoms — under a keel, 0.1 matters.
  depth: {
    m: { label: "m", convert: same, invert: same, decimals: always(1) },
    ft: {
      label: "ft",
      convert: (v) => v / 0.3048,
      invert: (v) => v * 0.3048,
      decimals: coarserAtTen(1, 0)
    },
    ftm: {
      label: "ftm",
      convert: (v) => v / 1.8288,
      invert: (v) => v * 1.8288,
      decimals: always(1)
    }
  },
  temperature: {
    C: { label: "\xB0C", convert: same, invert: same, decimals: always(1) },
    F: {
      label: "\xB0F",
      convert: (v) => v * 1.8 + 32,
      invert: (v) => (v - 32) / 1.8,
      decimals: always(1)
    }
  },
  // A barometer moves in whole hPa but in hundredths of an inch of mercury.
  pressure: {
    hPa: { label: "hPa", convert: same, invert: same, decimals: always(0) },
    inHg: {
      label: "inHg",
      convert: (v) => v / 33.86389,
      invert: (v) => v * 33.86389,
      decimals: always(2)
    }
  },
  volume: {
    L: {
      label: "L",
      convert: same,
      invert: same,
      decimals: coarserAtTen(1, 0)
    },
    gal: {
      label: "gal",
      convert: (v) => v / 3.78541,
      invert: (v) => v * 3.78541,
      decimals: coarserAtTen(1, 0)
    }
  }
};
var RATE_UNITS = {
  "L/h": { quantity: "L", period: "/h" }
};
function categoryFor(path, storeUnit) {
  if (path !== void 0 && path !== "") {
    if (path === "environment.depth" || path.startsWith("environment.depth."))
      return "depth";
    if (path === "navigation.speedOverGround" || path === "navigation.speedThroughWater" || path.startsWith("environment.wind.speed")) {
      return "speed";
    }
    if (path.endsWith(".temperature")) return "temperature";
    if (path.startsWith("environment.") && path.endsWith(".pressure"))
      return "pressure";
  }
  if (storeUnit === null || storeUnit === void 0 || storeUnit === "")
    return void 0;
  return UNIT_FALLBACK[canonicalUnit(storeUnit)];
}
var UNIT_FALLBACK = {
  kn: "speed",
  C: "temperature",
  m: "shortDistance",
  L: "volume"
};
var METRIC_LABELS = {
  C: "\xB0C",
  deg: "\xB0",
  kn: "kn",
  m: "m",
  hPa: "hPa",
  "%": "%",
  rpm: "rpm",
  V: "V",
  A: "A",
  W: "W",
  L: "L",
  "L/h": "L/h",
  s: "s",
  "deg/min": "\xB0/min"
};
var UNIT_ALIASES = {
  meter: "m",
  meters: "m",
  metre: "m",
  metres: "m",
  knot: "kn",
  knots: "kn",
  percent: "%",
  degree: "deg",
  degrees: "deg",
  celsius: "C",
  liter: "L",
  liters: "L",
  litre: "L",
  litres: "L"
};
function canonicalUnit(unit) {
  return UNIT_ALIASES[unit.toLowerCase()] ?? unit;
}
function displayUnitKnown(storeUnit) {
  if (storeUnit === null || storeUnit === void 0 || storeUnit === "")
    return false;
  return METRIC_LABELS[canonicalUnit(storeUnit)] !== void 0;
}
var WHOLE_PASSTHROUGH_UNITS = /* @__PURE__ */ new Set(["rpm", "W", "s", "deg", "hPa"]);
var WHOLE_FROM = {
  kn: 10,
  "%": 10,
  V: 50
};
function passthroughDecimals(canonical, abs) {
  if (WHOLE_PASSTHROUGH_UNITS.has(canonical)) return 0;
  const wholeFrom = WHOLE_FROM[canonical];
  if (wholeFrom !== void 0) return readsBelow(abs, 1, wholeFrom) ? 1 : 0;
  return abs >= 100 ? 0 : 1;
}
function roundedReading(value, storeUnit) {
  if (!Number.isFinite(value)) return void 0;
  if (storeUnit === null || storeUnit === void 0) return void 0;
  const canonical = canonicalUnit(storeUnit.trim());
  if (WHOLE_FROM[canonical] === void 0) return void 0;
  const decimals = passthroughDecimals(canonical, Math.abs(value));
  return (Number(value.toFixed(decimals)) + 0).toFixed(decimals);
}
function ruleFor(category, canonical, settings) {
  const rate = RATE_UNITS[canonical];
  if ((rate?.quantity ?? canonical) !== STORE_UNIT[category]) return void 0;
  const rules = DISPLAY_RULES[category];
  const rule = rules[settings[category]];
  if (rule === void 0 || rate === void 0) return rule;
  return {
    ...rule,
    label: `${rule.label}${rate.period}`,
    decimals: always(1)
  };
}
function resolve(value, storeUnit, category, settings) {
  if (storeUnit === null || storeUnit === void 0 || storeUnit === "") {
    return {
      value,
      unit: "",
      decimals: passthroughDecimals("", Math.abs(value))
    };
  }
  const canonical = canonicalUnit(storeUnit);
  const rule = category === void 0 ? void 0 : ruleFor(category, canonical, settings);
  if (rule === void 0) {
    return {
      value,
      unit: METRIC_LABELS[canonical] ?? canonical,
      decimals: passthroughDecimals(canonical, Math.abs(value))
    };
  }
  const converted = rule.convert(value);
  return {
    value: converted,
    unit: rule.label,
    decimals: rule.decimals(Math.abs(converted))
  };
}
function convertQuantity(value, storeUnit, category, settings) {
  const r = resolve(value, storeUnit, category, settings);
  return { value: r.value, unit: r.unit };
}
function formatQuantity(value, storeUnit, category, settings) {
  const r = resolve(value, storeUnit, category, settings);
  return { text: r.value.toFixed(r.decimals), unit: r.unit };
}
function toDisplay(value, unit, category, settings) {
  return convertQuantity(value, unit, category, settings);
}
function fromDisplay(value, storeUnit, category, settings) {
  if (storeUnit === null || storeUnit === void 0 || storeUnit === "")
    return value;
  if (category === void 0) return value;
  const rule = ruleFor(category, canonicalUnit(storeUnit), settings);
  return rule === void 0 ? value : rule.invert(value);
}
function formatDisplay(value, unit, category, settings, path) {
  if (isHourMeter(path, unit)) return { text: hourMeterText(value), unit: "h" };
  if (isSecondsUnit(unit))
    return { text: formatDurationSeconds(value), unit: "" };
  return formatQuantity(value, unit, category, settings);
}
function formatTargetRange(distanceM, settings) {
  const category = Math.abs(distanceM) < 1e3 ? "shortDistance" : "distance";
  return formatQuantity(distanceM, "m", category, settings);
}
function unitLabel(category, unit) {
  const rules = DISPLAY_RULES[category];
  return rules[unit]?.label ?? unit;
}
function unitConversionFacts() {
  const out = [];
  for (const category of Object.keys(DISPLAY_RULES)) {
    const rules = DISPLAY_RULES[category];
    for (const [unit, rule] of Object.entries(rules)) {
      const offset = rule.convert(0);
      const factor = Number((rule.convert(1) - offset).toPrecision(6));
      if (factor === 1 && offset === 0) continue;
      out.push({
        category,
        storeUnit: STORE_UNIT[category],
        unit,
        label: rule.label,
        factor,
        offset
      });
    }
  }
  return out;
}

// src/engine-summary.ts
var ENGINE_LOGBOOK_STATUSES = [
  "ok",
  "partial",
  "not_derived",
  "no_engines"
];
var ENGINE_SUMMARY_WINDOW_DAYS = 90;

// src/neo-status.ts
var NEO_STATUS_TONES = [
  "calm",
  "caution",
  "alarm",
  "unknown"
];
var NEO_STATUS_WATCH_TONES = ["warn", "bad"];
var NEO_STATUS_WATCH_KEYS = [
  "anchor",
  "alarm",
  "depth",
  "wind",
  "battery",
  "fresh_water"
];
var NEO_STATUS_ICONS = [
  "anchor",
  "alarm",
  "tide",
  "wind",
  "battery",
  "water",
  "tombolo"
];
var NEO_STATUS_SOURCES = ["rules"];
function isObj3(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function oneOf2(list2, value) {
  return list2.find((item) => item === value);
}
function text(value) {
  return typeof value === "string" ? value : void 0;
}
function parseWatch(value) {
  if (value === null) return null;
  if (!isObj3(value)) return void 0;
  const key = text(value.key);
  const tone = oneOf2(NEO_STATUS_WATCH_TONES, value.tone);
  const title = text(value.title);
  const body = text(value.body);
  if (key === void 0 || key.length === 0) return void 0;
  if (tone === void 0 || title === void 0 || title.length === 0) {
    return void 0;
  }
  if (body === void 0) return void 0;
  return {
    key,
    tone,
    icon: oneOf2(NEO_STATUS_ICONS, value.icon) ?? "tombolo",
    title,
    body
  };
}
function parseNeoStatus(value) {
  if (!isObj3(value)) return null;
  const headline = text(value.headline);
  const body = text(value.body);
  const composedAt = text(value.composedAt);
  const timeZone = text(value.timeZone);
  if (headline === void 0 || headline.length === 0 || body === void 0) {
    return null;
  }
  if (composedAt === void 0 || !Number.isFinite(Date.parse(composedAt))) {
    return null;
  }
  if (timeZone === void 0 || timeZone.length === 0) return null;
  return {
    tone: oneOf2(NEO_STATUS_TONES, value.tone) ?? "unknown",
    headline,
    body,
    watch: parseWatch(value.watch) ?? null,
    composedAt,
    timeZone,
    source: oneOf2(NEO_STATUS_SOURCES, value.source) ?? "rules"
  };
}

// src/firmware.ts
var FIRMWARE_MANIFEST_DOMAIN = "tombolo.neos.update.v1\n";
var PINNED_FIRMWARE_KEYS = [
  {
    keyId: "tombolo-os-2026a",
    publicJwk: {
      kty: "OKP",
      crv: "Ed25519",
      x: "-lSyq7Mz5mJZfdrpFPjf3fJVxHVLf5fwIDVEF_6_URU"
    },
    notBefore: "2026-09-01T00:00:00Z",
    notAfter: "2028-09-01T00:00:00Z"
  },
  {
    keyId: "tombolo-os-2027a",
    publicJwk: {
      kty: "OKP",
      crv: "Ed25519",
      x: "PD6EljPL9Ag0ZtiSn-90h-0tU_L79csl88LZdepzVQg"
    },
    notBefore: "2027-09-01T00:00:00Z",
    notAfter: "2029-09-01T00:00:00Z"
  }
];
var FIRMWARE_DOWNLOAD_DEADLINE_MS = 6 * 60 * 60 * 1e3;
var FIRMWARE_PENDING_STALE_MS = 60 * 60 * 1e3;
var FIRMWARE_OWNER_WAIT_LIMIT_MS = 7 * 60 * 60 * 1e3;
var STRICT_VERSION = /^(\d+)\.(\d+)\.(\d+)$/;
var SHA256_HEX2 = /^[0-9a-f]{64}$/;
function compareFirmwareVersions(a, b) {
  const left = STRICT_VERSION.exec(a);
  const right = STRICT_VERSION.exec(b);
  if (!left || !right) return null;
  for (let index = 1; index <= 3; index += 1) {
    const leftPart = Number(left[index]);
    const rightPart = Number(right[index]);
    if (!Number.isSafeInteger(leftPart) || !Number.isSafeInteger(rightPart))
      return null;
    if (leftPart !== rightPart) return leftPart - rightPart;
  }
  return 0;
}
function isDownloadUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}
function isFirmwareArtifact(value) {
  if (typeof value !== "object" || value === null) return false;
  const artifact = value;
  return typeof artifact.url === "string" && isDownloadUrl(artifact.url) && typeof artifact.sha256 === "string" && SHA256_HEX2.test(artifact.sha256) && typeof artifact.size === "number" && Number.isSafeInteger(artifact.size) && artifact.size > 0;
}
function isFirmwareManifest(value) {
  if (typeof value !== "object" || value === null) return false;
  const manifest = value;
  if (manifest.v !== 1) return false;
  if (typeof manifest.version !== "string" || compareFirmwareVersions(manifest.version, "0.0.0") === null) {
    return false;
  }
  if (manifest.channel !== "prod" && manifest.channel !== "staging")
    return false;
  if (typeof manifest.tomboloCoreVersion !== "string" || compareFirmwareVersions(manifest.tomboloCoreVersion, "0.0.0") === null) {
    return false;
  }
  if (manifest.notes !== void 0 && (typeof manifest.notes !== "string" || !isDownloadUrl(manifest.notes))) {
    return false;
  }
  if (typeof manifest.releasedAt !== "string" || Number.isNaN(Date.parse(manifest.releasedAt))) {
    return false;
  }
  if (typeof manifest.artifacts !== "object" || manifest.artifacts === null || Array.isArray(manifest.artifacts))
    return false;
  const artifacts = Object.entries(
    manifest.artifacts
  );
  if (artifacts.length === 0) return false;
  return artifacts.every(
    ([compatible, artifact]) => compatible.length > 0 && isFirmwareArtifact(artifact)
  );
}
function firmwareArtifactForCompatible(manifest, compatible) {
  if (!manifest || !compatible || !Object.hasOwn(manifest.artifacts, compatible)) {
    return null;
  }
  return manifest.artifacts[compatible] ?? null;
}
function firmwareUpdateAvailable(manifest, runningVersion, compatible) {
  if (!manifest || !runningVersion || !compatible) return null;
  const artifact = firmwareArtifactForCompatible(manifest, compatible);
  if (!artifact) return null;
  const comparison = compareFirmwareVersions(runningVersion, manifest.version);
  if (comparison === null || comparison >= 0) return null;
  return {
    version: manifest.version,
    tomboloCoreVersion: manifest.tomboloCoreVersion,
    size: artifact.size,
    ...manifest.notes !== void 0 ? { notes: manifest.notes } : {}
  };
}
function isFirmwareAcceptanceActive(phase, at, nowMs) {
  if (phase !== "accepted" || at === null || at === void 0) return false;
  const acceptedAt = Date.parse(at);
  if (!Number.isFinite(acceptedAt)) return false;
  const age = Math.max(0, nowMs - acceptedAt);
  return age <= FIRMWARE_OWNER_WAIT_LIMIT_MS;
}

// src/mode-archetype.ts
var OVERVIEW_ARCHETYPE_BY_MODE = /* @__PURE__ */ new Map([
  ["Underway", "underway"],
  ["Sailing", "underway"],
  ["Anchored", "anchored"],
  ["Moored", "anchored"],
  ["Docked", "docked"],
  ["Maintenance", "docked"]
]);
function modeToOverviewArchetype(mode) {
  return OVERVIEW_ARCHETYPE_BY_MODE.get(mode) ?? null;
}

// src/mode-command.ts
var MODE_COMMAND_VERSION = 1;
var MODE_COMMAND_ACTIONS = ["pin", "auto"];
var MODE_COMMAND_ID_MAX = 200;
var MODE_COMMAND_USER_LABEL_MAX = 64;
var MODE_COMMAND_STATUS_CLASS = "mode_command_status";
var MODE_COMMAND_STATUS_PHASES = [
  "accepted",
  "complete",
  "rejected"
];
var MODE_COMMAND_KEY_MAX = 200;
var MODE_COMMAND_STATUS_DETAIL_MAX = 500;
function isCanonicalMode(value) {
  return typeof value === "string" && CANONICAL_MODES.includes(value);
}
function parseModeCommandPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== MODE_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported mode command version (expected ${MODE_COMMAND_VERSION})`
    };
  }
  if (!isModeCommandAction(value.action)) {
    return { status: "rejected", reason: "missing or unknown action" };
  }
  const action = value.action;
  if (action === "pin") {
    if (value.mode === void 0) {
      return {
        status: "rejected",
        action,
        reason: "mode is required for action=pin"
      };
    }
    if (!isCanonicalMode(value.mode)) {
      return {
        status: "rejected",
        action,
        reason: `mode must be one of ${CANONICAL_MODES.join(", ")}`
      };
    }
  } else if (value.mode !== void 0) {
    return {
      status: "rejected",
      action,
      reason: "mode is not allowed for action=auto"
    };
  }
  if (typeof value.sourceUserId !== "string" || value.sourceUserId.length === 0 || value.sourceUserId.length > MODE_COMMAND_ID_MAX) {
    return {
      status: "rejected",
      action,
      reason: "sourceUserId must be a non-empty string"
    };
  }
  const label2 = typeof value.sourceUserLabel === "string" && value.sourceUserLabel.length > 0 ? value.sourceUserLabel.slice(0, MODE_COMMAND_USER_LABEL_MAX) : void 0;
  return {
    status: "accepted",
    payload: {
      v: MODE_COMMAND_VERSION,
      action,
      ...action === "pin" ? { mode: value.mode } : {},
      sourceUserId: value.sourceUserId,
      ...label2 !== void 0 ? { sourceUserLabel: label2 } : {}
    }
  };
}
function parseModeCommandStatusPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== MODE_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported mode command status version (expected ${MODE_COMMAND_VERSION})`
    };
  }
  if (typeof value.commandKey !== "string" || value.commandKey.length === 0 || value.commandKey.length > MODE_COMMAND_KEY_MAX) {
    return {
      status: "rejected",
      reason: `commandKey must be a non-empty string of at most ${MODE_COMMAND_KEY_MAX} characters`
    };
  }
  if (!isModeCommandAction(value.action)) {
    return { status: "rejected", reason: "missing or unknown action" };
  }
  if (!isModeCommandStatusPhase(value.phase)) {
    return { status: "rejected", reason: "missing or unknown phase" };
  }
  if (typeof value.at !== "string" || !Number.isFinite(Date.parse(value.at))) {
    return { status: "rejected", reason: "at must be an ISO-8601 timestamp" };
  }
  if (value.mode !== void 0 && !isCanonicalMode(value.mode)) {
    return {
      status: "rejected",
      reason: `mode must be one of ${CANONICAL_MODES.join(", ")}`
    };
  }
  if (value.detail !== void 0 && (typeof value.detail !== "string" || value.detail.length > MODE_COMMAND_STATUS_DETAIL_MAX)) {
    return {
      status: "rejected",
      reason: `detail must be a string of at most ${MODE_COMMAND_STATUS_DETAIL_MAX} characters`
    };
  }
  return {
    status: "accepted",
    payload: {
      v: MODE_COMMAND_VERSION,
      commandKey: value.commandKey,
      action: value.action,
      ...typeof value.mode === "string" ? { mode: value.mode } : {},
      phase: value.phase,
      at: value.at,
      ...typeof value.detail === "string" && value.detail.length > 0 ? { detail: value.detail } : {}
    }
  };
}
function isModeCommandAction(value) {
  return MODE_COMMAND_ACTIONS.includes(value);
}
function isModeCommandStatusPhase(value) {
  return MODE_COMMAND_STATUS_PHASES.includes(value);
}

// src/mode-state.ts
var MODE_STATE_SNAPSHOT_VERSION = 1;
var MODE_STATE_SNAPSHOT_CLASS = "mode_state_snapshot";
var MODE_STATE_STATUSES = [
  "manual",
  "resolved",
  "ambiguous",
  "unknown"
];
var MODE_STATE_CONFIDENCES = ["high", "medium", "low"];
var MODE_STATE_LABEL_MAX = 64;
var MODE_STATE_BECAUSE_MAX = 200;
var MODE_STATE_BECAUSE_CODE_MAX = 32;
var MODE_STATE_MAX_BECAUSE_PARAMS = 2;
var MODE_STATE_BECAUSE_PARAM_KEY_MAX = 24;
var MODE_STATE_BECAUSE_PARAM_UNIT_MAX = 8;
var MODE_STATE_MAX_CANDIDATES = 8;
var MODE_STATE_EVIDENCE_MAX_JSON_CHARS = 4096;
var MODE_STATE_SNAPSHOT_MAX_JSON_CHARS = 10240;
var MODE_STATE_SET_BY_MAX = 64;
var MODE_STATE_SET_VIA_CLOUD = "cloud";
var MODE_STATE_SET_VIA_LAN = "lan";
var MODE_STATE_BECAUSE_TEMPLATES = {
  moving_fast: "Moving at {speed} and making real ground.",
  swinging_at_anchor: "Swinging in a {radius} circle around one spot.",
  swinging_beyond_mooring: "Swinging in a {radius} circle \u2014 too wide for a mooring, so it is lying to an anchor.",
  swinging_at_mooring: "Swinging in a {radius} circle \u2014 tight enough to be a mooring buoy."
};
function renderModeStateBecause(code, params, format) {
  const template = Object.prototype.hasOwnProperty.call(
    MODE_STATE_BECAUSE_TEMPLATES,
    code
  ) ? MODE_STATE_BECAUSE_TEMPLATES[code] : void 0;
  if (template === void 0) return null;
  let unfilled = false;
  const sentence = template.replace(/\{(\w+)\}/g, (_hole, key) => {
    const param = params !== null && params !== void 0 && Object.prototype.hasOwnProperty.call(params, key) ? params[key] : void 0;
    const shown = param === void 0 ? null : format(param.value, param.unit);
    if (shown === null) {
      unfilled = true;
      return "";
    }
    return shown;
  });
  return unfilled ? null : sentence;
}
function parseModeStateSnapshotPayload(raw) {
  if (!isPlainObject(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  if (raw.v !== MODE_STATE_SNAPSHOT_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported mode state snapshot version (expected ${MODE_STATE_SNAPSHOT_VERSION})`
    };
  }
  if (!isLabel(raw.current)) {
    return { status: "rejected", reason: rejectLabel("current") };
  }
  if (!isTimestamp3(raw.since)) {
    return { status: "rejected", reason: rejectTimestamp("since") };
  }
  if (!isLabel(raw.detectedBy)) {
    return { status: "rejected", reason: rejectLabel("detectedBy") };
  }
  const context = parseContext(raw.context);
  if (!context.ok) return { status: "rejected", reason: context.reason };
  const setBy = parseSetBy(raw.setBy);
  const setVia = isLabel(raw.setVia) ? raw.setVia : void 0;
  return {
    status: "accepted",
    payload: {
      v: MODE_STATE_SNAPSHOT_VERSION,
      current: raw.current,
      since: raw.since,
      detectedBy: raw.detectedBy,
      ...setBy !== void 0 ? { setBy } : {},
      ...setVia !== void 0 ? { setVia } : {},
      context: context.context
    }
  };
}
function parseSetBy(raw) {
  if (typeof raw !== "string") return void 0;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return void 0;
  return trimmed.slice(0, MODE_STATE_SET_BY_MAX);
}
function parseContext(raw) {
  if (!isPlainObject(raw)) {
    return { ok: false, reason: "context must be an object" };
  }
  if (raw.primary !== void 0 && !isLabel(raw.primary)) {
    return { ok: false, reason: rejectLabel("context.primary") };
  }
  if (raw.lastKnownPrimary !== void 0 && !isLabel(raw.lastKnownPrimary)) {
    return { ok: false, reason: rejectLabel("context.lastKnownPrimary") };
  }
  if (!isStatus(raw.status)) {
    return {
      ok: false,
      reason: `context.status must be one of ${MODE_STATE_STATUSES.join(", ")}`
    };
  }
  if (!isConfidence(raw.confidence)) {
    return { ok: false, reason: rejectConfidence("context.confidence") };
  }
  if (!isLabel(raw.reasonCode)) {
    return { ok: false, reason: rejectLabel("context.reasonCode") };
  }
  if (!isTimestamp3(raw.computedAt)) {
    return { ok: false, reason: rejectTimestamp("context.computedAt") };
  }
  if (raw.evidenceAt !== void 0 && !isTimestamp3(raw.evidenceAt)) {
    return { ok: false, reason: rejectTimestamp("context.evidenceAt") };
  }
  if (raw.heldOver !== void 0 && typeof raw.heldOver !== "boolean") {
    return { ok: false, reason: "context.heldOver must be a boolean" };
  }
  if (raw.measuredAt !== void 0 && !isTimestamp3(raw.measuredAt)) {
    return { ok: false, reason: rejectTimestamp("context.measuredAt") };
  }
  if (!isCounter(raw.classifierVersion)) {
    return { ok: false, reason: rejectCounter("context.classifierVersion") };
  }
  if (!isCounter(raw.authorityEpoch)) {
    return { ok: false, reason: rejectCounter("context.authorityEpoch") };
  }
  if (!isCounter(raw.revision)) {
    return { ok: false, reason: rejectCounter("context.revision") };
  }
  const candidates = parseCandidates(raw.candidates);
  if (!candidates.ok) return { ok: false, reason: candidates.reason };
  const evidence = parseEvidence(raw.evidence);
  if (!evidence.ok) return { ok: false, reason: evidence.reason };
  return {
    ok: true,
    context: {
      ...raw.primary !== void 0 ? { primary: raw.primary } : {},
      ...raw.lastKnownPrimary !== void 0 ? { lastKnownPrimary: raw.lastKnownPrimary } : {},
      candidates: candidates.candidates,
      status: raw.status,
      confidence: raw.confidence,
      reasonCode: raw.reasonCode,
      ...raw.heldOver !== void 0 ? { heldOver: raw.heldOver } : {},
      ...raw.measuredAt !== void 0 ? { measuredAt: raw.measuredAt } : {},
      evidence: evidence.evidence,
      ...raw.evidenceAt !== void 0 ? { evidenceAt: raw.evidenceAt } : {},
      computedAt: raw.computedAt,
      classifierVersion: raw.classifierVersion,
      authorityEpoch: raw.authorityEpoch,
      revision: raw.revision
    }
  };
}
function parseCandidates(raw) {
  if (!Array.isArray(raw) || raw.length > MODE_STATE_MAX_CANDIDATES) {
    return {
      ok: false,
      reason: `context.candidates must be an array of at most ${MODE_STATE_MAX_CANDIDATES} entries`
    };
  }
  const candidates = [];
  for (const entry of raw) {
    if (!isPlainObject(entry)) {
      return {
        ok: false,
        reason: "context.candidates entries must be objects"
      };
    }
    if (!isLabel(entry.mode)) {
      return { ok: false, reason: rejectLabel("context.candidates[].mode") };
    }
    if (!isConfidence(entry.confidence)) {
      return {
        ok: false,
        reason: rejectConfidence("context.candidates[].confidence")
      };
    }
    if (typeof entry.because !== "string" || entry.because.length > MODE_STATE_BECAUSE_MAX) {
      return {
        ok: false,
        reason: `context.candidates[].because must be a string of at most ${MODE_STATE_BECAUSE_MAX} characters`
      };
    }
    if (entry.becauseCode !== void 0 && !isBecauseCode(entry.becauseCode)) {
      return {
        ok: false,
        reason: `context.candidates[].becauseCode must be a non-empty string of at most ${MODE_STATE_BECAUSE_CODE_MAX} characters`
      };
    }
    const params = parseBecauseParams(entry.becauseParams);
    if (!params.ok) return { ok: false, reason: params.reason };
    candidates.push({
      mode: entry.mode,
      confidence: entry.confidence,
      because: entry.because,
      ...entry.becauseCode !== void 0 ? { becauseCode: entry.becauseCode } : {},
      ...params.params !== void 0 ? { becauseParams: params.params } : {}
    });
  }
  return { ok: true, candidates };
}
function parseBecauseParams(raw) {
  if (raw === void 0) return { ok: true };
  if (!isPlainObject(raw)) {
    return {
      ok: false,
      reason: "context.candidates[].becauseParams must be an object"
    };
  }
  const entries = Object.entries(raw);
  if (entries.length > MODE_STATE_MAX_BECAUSE_PARAMS) {
    return {
      ok: false,
      reason: `context.candidates[].becauseParams must hold at most ${MODE_STATE_MAX_BECAUSE_PARAMS} entries`
    };
  }
  const parsed = [];
  for (const [key, entry] of entries) {
    if (key.length === 0 || key.length > MODE_STATE_BECAUSE_PARAM_KEY_MAX) {
      return {
        ok: false,
        reason: `context.candidates[].becauseParams keys must be non-empty and at most ${MODE_STATE_BECAUSE_PARAM_KEY_MAX} characters`
      };
    }
    if (!isPlainObject(entry)) {
      return {
        ok: false,
        reason: `context.candidates[].becauseParams.${key} must be an object`
      };
    }
    if (typeof entry.value !== "number" || !Number.isFinite(entry.value)) {
      return {
        ok: false,
        reason: `context.candidates[].becauseParams.${key}.value must be a finite number`
      };
    }
    if (typeof entry.unit !== "string" || entry.unit.length > MODE_STATE_BECAUSE_PARAM_UNIT_MAX) {
      return {
        ok: false,
        reason: `context.candidates[].becauseParams.${key}.unit must be a string of at most ${MODE_STATE_BECAUSE_PARAM_UNIT_MAX} characters`
      };
    }
    parsed.push([key, { value: entry.value, unit: entry.unit }]);
  }
  return { ok: true, params: Object.fromEntries(parsed) };
}
function parseEvidence(raw) {
  if (!isPlainObject(raw)) {
    return { ok: false, reason: "context.evidence must be an object" };
  }
  const entries = Object.entries(raw);
  for (const [key, entry] of entries) {
    const scalar = entry === null || typeof entry === "string" || typeof entry === "boolean" || typeof entry === "number" && Number.isFinite(entry);
    if (!scalar) {
      return {
        ok: false,
        reason: `context.evidence.${key} must be a string, finite number, boolean, or null`
      };
    }
  }
  if (JSON.stringify(raw).length > MODE_STATE_EVIDENCE_MAX_JSON_CHARS) {
    return {
      ok: false,
      reason: `context.evidence must serialize to at most ${MODE_STATE_EVIDENCE_MAX_JSON_CHARS} characters`
    };
  }
  return { ok: true, evidence: Object.fromEntries(entries) };
}
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isLabel(value) {
  return typeof value === "string" && value.length > 0 && value.length <= MODE_STATE_LABEL_MAX;
}
function isBecauseCode(value) {
  return typeof value === "string" && value.length > 0 && value.length <= MODE_STATE_BECAUSE_CODE_MAX;
}
function isStatus(value) {
  return MODE_STATE_STATUSES.includes(value);
}
function isConfidence(value) {
  return MODE_STATE_CONFIDENCES.includes(value);
}
function isTimestamp3(value) {
  return isLabel(value) && Number.isFinite(Date.parse(value));
}
function isCounter(value) {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
function rejectLabel(field) {
  return `${field} must be a non-empty string of at most ${MODE_STATE_LABEL_MAX} characters`;
}
function rejectTimestamp(field) {
  return `${field} must be an ISO-8601 timestamp`;
}
function rejectConfidence(field) {
  return `${field} must be one of ${MODE_STATE_CONFIDENCES.join(", ")}`;
}
function rejectCounter(field) {
  return `${field} must be a non-negative integer`;
}

// src/n2k-output.ts
var N2K_OUTPUT_CONFIG_VERSION = 1;
var N2K_OUTPUT_STATE_CLASS = "n2k_output_state";
var N2K_OUTPUT_MAX_ROWS = 64;
var N2K_OUTPUT_TARGET_ID_MAX = 64;
var N2K_OUTPUT_INPUT_PATH_MAX = 200;
var N2K_OUTPUT_SOURCE_ID_MAX = 200;
var N2K_OUTPUT_INSTANCE_MAX = 252;
var N2K_OUTPUT_COMMAND_ID_MAX = 200;
var N2K_OUTPUT_INSTALL_ID_MAX = 64;
var N2K_OUTPUT_COMMAND_USER_LABEL_MAX = 64;
var N2K_OUTPUT_MAX_STATE_TARGETS = 256;
var N2K_OUTPUT_MAX_STATE_GATEWAYS = 64;
var N2K_OUTPUT_MAX_RECEIVE_DEVICES = 64;
function n2kOutputConfigCanonical(config) {
  return {
    enabled: config.enabled === true,
    egressSourceId: typeof config.egressSourceId === "string" && config.egressSourceId !== "" ? config.egressSourceId : null,
    rows: config.rows.map((row) => ({
      targetId: row.targetId,
      inputPath: row.inputPath,
      instance: row.instance,
      enabled: row.enabled === true
    }))
  };
}
function n2kOutputConfigDigest(config) {
  return sha256Hex(canonicalJson(n2kOutputConfigCanonical(config)));
}
var N2K_OUTPUT_CONFIG_COMMAND_VERSION = 1;
function parseRow(raw, index) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { reason: `rows[${index}] must be an object` };
  }
  const value = raw;
  if (typeof value.targetId !== "string" || value.targetId.length === 0 || value.targetId.length > N2K_OUTPUT_TARGET_ID_MAX) {
    return { reason: `rows[${index}].targetId must be a non-empty string` };
  }
  if (typeof value.inputPath !== "string" || value.inputPath.length === 0 || value.inputPath.length > N2K_OUTPUT_INPUT_PATH_MAX) {
    return { reason: `rows[${index}].inputPath must be a non-empty string` };
  }
  if (typeof value.instance !== "number" || !Number.isInteger(value.instance) || value.instance < 0 || value.instance > N2K_OUTPUT_INSTANCE_MAX) {
    return {
      reason: `rows[${index}].instance must be an integer 0-${N2K_OUTPUT_INSTANCE_MAX}`
    };
  }
  if (typeof value.enabled !== "boolean") {
    return { reason: `rows[${index}].enabled must be a boolean` };
  }
  return {
    row: {
      targetId: value.targetId,
      inputPath: value.inputPath,
      instance: value.instance,
      enabled: value.enabled
    }
  };
}
function parseN2kOutputConfig(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { reason: "config must be an object" };
  }
  const value = raw;
  if (typeof value.enabled !== "boolean") {
    return { reason: "config.enabled must be a boolean" };
  }
  const egressRaw = value.egressSourceId;
  if (egressRaw !== null && egressRaw !== void 0 && (typeof egressRaw !== "string" || egressRaw.length > N2K_OUTPUT_SOURCE_ID_MAX)) {
    return { reason: "config.egressSourceId must be a string or null" };
  }
  if (!Array.isArray(value.rows)) {
    return { reason: "config.rows must be an array" };
  }
  if (value.rows.length > N2K_OUTPUT_MAX_ROWS) {
    return { reason: `config.rows exceeds ${N2K_OUTPUT_MAX_ROWS} entries` };
  }
  const rows = [];
  for (let i = 0; i < value.rows.length; i++) {
    const parsed = parseRow(value.rows[i], i);
    if ("reason" in parsed) return { reason: parsed.reason };
    rows.push(parsed.row);
  }
  return {
    config: {
      enabled: value.enabled,
      egressSourceId: typeof egressRaw === "string" && egressRaw !== "" ? egressRaw : null,
      rows
    }
  };
}
function parseN2kOutputConfigCommandPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== N2K_OUTPUT_CONFIG_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported n2k output command version (expected ${N2K_OUTPUT_CONFIG_COMMAND_VERSION})`
    };
  }
  const parsed = parseN2kOutputConfig(value.config);
  if ("reason" in parsed) {
    return { status: "rejected", reason: parsed.reason };
  }
  if (typeof value.configDigest !== "string" || value.configDigest.length === 0) {
    return { status: "rejected", reason: "configDigest must be a string" };
  }
  const actual = n2kOutputConfigDigest(parsed.config);
  if (actual !== value.configDigest) {
    return {
      status: "rejected",
      reason: "configDigest does not match the config it carries"
    };
  }
  if (value.baseDigest !== null && (typeof value.baseDigest !== "string" || value.baseDigest.length === 0)) {
    return {
      status: "rejected",
      reason: "baseDigest must be present, and a string or null"
    };
  }
  if (typeof value.sourceUserId !== "string" || value.sourceUserId.length === 0 || value.sourceUserId.length > N2K_OUTPUT_COMMAND_ID_MAX) {
    return {
      status: "rejected",
      reason: "sourceUserId must be a non-empty string"
    };
  }
  const label2 = typeof value.sourceUserLabel === "string" && value.sourceUserLabel.length > 0 ? value.sourceUserLabel.slice(0, N2K_OUTPUT_COMMAND_USER_LABEL_MAX) : void 0;
  return {
    status: "accepted",
    payload: {
      v: N2K_OUTPUT_CONFIG_COMMAND_VERSION,
      config: parsed.config,
      configDigest: value.configDigest,
      baseDigest: value.baseDigest ?? null,
      sourceUserId: value.sourceUserId,
      ...label2 !== void 0 ? { sourceUserLabel: label2 } : {}
    }
  };
}
var N2K_OUTPUT_STATE_VERSION = 1;
var N2K_OUTPUT_AUTHORITIES = ["cloud", "local"];
var N2K_OUTPUT_REJECTIONS = [
  /** The frame's digest did not match the document it carried. */
  "digest_mismatch",
  /** The editor's base digest is not what the boat holds — someone else saved first. */
  "stale_base",
  /** A row named a target this core build does not ship, or failed a bound. */
  "invalid_row",
  /** The pinned egress is not a write-capable gateway on this boat. */
  "invalid_egress",
  /** The boat's own operator page owns this document (unactivated boat). */
  "not_authoritative"
];
function n2kOutputInstanceLabel(kind) {
  switch (kind) {
    case "engine":
      return "Engine";
    case "battery":
      return "Battery";
    case "tank":
      return "Tank";
    case "sensor":
      return "Sensor";
    default:
      return "";
  }
}
var N2K_OUTPUT_MAX_INPUT_PATHS = 400;
function isRejectionReason(raw) {
  return N2K_OUTPUT_REJECTIONS.includes(raw);
}
function record(raw) {
  return raw !== null && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
}
function boundedText(raw, max, fallback = "") {
  return typeof raw === "string" && raw.length > 0 ? raw.slice(0, max) : fallback;
}
function boundedId(raw, max) {
  return typeof raw === "string" && raw.length > 0 && raw.length <= max ? raw : null;
}
function nonNegativeSafeInteger(raw) {
  return typeof raw === "number" && Number.isSafeInteger(raw) && raw >= 0 ? raw : null;
}
function nonNegativeFiniteNumber(raw) {
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 0 ? raw : null;
}
function finiteNumber(raw) {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}
function isoDateOrNull(raw) {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > 100) {
    return null;
  }
  const at = Date.parse(raw);
  return Number.isFinite(at) ? new Date(at).toISOString() : null;
}
function parseOrderField(value, field) {
  if (value[field] === void 0) return { value: 0 };
  const parsed = nonNegativeSafeInteger(value[field]);
  return parsed === null ? { reason: `${field} must be a non-negative safe integer` } : { value: parsed };
}
function parseStateTarget(raw) {
  const value = record(raw);
  if (!value) return null;
  const id = boundedId(value.id, N2K_OUTPUT_TARGET_ID_MAX);
  const pgn = nonNegativeSafeInteger(value.pgn);
  if (!id || pgn === null || pgn > 262143) return null;
  const receiveDevices = Array.isArray(value.receiveDevices) ? value.receiveDevices.slice(0, N2K_OUTPUT_MAX_RECEIVE_DEVICES).filter((device) => typeof device === "string").map((device) => device.slice(0, N2K_OUTPUT_INPUT_PATH_MAX)).filter((device) => device.length > 0) : [];
  const instanceMax = nonNegativeSafeInteger(value.instanceMax);
  return {
    id,
    label: boundedText(value.label, N2K_OUTPUT_INPUT_PATH_MAX, id),
    pgn,
    pgnLabel: boundedText(
      value.pgnLabel,
      N2K_OUTPUT_INPUT_PATH_MAX,
      String(pgn)
    ),
    unit: boundedText(value.unit, N2K_OUTPUT_INPUT_PATH_MAX),
    instanceKind: boundedText(value.instanceKind, N2K_OUTPUT_TARGET_ID_MAX),
    instanceMax: instanceMax !== null && instanceMax <= N2K_OUTPUT_INSTANCE_MAX ? instanceMax : 0,
    receiveDevices
  };
}
function parseStateGateway(raw) {
  const value = record(raw);
  if (!value) return null;
  const id = boundedId(value.id, N2K_OUTPUT_SOURCE_ID_MAX);
  if (!id) return null;
  return {
    id,
    label: boundedText(value.label, N2K_OUTPUT_INPUT_PATH_MAX, id),
    writable: value.writable === true,
    preferred: value.preferred === true,
    ...typeof value.restampsSource === "boolean" ? { restampsSource: value.restampsSource } : {}
  };
}
function parseStateRowStatus(raw) {
  const value = record(raw);
  if (!value) return null;
  const index = nonNegativeSafeInteger(value.index);
  if (index === null || index >= N2K_OUTPUT_MAX_ROWS) return null;
  return {
    index,
    inputValue: finiteNumber(value.inputValue),
    inputUnit: boundedText(value.inputUnit, N2K_OUTPUT_INPUT_PATH_MAX),
    fresh: value.fresh === true,
    inputAgeMs: nonNegativeFiniteNumber(value.inputAgeMs),
    unitMismatch: value.unitMismatch === true,
    lastTxAtMs: nonNegativeFiniteNumber(value.lastTxAtMs),
    lastTxAccepted: typeof value.lastTxAccepted === "boolean" ? value.lastTxAccepted : null
  };
}
function boundedCatalog(raw, max, parse) {
  if (!Array.isArray(raw)) return [];
  const result = [];
  const inspected = Math.min(raw.length, max);
  for (let index = 0; index < inspected; index += 1) {
    const parsed = parse(raw[index]);
    if (parsed !== null) result.push(parsed);
  }
  return result;
}
function parseN2kOutputStatePayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== N2K_OUTPUT_STATE_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported n2k output state version (expected ${N2K_OUTPUT_STATE_VERSION})`
    };
  }
  if (typeof value.boatId !== "string" || value.boatId.length === 0 || value.boatId.length > N2K_OUTPUT_COMMAND_ID_MAX) {
    return { status: "rejected", reason: "boatId must be a non-empty string" };
  }
  const generatedAt = isoDateOrNull(value.generatedAt);
  if (generatedAt === null) {
    return {
      status: "rejected",
      reason: "generatedAt must be a valid date"
    };
  }
  const parsed = parseN2kOutputConfig(value.config);
  if ("reason" in parsed) {
    return { status: "rejected", reason: parsed.reason };
  }
  if (typeof value.configDigest !== "string" || value.configDigest.length === 0) {
    return { status: "rejected", reason: "configDigest must be a string" };
  }
  if (n2kOutputConfigDigest(parsed.config) !== value.configDigest) {
    return {
      status: "rejected",
      reason: "configDigest does not match the config it carries"
    };
  }
  const parsedAuthorityEpoch = parseOrderField(value, "authorityEpoch");
  if ("reason" in parsedAuthorityEpoch) {
    return { status: "rejected", reason: parsedAuthorityEpoch.reason };
  }
  const parsedRevision = parseOrderField(value, "revision");
  if ("reason" in parsedRevision) {
    return { status: "rejected", reason: parsedRevision.reason };
  }
  const authorityEpoch = parsedAuthorityEpoch.value;
  const revision = parsedRevision.value;
  const installId = typeof value.installId === "string" && value.installId.length > 0 && value.installId.length <= N2K_OUTPUT_INSTALL_ID_MAX ? value.installId : "";
  const authority = N2K_OUTPUT_AUTHORITIES.includes(
    value.authority
  ) ? value.authority : "cloud";
  const rejectionRaw = record(value.lastRejection);
  const lastRejection = rejectionRaw && isRejectionReason(rejectionRaw.reason) ? {
    reason: rejectionRaw.reason,
    digest: typeof rejectionRaw.digest === "string" ? rejectionRaw.digest.slice(0, N2K_OUTPUT_INPUT_PATH_MAX) : "",
    detail: typeof rejectionRaw.detail === "string" ? rejectionRaw.detail.slice(0, N2K_OUTPUT_INPUT_PATH_MAX) : "",
    at: isoDateOrNull(rejectionRaw.at) ?? ""
  } : null;
  const capabilityRaw = record(value.capability) ?? {};
  const statusRaw = record(value.status) ?? {};
  const rawInputPaths = Array.isArray(capabilityRaw.inputPaths) ? capabilityRaw.inputPaths : [];
  const inputPaths = rawInputPaths.slice(0, N2K_OUTPUT_MAX_INPUT_PATHS).filter(
    (path) => typeof path === "string" && path.length > 0 && path.length <= N2K_OUTPUT_INPUT_PATH_MAX
  );
  return {
    status: "accepted",
    payload: {
      v: N2K_OUTPUT_STATE_VERSION,
      boatId: value.boatId,
      generatedAt,
      authorityEpoch,
      revision,
      installId,
      authority,
      config: parsed.config,
      configDigest: value.configDigest,
      appliedAt: isoDateOrNull(value.appliedAt),
      lastRejection,
      capability: {
        targets: boundedCatalog(
          capabilityRaw.targets,
          N2K_OUTPUT_MAX_STATE_TARGETS,
          parseStateTarget
        ),
        gateways: boundedCatalog(
          capabilityRaw.gateways,
          N2K_OUTPUT_MAX_STATE_GATEWAYS,
          parseStateGateway
        ),
        inputPaths,
        inputPathsTruncated: capabilityRaw.inputPathsTruncated === true || rawInputPaths.length > N2K_OUTPUT_MAX_INPUT_PATHS,
        coreVersion: typeof capabilityRaw.coreVersion === "string" ? capabilityRaw.coreVersion.slice(0, N2K_OUTPUT_INPUT_PATH_MAX) : ""
      },
      status: {
        running: statusRaw.running === true,
        resolvedEgressId: typeof statusRaw.resolvedEgressId === "string" && statusRaw.resolvedEgressId.length > 0 && statusRaw.resolvedEgressId.length <= N2K_OUTPUT_SOURCE_ID_MAX ? statusRaw.resolvedEgressId : null,
        framesSent: nonNegativeSafeInteger(statusRaw.framesSent) ?? 0,
        lastTxAtMs: nonNegativeFiniteNumber(statusRaw.lastTxAtMs),
        rows: boundedCatalog(
          statusRaw.rows,
          N2K_OUTPUT_MAX_ROWS,
          parseStateRowStatus
        )
      }
    }
  };
}
function n2kOutputStateSupersedes(incoming, stored) {
  if (!stored) return { accept: true, reason: "first" };
  if (incoming.installId !== "" && incoming.installId !== stored.installId) {
    if (stored.retiredInstallIds.includes(incoming.installId)) {
      return { accept: false, reason: "retired_install" };
    }
    return { accept: true, reason: "new_install" };
  }
  if (incoming.authorityEpoch !== stored.authorityEpoch) {
    return incoming.authorityEpoch > stored.authorityEpoch ? { accept: true, reason: "newer" } : { accept: false, reason: "older" };
  }
  return incoming.revision >= stored.revision ? { accept: true, reason: "newer" } : { accept: false, reason: "older" };
}
function n2kOutputShadowedRows(config) {
  const seen = /* @__PURE__ */ new Set();
  const shadowed = [];
  config.rows.forEach((row, index) => {
    if (!row.enabled) return;
    const key = `${row.targetId}:${row.instance}`;
    if (seen.has(key)) shadowed.push(index);
    else seen.add(key);
  });
  return shadowed;
}

// src/nav-aid.ts
var NAV_AID_CLASSES = [
  "buoy_lateral",
  "beacon_lateral",
  "buoy_cardinal",
  "beacon_cardinal",
  "buoy_isolated_danger",
  "beacon_isolated_danger",
  "buoy_safe_water",
  "beacon_safe_water",
  "buoy_special_purpose",
  "beacon_special_purpose",
  "light_major",
  "light_minor"
];
var NAV_AID_SOURCE_COLOURS = [
  "white",
  "black",
  "red",
  "green",
  "blue",
  "yellow",
  "grey",
  "brown",
  "amber",
  "violet",
  "orange",
  "magenta",
  "pink"
];
var NAV_AID_ATTRIBUTE_MAX = 48;
var NAV_AID_COLOURS_MAX = 6;
var NAV_AID_FORMS = [
  "buoy-can",
  "buoy-cone",
  "buoy-sphere",
  "buoy-pillar",
  "buoy-spar",
  "buoy-barrel",
  "buoy",
  "beacon-stake",
  "beacon-tower",
  "beacon",
  "light-major",
  "light-minor"
];
var NAV_AID_BODY_PAINTS = [
  "red",
  "green",
  "yellow",
  "black",
  "white",
  "orange",
  "black-yellow",
  "yellow-black",
  "black-yellow-black",
  "yellow-black-yellow",
  "black-red-black",
  "red-green-red",
  "green-red-green",
  "red-white-v",
  "neutral"
];
var NAV_AID_LIGHT_PAINTS = [
  "red",
  "green",
  "white",
  "yellow",
  "neutral"
];
var NAV_AID_TOPMARK_SHAPES = [
  "cone-up",
  "cone-down",
  "2cones-up",
  "2cones-down",
  "2cones-base",
  "2cones-point",
  "2spheres",
  "sphere",
  "x",
  "can",
  "square",
  "rhombus"
];
var NAV_AID_TOPMARK_PAINTS = [
  "red",
  "green",
  "black",
  "yellow",
  "neutral"
];
var NAV_AID_CODEBOOK_VERSION = 2;
var NAV_AID_GENERIC_ICON = {
  buoy: "buoy:neutral",
  beacon: "beacon:neutral",
  light: "light-minor:neutral"
};
var BUOY_SHAPES = {
  can: "buoy-can",
  conical: "buoy-cone",
  spherical: "buoy-sphere",
  pillar: "buoy-pillar",
  spar: "buoy-spar",
  barrel: "buoy-barrel"
};
var BEACON_SHAPES = {
  stake: "beacon-stake",
  pole: "beacon-stake",
  perch: "beacon-stake",
  post: "beacon-stake",
  withy: "beacon-stake",
  pile: "beacon-stake",
  tower: "beacon-tower",
  lattice: "beacon-tower"
};
var TOPMARK_SHAPES = {
  "cone, point up": "cone-up",
  "cone, point down": "cone-down",
  "2 cones up": "2cones-up",
  "2 cones down": "2cones-down",
  "2 cones base together": "2cones-base",
  "2 cones point together": "2cones-point",
  "2 spheres": "2spheres",
  sphere: "sphere",
  "x-shape": "x",
  cylinder: "can",
  // Day boards (common on US daybeacons): a flat triangle reads as the cone
  // it stands for; a square and a rhombus have art of their own.
  "triangle, point up": "cone-up",
  "triangle, point down": "cone-down",
  square: "square",
  rhombus: "rhombus"
};
var BANDED_PAINTS = [
  "black-yellow",
  "yellow-black",
  "black-yellow-black",
  "yellow-black-yellow",
  "black-red-black",
  "red-green-red",
  "green-red-green"
];
var SOLID_PAINTS = [
  "red",
  "green",
  "yellow",
  "black",
  "white",
  "orange"
];
function navAidFamily(cls) {
  if (cls.startsWith("buoy_")) return "buoy";
  if (cls.startsWith("beacon_")) return "beacon";
  return "light";
}
function bodyPaint(attrs) {
  const colours = attrs.colours;
  if (colours.length === 0) return "neutral";
  if (colours.length === 1) {
    const solid = SOLID_PAINTS.find((p) => p === colours[0]);
    return solid ?? "neutral";
  }
  const joined = colours.join("-");
  if (attrs.colourPattern === "horizontal") {
    return BANDED_PAINTS.find((p) => p === joined) ?? "neutral";
  }
  if (attrs.colourPattern === "vertical") {
    const pair2 = new Set(colours);
    return pair2.size === 2 && pair2.has("red") && pair2.has("white") ? "red-white-v" : "neutral";
  }
  return "neutral";
}
function lightPaint(colours) {
  if (colours.length !== 1) return "neutral";
  return NAV_AID_LIGHT_PAINTS.find((p) => p === colours[0]) ?? "neutral";
}
function topmarkKey(attrs) {
  if (attrs.topmarkShape === null) return null;
  const shape = TOPMARK_SHAPES[attrs.topmarkShape];
  if (shape === void 0) return null;
  const c = attrs.topmarkColours;
  const paint = c.length === 1 ? NAV_AID_TOPMARK_PAINTS.find((p) => p === c[0]) ?? "neutral" : "neutral";
  return `topmark-${shape}:${paint}`;
}
function navAidIcon(cls, attrs) {
  const family = navAidFamily(cls);
  if (family === "light") {
    const form2 = cls === "light_major" ? "light-major" : "light-minor";
    return {
      iconKey: `${form2}:${lightPaint(attrs.lightColours)}`,
      topmarkKey: null
    };
  }
  const shapes = family === "buoy" ? BUOY_SHAPES : BEACON_SHAPES;
  const form = (attrs.shape === null ? void 0 : shapes[attrs.shape]) ?? family;
  return {
    iconKey: `${form}:${bodyPaint(attrs)}`,
    topmarkKey: topmarkKey(attrs)
  };
}
function isNavAidIconKey(key) {
  const [form, paint, extra] = key.split(":");
  if (extra !== void 0 || form === void 0 || paint === void 0) {
    return false;
  }
  if (!NAV_AID_FORMS.includes(form)) return false;
  return form.startsWith("light-") ? NAV_AID_LIGHT_PAINTS.includes(paint) : NAV_AID_BODY_PAINTS.includes(paint);
}
function isNavAidTopmarkKey(key) {
  const m = /^topmark-([^:]+):([^:]+)$/.exec(key);
  return m !== null && NAV_AID_TOPMARK_SHAPES.includes(m[1]) && NAV_AID_TOPMARK_PAINTS.includes(m[2]);
}
function navAidDrawnIconKey(cls, iconKey) {
  return isNavAidIconKey(iconKey) ? iconKey : NAV_AID_GENERIC_ICON[navAidFamily(cls)];
}
var CLASS_WORDS = {
  buoy_lateral: "Lateral buoy",
  beacon_lateral: "Lateral beacon",
  buoy_cardinal: "Cardinal buoy",
  beacon_cardinal: "Cardinal beacon",
  buoy_isolated_danger: "Isolated danger buoy",
  beacon_isolated_danger: "Isolated danger beacon",
  buoy_safe_water: "Safe water buoy",
  beacon_safe_water: "Safe water beacon",
  buoy_special_purpose: "Special purpose buoy",
  beacon_special_purpose: "Special purpose beacon",
  light_major: "Major light",
  light_minor: "Minor light"
};
var PAINT_WORDS = {
  "black-yellow": "black over yellow",
  "yellow-black": "yellow over black",
  "black-yellow-black": "black, yellow and black bands",
  "yellow-black-yellow": "yellow, black and yellow bands",
  "black-red-black": "black, red and black bands",
  "red-green-red": "red, green and red bands",
  "green-red-green": "green, red and green bands",
  "red-white-v": "red and white stripes"
};
function navAidAccessibleLabel(cls, attrs) {
  const { iconKey } = navAidIcon(cls, attrs);
  const paint = iconKey.split(":")[1];
  const words = CLASS_WORDS[cls];
  if (paint === "neutral") return words;
  return `${words}, ${PAINT_WORDS[paint] ?? paint}`;
}
var NAV_AID_PRIORITY = {
  light_major: 1,
  buoy_cardinal: 2,
  beacon_cardinal: 2,
  buoy_isolated_danger: 3,
  beacon_isolated_danger: 3,
  buoy_lateral: 4,
  beacon_lateral: 4,
  buoy_safe_water: 5,
  beacon_safe_water: 5,
  light_minor: 6,
  buoy_special_purpose: 7,
  beacon_special_purpose: 7
};
var NAV_AID_MIN_ZOOM = {
  light_major: 8,
  buoy_cardinal: 8,
  beacon_cardinal: 8,
  buoy_isolated_danger: 8,
  beacon_isolated_danger: 8,
  buoy_lateral: 8,
  beacon_lateral: 8,
  buoy_safe_water: 8,
  beacon_safe_water: 8,
  light_minor: 8,
  buoy_special_purpose: 9,
  beacon_special_purpose: 9
};
var NAV_AID_FEED_MIN_ZOOM = 8;
function navAidIconHeight(zoom) {
  const z = Math.min(Math.max(zoom, 12), 16);
  return Math.round(18 + (z - 12) / 4 * 8);
}
var NAV_AID_MIN_SEPARATION = 14;
function navAidZoomForSpan(lonSpanDeg, widthPx) {
  if (!(lonSpanDeg > 0) || !(widthPx > 0)) return 0;
  return Math.log2(widthPx * 360 / (512 * lonSpanDeg));
}
function worldPx(lat, lon, zoom) {
  const size = 512 * 2 ** zoom;
  const clamped = Math.min(Math.max(lat, -85.05112878), 85.05112878);
  const s = Math.sin(clamped * Math.PI / 180);
  const x = (lon + 180) / 360 * size;
  const y = (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * size;
  return [x, y];
}
function compareNavAids(a, b) {
  if (a.properties.priority !== b.properties.priority) {
    return a.properties.priority - b.properties.priority;
  }
  return compareNavAidIds(a.id, b.id);
}
function compareNavAidIds(a, b) {
  const na = /^osm:node:(\d+)$/.exec(a);
  const nb = /^osm:node:(\d+)$/.exec(b);
  if (na !== null && nb !== null && na[1].length !== nb[1].length) {
    return na[1].length - nb[1].length;
  }
  return a < b ? -1 : a > b ? 1 : 0;
}
function navAidDeclutter(features, zoom, minSeparation = NAV_AID_MIN_SEPARATION) {
  const shown = features.filter((f) => zoom >= NAV_AID_MIN_ZOOM[f.properties.class]).slice().sort(compareNavAids);
  const kept = [];
  const points = [];
  const worldWidth = 512 * 2 ** zoom;
  for (const f of shown) {
    const [lon, lat] = f.geometry.coordinates;
    const [x, y] = worldPx(lat, lon, zoom);
    const clear = points.every(([px, py]) => {
      let dx = Math.abs(px - x);
      dx = Math.min(dx, worldWidth - dx);
      return Math.hypot(dx, py - y) >= minSeparation;
    });
    if (clear) {
      kept.push(f);
      points.push([x, y]);
    }
  }
  return kept;
}
var NAV_AID_CHUNK_ZOOM = 10;
var NAV_AID_CHUNKS_MAX = 9;
function tileY(lat, z) {
  const [, y] = worldPx(lat, 0, z);
  return Math.min(Math.max(Math.floor(y / 512), 0), 2 ** z - 1);
}
function tileX(lon, z) {
  const n = 2 ** z;
  const x = Math.floor((lon + 180) / 360 * n);
  return Math.min(Math.max(x, 0), n - 1);
}
function tileLat(y, z) {
  const n = Math.PI - 2 * Math.PI * y / 2 ** z;
  return 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}
var coord = (v) => Number(v.toFixed(6)).toString();
function navAidChunks(bounds) {
  const z = NAV_AID_CHUNK_ZOOM;
  const n = 2 ** z;
  const yTop = tileY(bounds.north, z);
  const yBottom = tileY(bounds.south, z);
  const xWest = tileX(bounds.west, z);
  const xEast = tileX(bounds.east, z);
  const xs = [];
  if (bounds.west <= bounds.east) {
    for (let x = xWest; x <= xEast; x++) xs.push(x);
  } else {
    for (let x = xWest; x < n; x++) xs.push(x);
    for (let x = 0; x <= xEast; x++) xs.push(x);
  }
  const rows = yBottom - yTop + 1;
  if (rows * xs.length > NAV_AID_CHUNKS_MAX) return null;
  const chunks = [];
  for (let y = yTop; y <= yBottom; y++) {
    for (const x of xs) {
      const west = x / n * 360 - 180;
      const east = (x + 1) / n * 360 - 180;
      const north = tileLat(y, z);
      const south = tileLat(y + 1, z);
      chunks.push({
        z,
        x,
        y,
        bbox: [west, south, east, north].map(coord).join(",")
      });
    }
  }
  return chunks;
}
var NAV_AID_OUTCOMES = [
  "complete",
  "truncated",
  "outside_coverage",
  "unavailable"
];
var NAV_AID_LIMIT_MAX = 500;
function isObj4(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function finite2(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function attrText(value) {
  if (value === null) return null;
  return typeof value === "string" && value.length > 0 && value.length <= NAV_AID_ATTRIBUTE_MAX && /^[a-z0-9 ,;._-]+$/.test(value) ? value : void 0;
}
function colourList(value) {
  if (!Array.isArray(value) || value.length > NAV_AID_COLOURS_MAX) {
    return void 0;
  }
  const out = [];
  for (const c of value) {
    if (typeof c !== "string" || !/^[a-z]{1,16}$/.test(c)) return void 0;
    out.push(c);
  }
  return out;
}
function parseNavAidAttributes(value) {
  if (!isObj4(value)) return null;
  const category = attrText(value.category);
  const shape = attrText(value.shape);
  const colourPattern = attrText(value.colourPattern);
  const system = attrText(value.system);
  const topmarkShape = attrText(value.topmarkShape);
  const colours = colourList(value.colours);
  const topmarkColours = colourList(value.topmarkColours);
  const lightColours = colourList(value.lightColours);
  if (category === void 0 || shape === void 0 || colourPattern === void 0 || system === void 0 || topmarkShape === void 0 || colours === void 0 || topmarkColours === void 0 || lightColours === void 0) {
    return null;
  }
  return {
    category,
    shape,
    colours,
    colourPattern,
    system,
    topmarkShape,
    topmarkColours,
    lightColours
  };
}
function parseNavAidFeature(value) {
  if (!isObj4(value) || value.type !== "Feature") return null;
  if (typeof value.id !== "string" || !/^osm:node:\d{1,15}$/.test(value.id)) {
    return null;
  }
  const g = value.geometry;
  if (!isObj4(g) || g.type !== "Point" || !Array.isArray(g.coordinates)) {
    return null;
  }
  const [lon, lat, extra] = g.coordinates;
  if (extra !== void 0 || !finite2(lon) || !finite2(lat) || lon < -180 || lon > 180 || lat < -90 || lat > 90) {
    return null;
  }
  const p = value.properties;
  if (!isObj4(p)) return null;
  const cls = NAV_AID_CLASSES.find((c) => c === p.class);
  if (cls === void 0) return null;
  if (typeof p.iconKey !== "string" || p.iconKey.length > 64) return null;
  if (p.topmarkKey !== null && (typeof p.topmarkKey !== "string" || !isNavAidTopmarkKey(p.topmarkKey))) {
    return null;
  }
  if (typeof p.label !== "string" || p.label.length === 0 || p.label.length > 120) {
    return null;
  }
  if (!finite2(p.priority) || !Number.isInteger(p.priority)) return null;
  if (p.mmsi !== null && (!finite2(p.mmsi) || !Number.isInteger(p.mmsi) || p.mmsi < 0 || p.mmsi > 999999999)) {
    return null;
  }
  const attrs = parseNavAidAttributes(p.attrs);
  if (attrs === null) return null;
  return {
    type: "Feature",
    id: value.id,
    geometry: { type: "Point", coordinates: [lon, lat] },
    properties: {
      class: cls,
      iconKey: p.iconKey,
      topmarkKey: p.topmarkKey,
      label: p.label,
      priority: p.priority,
      mmsi: p.mmsi,
      attrs
    }
  };
}
function parseDataset(value) {
  if (value === null) return null;
  if (!isObj4(value) || typeof value.version !== "string") return void 0;
  if (!Array.isArray(value.regions)) return void 0;
  const regions = [];
  for (const r of value.regions) {
    if (!isObj4(r) || typeof r.id !== "string" || typeof r.name !== "string" || typeof r.version !== "string" || typeof r.extractedAt !== "string" || typeof r.builtAt !== "string" || typeof r.sourceUrl !== "string") {
      return void 0;
    }
    regions.push({
      id: r.id,
      name: r.name,
      version: r.version,
      extractedAt: r.extractedAt,
      builtAt: r.builtAt,
      sourceUrl: r.sourceUrl
    });
  }
  const a = value.attribution;
  const l = value.licence;
  if (!isObj4(a) || typeof a.text !== "string" || typeof a.url !== "string" || !isObj4(l) || typeof l.name !== "string" || typeof l.url !== "string") {
    return void 0;
  }
  return {
    version: value.version,
    regions,
    attribution: { text: a.text, url: a.url },
    licence: { name: l.name, url: l.url }
  };
}
function parseNavAidFeed(value) {
  if (!isObj4(value) || value.type !== "FeatureCollection") return null;
  const outcome = NAV_AID_OUTCOMES.find((o) => o === value.outcome);
  const coverage = ["full", "partial", "none"].find(
    (c) => c === value.coverage
  );
  if (outcome === void 0 || coverage === void 0) return null;
  if (!finite2(value.limit) || value.limit < 0) return null;
  const dataset = parseDataset(value.dataset);
  if (dataset === void 0 || !Array.isArray(value.features)) return null;
  const features = [];
  for (const raw of value.features) {
    const f = parseNavAidFeature(raw);
    if (f !== null) features.push(f);
  }
  return {
    type: "FeatureCollection",
    outcome,
    coverage,
    limit: value.limit,
    dataset,
    features
  };
}
var NAV_AID_ART = {
  version: 2,
  width: 24,
  height: 32,
  anchor: [12, 31],
  dotRadius: 1.3,
  outlineWidth: 1.1,
  haloWidth: 3,
  looks: {
    light: { outline: "#1d2630", halo: "rgba(255,255,255,0.92)" },
    dark: { outline: "#0d1116", halo: "rgba(226,233,240,0.78)" }
  },
  colours: {
    red: "#d42a32",
    green: "#16924c",
    yellow: "#f2c018",
    black: "#1d2126",
    white: "#ffffff",
    orange: "#ef8a26",
    neutral: "#8b96a3"
  },
  forms: {
    "buoy-can": {
      path: "M7 13 L17 13 L17 29 L7 29 Z",
      paintBox: [7, 13, 17, 29]
    },
    "buoy-cone": { path: "M12 10 L18 29 L6 29 Z", paintBox: [6, 10, 18, 29] },
    "buoy-sphere": {
      path: "M12 15 C15.866 15 19 18.134 19 22 C19 25.866 15.866 29 12 29 C8.134 29 5 25.866 5 22 C5 18.134 8.134 15 12 15 Z",
      paintBox: [5, 15, 19, 29]
    },
    "buoy-pillar": {
      path: "M9.5 10 L14.5 10 L17 29 L7 29 Z",
      paintBox: [7, 10, 17, 29]
    },
    "buoy-spar": {
      path: "M10.6 9 L13.4 9 L14 29 L10 29 Z",
      paintBox: [10, 9, 14, 29]
    },
    "buoy-barrel": {
      path: "M6 19 C6 16.2 18 16.2 18 19 L18 26 C18 28.8 6 28.8 6 26 Z",
      paintBox: [6, 16.9, 18, 28.1]
    },
    buoy: {
      path: "M8.5 15 C8.5 12 15.5 12 15.5 15 L17 29 L7 29 Z",
      paintBox: [7, 12.75, 17, 29]
    },
    "beacon-stake": {
      path: "M10.9 10 L13.1 10 L13.1 29 L10.9 29 Z",
      paintBox: [10.9, 10, 13.1, 29]
    },
    "beacon-tower": {
      path: "M9.6 10 L14.4 10 L16.2 27 L18 27 L18 29 L6 29 L6 27 L7.8 27 Z",
      paintBox: [6, 10, 18, 29]
    },
    beacon: {
      path: "M10.4 10 L13.6 10 L13.6 27 L16.5 27 L16.5 29 L7.5 29 L7.5 27 L10.4 27 Z",
      paintBox: [7.5, 10, 16.5, 29]
    },
    "light-major": {
      path: "M12 31 C10.8 24.5 13.4 13 18.6 7.4 C22.4 11.4 19.8 23.4 12 31 Z",
      paintBox: [10.8, 7.4, 22.4, 31]
    },
    "light-minor": {
      path: "M12 31 C11.2 26.4 13 19.4 16.4 15.6 C19 18.4 17.6 25.6 12 31 Z",
      paintBox: [11.2, 15.6, 19, 31]
    }
  },
  topmarks: {
    "cone-up": {
      path: "M12 2.2 L15.4 8 L8.6 8 Z",
      paintBox: [8.6, 2.2, 15.4, 8]
    },
    "cone-down": {
      path: "M8.6 2.2 L15.4 2.2 L12 8 Z",
      paintBox: [8.6, 2.2, 15.4, 8]
    },
    "2cones-up": {
      path: "M12 0.6 L14.6 4.4 L9.4 4.4 Z M12 5 L14.6 8.8 L9.4 8.8 Z",
      paintBox: [9.4, 0.6, 14.6, 8.8]
    },
    "2cones-down": {
      path: "M9.4 0.6 L14.6 0.6 L12 4.4 Z M9.4 5 L14.6 5 L12 8.8 Z",
      paintBox: [9.4, 0.6, 14.6, 8.8]
    },
    "2cones-base": {
      path: "M12 0.6 L14.6 4.4 L9.4 4.4 Z M9.4 5 L14.6 5 L12 8.8 Z",
      paintBox: [9.4, 0.6, 14.6, 8.8]
    },
    "2cones-point": {
      path: "M9.4 0.6 L14.6 0.6 L12 4.4 Z M12 5 L14.6 8.8 L9.4 8.8 Z",
      paintBox: [9.4, 0.6, 14.6, 8.8]
    },
    "2spheres": {
      path: "M12 0.8 C13.05 0.8 13.9 1.65 13.9 2.7 C13.9 3.75 13.05 4.6 12 4.6 C10.95 4.6 10.1 3.75 10.1 2.7 C10.1 1.65 10.95 0.8 12 0.8 Z M12 5.2 C13.05 5.2 13.9 6.05 13.9 7.1 C13.9 8.15 13.05 9 12 9 C10.95 9 10.1 8.15 10.1 7.1 C10.1 6.05 10.95 5.2 12 5.2 Z",
      paintBox: [10.1, 0.8, 13.9, 9]
    },
    sphere: {
      path: "M12 2.4 C13.6 2.4 14.8 3.6 14.8 5.2 C14.8 6.8 13.6 8 12 8 C10.4 8 9.2 6.8 9.2 5.2 C9.2 3.6 10.4 2.4 12 2.4 Z",
      paintBox: [9.2, 2.4, 14.8, 8]
    },
    x: {
      path: "M8.8 2 L10.4 2 L12 4.1 L13.6 2 L15.2 2 L12.8 5.1 L15.2 8.2 L13.6 8.2 L12 6.1 L10.4 8.2 L8.8 8.2 L11.2 5.1 Z",
      paintBox: [8.8, 2, 15.2, 8.2]
    },
    can: {
      path: "M9.4 2.4 L14.6 2.4 L14.6 8 L9.4 8 Z",
      paintBox: [9.4, 2.4, 14.6, 8]
    },
    square: {
      path: "M9.2 2 L14.8 2 L14.8 7.6 L9.2 7.6 Z",
      paintBox: [9.2, 2, 14.8, 7.6]
    },
    rhombus: {
      path: "M12 1.2 L15.2 4.8 L12 8.4 L8.8 4.8 Z",
      paintBox: [8.8, 1.2, 15.2, 8.4]
    }
  }
};
function navAidPaintColours(paint) {
  if (paint === "red-white-v") {
    return { colours: ["red", "white", "red", "white"], direction: "vertical" };
  }
  const parts = paint.split("-");
  if (parts.length === 1) {
    const colour = NAV_AID_ART.colours[paint] ? paint : "neutral";
    return { colours: [colour], direction: "solid" };
  }
  return { colours: parts, direction: "horizontal" };
}

// src/neo-markers.ts
var NEO_CHART_ID = /^c[1-9]$/;
var NEO_CARD_ID = /^k[1-9]$/;
var CHART_MARKERS = {
  marker: /\{\{\s*chart\s*:\s*(c[1-9])\s*\}\}/gi,
  has: /\{\{\s*chart\s*:\s*c[1-9]\s*\}\}/i,
  id: NEO_CHART_ID,
  render: (id) => `{{chart:${id}}}`
};
var CARD_MARKERS = {
  marker: /\{\{\s*card\s*:\s*(k[1-9])\s*\}\}/gi,
  has: /\{\{\s*card\s*:\s*k[1-9]\s*\}\}/i,
  id: NEO_CARD_ID,
  render: (id) => `{{card:${id}}}`
};
var ANY_MARKER = /\{\{\s*(?:chart\s*:\s*(c[1-9])|card\s*:\s*(k[1-9]))\s*\}\}/gi;
var PARTIAL_TAIL = /\{\{[^{}\n]{0,24}$/;
function collapseBlankLines(text3) {
  return text3.replace(/\n{3,}/g, "\n\n").trim();
}
function normalizeMarkers(content, available, family) {
  const ids = new Set(available.filter((id) => family.id.test(id)));
  if (ids.size === 0 && !family.has.test(content))
    return { content, shown: [] };
  const placed = [];
  const lines = [];
  for (const line of content.split("\n")) {
    if (!family.has.test(line)) {
      lines.push(line);
      continue;
    }
    const found = [];
    const text3 = line.replace(family.marker, (_match, id) => {
      const key = id.toLowerCase();
      if (ids.has(key) && !placed.includes(key) && !found.includes(key)) {
        found.push(key);
      }
      return "";
    }).replace(/[ \t]+$/, "");
    lines.push(text3.trim().length > 0 ? text3 : "");
    for (const id of found) {
      placed.push(id);
      lines.push("", family.render(id), "");
    }
  }
  let out = collapseBlankLines(lines.join("\n"));
  let shown = placed;
  if (placed.length === 0 && ids.size > 0) {
    shown = [...ids].sort();
    out = collapseBlankLines(
      [out, ...shown.map((id) => family.render(id))].filter((p) => p.length > 0).join("\n\n")
    );
  }
  return { content: out, shown };
}
function splitReplyContent(content, opts = {}) {
  const source = opts.streaming === true ? content.replace(PARTIAL_TAIL, "") : content;
  const segments = [];
  const seen = /* @__PURE__ */ new Set();
  let cursor = 0;
  const pushText = (text3) => {
    const trimmed = text3.replace(/^\s*\n/, "").replace(/\n\s*$/, "");
    if (trimmed.trim().length > 0)
      segments.push({ kind: "text", text: trimmed });
  };
  for (const match of source.matchAll(ANY_MARKER)) {
    const chartId = match[1]?.toLowerCase();
    const id = chartId ?? match[2].toLowerCase();
    pushText(source.slice(cursor, match.index));
    cursor = (match.index ?? 0) + match[0].length;
    if (seen.has(id)) continue;
    seen.add(id);
    segments.push(
      chartId !== void 0 ? { kind: "chart", id } : { kind: "card", id }
    );
  }
  pushText(source.slice(cursor));
  return segments;
}
function stripReplyMarkers(content) {
  return collapseBlankLines(
    content.replace(ANY_MARKER, "").replace(PARTIAL_TAIL, "")
  );
}

// src/neo-answer.ts
var NEO_ANSWER_VERSION = 1;
var NEO_ANSWER_EVIDENCE_MAX = 24;
var NEO_ANSWER_ASSUMPTIONS_MAX = 8;
var NEO_ANSWER_RESULTS_MAX = 8;
var NEO_ANSWER_LIMITATIONS_MAX = 4;
var NEO_EVIDENCE_LIMITATIONS_MAX = 2;
var NEO_ANSWER_MISSING_MAX = 3;
var NEO_ANSWER_GROUPS_MAX = 3;
var NEO_ANSWER_FOLLOWUPS_MAX = 3;
var NEO_FOLLOWUP_LABEL_MAX = 48;
var NEO_ANSWER_TEXT_MAX = 160;
var NEO_ANSWER_LABEL_MAX = 80;
var NEO_ANSWER_GROUP_VIEWS_MIN = 2;
var NEO_ANSWER_GROUP_VIEWS_MAX = 3;
var NEO_EVIDENCE_IDENTITY_MAX = 200;
var NEO_ANSWER_UNIT_MAX = 32;
var NEO_ANSWER_CHANGE_ABS_MAX = 1e9;
var NEO_ANSWER_EVIDENCE_ID = /^e(?:[1-9]|1[0-9]|2[0-4])$/;
var NEO_ANSWER_ASSUMPTION_ID = /^a[1-8]$/;
var NEO_ANSWER_RESULT_ID = /^r[1-8]$/;
var NEO_ANSWER_GROUP_ID = /^g[1-3]$/;
var NEO_ANSWER_CALCULATION_ID = /^[a-z][a-z0-9_.-]{0,63}$/;
var NEO_FRESHNESS = [
  "current",
  "stale",
  "unknown"
];
var NEO_EVIDENCE_KINDS = [
  "reading",
  "history",
  "usage",
  "logbook",
  "voyage",
  "position",
  "forecast",
  "tide",
  "alarm",
  "vessels",
  "manual",
  "notebook",
  "other"
];
var NEO_ASSUMPTION_ORIGINS = [
  "owner",
  "measured",
  "assumed"
];
var NEO_FOLLOWUP_KINDS = [
  "ask",
  "show_view",
  "inspect_evidence",
  "recalculate",
  "drill_down"
];
var CONTROL2 = /[\p{Cc}\p{Cf}\p{Zl}\p{Zp}\p{Cs}\p{Co}\p{Cn}]/u;
var INVISIBLE_LETTER = /[ᅟᅠ⠀ㅤﾠ]/u;
var COMBINING_RUN = /\p{M}{4}/u;
var READABLE = /[\p{L}\p{N}]/u;
var MARKUP = /[`*_[\]<>|\\{}]|~~/;
var LEADING_MARKUP = /^(?:#|>|[-+]\s|\d+[.)]\s)/;
var LINK = /\b[a-z][a-z0-9+.-]*:[^\s]|\bwww\.|\b[a-z0-9-]+\.[a-z]{2,24}(?:\/|\b)|\b\d{1,3}(?:\.\d{1,3}){3}\b/i;
function codePoints(text3) {
  return [...text3].length;
}
function tooLong(raw, max) {
  return raw.length > max * 4 + 64;
}
function plainText(raw, max) {
  if (typeof raw !== "string" || tooLong(raw, max)) return null;
  if (CONTROL2.test(raw)) return null;
  const text3 = raw.normalize("NFC").replace(/\s+/g, " ").trim();
  if (text3.length === 0 || codePoints(text3) > max) return null;
  if (INVISIBLE_LETTER.test(text3) || COMBINING_RUN.test(text3)) return null;
  if (!READABLE.test(text3)) return null;
  if (MARKUP.test(text3) || LEADING_MARKUP.test(text3) || LINK.test(text3)) {
    return null;
  }
  return text3;
}
function neoFollowupLabel(raw) {
  return plainText(raw, NEO_FOLLOWUP_LABEL_MAX);
}
function neoAnswerText(raw) {
  return plainText(raw, NEO_ANSWER_TEXT_MAX);
}
function neoAnswerLabel(raw) {
  return plainText(raw, NEO_ANSWER_LABEL_MAX);
}
function neoFreshnessFrom(f) {
  if (f === "fresh") return "current";
  if (f === "stale" || f === "old") return "stale";
  return "unknown";
}
var EXPRESSION_TOKEN = /\s*(?:(\d+(?:\.\d+)?)|([ear](?:[1-9]|1[0-9]|2[0-4]))\b|(min|max|sum|round)\b|([+−×÷(),]))/y;
var EXPRESSION_ID = /^(?:e(?:[1-9]|1[0-9]|2[0-4])|a[1-8]|r[1-8])$/;
function expressionTokens(text3) {
  const out = [];
  let at = 0;
  while (at < text3.length) {
    if (text3.slice(at).trim().length === 0) break;
    EXPRESSION_TOKEN.lastIndex = at;
    const m = EXPRESSION_TOKEN.exec(text3);
    if (m === null) return null;
    at = EXPRESSION_TOKEN.lastIndex;
    if (m[1] !== void 0) out.push({ t: "num" });
    else if (m[2] !== void 0) {
      if (!EXPRESSION_ID.test(m[2])) return null;
      out.push({ t: "id", v: m[2] });
    } else if (m[3] !== void 0) out.push({ t: "fn", v: m[3] });
    else out.push({ t: "op", v: m[4] });
  }
  return out;
}
function neoAnswerExpressionIds(value) {
  if (typeof value !== "string" || tooLong(value, NEO_ANSWER_TEXT_MAX)) {
    return null;
  }
  if (CONTROL2.test(value) || MARKUP.test(value) || value.trim() !== value) {
    return null;
  }
  if (value.length === 0 || codePoints(value) > NEO_ANSWER_TEXT_MAX) {
    return null;
  }
  const tokens = expressionTokens(value);
  if (tokens === null || tokens.length === 0) return null;
  const ids = [];
  let i = 0;
  const op = (v) => {
    const tok = tokens[i];
    if (tok !== void 0 && tok.t === "op" && tok.v === v) {
      i++;
      return true;
    }
    return false;
  };
  const expr = () => {
    if (!term()) return false;
    while (op("+") || op("\u2212")) if (!term()) return false;
    return true;
  };
  const term = () => {
    if (!unary()) return false;
    while (op("\xD7") || op("\xF7")) if (!unary()) return false;
    return true;
  };
  const unary = () => op("\u2212") ? unary() : primary();
  const primary = () => {
    const tok = tokens[i];
    if (tok === void 0) return false;
    if (tok.t === "num") {
      i++;
      return true;
    }
    if (tok.t === "id") {
      i++;
      if (!ids.includes(tok.v)) ids.push(tok.v);
      return true;
    }
    if (tok.t === "fn") {
      i++;
      if (!op("(") || !expr()) return false;
      let args = 1;
      while (op(",")) {
        if (!expr()) return false;
        args++;
      }
      if (tok.v === "round" && args > 2) return false;
      return op(")");
    }
    if (op("(")) return expr() && op(")");
    return false;
  };
  return expr() && i === tokens.length ? ids : null;
}
function isNeoAnswerExpression(value, inputs) {
  const ids = neoAnswerExpressionIds(value);
  if (ids === null) return false;
  return inputs === void 0 || ids.every((id) => inputs.includes(id));
}
function isObj5(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function finite3(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function list(value) {
  return Array.isArray(value) ? value : [];
}
function matches(value, re) {
  return typeof value === "string" && re.test(value);
}
var UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;
var TIME_ZONE = /^(?:UTC|[A-Za-z]+(?:\/[A-Za-z0-9_+-]+){1,2})$/;
function isInstant(value) {
  if (typeof value !== "string" || !INSTANT.test(value)) return false;
  const at = Date.parse(value);
  if (!Number.isFinite(at)) return false;
  const iso2 = new Date(at).toISOString();
  return iso2 === value || iso2 === `${value.slice(0, -1)}.000Z`;
}
var ZONES = /* @__PURE__ */ new Map();
function isTimeZone(value) {
  if (typeof value !== "string" || value.length > 64) return false;
  if (!TIME_ZONE.test(value)) return false;
  let known = ZONES.get(value);
  if (known === void 0) {
    try {
      new Intl.DateTimeFormat("en-GB", { timeZone: value });
      known = true;
    } catch {
      known = false;
    }
    if (ZONES.size < 512) ZONES.set(value, known);
  }
  return known;
}
function isUnit(value) {
  return typeof value === "string" && !tooLong(value, NEO_ANSWER_UNIT_MAX) && value.length >= 1 && codePoints(value) <= NEO_ANSWER_UNIT_MAX && value.trim() === value && !/\s{2}/.test(value) && !CONTROL2.test(value) && !MARKUP.test(value) && !LINK.test(value);
}
var IDENTITY_MARKUP = /[`*<>{}[\]\\|]/;
function isIdentityField(value) {
  return typeof value === "string" && !tooLong(value, NEO_EVIDENCE_IDENTITY_MAX) && value.length >= 1 && codePoints(value) <= NEO_EVIDENCE_IDENTITY_MAX && value.trim() === value && !CONTROL2.test(value) && !IDENTITY_MARKUP.test(value);
}
function storedLabel(value, max) {
  return typeof value === "string" && plainText(value, max) === value;
}
function texts(value, max) {
  const out = [];
  for (const raw of list(value).slice(0, max)) {
    if (!storedLabel(raw, NEO_ANSWER_TEXT_MAX)) continue;
    if (!out.includes(raw)) out.push(raw);
  }
  return out;
}
function parseIdentity(value) {
  if (!isObj5(value)) return void 0;
  const out = {};
  if (isIdentityField(value.path)) out.path = value.path;
  if (isIdentityField(value.source)) out.source = value.source;
  if (isIdentityField(value.deviceId)) out.deviceId = value.deviceId;
  if (isIdentityField(value.ref)) out.ref = value.ref;
  return Object.keys(out).length === 0 ? void 0 : out;
}
function parseRange(value) {
  if (!isObj5(value)) return void 0;
  const { start, end, timeZone } = value;
  if (!isInstant(start) || !isInstant(end) || !isTimeZone(timeZone)) {
    return void 0;
  }
  if (Date.parse(start) > Date.parse(end)) return void 0;
  return { start, end, timeZone };
}
function parseCoverage(value) {
  if (!isObj5(value)) return void 0;
  const { actual, expected } = value;
  if (!Number.isInteger(actual) || actual < 0) return void 0;
  if (expected === void 0) return { actual };
  if (!Number.isInteger(expected) || expected < 0) {
    return { actual };
  }
  return { actual, expected };
}
function parseNeoEvidenceRef(value) {
  if (!isObj5(value)) return null;
  const { id, sourceLabel, provider } = value;
  if (!matches(id, NEO_ANSWER_EVIDENCE_ID)) return null;
  if (!storedLabel(sourceLabel, NEO_ANSWER_LABEL_MAX)) return null;
  if (!storedLabel(provider, NEO_ANSWER_LABEL_MAX)) return null;
  if (typeof value.kind !== "string") return null;
  const kind = NEO_EVIDENCE_KINDS.find((k) => k === value.kind) ?? "other";
  const freshness = NEO_FRESHNESS.find((f) => f === value.freshness) ?? "unknown";
  const out = { id, kind, sourceLabel, provider, freshness };
  const identity = parseIdentity(value.identity);
  if (identity !== void 0) out.identity = identity;
  const range = parseRange(value.range);
  if (range !== void 0) out.range = range;
  if (isUnit(value.unit)) out.unit = value.unit;
  if (value.value === null || finite3(value.value)) out.value = value.value;
  if (isInstant(value.observedAt)) out.observedAt = value.observedAt;
  if (isInstant(value.fetchedAt)) out.fetchedAt = value.fetchedAt;
  const coverage = parseCoverage(value.coverage);
  if (coverage !== void 0) out.coverage = coverage;
  const limitations = texts(value.limitations, NEO_EVIDENCE_LIMITATIONS_MAX);
  if (limitations.length > 0) out.limitations = limitations;
  if (value.method === "measured" || value.method === "estimated") {
    out.method = value.method;
  }
  return out;
}
function parseAssumption(value, evidenceIds) {
  if (!isObj5(value)) return null;
  const { id, label: label2 } = value;
  if (!matches(id, NEO_ANSWER_ASSUMPTION_ID)) return null;
  if (!storedLabel(label2, NEO_ANSWER_LABEL_MAX)) return null;
  if (!finite3(value.value)) return null;
  const origin = NEO_ASSUMPTION_ORIGINS.find((o) => o === value.origin);
  if (origin === void 0) return null;
  const out = { id, label: label2, value: value.value, origin };
  if (isUnit(value.unit)) out.unit = value.unit;
  if (typeof value.evidenceId === "string" && evidenceIds.has(value.evidenceId)) {
    out.evidenceId = value.evidenceId;
  }
  return out;
}
function parseCalculation(value) {
  if (!isObj5(value)) return null;
  const { id, version, words } = value;
  if (!matches(id, NEO_ANSWER_CALCULATION_ID)) return null;
  if (!Number.isInteger(version) || version < 1) return null;
  if (!storedLabel(words, NEO_ANSWER_TEXT_MAX)) return null;
  const out = {
    id,
    version,
    words
  };
  if (typeof value.expression === "string") {
    out.expression = value.expression;
  }
  return out;
}
var INPUTS_MAX = NEO_ANSWER_EVIDENCE_MAX + NEO_ANSWER_ASSUMPTIONS_MAX + NEO_ANSWER_RESULTS_MAX;
function parseResult(value, known) {
  if (!isObj5(value)) return null;
  const { id, label: label2 } = value;
  if (!matches(id, NEO_ANSWER_RESULT_ID)) return null;
  if (!storedLabel(label2, NEO_ANSWER_LABEL_MAX)) return null;
  if (!finite3(value.value)) return null;
  const calculation = parseCalculation(value.calculation);
  if (calculation === null || !Array.isArray(value.inputs)) return null;
  if (value.inputs.length > INPUTS_MAX) return null;
  const inputs = [];
  for (const input of value.inputs) {
    if (typeof input !== "string" || !known.has(input)) return null;
    if (!inputs.includes(input)) inputs.push(input);
  }
  if (calculation.expression !== void 0 && !isNeoAnswerExpression(calculation.expression, inputs)) {
    delete calculation.expression;
  }
  const out = {
    id,
    label: label2,
    value: value.value,
    calculation,
    inputs
  };
  if (isUnit(value.unit)) out.unit = value.unit;
  return out;
}
function parseGroup(value) {
  if (!isObj5(value) || !matches(value.id, NEO_ANSWER_GROUP_ID)) return null;
  if (!Array.isArray(value.views)) return null;
  if (value.views.length < NEO_ANSWER_GROUP_VIEWS_MIN || value.views.length > NEO_ANSWER_GROUP_VIEWS_MAX) {
    return null;
  }
  const views = [];
  for (const view of value.views) {
    if (!isObj5(view)) return null;
    const { recordId, label: label2 } = view;
    if (!matches(recordId, NEO_CHART_ID)) return null;
    if (!storedLabel(label2, NEO_ANSWER_LABEL_MAX)) return null;
    if (views.some((v) => v.recordId === recordId)) return null;
    views.push({ recordId, label: label2 });
  }
  return { id: value.id, views };
}
function parseNeoFollowupIntent(value) {
  if (!isObj5(value)) return null;
  switch (value.kind) {
    case "ask":
      return { kind: "ask" };
    case "show_view":
      return matches(value.groupId, NEO_ANSWER_GROUP_ID) && matches(value.recordId, NEO_CHART_ID) ? {
        kind: "show_view",
        groupId: value.groupId,
        recordId: value.recordId
      } : null;
    case "inspect_evidence":
      if (value.evidenceId === void 0 || value.evidenceId === null) {
        return { kind: "inspect_evidence" };
      }
      return matches(value.evidenceId, NEO_ANSWER_EVIDENCE_ID) ? { kind: "inspect_evidence", evidenceId: value.evidenceId } : null;
    case "recalculate": {
      if (!matches(value.resultId, NEO_ANSWER_RESULT_ID)) return null;
      if (!Array.isArray(value.changes)) return null;
      if (value.changes.length < 1 || value.changes.length > NEO_ANSWER_ASSUMPTIONS_MAX) {
        return null;
      }
      const changes = [];
      for (const change of value.changes) {
        if (!isObj5(change)) return null;
        const { assumptionId } = change;
        if (!matches(assumptionId, NEO_ANSWER_ASSUMPTION_ID)) return null;
        if (!finite3(change.value)) return null;
        if (Math.abs(change.value) > NEO_ANSWER_CHANGE_ABS_MAX) return null;
        if (changes.some((c) => c.assumptionId === assumptionId)) return null;
        changes.push({ assumptionId, value: change.value });
      }
      return { kind: "recalculate", resultId: value.resultId, changes };
    }
    case "drill_down":
      return matches(value.recordId, NEO_CHART_ID) && (value.by === "hour" || value.by === "day") ? { kind: "drill_down", recordId: value.recordId, by: value.by } : null;
    default:
      return null;
  }
}
function neoFollowupResolves(record2, intent) {
  switch (intent.kind) {
    case "ask":
    case "drill_down":
      return true;
    case "show_view":
      return record2.visuals.some(
        (g) => g.id === intent.groupId && g.views.some((v) => v.recordId === intent.recordId)
      );
    case "inspect_evidence":
      return intent.evidenceId === void 0 || record2.evidence.some((e) => e.id === intent.evidenceId);
    case "recalculate": {
      if (!record2.results.some((r) => r.id === intent.resultId)) return false;
      const uses = /* @__PURE__ */ new Set();
      const visit = (id) => {
        const result = record2.results.find((r) => r.id === id);
        for (const input of result?.inputs ?? []) {
          if (uses.has(input)) continue;
          uses.add(input);
          visit(input);
        }
      };
      visit(intent.resultId);
      return intent.changes.every(
        (c) => uses.has(c.assumptionId) && record2.assumptions.some((a) => a.id === c.assumptionId)
      );
    }
  }
}
function parseNeoFollowup(value) {
  if (!isObj5(value)) return null;
  if (!storedLabel(value.label, NEO_FOLLOWUP_LABEL_MAX)) return null;
  const intent = parseNeoFollowupIntent(value.intent);
  return intent === null ? null : { label: value.label, intent };
}
function parseSource(value) {
  if (!isObj5(value) || !storedLabel(value.label, NEO_ANSWER_LABEL_MAX)) {
    return null;
  }
  const out = { label: value.label };
  if (isInstant(value.at)) out.at = value.at;
  if (isTimeZone(value.timeZone)) out.timeZone = value.timeZone;
  return out;
}
function parseNeoAnswer(value) {
  if (!isObj5(value)) return null;
  if (value.version !== NEO_ANSWER_VERSION) return null;
  if (!matches(value.answerId, UUID) || !isInstant(value.resolvedAt)) {
    return null;
  }
  const evidence = [];
  for (const raw of list(value.evidence).slice(0, NEO_ANSWER_EVIDENCE_MAX)) {
    if (evidence.length >= NEO_ANSWER_EVIDENCE_MAX) break;
    const item = parseNeoEvidenceRef(raw);
    if (item !== null && !evidence.some((e) => e.id === item.id)) {
      evidence.push(item);
    }
  }
  const evidenceIds = new Set(evidence.map((e) => e.id));
  const assumptions = [];
  for (const raw of list(value.assumptions).slice(
    0,
    NEO_ANSWER_ASSUMPTIONS_MAX
  )) {
    if (assumptions.length >= NEO_ANSWER_ASSUMPTIONS_MAX) break;
    const item = parseAssumption(raw, evidenceIds);
    if (item !== null && !assumptions.some((a) => a.id === item.id)) {
      assumptions.push(item);
    }
  }
  const known = /* @__PURE__ */ new Set([...evidenceIds, ...assumptions.map((a) => a.id)]);
  const results = [];
  for (const raw of list(value.results).slice(0, NEO_ANSWER_RESULTS_MAX)) {
    if (results.length >= NEO_ANSWER_RESULTS_MAX) break;
    const item = parseResult(raw, known);
    if (item !== null && !known.has(item.id)) {
      results.push(item);
      known.add(item.id);
    }
  }
  const visuals = [];
  for (const raw of list(value.visuals).slice(0, NEO_ANSWER_GROUPS_MAX)) {
    if (visuals.length >= NEO_ANSWER_GROUPS_MAX) break;
    const item = parseGroup(raw);
    if (item !== null && !visuals.some((g) => g.id === item.id)) {
      visuals.push(item);
    }
  }
  const resolved = { evidence, assumptions, results, visuals };
  const followups = [];
  for (const raw of list(value.followups).slice(0, NEO_ANSWER_FOLLOWUPS_MAX)) {
    if (followups.length >= NEO_ANSWER_FOLLOWUPS_MAX) break;
    const item = parseNeoFollowup(raw);
    if (item === null || !neoFollowupResolves(resolved, item.intent)) continue;
    if (followups.some((f) => f.label === item.label)) continue;
    followups.push(item);
  }
  return {
    version: NEO_ANSWER_VERSION,
    answerId: value.answerId,
    resolvedAt: value.resolvedAt,
    source: parseSource(value.source),
    evidence,
    assumptions,
    results,
    missingInputs: texts(value.missingInputs, NEO_ANSWER_MISSING_MAX),
    limitations: texts(value.limitations, NEO_ANSWER_LIMITATIONS_MAX),
    visuals,
    followups
  };
}

// src/neo-camera-snapshot.ts
var NEO_CAMERA_ID_MAX = 128;
var NEO_CAMERA_NAME_MAX = 64;
var NEO_CAMERA_SNAPSHOT_DIMENSION_MAX = 16384;
var NEO_CAMERA_EVENT_TYPES = [
  "person",
  "smart",
  "audio",
  "motion",
  "offline",
  "gap"
];
var NEO_CAMERA_SNAPSHOT_CAPTION = "Camera snapshot";
var CONTROL3 = /[\p{Cc}\p{Cf}]/u;
function isObj6(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isNeoCameraId(value) {
  return typeof value === "string" && value.length >= 1 && value.length <= NEO_CAMERA_ID_MAX && value.trim() === value && !CONTROL3.test(value);
}
function isNeoCameraName(value) {
  return typeof value === "string" && value.length >= 1 && value.length <= NEO_CAMERA_NAME_MAX && value.trim().length > 0 && !CONTROL3.test(value);
}
function pixels(value) {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= NEO_CAMERA_SNAPSHOT_DIMENSION_MAX;
}
function parseNeoCameraSnapshotSpecParts(spec) {
  const { cameraId, snapshotId } = spec;
  return isNeoCameraId(cameraId) && isNeoCameraId(snapshotId) ? { cameraId, snapshotId } : null;
}
function parseNeoCameraSnapshotSnapshot(value) {
  if (!isObj6(value) || value.kind !== "camera_snapshot") return null;
  const { cameraName, takenAt, width, height } = value;
  if (cameraName !== null && !isNeoCameraName(cameraName)) return null;
  if (typeof takenAt !== "string" || Number.isNaN(Date.parse(takenAt))) {
    return null;
  }
  if (!pixels(width) || !pixels(height)) return null;
  const eventType = NEO_CAMERA_EVENT_TYPES.find((t) => t === value.eventType);
  return {
    kind: "camera_snapshot",
    cameraName,
    takenAt,
    width,
    height,
    ...eventType === void 0 ? {} : { eventType }
  };
}

// src/signal-label.ts
var KNOWN_ROOTS = /* @__PURE__ */ new Set([
  "propulsion",
  "electrical",
  "tanks",
  "environment",
  "navigation",
  "steering",
  "computed"
]);
var LEAF_VOCAB = {
  currentLevel: "level",
  currentVolume: "volume"
};
var ACRONYMS = {
  ac: "AC",
  dc: "DC",
  bms: "BMS",
  vebus: "VE.Bus",
  leds: "LEDs"
};
var CHARGING_SOURCE_NOUNS = {
  chargers: "Charger",
  inverters: "Inverter",
  solar: "Solar",
  alternator: "Alternator"
};
var AC_IN_OUT = /^(ac|dc)(in|out)(\d*)$/i;
var WORD_NUMBER = /^(in|out)(\d+)$/;
function isNumericSegment(seg) {
  return /^\d+$/.test(seg);
}
var ORDINAL_INSTANCE_LIMIT = 100;
function displayInstanceNumber(seg) {
  const n = Number(seg);
  return String(n < ORDINAL_INSTANCE_LIMIT ? n + 1 : n);
}
function humanizeCamel(seg) {
  return seg.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
}
function humanizeSegment(seg) {
  if (/^[A-Z]$/.test(seg)) return seg;
  const inOut = AC_IN_OUT.exec(seg);
  if (inOut !== null) {
    const [, current, way, number] = inOut;
    return `${current.toUpperCase()} ${way.toLowerCase()}${number ? ` ${number}` : ""}`;
  }
  return humanizeCamel(seg).split(" ").map((w) => {
    const numbered = WORD_NUMBER.exec(w);
    return ACRONYMS[w] ?? (numbered === null ? w : `${numbered[1]} ${numbered[2]}`);
  }).join(" ");
}
function capitalizeFirst(s) {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}
function titleize(seg) {
  return capitalizeFirst(humanizeSegment(seg));
}
function measurementPhrase(segs) {
  return segmentWords(segs, (s) => LEAF_VOCAB[s]).join(" ").replace(/\s+/g, " ").trim();
}
function segmentWords(segs, vocab = () => void 0) {
  return segs.map((s, i) => {
    const after = segs[i - 1];
    if (isNumericSegment(s) && after !== void 0 && AC_IN_OUT.test(after)) {
      return s;
    }
    return vocab(s) ?? (isNumericSegment(s) ? displayInstanceNumber(s) : humanizeSegment(s));
  });
}
function composeLabel(seg) {
  const [root, s1, s2] = seg;
  if (root === void 0) return "";
  if (root === "propulsion" && s1 !== void 0 && seg.length >= 3) {
    const measurement = measurementPhrase(seg.slice(2));
    return isNumericSegment(s1) ? `Engine ${displayInstanceNumber(s1)} ${measurement}`.trim() : `${titleize(s1)} engine ${measurement}`.trim();
  }
  if (root === "electrical" && s1 === "batteries" && s2 !== void 0 && seg.length >= 4) {
    const measurement = measurementPhrase(seg.slice(3));
    return isNumericSegment(s2) ? `Battery ${displayInstanceNumber(s2)} ${measurement}`.trim() : `${titleize(s2)} battery ${measurement}`.trim();
  }
  const source = root === "electrical" && s1 !== void 0 ? CHARGING_SOURCE_NOUNS[s1] : void 0;
  if (source !== void 0 && s2 !== void 0 && seg.length >= 4) {
    const measurement = measurementPhrase(seg.slice(3));
    return isNumericSegment(s2) ? `${source} ${displayInstanceNumber(s2)} ${measurement}`.trim() : `${titleize(s2)} ${source.toLowerCase()} ${measurement}`.trim();
  }
  if (root === "electrical" && s1 === "switches" && s2 === "bank" && seg.length === 6) {
    const bank = seg[3] ?? "";
    const channel = seg[4] ?? "";
    const bankDisplay = isNumericSegment(bank) ? displayInstanceNumber(bank) : titleize(bank);
    return `Switch bank ${bankDisplay} channel ${channel}`;
  }
  if (root === "tanks" && s1 !== void 0 && s2 !== void 0 && seg.length >= 4) {
    const type = titleize(s1);
    const instDisplay = isNumericSegment(s2) ? displayInstanceNumber(s2) : titleize(s2);
    const measurement = measurementPhrase(seg.slice(3));
    return `${type} tank ${instDisplay} ${measurement}`.replace(/\s+/g, " ").trim();
  }
  if (root === "network" && s1 === "providers" && s2 === "starlink") {
    const words2 = seg.slice(3).flatMap((s) => s.split("_")).map((w) => w === "snr" ? "SNR" : humanizeCamel(w));
    return ["Starlink", ...words2].join(" ");
  }
  const rest = KNOWN_ROOTS.has(root) ? seg.slice(1) : seg;
  const words = segmentWords(rest);
  return capitalizeFirst(words.join(" ").replace(/\s+/g, " ").trim());
}
function signalLabel(path, opts = {}) {
  const channel = opts.channelLabel?.trim();
  if (channel !== void 0 && channel.length > 0) return channel;
  const entity = opts.entityName?.trim();
  const instance = namedInstanceOf(path);
  if (entity && instance !== null) {
    const normalized = path.startsWith("notifications.propulsion.") ? path.slice("notifications.".length) : path;
    const measurement = measurementPhrase(
      normalized.slice(instance.key.length + 1).split(".")
    );
    return `${entity} ${measurement}`.trim();
  }
  const seg = path.split(".").filter((s) => s.length > 0);
  const label2 = composeLabel(seg);
  if (label2.length > 0) return label2;
  const device = opts.deviceName?.trim();
  if (device !== void 0 && device.length > 0) return device;
  return "Unknown signal";
}
function signalMeasurementLabel(path) {
  const instance = namedInstanceOf(path);
  if (instance === null) return null;
  const normalized = path.startsWith("notifications.propulsion.") ? path.slice("notifications.".length) : path;
  if (!normalized.startsWith(`${instance.key}.`)) return null;
  const phrase = measurementPhrase(
    normalized.slice(instance.key.length + 1).split(".")
  ).replace(/^capacity (?=\S)/, "");
  return phrase.length === 0 ? null : capitalizeFirst(phrase);
}

// src/neo-chart.ts
var NEO_CHART_VERSION = 1;
var NEO_CHARTS_PER_ANSWER_MAX = 3;
var NEO_CHART_TREND_PATHS_MAX = 3;
var NEO_CHART_READOUT_PATHS_MAX = 6;
var NEO_CHART_BARS_MAX = 14;
var NEO_CHART_TREND_SLOTS = 96;
var NEO_CHART_KINDS = [
  "trend",
  "readouts",
  "bars"
];
var NEO_CHART_CHIPS = [
  "last",
  "min",
  "max",
  "mean",
  "change"
];
var NEO_CHART_BARS_METRICS = [
  "engine_hours",
  "voyage_distance"
];
var NEO_CHART_BARS_BY = ["day", "week"];
var NEO_CHARTS_PER_ANSWER_WITH_VIEWS_MAX = 6;
var NEO_CHART_VIEWS_MIN = 2;
var NEO_CHART_VIEWS_MAX = 3;
var NEO_CHART_VIEW_LABEL_MAX = 16;
var NEO_CHART_HOUR_BARS_MAX = 26;
var NEO_CHART_FORECAST_FIELDS_MAX = 3;
var NEO_CHART_FORECAST_HOURS_MAX = 168;
var NEO_CHART_ALLOCATION_PARTS_MAX = 4;
var NEO_CHART_LABEL_MAX = 40;
var NEO_CHART_TITLE_MAX = 60;
var NEO_ANSWER_CHART_KINDS = [
  "trend",
  "readouts",
  "bars",
  "forecast",
  "allocation"
];
var NEO_CHART_AGGREGATES = [
  "sum",
  "mean",
  "max",
  "min",
  "delta"
];
var NEO_CHART_ANSWER_BARS_METRICS = ["reading", "usage"];
var NEO_CHART_PERIODS = [
  "hour",
  "day",
  "week"
];
var NEO_CHART_SUM_UNITS = {
  W: { unit: "kWh", perSecond: 1 / 36e5 },
  A: { unit: "Ah", perSecond: 1 / 3600 },
  "L/h": { unit: "L", perSecond: 1 / 3600 },
  kn: { unit: "nm", perSecond: 1 / 3600 }
};
var NEO_FORECAST_FIELDS = {
  wind: { label: "Wind", half: "forecast", key: "wind_speed_10m", unit: "kn" },
  gust: {
    label: "Gusts",
    half: "forecast",
    key: "wind_gusts_10m",
    unit: "kn"
  },
  pressure: {
    label: "Pressure",
    half: "forecast",
    key: "pressure_msl",
    unit: "hPa"
  },
  temperature: {
    label: "Air temperature",
    half: "forecast",
    key: "temperature_2m",
    unit: "C"
  },
  feels_like: {
    label: "Feels like",
    half: "forecast",
    key: "apparent_temperature",
    unit: "C"
  },
  rain: { label: "Rain", half: "forecast", key: "precipitation", unit: "mm" },
  rain_chance: {
    label: "Chance of rain",
    half: "forecast",
    key: "precipitation_probability",
    unit: "%"
  },
  cloud: {
    label: "Cloud cover",
    half: "forecast",
    key: "cloud_cover",
    unit: "%"
  },
  humidity: {
    label: "Humidity",
    half: "forecast",
    key: "relative_humidity_2m",
    unit: "%"
  },
  visibility: {
    label: "Visibility",
    half: "forecast",
    key: "visibility",
    unit: "m"
  },
  uv: { label: "UV index", half: "forecast", key: "uv_index", unit: "" },
  solar: {
    label: "Sunshine",
    half: "forecast",
    key: "shortwave_radiation",
    unit: "W/m2"
  },
  waves: {
    label: "Wave height",
    half: "marine",
    key: "wave_height",
    unit: "m"
  },
  wave_period: {
    label: "Wave period",
    half: "marine",
    key: "wave_period",
    unit: "s"
  },
  swell: {
    label: "Swell height",
    half: "marine",
    key: "swell_wave_height",
    unit: "m"
  },
  swell_period: {
    label: "Swell period",
    half: "marine",
    key: "swell_wave_period",
    unit: "s"
  }
};
var NEO_FORECAST_FIELD_NAMES = Object.keys(
  NEO_FORECAST_FIELDS
);
var NEO_ALLOCATION_ROLES = [
  "use",
  "reserve",
  "other"
];
function neoChartIsBase(chart) {
  const spec = chart.spec;
  const snapshot = chart.snapshot;
  if (spec.kind !== snapshot.kind) return false;
  if (spec.kind === "readouts") return true;
  if (spec.kind === "trend") {
    return spec.compare === void 0 && spec.shade === void 0 && snapshot.kind === "trend" && snapshot.gap === void 0 && snapshot.series.every(
      (s) => s.role === void 0 && s.shiftSeconds === void 0
    );
  }
  if (spec.kind === "bars") {
    return NEO_CHART_BARS_METRICS.includes(spec.metric) && NEO_CHART_BARS_BY.includes(spec.by) && spec.usual === void 0 && spec.highlight === void 0 && snapshot.kind === "bars" && (snapshot.unit === "h" || snapshot.unit === "nm");
  }
  return false;
}
var AGGREGATE_WORD = {
  sum: "total",
  mean: "average",
  max: "highest",
  min: "lowest",
  delta: "change"
};
function neoChartTitle(chart) {
  const spec = chart.spec;
  const snapshot = chart.snapshot;
  if (spec.kind === "trend") {
    const labelOf = (p) => snapshot.kind === "trend" ? snapshot.series.find((s) => s.path === p && s.role !== "compare")?.label ?? snapshot.series.find((s) => s.path === p)?.label ?? signalLabel(p) : signalLabel(p);
    return spec.paths.map(labelOf).join(" \xB7 ");
  }
  if (spec.kind === "readouts") return "Current readings";
  if (spec.kind === "forecast") {
    return `Forecast \xB7 ${spec.fields.map((f) => NEO_FORECAST_FIELDS[f].label).join(" \xB7 ")}`;
  }
  if (spec.kind === "allocation") return spec.title ?? "How it adds up";
  if (spec.metric === "reading" || spec.metric === "usage") {
    const label2 = (snapshot.kind === "bars" ? snapshot.label : void 0) ?? (spec.path !== void 0 ? signalLabel(spec.path) : "Use");
    if (spec.metric === "usage") return `${label2} \xB7 use per ${spec.by}`;
    return `${label2} \xB7 ${AGGREGATE_WORD[spec.aggregate ?? "mean"]} per ${spec.by}`;
  }
  if (spec.metric === "voyage_distance") return "Distance over ground";
  const engine = snapshot.kind === "bars" ? snapshot.engineLabel : null;
  return engine === null ? "Engine hours" : `Engine hours \xB7 ${engine}`;
}
function neoChartLabel(raw, max) {
  if (typeof raw !== "string") return null;
  const text3 = raw.replace(/\s+/g, " ").trim();
  const length = [...text3].length;
  if (length === 0 || length > max) return null;
  if (/[<>{}[\]`*_#|\\]/.test(text3)) return null;
  if (/https?:|www\.|\]\(/i.test(text3)) return null;
  for (const ch of text3) {
    const cp = ch.codePointAt(0);
    if (cp < 32 || cp === 127) return null;
    if (cp >= 8234 && cp <= 8238 || cp >= 8294 && cp <= 8297)
      return null;
  }
  return text3;
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
var isFiniteNumber = (v) => typeof v === "number" && Number.isFinite(v);
var isNumberOrNull = (v) => v === null || isFiniteNumber(v);
var isIsoString = (v) => typeof v === "string" && Number.isFinite(Date.parse(v));
function validSlots(value) {
  return isRecord(value) && isIsoString(value.start) && isFiniteNumber(value.stepSeconds) && value.stepSeconds > 0 && Array.isArray(value.mean) && value.mean.every(isNumberOrNull);
}
function validWindow(value) {
  return isRecord(value) && isIsoString(value.from) && isIsoString(value.to);
}
function validSpec(spec) {
  if (spec.v !== 1 || typeof spec.boatId !== "string") return false;
  if (!validWindow(spec.window)) return false;
  switch (spec.kind) {
    case "trend":
    case "readouts":
      return Array.isArray(spec.paths) && spec.paths.every((p) => typeof p === "string");
    case "bars":
      return typeof spec.metric === "string" && (NEO_CHART_BARS_METRICS.includes(spec.metric) || NEO_CHART_ANSWER_BARS_METRICS.includes(
        spec.metric
      )) && NEO_CHART_PERIODS.includes(spec.by);
    case "forecast":
      return Array.isArray(spec.fields) && spec.fields.length > 0 && spec.fields.every(
        (f) => NEO_FORECAST_FIELD_NAMES.includes(f)
      );
    case "allocation":
      return typeof spec.unit === "string" && isRecord(spec.whole) && Array.isArray(spec.parts);
    default:
      return false;
  }
}
function validSnapshot(kind, snapshot) {
  switch (kind) {
    case "trend":
      return Array.isArray(snapshot.series) && snapshot.series.length > 0 && snapshot.series.every(
        (s) => isRecord(s) && typeof s.path === "string" && (s.label === void 0 || typeof s.label === "string") && validSlots(s.slots)
      ) && (snapshot.gap === void 0 || snapshot.gap === null || isRecord(snapshot.gap) && validSlots(snapshot.gap.slots));
    case "readouts":
      return Array.isArray(snapshot.items) && snapshot.items.length > 0 && snapshot.items.every(
        (i) => isRecord(i) && typeof i.path === "string" && isNumberOrNull(i.value) && (i.trend === null || validSlots(i.trend))
      );
    case "bars":
      return typeof snapshot.unit === "string" && Array.isArray(snapshot.bars) && snapshot.bars.length > 0 && snapshot.bars.every(
        (b) => isRecord(b) && typeof b.key === "string" && isNumberOrNull(b.value)
      );
    case "forecast":
      return typeof snapshot.provider === "string" && isIsoString(snapshot.fetchedAt) && Array.isArray(snapshot.series) && snapshot.series.length > 0 && snapshot.series.every(
        (s) => isRecord(s) && NEO_FORECAST_FIELD_NAMES.includes(
          s.field
        ) && typeof s.unit === "string" && isRecord(s.points) && isIsoString(s.points.start) && isFiniteNumber(s.points.stepSeconds) && s.points.stepSeconds > 0 && Array.isArray(s.points.values) && s.points.values.every(isNumberOrNull)
      );
    case "allocation":
      return typeof snapshot.unit === "string" && isRecord(snapshot.whole) && isFiniteNumber(snapshot.whole.value) && Array.isArray(snapshot.segments) && snapshot.segments.length > 0 && snapshot.segments.every(
        (s) => isRecord(s) && typeof s.label === "string" && isFiniteNumber(s.value) && isFiniteNumber(s.covered)
      ) && isRecord(snapshot.remainder) && isFiniteNumber(snapshot.remainder.value);
    default:
      return false;
  }
}
function parseNeoAnswerChart(value) {
  if (!isRecord(value)) return null;
  if (typeof value.id !== "string" || !NEO_CHART_ID.test(value.id))
    return null;
  const { spec, snapshot } = value;
  if (!isRecord(spec) || !isRecord(snapshot)) return null;
  if (typeof spec.kind !== "string" || !NEO_ANSWER_CHART_KINDS.includes(spec.kind) || snapshot.kind !== spec.kind)
    return null;
  if (!validSpec(spec) || !validSnapshot(spec.kind, snapshot)) return null;
  if (value.resolvedAt !== void 0 && !isIsoString(value.resolvedAt))
    return null;
  return value;
}
function neoChartMarker(id) {
  return CHART_MARKERS.render(id);
}
function normalizeNeoChartMarkers(content, available, groups = []) {
  const lead = /* @__PURE__ */ new Map();
  const members = /* @__PURE__ */ new Map();
  for (const group of groups) {
    const views = group.filter((id) => available.includes(id));
    if (views.length === 0) continue;
    for (const id of views) lead.set(id, views[0]);
    members.set(views[0], views);
  }
  if (lead.size === 0)
    return normalizeMarkers(content, available, CHART_MARKERS);
  const rewritten = content.replace(
    CHART_MARKERS.marker,
    (match, id) => {
      const head = lead.get(id.toLowerCase());
      return head === void 0 ? match : CHART_MARKERS.render(head);
    }
  );
  const leads = available.filter((id) => (lead.get(id) ?? id) === id);
  const normalized = normalizeMarkers(rewritten, leads, CHART_MARKERS);
  const shown = normalized.shown.flatMap((id) => members.get(id) ?? [id]);
  return { content: normalized.content, shown: [...new Set(shown)].sort() };
}
function splitNeoChartContent(content, opts = {}) {
  return splitReplyContent(content, opts);
}
function stripNeoChartMarkers(content) {
  return stripReplyMarkers(content);
}

// src/watched-vessel.ts
var WATCHED_VESSELS_MAX = 50;
var WATCHED_VESSEL_LABEL_MAX = 60;
var FRIEND_RANGE_M = 3e5;
var FLEET_ATTRIBUTION = "Data by Saillogger";
var WATCHED_VESSEL_STATUSES = ["fresh", "stale", "unknown"];
function isMmsi(value) {
  return typeof value === "number" && Number.isInteger(value) && value >= 1e8 && value <= 999999999;
}
function normalizeWatchedVesselLabel(value) {
  const oneLine = Array.from(value, (c) => {
    const code = c.charCodeAt(0);
    return code < 32 || code === 127 ? " " : c;
  }).join("");
  const text3 = oneLine.replace(/\s+/g, " ").trim();
  if (text3.length === 0 || text3.length > WATCHED_VESSEL_LABEL_MAX) return null;
  return text3;
}
function isObj7(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function finite4(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function nullableFinite(value) {
  if (value === null) return null;
  return finite4(value) ? value : void 0;
}
function nullableStr(value) {
  if (value === null) return null;
  return typeof value === "string" ? value : void 0;
}
function iso(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value)) ? value : null;
}
function parseWatchedVesselPosition(value) {
  if (value === null) return null;
  if (!isObj7(value)) return void 0;
  const sogKn = nullableFinite(value.sogKn);
  const cogDeg = nullableFinite(value.cogDeg);
  const bearingDeg = nullableFinite(value.bearingDeg);
  const observedAt = iso(value.observedAt);
  if (!finite4(value.lat) || !finite4(value.lon) || !finite4(value.distanceM) || sogKn === void 0 || cogDeg === void 0 || bearingDeg === void 0 || observedAt === null) {
    return void 0;
  }
  return {
    lat: value.lat,
    lon: value.lon,
    sogKn,
    cogDeg,
    observedAt,
    distanceM: value.distanceM,
    bearingDeg
  };
}
function parseWatchedVesselState(value) {
  const status = WATCHED_VESSEL_STATUSES.find((s) => s === value.status);
  const latest = parseWatchedVesselPosition(value.latest);
  if (status === void 0 || latest === void 0) return null;
  if (status === "unknown" !== (latest === null)) return null;
  return { status, latest };
}
function parseWatchedVessel(value) {
  if (!isObj7(value) || !isMmsi(value.mmsi)) return null;
  const label2 = nullableStr(value.label);
  const name = nullableStr(value.name);
  const since = iso(value.since);
  const state = parseWatchedVesselState(value);
  if (label2 === void 0 || name === void 0 || since === null || state === null) {
    return null;
  }
  const vessel = {
    mmsi: value.mmsi,
    label: label2,
    name,
    since,
    ...state
  };
  if (value.locationContext !== void 0) {
    vessel.locationContext = state.status === "unknown" ? null : parseMapLocationContext(value.locationContext);
  }
  return vessel;
}

// src/neo-map.ts
var NEO_MAP_LAYERS = [
  "track",
  "anchor",
  "ais",
  "friends",
  "route"
];
var NEO_MAP_TRACK_POINTS_MAX = 500;
var NEO_MAP_TRACK_SPAN_MAX_MS = 7 * 864e5;
var NEO_MAP_AIS_VESSELS_MAX = 50;
var NEO_MAP_FRIENDS_MAX = WATCHED_VESSELS_MAX;
var NEO_MAP_TEXT_MAX = 160;
var NEO_MAP_VESSEL_PICKS_MAX = 10;
var NEO_MAP_VESSEL_PICK_NAME_MAX = 60;
var NEO_MAP_MISSING_REASONS = [
  "no_data",
  "off",
  "no_receiver",
  "receiver_quiet",
  "stale_position",
  "no_route",
  "unavailable"
];
function isObj8(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function finite5(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function isIso2(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}
function isLat2(value) {
  return finite5(value) && value >= -90 && value <= 90;
}
function isLon2(value) {
  return finite5(value) && value >= -180 && value <= 180;
}
function text2(value) {
  if (value === null) return null;
  return typeof value === "string" && value.length <= NEO_MAP_TEXT_MAX ? value : void 0;
}
function nullableNum2(value) {
  if (value === null) return null;
  return finite5(value) ? value : void 0;
}
function nullableDeg(value) {
  const n = nullableNum2(value);
  return n === null || n === void 0 || n >= 0 && n <= 360 ? n : void 0;
}
function count(value) {
  return finite5(value) && Number.isInteger(value) && value >= 0;
}
function parseMapWindow(value) {
  if (!isObj8(value) || !isIso2(value.from) || !isIso2(value.to)) return null;
  if (typeof value.live !== "boolean") return null;
  const span = Date.parse(value.to) - Date.parse(value.from);
  if (!(span > 0 && span <= NEO_MAP_TRACK_SPAN_MAX_MS)) return null;
  return { from: value.from, to: value.to, live: value.live };
}
function parseVesselPicks(value) {
  if (!Array.isArray(value)) return void 0;
  if (value.length === 0 || value.length > NEO_MAP_VESSEL_PICKS_MAX) {
    return void 0;
  }
  const seen = /* @__PURE__ */ new Set();
  for (const pick of value) {
    const ok = isMmsi(pick) || typeof pick === "string" && pick.trim().length > 0 && pick.length <= NEO_MAP_VESSEL_PICK_NAME_MAX && !/[\u0000-\u001f\u007f]/.test(pick);
    if (!ok || seen.has(pick)) return void 0;
    seen.add(pick);
  }
  return value;
}
function parseNeoMapSpecParts(spec) {
  if (!Array.isArray(spec.layers)) return null;
  if (spec.layers.some((l) => typeof l !== "string")) return null;
  const named = new Set(spec.layers);
  if (named.size !== spec.layers.length) return null;
  const layers = NEO_MAP_LAYERS.filter((l) => named.has(l));
  let vessels;
  if (spec.vessels !== void 0) {
    if (!layers.includes("ais")) return null;
    vessels = parseVesselPicks(spec.vessels);
    if (vessels === void 0) return null;
  }
  const picks = vessels === void 0 ? {} : { vessels };
  if (!layers.includes("track")) {
    return spec.window === null ? { layers, window: null, ...picks } : null;
  }
  const window = parseMapWindow(spec.window);
  return window === null ? null : { layers, window, ...picks };
}
function parseBoat(value) {
  if (!isObj8(value) || !isLat2(value.lat) || !isLon2(value.lon)) return null;
  if (!isIso2(value.at)) return null;
  if (value.status !== "current" && value.status !== "stale") return null;
  const headingDeg = nullableDeg(value.headingDeg);
  const cogDeg = nullableDeg(value.cogDeg);
  const sogKn = nullableNum2(value.sogKn);
  if (headingDeg === void 0 || cogDeg === void 0 || sogKn === void 0) {
    return null;
  }
  return {
    lat: value.lat,
    lon: value.lon,
    at: value.at,
    status: value.status,
    headingDeg,
    cogDeg,
    sogKn
  };
}
function parsePlace(value) {
  if (value === null) return null;
  if (!isObj8(value) || !isLat2(value.lat) || !isLon2(value.lon)) return void 0;
  const name = text2(value.name);
  const label2 = text2(value.label);
  const kind = text2(value.kind);
  if (typeof name !== "string" || typeof label2 !== "string" || typeof kind !== "string" || !finite5(value.distanceM) || value.distanceM < 0 || typeof value.at !== "boolean") {
    return void 0;
  }
  return {
    name,
    label: label2,
    kind,
    lat: value.lat,
    lon: value.lon,
    distanceM: value.distanceM,
    at: value.at
  };
}
function parseTrack(value) {
  if (!isIso2(value.from) || !isIso2(value.to) || !count(value.total)) {
    return null;
  }
  if (!Array.isArray(value.points)) return null;
  if (value.points.length > NEO_MAP_TRACK_POINTS_MAX) return null;
  if (value.points.length > value.total) return null;
  const points = [];
  for (const p of value.points) {
    if (!isObj8(p) || !isIso2(p.t) || !isLat2(p.lat) || !isLon2(p.lon)) {
      return null;
    }
    points.push({ t: p.t, lat: p.lat, lon: p.lon });
  }
  return { from: value.from, to: value.to, total: value.total, points };
}
function parseAnchor(value) {
  if (!isLat2(value.lat) || !isLon2(value.lon)) return null;
  const radiusM2 = nullableNum2(value.radiusM);
  const status = ANCHOR_WATCH_STATUS_CODES.find((s) => s === value.status);
  if (radiusM2 === void 0 || radiusM2 !== null && radiusM2 <= 0 || status === void 0 || typeof value.stale !== "boolean") {
    return null;
  }
  return {
    lat: value.lat,
    lon: value.lon,
    radiusM: radiusM2,
    status,
    stale: value.stale
  };
}
function parseAisVessel(value) {
  if (!isObj8(value) || !isLat2(value.lat) || !isLon2(value.lon)) return null;
  const mmsi = value.mmsi;
  if (!count(mmsi) || mmsi > 999999999) return null;
  const name = text2(value.name);
  const sogKn = nullableNum2(value.sogKn);
  const cogDeg = nullableDeg(value.cogDeg);
  const headingDeg = nullableDeg(value.headingDeg);
  const aisClass = ["A", "B", "unknown"].find(
    (c) => c === value.aisClass
  );
  if (name === void 0 || sogKn === void 0 || cogDeg === void 0 || headingDeg === void 0 || !finite5(value.ageSeconds) || value.ageSeconds < 0 || value.status !== "fresh" && value.status !== "stale" || aisClass === void 0) {
    return null;
  }
  const shipType = text2(value.shipType);
  const destination = text2(value.destination);
  return {
    mmsi,
    name,
    lat: value.lat,
    lon: value.lon,
    sogKn,
    cogDeg,
    headingDeg,
    ageSeconds: value.ageSeconds,
    status: value.status,
    aisClass,
    ...typeof shipType === "string" && shipType.length > 0 ? { shipType } : {},
    ...typeof destination === "string" && destination.length > 0 ? { destination } : {}
  };
}
function parseAis(value) {
  if (!count(value.heard) || !Array.isArray(value.vessels)) return null;
  if (value.vessels.length > NEO_MAP_AIS_VESSELS_MAX) return null;
  const vessels = [];
  for (const raw of value.vessels) {
    const vessel = parseAisVessel(raw);
    if (vessel === null) return null;
    vessels.push(vessel);
  }
  return { heard: value.heard, vessels };
}
function parseFriend(value) {
  if (!isObj8(value) || !isMmsi(value.mmsi)) return null;
  const label2 = text2(value.label);
  const name = text2(value.name);
  const state = parseWatchedVesselState(value);
  if (label2 === void 0 || name === void 0 || state === null) return null;
  const latest = state.latest;
  if (state.status === "unknown" || latest === null || !isLat2(latest.lat) || !isLon2(latest.lon)) {
    return null;
  }
  return { mmsi: value.mmsi, label: label2, name, status: state.status, latest };
}
function parseFriends(value) {
  if (!Array.isArray(value.boats) || !count(value.outOfRange)) return null;
  if (value.boats.length > NEO_MAP_FRIENDS_MAX) return null;
  if (typeof value.attribution !== "string") return null;
  const boats = [];
  for (const raw of value.boats) {
    const boat = parseFriend(raw);
    if (boat === null) return null;
    boats.push(boat);
  }
  return {
    boats,
    outOfRange: value.outOfRange,
    attribution: FLEET_ATTRIBUTION
  };
}
function parseWaypoint(value) {
  if (value === null) return null;
  if (!isObj8(value) || !isLat2(value.lat) || !isLon2(value.lon)) return void 0;
  const name = text2(value.name);
  if (name === void 0) return void 0;
  return { name, lat: value.lat, lon: value.lon };
}
function parseRoute(value) {
  const name = text2(value.name);
  const previous = parseWaypoint(value.previous);
  const next = parseWaypoint(value.next);
  if (name === void 0 || previous === void 0 || next === void 0) {
    return null;
  }
  return { name, previous, next };
}
function parseBounds(value) {
  if (!isObj8(value)) return null;
  const { south, west, north, east } = value;
  if (!isLat2(south) || !isLat2(north) || !isLon2(west) || !isLon2(east)) {
    return null;
  }
  return south <= north ? { south, west, north, east } : null;
}
var LAYER_PARSERS = {
  track: parseTrack,
  anchor: parseAnchor,
  ais: parseAis,
  friends: parseFriends,
  route: parseRoute
};
function parseNeoMapSnapshot(value, layers) {
  if (!isObj8(value) || value.kind !== "map" || !isObj8(value.missing)) {
    return null;
  }
  const boat = parseBoat(value.boat);
  const place = parsePlace(value.place);
  const bounds = parseBounds(value.bounds);
  if (boat === null || place === void 0 || bounds === null) return null;
  const snapshot = {
    kind: "map",
    boat,
    place,
    track: null,
    anchor: null,
    ais: null,
    friends: null,
    route: null,
    missing: {},
    bounds
  };
  if (value.locationContext !== void 0) {
    const context = parseMapLocationContext(value.locationContext);
    if (context !== null) snapshot.locationContext = context;
  }
  if (isIso2(value.drawnAt)) snapshot.drawnAt = value.drawnAt;
  for (const layer of NEO_MAP_LAYERS) {
    const raw = value[layer] ?? null;
    const why = value.missing[layer];
    if (!layers.includes(layer)) {
      if (raw !== null || why !== void 0) return null;
      continue;
    }
    if (raw === null) {
      if (typeof why !== "string") return null;
      snapshot.missing[layer] = NEO_MAP_MISSING_REASONS.find((r) => r === why) ?? "unavailable";
      continue;
    }
    if (why !== void 0 || !isObj8(raw)) return null;
    const parsed = LAYER_PARSERS[layer](raw);
    if (parsed === null) return null;
    snapshot[layer] = parsed;
  }
  return snapshot;
}

// src/units.ts
var DISTANCE_UNITS = ["nm", "mi", "km"];
var SHORT_DISTANCE_UNITS = ["m", "ft"];
var SPEED_UNITS = ["kn", "mph", "kmh"];
var DEPTH_UNITS = ["m", "ft", "ftm"];
var TEMPERATURE_UNITS = ["C", "F"];
var PRESSURE_UNITS = ["hPa", "inHg"];
var VOLUME_UNITS = ["L", "gal"];
var DEFAULT_UNIT_SETTINGS = {
  distance: "nm",
  shortDistance: "m",
  speed: "kn",
  depth: "m",
  temperature: "C",
  pressure: "hPa",
  volume: "L"
};
var UNIT_OPTIONS = {
  distance: DISTANCE_UNITS,
  shortDistance: SHORT_DISTANCE_UNITS,
  speed: SPEED_UNITS,
  depth: DEPTH_UNITS,
  temperature: TEMPERATURE_UNITS,
  pressure: PRESSURE_UNITS,
  volume: VOLUME_UNITS
};
var UNIT_CATEGORIES = Object.keys(UNIT_OPTIONS);
function unitSettingsFromPreset(preset) {
  if (preset === "imperial") {
    return {
      distance: "nm",
      shortDistance: "ft",
      speed: "kn",
      depth: "ft",
      temperature: "F",
      pressure: "inHg",
      volume: "gal"
    };
  }
  return { ...DEFAULT_UNIT_SETTINGS };
}
function isOneOf6(options, value) {
  return options.includes(value);
}
function parseUnitSettings(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value;
  for (const key of Object.keys(raw)) {
    if (!Object.prototype.hasOwnProperty.call(UNIT_OPTIONS, key)) return null;
  }
  for (const category of UNIT_CATEGORIES) {
    if (!isOneOf6(UNIT_OPTIONS[category], raw[category])) return null;
  }
  return {
    distance: raw.distance,
    shortDistance: raw.shortDistance,
    speed: raw.speed,
    depth: raw.depth,
    temperature: raw.temperature,
    pressure: raw.pressure,
    volume: raw.volume
  };
}
function resolveUnitSettings(preset, overrides) {
  const base = unitSettingsFromPreset(
    preset === "imperial" ? "imperial" : "metric"
  );
  const map = overrides && typeof overrides === "object" && !Array.isArray(overrides) ? overrides : void 0;
  const pick = (category) => {
    const value = map?.[category];
    return isOneOf6(UNIT_OPTIONS[category], value) ? value : base[category];
  };
  return {
    distance: pick("distance"),
    shortDistance: pick("shortDistance"),
    speed: pick("speed"),
    depth: pick("depth"),
    temperature: pick("temperature"),
    pressure: pick("pressure"),
    volume: pick("volume")
  };
}
function unitSettingsToVesselUnits(units) {
  const preset = units.shortDistance === "ft" && units.temperature === "F" && units.pressure === "inHg" ? "imperial" : "metric";
  const overrides = {};
  for (const category of UNIT_CATEGORIES) {
    overrides[category] = units[category];
  }
  return { preset, overrides };
}
var UNITS_COMMAND_VERSION = 1;
var UNITS_COMMAND_ID_MAX = 200;
var UNITS_COMMAND_USER_LABEL_MAX = 64;
function parseUnitsCommandPayload(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value;
  if (raw.v !== UNITS_COMMAND_VERSION) return null;
  const units = parseUnitSettings(raw.units);
  if (!units) return null;
  if (typeof raw.sourceUserId !== "string" || raw.sourceUserId.length === 0 || raw.sourceUserId.length > UNITS_COMMAND_ID_MAX) {
    return null;
  }
  const label2 = typeof raw.sourceUserLabel === "string" && raw.sourceUserLabel.length > 0 ? raw.sourceUserLabel.slice(0, UNITS_COMMAND_USER_LABEL_MAX) : void 0;
  return {
    v: UNITS_COMMAND_VERSION,
    units,
    sourceUserId: raw.sourceUserId,
    ...label2 !== void 0 ? { sourceUserLabel: label2 } : {}
  };
}

// src/neo-control.ts
var NEO_CONTROL_IDS = [
  "number",
  "arc-gauge",
  "needle-gauge",
  "vertical-meter",
  "horizontal-meter",
  "tank",
  "compass",
  "wind-rose",
  "wind-angle",
  "rudder",
  "divider",
  "trend-number",
  "sparkline",
  "history"
];
function isNeoControlId(value) {
  return NEO_CONTROL_IDS.includes(value);
}
var NEO_CONTROL_NAMES = {
  number: "Number",
  "arc-gauge": "Arc gauge",
  "needle-gauge": "Needle gauge",
  "vertical-meter": "Vertical meter",
  "horizontal-meter": "Horizontal meter",
  tank: "Tank",
  compass: "Compass",
  "wind-rose": "Wind rose",
  "wind-angle": "Wind angle",
  rudder: "Rudder",
  divider: "Divider",
  "trend-number": "Trend number",
  sparkline: "Sparkline",
  history: "History"
};
function neoControlName(control) {
  return isNeoControlId(control) ? NEO_CONTROL_NAMES[control] : "Panel";
}
var NEO_CONTROL_PATHS_MAX = 6;
var role = (name, required = true) => ({
  role: name,
  required
});
var VALUE = [role("value")];
var NEO_CONTROL_ROLES = {
  number: [role("value"), role("secondary", false)],
  "arc-gauge": VALUE,
  "needle-gauge": VALUE,
  "vertical-meter": VALUE,
  "horizontal-meter": VALUE,
  tank: [role("level"), role("capacity", false), role("quantity", false)],
  compass: [role("heading")],
  "wind-rose": [
    role("apparentAngle"),
    role("apparentSpeed"),
    role("trueAngle", false),
    role("trueSpeed", false),
    role("heading", false)
  ],
  "wind-angle": [role("angle"), role("speed", false)],
  rudder: [role("actualAngle"), role("orderedAngle", false)],
  divider: [],
  "trend-number": VALUE,
  sparkline: VALUE,
  history: VALUE
};
var NEO_CONTROL_VALUE_FIELDS = {
  number: scaleFields("value"),
  "arc-gauge": {
    ...scaleFields("value"),
    min: "value",
    max: "value",
    referenceMarker: "value"
  },
  "needle-gauge": { ...scaleFields("value"), "range[]": "value" },
  "vertical-meter": {
    ...scaleFields("value"),
    "bounds[]": "value",
    "thresholds[].value": "value"
  },
  "horizontal-meter": { ...scaleFields("value"), targetMarker: "value" },
  tank: { ...scaleFields("level"), lowMarker: "level" },
  compass: scaleFields("heading"),
  "wind-rose": {
    ...scaleFields("apparentAngle"),
    "sectors[].min": "apparentAngle",
    "sectors[].max": "apparentAngle"
  },
  "wind-angle": {
    ...scaleFields("angle"),
    "bounds[]": "angle",
    "targetSectors[].min": "angle",
    "targetSectors[].max": "angle"
  },
  rudder: { ...scaleFields("actualAngle"), "bounds[]": "actualAngle" },
  divider: {},
  "trend-number": scaleFields("value"),
  sparkline: scaleFields("value"),
  history: { ...scaleFields("value"), "thresholds[].value": "value" }
};
function scaleFields(primary) {
  return {
    scaleMin: primary,
    scaleMax: primary,
    "zones[].min": primary,
    "zones[].max": primary,
    "markers[].value": primary
  };
}
var CONTROL_ID_MAX = 80;
var ROLE_MAX = 40;
var PATH_MAX = 200;
var BINDINGS_MAX = 12;
var PRESET_ID_MAX = 80;
var OPTION_KEY_MAX = 64;
var OPTION_DEPTH_MAX = 8;
var UUID2 = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isRecord2(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isPath(value) {
  return typeof value === "string" && value.length > 0 && value.length <= PATH_MAX;
}
function jsonFrom(value, depth) {
  if (value === null || typeof value === "boolean" || typeof value === "string")
    return value;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : void 0;
  }
  if (depth >= OPTION_DEPTH_MAX) return void 0;
  if (Array.isArray(value)) {
    const out2 = [];
    for (const entry of value) {
      const copy = jsonFrom(entry, depth + 1);
      if (copy === void 0) return void 0;
      out2.push(copy);
    }
    return out2;
  }
  if (!isRecord2(value)) return void 0;
  const out = {};
  for (const [key, entry] of Object.entries(value)) {
    const copy = jsonFrom(entry, depth + 1);
    if (copy === void 0) return void 0;
    out[key] = copy;
  }
  return out;
}
function bindingsFrom(value) {
  if (value === void 0) return {};
  if (!isRecord2(value)) return null;
  const entries = Object.entries(value);
  if (entries.length > BINDINGS_MAX) return null;
  const out = {};
  for (const [role2, path] of entries) {
    if (role2.length === 0 || role2.length > ROLE_MAX || !isPath(path))
      return null;
    out[role2] = path;
  }
  return out;
}
function optionsFrom(value) {
  if (value === void 0) return {};
  if (!isRecord2(value)) return null;
  const out = {};
  for (const [key, entry] of Object.entries(value)) {
    if (key.length === 0 || key.length > OPTION_KEY_MAX) return null;
    const copy = jsonFrom(entry, 1);
    if (copy === void 0) return null;
    out[key] = copy;
  }
  return out;
}
function parseNeoControlOptions(value) {
  return isRecord2(value) ? optionsFrom(value) : null;
}
function neoControlPaths(bindings) {
  const paths = [];
  for (const role2 of Object.keys(bindings).sort()) {
    const path = bindings[role2];
    if (!paths.includes(path)) paths.push(path);
    if (paths.length === NEO_CONTROL_PATHS_MAX) break;
  }
  return paths;
}
function pathsMatch(paths, bindings) {
  const bound = new Set(Object.values(bindings));
  return paths.length === Math.min(bound.size, NEO_CONTROL_PATHS_MAX) && new Set(paths).size === paths.length && paths.every((p) => bound.has(p));
}
function parseNeoBoardControlSpec(value) {
  if (!isRecord2(value) || value.v !== 1 || value.kind !== "control")
    return null;
  if (typeof value.boatId !== "string") return null;
  const control = value.control;
  if (typeof control !== "string" || control.length === 0 || control.length > CONTROL_ID_MAX) {
    return null;
  }
  const bindings = bindingsFrom(value.bindings);
  if (bindings === null) return null;
  const options = optionsFrom(value.options);
  if (options === null) return null;
  const stored = value.paths;
  if (stored !== void 0 && !(Array.isArray(stored) && stored.every((p) => typeof p === "string"))) {
    return null;
  }
  const paths = stored !== void 0 && pathsMatch(stored, bindings) ? [...stored] : neoControlPaths(bindings);
  const presetId = value.presetId;
  if (presetId !== void 0 && !(typeof presetId === "string" && presetId.length > 0 && presetId.length <= PRESET_ID_MAX)) {
    return null;
  }
  const fromNamed = value.fromNamed;
  if (fromNamed !== void 0 && !(typeof fromNamed === "string" && UUID2.test(fromNamed))) {
    return null;
  }
  return {
    v: 1,
    kind: "control",
    boatId: value.boatId,
    control,
    bindings,
    paths,
    ...presetId !== void 0 ? { presetId } : {},
    options,
    ...fromNamed !== void 0 ? { fromNamed } : {}
  };
}
var NEO_CONTROL_STAND_IN_SUBTITLE = "Shown simply in this app version";
var STAND_IN_WIDGET = {
  number: "tile",
  "trend-number": "tile",
  "arc-gauge": "gauge",
  "needle-gauge": "gauge",
  "vertical-meter": "level",
  "horizontal-meter": "level",
  tank: "level",
  compass: "dial",
  "wind-rose": "dial",
  "wind-angle": "dial",
  rudder: "dial",
  sparkline: "sparkline",
  history: "chart",
  divider: null
};
function neoControlAsInstrument(spec) {
  const widget = isNeoControlId(spec.control) ? STAND_IN_WIDGET[spec.control] : null;
  if (widget === null) return null;
  const paths = spec.paths.slice(0, NEO_CONTROL_PATHS_MAX);
  if (paths.length === 0) return null;
  const scale = widget === "gauge" || widget === "level" ? neoControlScale(spec) : null;
  return {
    v: 1,
    kind: "instrument",
    boatId: spec.boatId,
    widget,
    paths,
    subtitle: NEO_CONTROL_STAND_IN_SUBTITLE,
    ...scale !== null ? { range: scale } : {}
  };
}
var OWN_SCALE = {
  "arc-gauge": "minMax",
  "needle-gauge": "range",
  "vertical-meter": "bounds"
};
function numberPair(min, max) {
  return typeof min === "number" && typeof max === "number" && min < max ? { min, max } : null;
}
function neoControlScale(spec) {
  const o = spec.options;
  const own = isNeoControlId(spec.control) ? OWN_SCALE[spec.control] : void 0;
  const pair2 = (v) => Array.isArray(v) && v.length === 2 ? numberPair(v[0], v[1]) : null;
  const fromOwn = own === "minMax" ? numberPair(o.min, o.max) : own === "range" ? pair2(o.range) : own === "bounds" ? pair2(o.bounds) : null;
  return fromOwn ?? numberPair(o.scaleMin, o.scaleMax);
}
function isTankLevel(path) {
  return /^tanks\.[^.]+\.[^.]+\.currentLevel$/.test(path);
}
function isWindAngle(path) {
  return /^environment\.wind\.angle(Apparent|TrueWater|TrueGround)$/.test(path);
}
function isHeading(path) {
  return /^navigation\.(heading|courseOverGround)(True|Magnetic)$/.test(path);
}
function neoControlForInstrument(spec) {
  const first = spec.paths[0] ?? "";
  switch (spec.widget) {
    case "tile":
      return "number";
    case "gauge":
      return "arc-gauge";
    case "level":
      return isTankLevel(first) ? "tank" : "vertical-meter";
    case "dial":
      return isWindAngle(first) ? "wind-angle" : isHeading(first) ? "compass" : "needle-gauge";
    case "sparkline":
      return "sparkline";
    case "chart":
      return "history";
  }
}
function instrumentAsPanel(spec) {
  const control = neoControlForInstrument(spec);
  const roles = NEO_CONTROL_ROLES[control];
  const ordered = [
    ...roles.filter((r) => r.required),
    ...roles.filter((r) => !r.required)
  ];
  const bindings = {};
  spec.paths.forEach((path, at) => {
    const target = ordered[at];
    if (target !== void 0) bindings[target.role] = path;
  });
  const options = {};
  const range = spec.range;
  if (range !== void 0 && range.min < range.max) {
    options.scaleMin = range.min;
    options.scaleMax = range.max;
    const own = OWN_SCALE[control];
    if (own === "minMax") {
      options.min = range.min;
      options.max = range.max;
    } else if (own === "range") {
      options.range = [range.min, range.max];
    } else if (own === "bounds") {
      options.bounds = [range.min, range.max];
    }
  }
  const paths = [];
  for (const r of roles) {
    const path = bindings[r.role];
    if (path !== void 0 && !paths.includes(path)) paths.push(path);
  }
  return {
    v: 1,
    kind: "control",
    boatId: spec.boatId,
    control,
    bindings,
    paths: paths.slice(0, NEO_CONTROL_PATHS_MAX),
    options
  };
}
function neoControlIsStandIn(spec) {
  return spec.kind === "instrument" && spec.subtitle === NEO_CONTROL_STAND_IN_SUBTITLE;
}

// src/neo-page.ts
var NEO_PAGE_NAME = /^[a-z][a-z0-9-]{0,23}$/;
var NEO_PAGE_READINGS_MAX = 12;
var NEO_PAGE_ZONES_MAX = 6;
var NEO_PAGE_FACES_MAX = 24;
var NEO_PAGE_BYTES_MAX = 32768;
var NEO_PAGE_FACE_LOOK_BYTES_MAX = 4096;
var NEO_PAGE_NIGHT = ["tint", "keep"];
var NEO_PAGE_TITLE = "Page";
var NEO_PAGE_V2_READINGS_MAX = 64;
var NEO_PAGE_V2_BYTES_MAX = 262144;
var NEO_PAGE_JS_BYTES_MAX = 32768;
var NEO_PAGE_LIVE_MAX = 2;
var NEO_PAGE_FEEDS_MAX = 4;
var NEO_PAGE_HISTORY_MAX = 32;
var NEO_PAGE_SERIES_POINTS_MIN = 24;
var NEO_PAGE_SERIES_POINTS_DEFAULT = 96;
var NEO_PAGE_SERIES_POINTS_MAX = 400;
var NEO_PAGE_FEED_SLOTS_MAX = 14;
var NEO_PAGE_WATER_ROWS_MAX = 6;
var NEO_PAGE_WINDOW_MIN_MS = 3e5;
var NEO_PAGE_WINDOW_MAX_MS = 366 * 864e5;
var NEO_PAGE_TEMPLATE_SOURCE_BYTES_MAX = 16384;
var NEO_BOARD_PAGES_BYTES_MAX = 2097152;
var NEO_PAGE_PART_ID = /^[a-z][a-z0-9-]{0,23}$/;
var NEO_PAGE_STATUS_KINDS = ["mode", "anchor"];
var NEO_PAGE_FEED_KINDS = [
  "engine-hours",
  "voyage-distance",
  "water",
  "module"
];
var NEO_PAGE_MODULE_PORTS = [
  "depth-safety",
  "engine-vitals",
  "environment-outlook",
  "forecast",
  "helm",
  "power-budget",
  "pressure",
  "reserves",
  "tanks-budget",
  "tide",
  "traffic"
];
var NEO_PAGE_MODULE_SLOTS_MAX = {
  /** tanks-budget rows. */
  tank: 12,
  /** traffic targets: the module's own maxTargets cap. */
  traffic: 20,
  /** engine-vitals engines, as UnderwayView reads e0 to e3. */
  engine: 4,
  /** engine-vitals named probes. */
  probe: 8,
  /** engine-vitals engines at rest. */
  rest: 4,
  /** power-budget charge sources. */
  source: 4,
  /** environment-outlook's second line. */
  sub: 2,
  /** forecast hours: horizonHours runs 3 to 48. */
  hour: 48,
  /** tide turns in its window. */
  turn: 6
};
var NEO_PAGE_MODULE_SERIES_MAX = 4;
var NEO_PAGE_LIVE_MIN_PX = {
  "anchor-watch": [320, 240],
  module: [160, 96],
  map: [160, 120],
  anchor: [160, 120],
  "how-to": [160, 96],
  friend: [160, 96]
};
var NEO_PAGE_SLOT_VIEWPORTS = {
  bento: [1440, 900],
  phone: [390, 844]
};
var NEO_PAGE_SLOT_FRAMES = {
  bento: {
    "anchor-watch": [721.5, 640],
    "power-budget": [638.5, 102.5],
    "environment-outlook": [302.5, 176.5],
    "tanks-budget": [302.5, 116],
    forecast: [302.5, 195.5],
    tide: [302.5, 80.5],
    pressure: [302.5, 80.5],
    "nearby-vessels": [697.5, 592.5],
    "now-left": [302.5, 176.5],
    "now-right": [302.5, 176.5],
    "now-row": [638.5, 176.5],
    "coming-top": [302.5, 70.5],
    "coming-bottom": [302.5, 70.5],
    "coming-right": [302.5, 198.5],
    strip: [1370, 24.5],
    hero: [565.5, 464],
    wide: [201.5, 153.5],
    "flex-1": [531, 153.5],
    "flex-2": [768.5, 86],
    "flex-3": [768.5, 84]
  },
  phone: {
    "anchor-watch": [334, 320],
    "power-budget": [334, 219],
    "environment-outlook": [334, 150],
    "tanks-budget": [334, 264],
    forecast: [334, 252],
    tide: [334, 139.5],
    pressure: [334, 107],
    "nearby-vessels": [334, 251],
    "now-left": [334, 179],
    "now-right": [334, 179],
    "now-row": [334, 179],
    "coming-top": [334, 179],
    "coming-bottom": [334, 179],
    "coming-right": [334, 179],
    strip: [328, 24.5],
    hero: [328, 127],
    wide: [328, 153.5],
    "flex-1": [328, 77.5],
    "flex-2": [328, 147],
    "flex-3": [328, 102]
  }
};
var NEO_PAGE_TEMPLATE_ID = /^(?:chart\.(?:trend|readouts|bars)|card\.(?:water|mode|anchor|map|how-to|friend|diagram)|instrument\.(?:gauge|dial|level|tile|sparkline|chart)|module\.[a-z][a-z-]{1,31}|control\.[a-z][a-z0-9-]{0,39})$/;
var NEO_PAGE_READING_UNITS = [
  .../* @__PURE__ */ new Set([
    ...DISTANCE_UNITS,
    ...SHORT_DISTANCE_UNITS,
    ...SPEED_UNITS,
    ...DEPTH_UNITS,
    ...TEMPERATURE_UNITS,
    ...PRESSURE_UNITS,
    ...VOLUME_UNITS
  ])
];
function isNeoPageReadingUnit(value) {
  return typeof value === "string" && NEO_PAGE_READING_UNITS.includes(value);
}
var NEO_PAGE_NEEDS = ["page3"];
var PATH_MAX2 = 200;
var CONTROL_ID = /^[a-z][a-z0-9-]{0,79}$/;
var ROLE_MAX2 = 40;
var FACE_ROLES_MAX = 12;
var UUID3 = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isRecord3(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isPath2(value) {
  return typeof value === "string" && value.length > 0 && value.length <= PATH_MAX2;
}
function isFiniteNumber2(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function isNeoPageName(value) {
  return typeof value === "string" && NEO_PAGE_NAME.test(value);
}
function neoPageUtf8Bytes(text3) {
  let bytes = 0;
  for (let i = 0; i < text3.length; i += 1) {
    const code = text3.charCodeAt(i);
    if (code < 128) bytes += 1;
    else if (code < 2048) bytes += 2;
    else if (code >= 55296 && code <= 56319 && i + 1 < text3.length && (text3.charCodeAt(i + 1) & 64512) === 56320) {
      bytes += 4;
      i += 1;
    } else bytes += 3;
  }
  return bytes;
}
function zoneFrom(value) {
  if (!isRecord3(value) || !isNeoPageName(value.name)) return null;
  const { below, above } = value;
  if (below !== void 0 && !isFiniteNumber2(below)) return null;
  if (above !== void 0 && !isFiniteNumber2(above)) return null;
  return {
    name: value.name,
    ...below !== void 0 ? { below } : {},
    ...above !== void 0 ? { above } : {}
  };
}
function readingFrom(value) {
  if (!isRecord3(value) || !isPath2(value.path)) return null;
  const range = value.range;
  if (range !== void 0 && !(Array.isArray(range) && range.length === 2 && isFiniteNumber2(range[0]) && isFiniteNumber2(range[1]))) {
    return null;
  }
  let zones;
  if (value.zones !== void 0) {
    if (!Array.isArray(value.zones) || value.zones.length > NEO_PAGE_ZONES_MAX)
      return null;
    zones = [];
    for (const entry of value.zones) {
      const zone = zoneFrom(entry);
      if (zone === null) return null;
      zones.push(zone);
    }
  }
  if (value.unit !== void 0 && typeof value.unit !== "string") return null;
  const unit = isNeoPageReadingUnit(value.unit) ? value.unit : void 0;
  return {
    path: value.path,
    ...range !== void 0 ? { range: [range[0], range[1]] } : {},
    ...zones !== void 0 ? { zones } : {},
    ...unit !== void 0 ? { unit } : {}
  };
}
function parseNeoPageReading(value) {
  return readingFrom(value);
}
function faceFrom(value) {
  if (!isRecord3(value)) return null;
  const control = value.control;
  if (typeof control !== "string" || !CONTROL_ID.test(control)) return null;
  if (!isRecord3(value.readings)) return null;
  const roles = Object.entries(value.readings);
  if (roles.length > FACE_ROLES_MAX) return null;
  const readings = {};
  for (const [role2, name] of roles) {
    if (role2.length === 0 || role2.length > ROLE_MAX2 || !isNeoPageName(name))
      return null;
    readings[role2] = name;
  }
  let look;
  if (value.look !== void 0) {
    const options = parseNeoControlOptions(value.look);
    if (options === null) return null;
    if (neoPageUtf8Bytes(JSON.stringify(options)) > NEO_PAGE_FACE_LOOK_BYTES_MAX)
      return null;
    look = options;
  }
  return { control, readings, ...look !== void 0 ? { look } : {} };
}
function facesFrom(value) {
  if (value === void 0) return [];
  if (!Array.isArray(value) || value.length > NEO_PAGE_FACES_MAX) return null;
  const out = [];
  for (const entry of value) {
    const face = faceFrom(entry);
    if (face === null) return null;
    out.push(face);
  }
  return out;
}
function neoPagePaths(readings) {
  const paths = [];
  for (const reading of Object.values(readings)) {
    if (!paths.includes(reading.path)) paths.push(reading.path);
  }
  return paths;
}
function pathsMatch2(paths, readings) {
  const read = new Set(Object.values(readings).map((r) => r.path));
  return paths.length === read.size && new Set(paths).size === paths.length && paths.every((p) => read.has(p));
}
function readingsFrom(value, max) {
  if (value === void 0) return {};
  if (!isRecord3(value)) return null;
  const entries = Object.entries(value);
  if (entries.length > max) return null;
  const out = {};
  for (const [name, entry] of entries) {
    if (!isNeoPageName(name)) return null;
    const reading = readingFrom(entry);
    if (reading === null) return null;
    out[name] = reading;
  }
  return out;
}
function commonFrom(value, readingsMax, bytesMax) {
  if (typeof value.boatId !== "string") return null;
  const readings = readingsFrom(value.readings, readingsMax);
  if (readings === null) return null;
  const faces = facesFrom(value.faces);
  if (faces === null) return null;
  const { html, css } = value;
  if (typeof html !== "string" || typeof css !== "string") return null;
  if (neoPageUtf8Bytes(html) + neoPageUtf8Bytes(css) > bytesMax) return null;
  const night = value.night;
  if (night !== void 0 && !NEO_PAGE_NIGHT.includes(night)) {
    return null;
  }
  const fromNamed = value.fromNamed;
  if (fromNamed !== void 0 && !(typeof fromNamed === "string" && UUID3.test(fromNamed))) {
    return null;
  }
  const stored = value.paths;
  if (stored !== void 0 && !(Array.isArray(stored) && stored.every((p) => typeof p === "string"))) {
    return null;
  }
  const paths = stored !== void 0 && pathsMatch2(stored, readings) ? [...stored] : neoPagePaths(readings);
  return {
    boatId: value.boatId,
    readings,
    paths,
    faces,
    html,
    css,
    ...night !== void 0 ? { night } : {},
    ...fromNamed !== void 0 ? { fromNamed } : {}
  };
}
function isNeoPagePartId(value) {
  return typeof value === "string" && NEO_PAGE_PART_ID.test(value);
}
function isPositiveInteger(value) {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}
function liveSpecFrom(value, boatId) {
  if (!isRecord3(value) || value.v !== 1 || value.boatId !== boatId) {
    return null;
  }
  switch (value.kind) {
    case "module":
      return parseNeoBoardModuleSpec(value);
    case "map": {
      const parts = parseNeoMapSpecParts(value);
      return parts === null ? null : { v: 1, kind: "map", boatId, ...parts };
    }
    // An anchor or friend card's offered change stays with its live area, as
    // the card's own reader keeps it (parseNeoCard; TOM-665): a converted card
    // that offered a change still offers it.
    case "anchor": {
      const confirm = parseAnchorConfirm(value.confirm);
      if (confirm === null) return null;
      return {
        v: 1,
        kind: "anchor",
        boatId,
        ...confirm === void 0 ? {} : { confirm }
      };
    }
    case "how-to": {
      const { procedureId } = value;
      return typeof procedureId === "string" && procedureId.length >= 1 && procedureId.length <= 100 ? { v: 1, kind: "how-to", boatId, procedureId } : null;
    }
    case "friend": {
      if (!isMmsi(value.mmsi)) return null;
      const confirm = parseFriendConfirm(value.confirm, value.mmsi);
      if (confirm === null) return null;
      return {
        v: 1,
        kind: "friend",
        boatId,
        mmsi: value.mmsi,
        ...confirm === void 0 ? {} : { confirm }
      };
    }
    default:
      return null;
  }
}
function liveFrom(value, boatId) {
  if (value === void 0) return [];
  if (!Array.isArray(value) || value.length > NEO_PAGE_LIVE_MAX) return null;
  const out = [];
  const ids = /* @__PURE__ */ new Set();
  for (const entry of value) {
    if (!isRecord3(entry) || !isNeoPagePartId(entry.id) || ids.has(entry.id)) {
      return null;
    }
    ids.add(entry.id);
    const spec = liveSpecFrom(entry.spec, boatId);
    if (spec === null) return null;
    const { rolling } = entry;
    if (rolling !== null && !isPositiveInteger(rolling)) return null;
    if (rolling !== null && !(spec.kind === "map" && spec.window !== null)) {
      return null;
    }
    out.push({ id: entry.id, spec, rolling });
  }
  return out;
}
function feedFrom(value, boatId) {
  if (!isRecord3(value) || !isNeoPagePartId(value.id)) return null;
  const { id, kind } = value;
  if (kind === "water") return { id, kind };
  if (kind === "module") {
    const spec = parseNeoBoardModuleSpec(value.spec);
    if (spec === null || spec.boatId !== boatId) return null;
    if (!neoPageModuleIsPorted(spec.module)) return null;
    return { id, kind, spec };
  }
  if (kind !== "engine-hours" && kind !== "voyage-distance") return null;
  const { by, slots } = value;
  if (by !== "day" && by !== "week") return null;
  if (!(typeof slots === "number" && Number.isInteger(slots) && slots >= 1 && slots <= NEO_PAGE_FEED_SLOTS_MAX)) {
    return null;
  }
  const span = value.window;
  if (span !== void 0 && !(typeof span === "string" && parseNeoPageWindow(span) !== null)) {
    return null;
  }
  const window = span;
  if (kind === "voyage-distance") {
    return {
      id,
      kind,
      by,
      slots,
      ...window !== void 0 ? { window } : {}
    };
  }
  const { engine } = value;
  if (engine !== null && !(typeof engine === "string" && engine.length >= 1 && engine.length <= 80)) {
    return null;
  }
  return {
    id,
    kind,
    engine,
    by,
    slots,
    ...window !== void 0 ? { window } : {}
  };
}
function feedsFrom(value, boatId, live) {
  if (value === void 0) return [];
  if (!Array.isArray(value) || value.length > NEO_PAGE_FEEDS_MAX) return null;
  const out = [];
  const ids = /* @__PURE__ */ new Set();
  for (const entry of value) {
    const feed = feedFrom(entry, boatId);
    if (feed === null || ids.has(feed.id)) return null;
    ids.add(feed.id);
    out.push(feed);
  }
  if (out.filter((f) => f.kind === "water").length > 1) return null;
  const modules = out.filter((f) => f.kind === "module").length;
  if (modules > 1) return null;
  if (modules > 0 && live.some((area) => area.spec.kind === "module")) {
    return null;
  }
  return out;
}
function historyFrom(value, readings) {
  if (value === void 0) return [];
  if (!Array.isArray(value) || value.length > NEO_PAGE_HISTORY_MAX) return null;
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const entry of value) {
    if (!isRecord3(entry)) return null;
    const { reading, window, points } = entry;
    if (typeof reading !== "string" || readings[reading] === void 0) {
      return null;
    }
    if (typeof window !== "string" || parseNeoPageWindow(window) === null) {
      return null;
    }
    if (!(typeof points === "number" && Number.isInteger(points) && points >= NEO_PAGE_SERIES_POINTS_MIN && points <= NEO_PAGE_SERIES_POINTS_MAX)) {
      return null;
    }
    const key = JSON.stringify([reading, window, points]);
    if (seen.has(key)) return null;
    seen.add(key);
    out.push({ reading, window, points });
  }
  return out;
}
function statusesFrom(value) {
  if (value === void 0) return [];
  if (!Array.isArray(value)) return null;
  const out = [];
  for (const entry of value) {
    if (!NEO_PAGE_STATUS_KINDS.includes(entry) || out.includes(entry)) {
      return null;
    }
    out.push(entry);
  }
  return out;
}
var WIDGET_ICONS = WIDGET_TYPES;
function cardFrom(value, readings, feeds) {
  if (value === void 0) return void 0;
  if (!isRecord3(value)) return null;
  const feedOf = (id) => feeds.find((f) => f.id === id);
  switch (value.kind) {
    case "chart": {
      if (typeof value.window !== "boolean") return null;
      let second;
      if (value.secondChip !== void 0) {
        const s = value.secondChip;
        if (!isRecord3(s) || typeof s.reading !== "string" || readings[s.reading] === void 0 || !NEO_CHART_CHIPS.includes(s.stat) || !isRecord3(value.chip) || value.chip.reading === void 0) {
          return null;
        }
        second = { reading: s.reading, stat: s.stat };
      }
      const withSecond = second === void 0 ? {} : { secondChip: second };
      const chip = value.chip;
      if (chip === null) return { kind: "chart", window: value.window, chip };
      if (!isRecord3(chip)) return null;
      if (chip.reading !== void 0) {
        if (typeof chip.reading !== "string" || readings[chip.reading] === void 0 || !NEO_CHART_CHIPS.includes(chip.stat)) {
          return null;
        }
        return {
          kind: "chart",
          window: value.window,
          chip: { reading: chip.reading, stat: chip.stat },
          ...withSecond
        };
      }
      const feed = feedOf(chip.feed);
      if (feed === void 0 || feed.kind !== "engine-hours" && feed.kind !== "voyage-distance" || chip.field !== "total") {
        return null;
      }
      return {
        kind: "chart",
        window: value.window,
        chip: { feed: feed.id, field: "total" }
      };
    }
    case "status": {
      const { icon, meta } = value;
      if (icon !== "water" && icon !== "mode" && icon !== "anchor") return null;
      if (meta === null) return { kind: "status", icon, meta };
      if (!isRecord3(meta)) return null;
      if (meta.feed !== void 0) {
        const feed = feedOf(meta.feed);
        if (feed?.kind !== "water" || meta.field !== "as-of") return null;
        return {
          kind: "status",
          icon,
          meta: { feed: feed.id, field: "as-of" }
        };
      }
      if (meta.status === "mode" && meta.field === "tag") {
        return { kind: "status", icon, meta: { status: "mode", field: "tag" } };
      }
      if (meta.status === "anchor" && meta.field === "word") {
        return {
          kind: "status",
          icon,
          meta: { status: "anchor", field: "word" }
        };
      }
      return null;
    }
    case "panel": {
      const { icon } = value;
      if (icon !== null && !WIDGET_ICONS.includes(icon)) {
        return null;
      }
      return { kind: "panel", icon };
    }
    case "module": {
      const feed = feedOf(value.feed);
      return feed?.kind === "module" ? { kind: "module", feed: feed.id } : null;
    }
    default:
      return null;
  }
}
function templateFrom(value, boatId) {
  if (value === void 0) return void 0;
  if (!isRecord3(value)) return null;
  const { id, version, edited, source } = value;
  if (typeof id !== "string" || !NEO_PAGE_TEMPLATE_ID.test(id)) return null;
  if (!(typeof version === "number" && Number.isInteger(version) && version >= 1)) {
    return null;
  }
  if (typeof edited !== "boolean" || !isRecord3(source)) return null;
  const { spec, rolling } = source;
  if (!isRecord3(spec) || spec.v !== 1 || typeof spec.kind !== "string" || spec.kind === "page" || spec.boatId !== boatId) {
    return null;
  }
  if (neoPageUtf8Bytes(JSON.stringify(spec)) > NEO_PAGE_TEMPLATE_SOURCE_BYTES_MAX) {
    return null;
  }
  if (rolling !== null && !isPositiveInteger(rolling)) return null;
  return {
    id,
    version,
    edited,
    source: { spec, rolling }
  };
}
function parseV2(value) {
  const common = commonFrom(
    value,
    NEO_PAGE_V2_READINGS_MAX,
    NEO_PAGE_V2_BYTES_MAX
  );
  if (common === null) return null;
  const live = liveFrom(value.live, common.boatId);
  if (live === null) return null;
  const feeds = feedsFrom(value.feeds, common.boatId, live);
  if (feeds === null) return null;
  const history = historyFrom(value.history, common.readings);
  if (history === null) return null;
  const statuses = statusesFrom(value.statuses);
  if (statuses === null) return null;
  const card = cardFrom(value.card, common.readings, feeds);
  if (card === null) return null;
  const template = templateFrom(value.template, common.boatId);
  if (template === null) return null;
  const needs = NEO_PAGE_NEEDS.includes(value.needs) ? value.needs : void 0;
  const js = value.js;
  if (js !== void 0 && !(typeof js === "string" && js.length > 0 && neoPageUtf8Bytes(js) <= NEO_PAGE_JS_BYTES_MAX)) {
    return null;
  }
  const { night, fromNamed, ...rest } = common;
  return {
    v: 2,
    kind: "page",
    ...rest,
    history,
    statuses,
    live,
    feeds,
    ...card !== void 0 ? { card } : {},
    ...night !== void 0 ? { night } : {},
    ...fromNamed !== void 0 ? { fromNamed } : {},
    ...template !== void 0 ? { template } : {},
    ...needs !== void 0 ? { needs } : {},
    ...js !== void 0 ? { js } : {}
  };
}
function parseNeoBoardPageSpec(value) {
  if (!isRecord3(value) || value.kind !== "page") return null;
  if (value.v === 2) return parseV2(value);
  if (value.v !== 1) return null;
  const common = commonFrom(value, NEO_PAGE_READINGS_MAX, NEO_PAGE_BYTES_MAX);
  if (common === null) return null;
  const { night, fromNamed, ...rest } = common;
  return {
    v: 1,
    kind: "page",
    ...rest,
    ...night !== void 0 ? { night } : {},
    ...fromNamed !== void 0 ? { fromNamed } : {}
  };
}
function neoPageHasScript(spec) {
  return spec.v === 2 && typeof spec.js === "string" && spec.js.length > 0;
}
function neoPageWithoutScript(spec) {
  if (spec.v !== 2 || spec.js === void 0) return spec;
  const { js: _js, ...rest } = spec;
  return rest;
}
var NEO_PAGE_POSITION_PATH = /^navigation\.(?:position|gnss|anchor|courseOverGround|speedOverGround|track)/;
var NEO_PAGE_POSITION_MODULES = ["helm", "traffic"];
function neoPagePositionParts(spec) {
  const readings = Object.entries(spec.readings).filter(([, r]) => NEO_PAGE_POSITION_PATH.test(r.path)).map(([name]) => name);
  const feeds = spec.v === 2 ? spec.feeds.filter(
    (f) => f.kind === "module" && NEO_PAGE_POSITION_MODULES.includes(f.spec.module)
  ).map((f) => f.id) : [];
  const statuses = spec.v === 2 && spec.statuses.includes("anchor") ? ["anchor"] : [];
  return { readings, feeds, statuses };
}
function neoPageIsV2(spec) {
  return spec.v === 2;
}
function neoPageTemplateOf(spec) {
  return neoPageIsV2(spec) ? spec.template ?? null : null;
}
function neoPageIsUnedited(spec) {
  const mark = neoPageTemplateOf(spec);
  return mark !== null && !mark.edited;
}
function neoPageModuleIsPorted(kind) {
  return NEO_PAGE_MODULE_PORTS.includes(kind);
}
function neoPageTemplateIsHosted(id) {
  if (id === "card.map" || id === "card.how-to" || id === "card.friend") {
    return true;
  }
  return id.startsWith("module.") && !neoPageModuleIsPorted(id.slice(7));
}
function neoPageTemplateIsPortedModule(id) {
  return id.startsWith("module.") && neoPageModuleIsPorted(id.slice(7));
}
function neoPageCardOf(spec) {
  return spec.card ?? { kind: "panel", icon: null };
}
function neoPageModuleFeed(spec) {
  for (const feed of spec.feeds) {
    if (feed.kind === "module") return feed;
  }
  return null;
}
function neoPageUneditedSource(spec) {
  if (!neoPageIsV2(spec)) return null;
  const mark = spec.template;
  if (mark === void 0 || mark.edited) return null;
  if (neoPageTemplateIsHosted(mark.id)) {
    const area = spec.live[0];
    if (area !== void 0) return { spec: area.spec, rolling: area.rolling };
  } else if (neoPageTemplateIsPortedModule(mark.id)) {
    const feed = neoPageModuleFeed(spec);
    if (feed !== null) return { spec: feed.spec, rolling: null };
  }
  return { spec: mark.source.spec, rolling: mark.source.rolling };
}
function neoPageTemplateSourceAgrees(spec) {
  const mark = neoPageTemplateOf(spec);
  if (mark === null || !neoPageIsV2(spec)) return true;
  const same2 = (a, b) => JSON.stringify(sortedDeep(a)) === JSON.stringify(sortedDeep(b));
  if (neoPageTemplateIsHosted(mark.id)) {
    const area = spec.live[0];
    return area !== void 0 && same2(area.spec, mark.source.spec) && area.rolling === mark.source.rolling;
  }
  if (neoPageTemplateIsPortedModule(mark.id)) {
    const feed = neoPageModuleFeed(spec);
    return feed !== null && same2(feed.spec, mark.source.spec);
  }
  return true;
}
function sortedDeep(value) {
  if (Array.isArray(value)) return value.map(sortedDeep);
  if (isRecord3(value)) {
    return Object.fromEntries(
      Object.keys(value).sort().map((key) => [key, sortedDeep(value[key])])
    );
  }
  return value;
}
var NEO_PAGE_STAND_IN_TEXT = "Update the app to see this panel's design";
var STAND_IN_HTML = `<div class="tc-stand-in"><p>${NEO_PAGE_STAND_IN_TEXT}</p></div>`;
var STAND_IN_CSS = ".tc-stand-in{display:flex;align-items:center;justify-content:center;box-sizing:border-box;height:100%;min-height:64px;padding:12px;text-align:center}.tc-stand-in p{margin:0;font-size:14px;line-height:1.35;color:var(--text);opacity:.72}";
function neoPageStandIn(spec) {
  if (neoPageIsV2(spec)) {
    const { template: _template, ...rest } = spec;
    return {
      ...rest,
      html: STAND_IN_HTML,
      css: STAND_IN_CSS,
      faces: [],
      history: [],
      statuses: [],
      live: [],
      feeds: []
    };
  }
  return { ...spec, html: STAND_IN_HTML, css: STAND_IN_CSS, faces: [] };
}
function neoPageDrawsDirect(view) {
  const mark = view.template;
  return mark !== null && !mark.edited && (neoPageTemplateIsHosted(mark.id) || neoPageTemplateIsPortedModule(mark.id));
}
var ROLLING_WINDOW = /^([1-9][0-9]{0,5})(m|h|d)$/;
var WINDOW_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?Z$/;
var UNIT_MS = { m: 6e4, h: 36e5, d: 864e5 };
function spanAllowed(ms) {
  return ms >= NEO_PAGE_WINDOW_MIN_MS && ms <= NEO_PAGE_WINDOW_MAX_MS;
}
function parseNeoPageWindow(text3) {
  if (typeof text3 !== "string") return null;
  const rolling = ROLLING_WINDOW.exec(text3);
  if (rolling !== null) {
    const ms = Number(rolling[1]) * UNIT_MS[rolling[2]];
    return spanAllowed(ms) ? { kind: "rolling", ms } : null;
  }
  const parts = text3.split("/");
  if (parts.length !== 2) return null;
  const [from, to] = parts;
  if (!WINDOW_INSTANT.test(from) || !WINDOW_INSTANT.test(to)) return null;
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
  if (new Date(fromMs).toISOString().slice(0, 10) !== from.slice(0, 10) || new Date(toMs).toISOString().slice(0, 10) !== to.slice(0, 10)) {
    return null;
  }
  return toMs > fromMs && spanAllowed(toMs - fromMs) ? { kind: "fixed", fromMs, toMs } : null;
}
function neoPageWindowFromRolling(seconds) {
  if (Number.isNaN(seconds)) return "5m";
  const ms = Math.min(
    NEO_PAGE_WINDOW_MAX_MS,
    Math.max(NEO_PAGE_WINDOW_MIN_MS, Math.round(seconds) * 1e3)
  );
  if (ms % UNIT_MS.d === 0) return `${ms / UNIT_MS.d}d`;
  if (ms % UNIT_MS.h === 0) return `${ms / UNIT_MS.h}h`;
  return `${Math.max(5, Math.round(ms / UNIT_MS.m))}m`;
}
function neoPageWindowFromRange(from, to) {
  return `${new Date(from).toISOString()}/${new Date(to).toISOString()}`;
}
function neoPageWindowBounds(window, nowMs) {
  const span = parseNeoPageWindow(window);
  if (span === null) return null;
  return span.kind === "rolling" ? [nowMs - span.ms, nowMs] : [span.fromMs, span.toMs];
}
function neoPageAsControl(spec) {
  if (neoPageIsV2(spec)) return null;
  const face = spec.faces[0];
  if (face === void 0 || !isNeoControlId(face.control)) return null;
  const bindings = {};
  for (const [role2, name] of Object.entries(face.readings)) {
    const reading = spec.readings[name];
    if (reading !== void 0) bindings[role2] = reading.path;
  }
  if (Object.keys(bindings).length === 0) return null;
  const paths = [];
  for (const { role: role2 } of NEO_CONTROL_ROLES[face.control]) {
    const path = bindings[role2];
    if (path !== void 0 && !paths.includes(path)) paths.push(path);
  }
  for (const path of Object.values(bindings)) {
    if (!paths.includes(path)) paths.push(path);
  }
  return parseNeoBoardControlSpec({
    v: 1,
    kind: "control",
    boatId: spec.boatId,
    control: face.control,
    bindings,
    paths: paths.slice(0, NEO_CONTROL_PATHS_MAX),
    options: face.look ?? {}
  });
}
function neoPageAsInstrument(spec) {
  if (neoPageIsV2(spec)) return null;
  const face = neoPageAsControl(spec);
  const fromFace = face === null ? null : neoControlAsInstrument(face);
  if (fromFace !== null) return fromFace;
  const first = spec.paths[0];
  if (first === void 0) return null;
  return {
    v: 1,
    kind: "instrument",
    boatId: spec.boatId,
    widget: "tile",
    paths: [first],
    subtitle: NEO_CONTROL_STAND_IN_SUBTITLE
  };
}

// src/neo-chat-page.ts
var NEO_CHAT_PAGE_BYTES_MAX = 16384;
var NEO_CHAT_PAGE_SERIES_POINTS_MAX = 96;
var NEO_CHAT_PAGE_TITLE_MAX = 48;
var NEO_CHAT_PAGE_RESULTS_MAX = 8;
var NEO_CHAT_PAGE_RESULT_ID = /^r[1-8]$/;
var NEO_CHAT_PAGE_RESULT_LABEL_MAX = 80;
var NEO_CHAT_PAGE_RESULT_ORIGINS = [
  "measured",
  "owner",
  "worked_out"
];
var NEO_CHAT_PAGE_CARD = { kind: "panel", icon: null };
var CONTROL4 = /[\p{Cc}\p{Cf}]/u;
var RESULT_BINDING = /data-tc-result\s*=\s*["']?(r[1-8])\b/gu;
var SPEC_KEYS = /* @__PURE__ */ new Set([
  "v",
  "kind",
  "boatId",
  "readings",
  "paths",
  "faces",
  "history",
  "statuses",
  "live",
  "feeds",
  "html",
  "css",
  "card"
]);
var TEXT_MAX = 160;
var PATH_MAX3 = 200;
var UNIT_MAX = 32;
function isObj9(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isFiniteNumber3(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function plainLine(value, max) {
  return typeof value === "string" && value.length >= 1 && value.length <= max && value.trim() === value && !CONTROL4.test(value);
}
function unitOrNull(value) {
  return value === null || typeof value === "string" && value.length <= UNIT_MAX && !CONTROL4.test(value);
}
function neoChatPageSeriesKey(reading, window, points) {
  return `${reading}|${window}|${points}`;
}
function neoChatPageResultIds(html) {
  const ids = [];
  for (const match of html.matchAll(RESULT_BINDING)) {
    const id = match[1];
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
}
function neoChatPageBindsResults(page) {
  return neoChatPageResultIds(page.html).length > 0;
}
function isNeoChatPageTitle(value) {
  return plainLine(value, NEO_CHAT_PAGE_TITLE_MAX);
}
function parseNeoChatPageSpec(value, boatId) {
  if (!isObj9(value)) return null;
  if (Object.keys(value).some((key) => !SPEC_KEYS.has(key))) return null;
  if (value.v !== 2 || value.kind !== "page" || value.boatId !== boatId) {
    return null;
  }
  const card = value.card;
  if (card !== void 0 && !(isObj9(card) && card.kind === "panel" && card.icon === null && Object.keys(card).length === 2)) {
    return null;
  }
  const parsed = parseNeoBoardPageSpec(value);
  if (parsed === null || parsed.v !== 2) return null;
  if (parsed.live.length > 0 || parsed.feeds.length > 0 || parsed.statuses.length > 0) {
    return null;
  }
  if (neoPageUtf8Bytes(parsed.html + parsed.css) > NEO_CHAT_PAGE_BYTES_MAX) {
    return null;
  }
  if (parsed.history.some((h) => h.points > NEO_CHAT_PAGE_SERIES_POINTS_MAX)) {
    return null;
  }
  if (neoChatPageResultIds(parsed.html).length > NEO_CHAT_PAGE_RESULTS_MAX) {
    return null;
  }
  return {
    v: 2,
    kind: "page",
    boatId: parsed.boatId,
    readings: parsed.readings,
    paths: parsed.paths,
    faces: parsed.faces,
    history: parsed.history,
    statuses: [],
    live: [],
    feeds: [],
    html: parsed.html,
    css: parsed.css,
    card: { kind: "panel", icon: null }
  };
}
function parseNeoChatPageSpecParts(spec, boatId) {
  if (!isNeoChatPageTitle(spec.title)) return null;
  const page = parseNeoChatPageSpec(spec.page, boatId);
  return page === null ? null : { title: spec.title, page };
}
function readingViewFrom(value) {
  if (!isObj9(value)) return null;
  const reading = parseNeoPageReading(value);
  if (reading === null) return null;
  const { storeUnit, label: label2, equipment, device, available } = value;
  if (!unitOrNull(storeUnit)) return null;
  if (!plainLine(label2, TEXT_MAX)) return null;
  if (equipment !== null && !plainLine(equipment, TEXT_MAX)) return null;
  if (device !== null && !plainLine(device, TEXT_MAX)) return null;
  if (typeof available !== "boolean") return null;
  return {
    path: reading.path,
    storeUnit,
    ...reading.range !== void 0 ? { range: reading.range } : {},
    ...reading.zones !== void 0 ? { zones: reading.zones } : {},
    ...reading.unit !== void 0 ? { unit: reading.unit } : {},
    label: label2,
    equipment,
    device,
    available
  };
}
function sampleFrom(value) {
  if (!isObj9(value)) return null;
  const { unit, atMs } = value;
  const sample = value.value;
  if (!(sample === null || isFiniteNumber3(sample) || typeof sample === "boolean" || typeof sample === "string" && sample.length <= TEXT_MAX && !CONTROL4.test(sample))) {
    return null;
  }
  if (!unitOrNull(unit)) return null;
  if (atMs !== null && !isFiniteNumber3(atMs)) return null;
  return { value: sample, unit, atMs };
}
function slotFrom(value) {
  if (value === null) return null;
  if (!Array.isArray(value) || value.length !== 4) return void 0;
  const [avg, min, max, count2] = value;
  if (!isFiniteNumber3(avg) || !isFiniteNumber3(count2)) return void 0;
  if (min !== null && !isFiniteNumber3(min)) return void 0;
  if (max !== null && !isFiniteNumber3(max)) return void 0;
  return [avg, min, max, count2];
}
function seriesFrom(value, points) {
  if (value === null) return null;
  if (!isObj9(value)) return void 0;
  const { fromMs, toMs, bucketMs, angle, slots } = value;
  if (!isFiniteNumber3(fromMs) || !isFiniteNumber3(toMs) || toMs < fromMs) {
    return void 0;
  }
  if (!isFiniteNumber3(bucketMs) || bucketMs <= 0) return void 0;
  if (typeof angle !== "boolean") return void 0;
  if (!Array.isArray(slots) || slots.length > points + 1) return void 0;
  const out = [];
  for (const entry of slots) {
    const slot = slotFrom(entry);
    if (slot === void 0) return void 0;
    out.push(slot);
  }
  return { fromMs, toMs, bucketMs, angle, slots: out };
}
function resultFrom(value) {
  if (!isObj9(value)) return null;
  const { label: label2, unit, origin } = value;
  if (!plainLine(label2, NEO_CHAT_PAGE_RESULT_LABEL_MAX)) return null;
  if (!isFiniteNumber3(value.value)) return null;
  if (!unitOrNull(unit)) return null;
  const known = NEO_CHAT_PAGE_RESULT_ORIGINS.find((o) => o === origin);
  if (known === void 0) return null;
  return { label: label2, value: value.value, unit, origin: known };
}
function parseNeoChatPageSnapshot(value, page) {
  if (!isObj9(value) || value.kind !== "chat_page") return null;
  const { readings, samples, series, results, timeZone } = value;
  if (!isObj9(readings) || !isObj9(samples) || !isObj9(series)) return null;
  if (!isObj9(results)) return null;
  if (timeZone !== null && !plainLine(timeZone, 64)) return null;
  const names = Object.keys(page.readings);
  if (Object.keys(readings).length !== names.length) return null;
  const readingsOut = {};
  for (const name of names) {
    const view = readingViewFrom(readings[name]);
    if (view === null || view.path !== page.readings[name].path) return null;
    readingsOut[name] = view;
  }
  const samplesOut = {};
  for (const [path, entry] of Object.entries(samples)) {
    if (path.length > PATH_MAX3 || !page.paths.includes(path)) return null;
    const sample = sampleFrom(entry);
    if (sample === null) return null;
    samplesOut[path] = sample;
  }
  const seriesOut = {};
  if (Object.keys(series).length !== page.history.length) return null;
  for (const h of page.history) {
    const key = neoChatPageSeriesKey(h.reading, h.window, h.points);
    if (!(key in series)) return null;
    const shape = seriesFrom(series[key], h.points);
    if (shape === void 0) return null;
    seriesOut[key] = shape;
  }
  const ids = neoChatPageResultIds(page.html);
  if (Object.keys(results).length !== ids.length) return null;
  const resultsOut = {};
  for (const id of ids) {
    const result = resultFrom(results[id]);
    if (result === null) return null;
    resultsOut[id] = result;
  }
  return {
    kind: "chat_page",
    readings: readingsOut,
    samples: samplesOut,
    series: seriesOut,
    results: resultsOut,
    timeZone
  };
}
function neoChatPageView(page, snapshot) {
  const readings = {};
  for (const name of Object.keys(page.readings)) {
    const view = snapshot.readings[name];
    if (view !== void 0 && isNeoPageName(name)) readings[name] = view;
  }
  return {
    v: 2,
    hash: "",
    sourceOmitted: false,
    night: "tint",
    card: NEO_CHAT_PAGE_CARD,
    readings,
    faces: page.faces,
    history: page.history,
    statuses: [],
    live: {},
    feeds: {},
    template: null,
    mine: []
  };
}

// src/neo-diagram.ts
var NEO_DIAGRAM_ROLES = [
  "source",
  "converter",
  "storage",
  "bus",
  "load",
  "device",
  "other"
];
var NEO_DIAGRAM_ARROWS = ["forward", "both", "none"];
var NEO_DIAGRAM_DIRECTIONS = ["down", "across"];
var NEO_DIAGRAM_TITLE_MAX = 48;
var NEO_DIAGRAM_NODES_MIN = 2;
var NEO_DIAGRAM_NODES_MAX = 12;
var NEO_DIAGRAM_EDGES_MIN = 1;
var NEO_DIAGRAM_EDGES_MAX = 16;
var NEO_DIAGRAM_NODE_LABEL_MAX = 32;
var NEO_DIAGRAM_EDGE_LABEL_MAX = 20;
var NEO_DIAGRAM_NODE_READINGS_MAX = 3;
var NEO_DIAGRAM_PATHS_MAX = 12;
var NEO_DIAGRAM_NODE_ID = /^[a-z][a-z0-9_]{0,23}$/;
var NEO_DIAGRAM_PATH_MAX = 160;
var NEO_DIAGRAM_READING_LABEL_MAX = 160;
var NEO_DIAGRAM_UNIT_MAX = 32;
var NEO_DIAGRAM_WIDTH_MAX = 400;
var NEO_DIAGRAM_HEIGHT_MAX = 1400;
var NEO_DIAGRAM_LABEL_LINES_MAX = 3;
var NEO_DIAGRAM_EDGE_POINTS_MIN = 2;
var NEO_DIAGRAM_EDGE_POINTS_MAX = 24;
var NEO_DIAGRAM_TEXT = {
  label: 13,
  reading: 12,
  edgeLabel: 11,
  lineHeight: 1.25,
  padX: 8,
  padY: 6,
  edgePadX: 4,
  edgePadY: 2
};
function neoDiagramPaths(parts) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  const add = (path) => {
    if (seen.has(path)) return;
    seen.add(path);
    out.push(path);
  };
  for (const node of parts.nodes) {
    for (const r of node.readings ?? []) add(r.path);
  }
  for (const edge of parts.edges) {
    if (edge.reading !== void 0) add(edge.reading.path);
  }
  return out;
}
function neoDiagramPairKey(from, to) {
  return from < to ? `${from}\0${to}` : `${to}\0${from}`;
}
var FRESHNESS = [
  "fresh",
  "stale",
  "old",
  "future"
];
function isObj10(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function finite6(value) {
  return typeof value === "number" && Number.isFinite(value);
}
var CONTROL5 = /[\p{Cc}\p{Cf}]/u;
function label(value, max) {
  return typeof value === "string" && value.length >= 1 && value.length <= max && value.trim().length > 0 && !CONTROL5.test(value) && value === value.normalize("NFKC");
}
function isPath3(value) {
  return typeof value === "string" && value.length >= 1 && value.length <= NEO_DIAGRAM_PATH_MAX && !CONTROL5.test(value) && !/\s/.test(value);
}
function parseRef(value) {
  return isObj10(value) && isPath3(value.path) ? { path: value.path } : null;
}
function parseNode(value) {
  if (!isObj10(value)) return null;
  const { id } = value;
  if (typeof id !== "string" || !NEO_DIAGRAM_NODE_ID.test(id)) return null;
  if (!label(value.label, NEO_DIAGRAM_NODE_LABEL_MAX)) return null;
  const role2 = NEO_DIAGRAM_ROLES.find((r) => r === value.role) ?? "other";
  if (typeof value.role !== "string") return null;
  if (value.readings === void 0) return { id, label: value.label, role: role2 };
  if (!Array.isArray(value.readings)) return null;
  if (value.readings.length > NEO_DIAGRAM_NODE_READINGS_MAX) return null;
  const readings = [];
  for (const raw of value.readings) {
    const ref = parseRef(raw);
    if (ref === null || readings.some((r) => r.path === ref.path)) return null;
    readings.push(ref);
  }
  return readings.length === 0 ? { id, label: value.label, role: role2 } : { id, label: value.label, role: role2, readings };
}
function parseEdge(value, ids) {
  if (!isObj10(value)) return null;
  const { from, to } = value;
  if (typeof from !== "string" || typeof to !== "string") return null;
  if (!ids.has(from) || !ids.has(to) || from === to) return null;
  let arrow = "forward";
  if (value.arrow !== void 0) {
    if (typeof value.arrow !== "string") return null;
    arrow = NEO_DIAGRAM_ARROWS.find((a) => a === value.arrow) ?? "none";
  }
  const edge = { from, to, arrow };
  if (value.label !== void 0) {
    if (!label(value.label, NEO_DIAGRAM_EDGE_LABEL_MAX)) return null;
    edge.label = value.label;
  }
  if (value.reading !== void 0) {
    const ref = parseRef(value.reading);
    if (ref === null) return null;
    edge.reading = ref;
  }
  return edge;
}
function parseNeoDiagramSpecParts(spec) {
  if (!label(spec.title, NEO_DIAGRAM_TITLE_MAX)) return null;
  if (!Array.isArray(spec.nodes) || !Array.isArray(spec.edges)) return null;
  if (spec.nodes.length < NEO_DIAGRAM_NODES_MIN || spec.nodes.length > NEO_DIAGRAM_NODES_MAX || spec.edges.length < NEO_DIAGRAM_EDGES_MIN || spec.edges.length > NEO_DIAGRAM_EDGES_MAX) {
    return null;
  }
  const nodes = [];
  const ids = /* @__PURE__ */ new Set();
  for (const raw of spec.nodes) {
    const node = parseNode(raw);
    if (node === null || ids.has(node.id)) return null;
    ids.add(node.id);
    nodes.push(node);
  }
  const edges = [];
  const pairs = /* @__PURE__ */ new Set();
  for (const raw of spec.edges) {
    const edge = parseEdge(raw, ids);
    if (edge === null) return null;
    const pair2 = neoDiagramPairKey(edge.from, edge.to);
    if (pairs.has(pair2)) return null;
    pairs.add(pair2);
    edges.push(edge);
  }
  const parts = { title: spec.title, nodes, edges };
  return neoDiagramPaths(parts).length > NEO_DIAGRAM_PATHS_MAX ? null : parts;
}
function within(value, max) {
  return finite6(value) && value >= 0 && value <= max;
}
function parseBox(value, width, height) {
  if (!isObj10(value)) return null;
  const { x, y, w, h } = value;
  if (!within(x, width) || !within(y, height)) return null;
  if (!finite6(w) || !finite6(h) || w <= 0 || h <= 0) return null;
  if (x + w > width || y + h > height) return null;
  return { x, y, w, h };
}
function parseLayoutNode(value, node, width, height) {
  if (!isObj10(value) || value.id !== node.id) return null;
  const box = parseBox(value, width, height);
  if (box === null || !Array.isArray(value.lines)) return null;
  const lines = value.lines;
  if (lines.length < 1 || lines.length > NEO_DIAGRAM_LABEL_LINES_MAX) {
    return null;
  }
  if (!lines.every((l) => label(l, NEO_DIAGRAM_NODE_LABEL_MAX))) return null;
  return { id: node.id, ...box, lines: [...lines] };
}
function parseLayoutEdge(value, edge, width, height) {
  if (!isObj10(value) || value.from !== edge.from || value.to !== edge.to) {
    return null;
  }
  if (!Array.isArray(value.points)) return null;
  if (value.points.length < NEO_DIAGRAM_EDGE_POINTS_MIN || value.points.length > NEO_DIAGRAM_EDGE_POINTS_MAX) {
    return null;
  }
  const points = [];
  for (const p of value.points) {
    if (!Array.isArray(p) || p.length !== 2) return null;
    const [x, y] = p;
    if (!within(x, width) || !within(y, height)) return null;
    points.push([x, y]);
  }
  const labelled = edge.label !== void 0 || edge.reading !== void 0;
  if (!labelled) return { from: edge.from, to: edge.to, points, labelAt: null };
  const labelAt = parseBox(value.labelAt, width, height);
  return labelAt === null ? null : { from: edge.from, to: edge.to, points, labelAt };
}
function parseLayout(value, parts) {
  if (!isObj10(value)) return null;
  const { width, height } = value;
  if (!finite6(width) || width <= 0 || width > NEO_DIAGRAM_WIDTH_MAX) {
    return null;
  }
  if (!finite6(height) || height <= 0 || height > NEO_DIAGRAM_HEIGHT_MAX) {
    return null;
  }
  const direction = NEO_DIAGRAM_DIRECTIONS.find((d) => d === value.direction) ?? "down";
  if (!Array.isArray(value.nodes) || !Array.isArray(value.edges)) return null;
  if (value.nodes.length !== parts.nodes.length || value.edges.length !== parts.edges.length) {
    return null;
  }
  const nodes = [];
  for (const [i, node] of parts.nodes.entries()) {
    const laid = parseLayoutNode(value.nodes[i], node, width, height);
    if (laid === null) return null;
    nodes.push(laid);
  }
  const edges = [];
  for (const [i, edge] of parts.edges.entries()) {
    const laid = parseLayoutEdge(value.edges[i], edge, width, height);
    if (laid === null) return null;
    edges.push(laid);
  }
  return { width, height, direction, nodes, edges };
}
function nullableText(value, max) {
  if (value === null) return null;
  return typeof value === "string" && value.length <= max && !CONTROL5.test(value) ? value : void 0;
}
function parseReading(value, path) {
  if (!isObj10(value) || value.path !== path) return null;
  if (!label(value.label, NEO_DIAGRAM_READING_LABEL_MAX)) return null;
  const short = nullableText(value.short, NEO_DIAGRAM_READING_LABEL_MAX);
  const unit = nullableText(value.unit, NEO_DIAGRAM_UNIT_MAX);
  if (short === void 0 || unit === void 0) return null;
  if (short !== null && !label(short, NEO_DIAGRAM_READING_LABEL_MAX)) {
    return null;
  }
  if (value.value === null) {
    return value.sampledAt === null && value.freshness === null ? {
      path,
      label: value.label,
      short,
      value: null,
      unit,
      sampledAt: null,
      freshness: null
    } : null;
  }
  const freshness = FRESHNESS.find((f) => f === value.freshness);
  if (!finite6(value.value) || typeof value.sampledAt !== "string" || !Number.isFinite(Date.parse(value.sampledAt)) || freshness === void 0) {
    return null;
  }
  return {
    path,
    label: value.label,
    short,
    value: value.value,
    unit,
    sampledAt: value.sampledAt,
    freshness
  };
}
function parseNeoDiagramSnapshot(value, parts) {
  if (!isObj10(value) || value.kind !== "diagram") return null;
  const layout = parseLayout(value.layout, parts);
  if (layout === null || !Array.isArray(value.readings)) return null;
  const paths = neoDiagramPaths(parts);
  if (value.readings.length !== paths.length) return null;
  const readings = [];
  for (const [i, path] of paths.entries()) {
    const reading = parseReading(value.readings[i], path);
    if (reading === null) return null;
    readings.push(reading);
  }
  return { kind: "diagram", layout, readings };
}

// src/neo-picture.ts
var NEO_PICTURE_ASPECTS = ["square", "wide", "tall"];
var NEO_PICTURE_MIMES = [
  "image/png",
  "image/jpeg",
  "image/webp"
];
var NEO_PICTURE_SUBJECT_MIN = 10;
var NEO_PICTURE_SUBJECT_MAX = 400;
var NEO_PICTURE_DIMENSION_MAX = 8e3;
var NEO_PICTURE_CAPTION = "Illustration made by AI \u2014 not your boat";
var UUID4 = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
var CONTROL6 = /[\p{Cc}\p{Cf}]/u;
function isObj11(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function pixels2(value) {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= NEO_PICTURE_DIMENSION_MAX;
}
function isNeoPictureSubject(value) {
  return typeof value === "string" && value.length >= NEO_PICTURE_SUBJECT_MIN && value.length <= NEO_PICTURE_SUBJECT_MAX && value.trim().length > 0 && !CONTROL6.test(value) && value === value.normalize("NFKC");
}
function neoPictureAspectOf(width, height) {
  if (width > height) return "wide";
  if (height > width) return "tall";
  return "square";
}
function parseNeoPictureSpecParts(spec) {
  const { attachmentId } = spec;
  return typeof attachmentId === "string" && UUID4.test(attachmentId) ? { attachmentId } : null;
}
function parseNeoPictureSnapshot(value, parts) {
  if (!isObj11(value) || value.kind !== "picture") return null;
  if (value.attachmentId !== parts.attachmentId) return null;
  const mime = NEO_PICTURE_MIMES.find((m) => m === value.mime);
  const { width, height, subject: subject2 } = value;
  if (mime === void 0 || !pixels2(width) || !pixels2(height)) return null;
  if (!isNeoPictureSubject(subject2)) return null;
  if (typeof value.aspect !== "string") return null;
  const aspect = NEO_PICTURE_ASPECTS.find((a) => a === value.aspect) ?? neoPictureAspectOf(width, height);
  return {
    kind: "picture",
    attachmentId: parts.attachmentId,
    mime,
    width,
    height,
    subject: subject2,
    aspect
  };
}

// src/procedure.ts
var PROCEDURE_SOURCES = ["owner", "manual", "tombolo"];
var PROCEDURE_PRECHECK_OPS = [
  "lt",
  "le",
  "gt",
  "ge",
  "eq",
  "ne",
  "between"
];
var PROCEDURE_CHECK_RESULTS = ["pass", "fail", "unknown"];
var PROCEDURE_CHECK_REASONS = [
  "no_reading",
  "stale",
  "unit_mismatch",
  "not_comparable"
];
var PROCEDURES_PER_BOAT_MAX = 100;
var PROCEDURE_TITLE_MAX = 120;
var PROCEDURE_SYSTEM_MAX = 60;
var PROCEDURE_STEPS_MAX = 30;
var PROCEDURE_STEP_TEXT_MAX = 500;
var PROCEDURE_STEP_TIP_MAX = 300;
var PROCEDURE_PRECHECKS_MAX = 8;
var PROCEDURE_PRECHECK_LABEL_MAX = 60;
var PROCEDURE_PRECHECK_TEXT_VALUE_MAX = 60;
var PROCEDURE_UNIT_MAX = 16;
var PROCEDURE_CITATIONS_MAX = 8;
var PROCEDURE_STALE_AFTER_MAX_S = 7 * 24 * 60 * 60;
var PROCEDURE_STALE_AFTER_DEFAULT_S = 15 * 60;
function isObj12(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function str(value) {
  return typeof value === "string" ? value : null;
}
function finite7(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function nullableStr2(value) {
  if (value === null) return null;
  return typeof value === "string" ? value : void 0;
}
function nullableValue(value) {
  if (value === null) return null;
  if (typeof value === "string" || typeof value === "boolean") return value;
  return finite7(value) ?? void 0;
}
function nullableRange(value) {
  if (value === null) return null;
  if (!Array.isArray(value) || value.length !== 2) return void 0;
  const min = finite7(value[0]);
  const max = finite7(value[1]);
  return min === null || max === null ? void 0 : [min, max];
}
function oneOf3(list2, value) {
  return list2.find((item) => item === value);
}
function parseProcedureStep(value) {
  if (!isObj12(value)) return null;
  const id = str(value.id);
  const text3 = str(value.text);
  const tip = nullableStr2(value.tip);
  if (id === null || text3 === null || tip === void 0) return null;
  return { id, text: text3, tip };
}
function parseProcedureCitation(value) {
  if (!isObj12(value)) return null;
  const manualId = str(value.manualId);
  const title = str(value.title);
  const section = nullableStr2(value.section);
  const page = value.page === null ? null : finite7(value.page);
  if (manualId === null || title === null || section === void 0 || page === null && value.page !== null) {
    return null;
  }
  return { manualId, title, section, page };
}
function parseProcedurePrecheck(value) {
  if (!isObj12(value)) return null;
  const id = str(value.id);
  const label2 = str(value.label);
  const path = str(value.path);
  const op = oneOf3(PROCEDURE_PRECHECK_OPS, value.op);
  const target = nullableValue(value.value);
  const range = nullableRange(value.range);
  const unit = nullableStr2(value.unit);
  const staleAfterS = finite7(value.staleAfterS);
  if (id === null || label2 === null || path === null || op === void 0 || target === void 0 || range === void 0 || unit === void 0 || staleAfterS === null) {
    return null;
  }
  const shaped = op === "between" ? target === null && range !== null && range[0] <= range[1] : range === null && target !== null && (op === "eq" || op === "ne" || typeof target === "number");
  if (!shaped) return null;
  return { id, label: label2, path, op, value: target, range, unit, staleAfterS };
}
function parseProcedureCheck(value) {
  if (!isObj12(value)) return null;
  const id = str(value.id);
  const label2 = str(value.label);
  const path = str(value.path);
  const op = oneOf3(PROCEDURE_PRECHECK_OPS, value.op);
  const expected = nullableValue(value.expected);
  const range = nullableRange(value.range);
  const unit = nullableStr2(value.unit);
  const staleAfterS = finite7(value.staleAfterS);
  const reading = nullableValue(value.value);
  const readingUnit = nullableStr2(value.readingUnit);
  const sampledAt = nullableStr2(value.sampledAt);
  const result = oneOf3(PROCEDURE_CHECK_RESULTS, value.result);
  const reason = value.reason === null ? null : oneOf3(PROCEDURE_CHECK_REASONS, value.reason);
  if (id === null || label2 === null || path === null || op === void 0 || expected === void 0 || range === void 0 || unit === void 0 || staleAfterS === null || reading === void 0 || readingUnit === void 0 || sampledAt === void 0 || result === void 0 || reason === void 0 || // A check is unknown exactly when it has a reason.
  result === "unknown" !== (reason !== null)) {
    return null;
  }
  return {
    id,
    label: label2,
    path,
    op,
    expected,
    range,
    unit,
    staleAfterS,
    value: reading,
    readingUnit,
    sampledAt,
    result,
    reason
  };
}
function parseProcedureList(value, parse, max) {
  if (!Array.isArray(value) || value.length > max) return null;
  const out = [];
  for (const raw of value) {
    const item = parse(raw);
    if (item === null) return null;
    out.push(item);
  }
  return out;
}
function parseProcedureSource(value) {
  return oneOf3(PROCEDURE_SOURCES, value);
}

// src/neo-card.ts
var NEO_CARD_VERSION = 1;
var NEO_CARDS_PER_ANSWER_MAX = 3;
var NEO_CARD_WATER_TANKS_MAX = 6;
var NEO_CARD_KINDS = [
  "water",
  "engine",
  "mode",
  "anchor",
  "friend",
  "how-to",
  "report",
  "map",
  "diagram",
  "picture",
  "camera_snapshot",
  "chat_page"
];
var NEO_CARD_DRAWN_KINDS = [
  "water",
  "mode",
  "anchor",
  "how-to",
  "friend",
  "map",
  "diagram",
  "picture",
  "camera_snapshot",
  "chat_page"
];
var NEO_CARD_CONFIRM_KINDS = [
  "mode",
  "anchor",
  "friend"
];
var NEO_CARD_CONFIRM_TTL_MS = 15 * 6e4;
var NEO_CARD_IDEMPOTENCY_KEY_MAX = 200;
var FRESHNESS2 = [
  "fresh",
  "stale",
  "old",
  "future"
];
var MODE_STATUSES = [
  "ready",
  "stale",
  "insufficient_data"
];
function neoCardTitle(card) {
  switch (card.spec.kind) {
    case "water":
      return "Fresh water";
    case "mode":
      return "Boat mode";
    case "anchor":
      return "Anchor watch";
    case "how-to":
      return "How to";
    case "friend":
      return "Friend boat";
    case "map":
      return "Map";
    case "diagram":
      return card.spec.title;
    case "picture":
      return "Picture";
    case "camera_snapshot":
      return "Camera snapshot";
    case "chat_page":
      return card.spec.title;
  }
}
function neoCardConfirmRequest(card) {
  if (card.spec.kind === "water" || card.spec.kind === "how-to" || card.spec.kind === "map" || card.spec.kind === "diagram" || card.spec.kind === "picture" || card.spec.kind === "camera_snapshot" || card.spec.kind === "chat_page" || card.spec.confirm === void 0) {
    return null;
  }
  const boat = `/v1/boats/${encodeURIComponent(card.spec.boatId)}`;
  const idempotencyKey = card.spec.confirm.idempotencyKey;
  if (card.spec.kind === "friend") {
    const change = card.spec.confirm.body;
    if (change.action === "stop") {
      return {
        method: "DELETE",
        path: `${boat}/watched-vessels/${card.spec.mmsi}`,
        idempotencyKey,
        body: null
      };
    }
    return {
      method: "POST",
      path: `${boat}/watched-vessels`,
      idempotencyKey,
      body: change.label === void 0 ? { mmsi: card.spec.mmsi } : { mmsi: card.spec.mmsi, label: change.label }
    };
  }
  const route = card.spec.kind === "mode" ? "mode" : "anchor-watch";
  return {
    method: "POST",
    path: `${boat}/${route}`,
    idempotencyKey,
    body: card.spec.confirm.body
  };
}
function neoCardConfirmOpen(card, now) {
  if (card.spec.kind === "water" || card.spec.kind === "how-to" || card.spec.kind === "map" || card.spec.kind === "diagram" || card.spec.kind === "picture" || card.spec.kind === "camera_snapshot" || card.spec.kind === "chat_page" || card.spec.confirm === void 0) {
    return false;
  }
  const at = typeof now === "number" ? now : now.getTime();
  return at < Date.parse(card.spec.confirm.expiresAt);
}
function isObj13(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function str2(value) {
  return typeof value === "string" ? value : null;
}
function num(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function nullableStr3(value) {
  if (value === null) return null;
  return typeof value === "string" ? value : void 0;
}
function parseReading2(value) {
  if (value === null) return null;
  if (!isObj13(value)) return void 0;
  const path = str2(value.path);
  const v = num(value.value);
  const unit = nullableStr3(value.unit);
  const sampledAt = str2(value.sampledAt);
  const freshness = FRESHNESS2.find((f) => f === value.freshness);
  if (path === null || v === null || unit === void 0 || sampledAt === null || freshness === void 0) {
    return void 0;
  }
  return { path, value: v, unit, sampledAt, freshness };
}
function parseTank(value) {
  if (!isObj13(value)) return void 0;
  const instance = str2(value.instance);
  const label2 = str2(value.label);
  const level = parseReading2(value.level);
  const volume = parseReading2(value.volume);
  const capacity = parseReading2(value.capacity);
  if (instance === null || label2 === null || level === void 0 || volume === void 0 || capacity === void 0) {
    return void 0;
  }
  return { instance, label: label2, level, volume, capacity };
}
function parseNeoWaterCardSnapshot(value) {
  return isObj13(value) && value.kind === "water" ? parseWaterSnapshot(value) : null;
}
function parseWaterSnapshot(value) {
  if (!Array.isArray(value.tanks)) return null;
  const tanks = [];
  for (const raw of value.tanks.slice(0, NEO_CARD_WATER_TANKS_MAX)) {
    const tank = parseTank(raw);
    if (tank === void 0) return null;
    tanks.push(tank);
  }
  const total = isObj13(value.total) ? value.total : null;
  const forecast = isObj13(value.forecast) ? value.forecast : null;
  if (total === null || forecast === null) return null;
  const level = total.level === null ? null : num(total.level);
  const unit = nullableStr3(total.unit);
  if (level === null && total.level !== null || unit === void 0) {
    return null;
  }
  const use = forecast.usePerDay;
  const usePerDay = isObj13(use) && num(use.value) !== null && str2(use.unit) !== null ? { value: num(use.value), unit: str2(use.unit) } : null;
  return {
    kind: "water",
    tanks,
    total: { level, unit },
    forecast: {
      emptyAt: str2(forecast.emptyAt),
      daysLeft: num(forecast.daysLeft),
      usePerDay
    }
  };
}
function parseModeSnapshot(value) {
  const status = MODE_STATUSES.find((s) => s === value.status);
  const archetype = value.archetype === null ? null : ["underway", "anchored", "docked"].find(
    (a) => a === value.archetype
  );
  const confidence = str2(value.confidence);
  if (status === void 0 || archetype === void 0 || confidence === null || typeof value.heldOver !== "boolean") {
    return null;
  }
  const fields = {
    mode: nullableStr3(value.mode),
    since: nullableStr3(value.since),
    detectedBy: nullableStr3(value.detectedBy),
    modeStatus: nullableStr3(value.modeStatus),
    measuredAt: nullableStr3(value.measuredAt),
    boatLastSeenAt: nullableStr3(value.boatLastSeenAt)
  };
  if (Object.values(fields).some((f) => f === void 0)) return null;
  return {
    kind: "mode",
    status,
    mode: fields.mode ?? null,
    archetype,
    since: fields.since ?? null,
    detectedBy: fields.detectedBy ?? null,
    modeStatus: fields.modeStatus ?? null,
    confidence,
    heldOver: value.heldOver,
    measuredAt: fields.measuredAt ?? null,
    boatLastSeenAt: fields.boatLastSeenAt ?? null
  };
}
function parseHowToSnapshot(value) {
  const procedureId = str2(value.procedureId);
  const title = str2(value.title);
  const system = nullableStr3(value.system);
  const source = parseProcedureSource(value.source);
  const version = num(value.version);
  const citations = parseProcedureList(
    value.citations,
    parseProcedureCitation,
    PROCEDURE_CITATIONS_MAX
  );
  const steps = parseProcedureList(
    value.steps,
    parseProcedureStep,
    PROCEDURE_STEPS_MAX
  );
  const checks = parseProcedureList(
    value.checks,
    parseProcedureCheck,
    PROCEDURE_PRECHECKS_MAX
  );
  if (procedureId === null || title === null || system === void 0 || source === void 0 || version === null || typeof value.edited !== "boolean" || citations === null || steps === null || checks === null) {
    return null;
  }
  return {
    kind: "how-to",
    procedureId,
    title,
    system,
    source,
    citations,
    edited: value.edited,
    version,
    steps,
    checks
  };
}
function parseAnchorSnapshot(value) {
  const status = parseAnchorWatchStatus(value);
  return status === null ? null : { kind: "anchor", ...status };
}
function parseFriendSnapshot(value, mmsi) {
  const label2 = nullableStr3(value.label);
  const name = nullableStr3(value.name);
  const since = nullableStr3(value.since);
  const rangeM = num(value.rangeM);
  const state = parseWatchedVesselState(value);
  if (value.mmsi !== mmsi || label2 === void 0 || name === void 0 || since === void 0 || since !== null && !Number.isFinite(Date.parse(since)) || rangeM === null || str2(value.attribution) === null || state === null) {
    return null;
  }
  return {
    kind: "friend",
    mmsi,
    label: label2,
    name,
    since,
    ...state,
    rangeM,
    // The credit is the partner's words, never the record's to change.
    attribution: FLEET_ATTRIBUTION
  };
}
var IDEMPOTENCY_KEY = /^[\x21-\x7e]+$/;
function parseConfirmBase(value) {
  const idempotencyKey = str2(value.idempotencyKey);
  const expiresAt = str2(value.expiresAt);
  if (idempotencyKey === null || idempotencyKey.length > NEO_CARD_IDEMPOTENCY_KEY_MAX || !IDEMPOTENCY_KEY.test(idempotencyKey) || expiresAt === null || !Number.isFinite(Date.parse(expiresAt))) {
    return null;
  }
  return { idempotencyKey, expiresAt };
}
function parseModeChange(value) {
  if (!isObj13(value)) return null;
  if (value.action === "auto") {
    return value.mode === void 0 ? { action: "auto" } : null;
  }
  if (value.action !== "pin") return null;
  const mode = CANONICAL_MODES.find((m) => m === value.mode);
  return mode === void 0 ? null : { action: "pin", mode };
}
function radiusM(value) {
  const r = num(value);
  return r !== null && r >= ANCHOR_WATCH_RADIUS_MIN_M && r <= ANCHOR_WATCH_RADIUS_MAX_M ? r : null;
}
function parseAnchorChange(value) {
  if (!isObj13(value)) return null;
  if (value.action === "clear") {
    return value.radiusM === void 0 ? { action: "clear" } : null;
  }
  if (value.action === "set") {
    if (value.radiusM === void 0) return { action: "set" };
    const r = radiusM(value.radiusM);
    return r === null ? null : { action: "set", radiusM: r };
  }
  if (value.action === "radius") {
    const r = radiusM(value.radiusM);
    return r === null ? null : { action: "radius", radiusM: r };
  }
  return null;
}
function parseFriendChange(value, mmsi) {
  if (!isObj13(value) || value.mmsi !== mmsi) return null;
  if (value.action === "stop") {
    return value.label === void 0 ? { action: "stop", mmsi } : null;
  }
  if (value.action !== "watch") return null;
  if (value.label === void 0) return { action: "watch", mmsi };
  if (typeof value.label !== "string" || normalizeWatchedVesselLabel(value.label) !== value.label) {
    return null;
  }
  return { action: "watch", mmsi, label: value.label };
}
function parseModeConfirm(value) {
  if (value === void 0) return void 0;
  if (!isObj13(value) || typeof value.stopsAnchorWatch !== "boolean") {
    return null;
  }
  const base = parseConfirmBase(value);
  const body = parseModeChange(value.body);
  if (base === null || body === null) return null;
  return { body, ...base, stopsAnchorWatch: value.stopsAnchorWatch };
}
function parseAnchorConfirm(value) {
  if (value === void 0) return void 0;
  if (!isObj13(value)) return null;
  const base = parseConfirmBase(value);
  const body = parseAnchorChange(value.body);
  if (base === null || body === null) return null;
  return { body, ...base };
}
function parseFriendConfirm(value, mmsi) {
  if (value === void 0) return void 0;
  if (!isObj13(value)) return null;
  const base = parseConfirmBase(value);
  const body = parseFriendChange(value.body, mmsi);
  if (base === null || body === null) return null;
  return { body, ...base };
}
function parseNeoCard(value) {
  if (!isObj13(value)) return null;
  const spec = value.spec;
  const raw = value.snapshot;
  if (!isObj13(spec) || !isObj13(raw)) return null;
  const id = str2(value.id);
  const resolvedAt = str2(value.resolvedAt);
  const boatId = str2(spec.boatId);
  const kind = NEO_CARD_DRAWN_KINDS.find((k) => k === spec.kind);
  if (id === null || !NEO_CARD_ID.test(id) || resolvedAt === null || boatId === null || spec.v !== NEO_CARD_VERSION || kind === void 0 || raw.kind !== kind) {
    return null;
  }
  if (kind === "how-to") {
    const procedureId = str2(spec.procedureId);
    const snapshot2 = parseHowToSnapshot(raw);
    return procedureId === null || snapshot2 === null || snapshot2.procedureId !== procedureId ? null : { id, spec: { v: 1, kind, boatId, procedureId }, snapshot: snapshot2, resolvedAt };
  }
  if (kind === "map") {
    const parts = parseNeoMapSpecParts(spec);
    const snapshot2 = parts === null ? null : parseNeoMapSnapshot(raw, parts.layers);
    if (parts === null || snapshot2 === null) return null;
    return {
      id,
      spec: { v: 1, kind, boatId, ...parts },
      snapshot: snapshot2,
      resolvedAt
    };
  }
  if (kind === "diagram") {
    const parts = parseNeoDiagramSpecParts(spec);
    const snapshot2 = parts === null ? null : parseNeoDiagramSnapshot(raw, parts);
    if (parts === null || snapshot2 === null) return null;
    return {
      id,
      spec: { v: 1, kind, boatId, ...parts },
      snapshot: snapshot2,
      resolvedAt
    };
  }
  if (kind === "picture") {
    const parts = parseNeoPictureSpecParts(spec);
    const snapshot2 = parts === null ? null : parseNeoPictureSnapshot(raw, parts);
    if (parts === null || snapshot2 === null) return null;
    return {
      id,
      spec: { v: 1, kind, boatId, attachmentId: parts.attachmentId },
      snapshot: snapshot2,
      resolvedAt
    };
  }
  if (kind === "camera_snapshot") {
    const parts = parseNeoCameraSnapshotSpecParts(spec);
    const snapshot2 = parseNeoCameraSnapshotSnapshot(raw);
    if (parts === null || snapshot2 === null) return null;
    return {
      id,
      spec: { v: 1, kind, boatId, ...parts },
      snapshot: snapshot2,
      resolvedAt
    };
  }
  if (kind === "chat_page") {
    const parts = parseNeoChatPageSpecParts(spec, boatId);
    const snapshot2 = parts === null ? null : parseNeoChatPageSnapshot(raw, parts.page);
    if (parts === null || snapshot2 === null) return null;
    return {
      id,
      spec: { v: 1, kind, boatId, ...parts },
      snapshot: snapshot2,
      resolvedAt
    };
  }
  if (kind === "water") {
    const snapshot2 = parseWaterSnapshot(raw);
    return snapshot2 === null ? null : { id, spec: { v: 1, kind, boatId }, snapshot: snapshot2, resolvedAt };
  }
  if (kind === "mode") {
    const snapshot2 = parseModeSnapshot(raw);
    const confirm2 = parseModeConfirm(spec.confirm);
    if (snapshot2 === null || confirm2 === null) return null;
    return {
      id,
      spec: {
        v: 1,
        kind,
        boatId,
        ...confirm2 === void 0 ? {} : { confirm: confirm2 }
      },
      snapshot: snapshot2,
      resolvedAt
    };
  }
  if (kind === "friend") {
    const mmsi = spec.mmsi;
    if (!isMmsi(mmsi)) return null;
    const snapshot2 = parseFriendSnapshot(raw, mmsi);
    const confirm2 = parseFriendConfirm(spec.confirm, mmsi);
    if (snapshot2 === null || confirm2 === null) return null;
    return {
      id,
      spec: {
        v: 1,
        kind,
        boatId,
        mmsi,
        ...confirm2 === void 0 ? {} : { confirm: confirm2 }
      },
      snapshot: snapshot2,
      resolvedAt
    };
  }
  const snapshot = parseAnchorSnapshot(raw);
  const confirm = parseAnchorConfirm(spec.confirm);
  if (snapshot === null || confirm === null) return null;
  return {
    id,
    spec: { v: 1, kind, boatId, ...confirm === void 0 ? {} : { confirm } },
    snapshot,
    resolvedAt
  };
}
function parseNeoCards(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  for (const raw of value) {
    const card = parseNeoCard(raw);
    if (card === null || seen.has(card.id)) continue;
    seen.add(card.id);
    out.push(card);
  }
  return out;
}
function neoCardMarker(id) {
  return CARD_MARKERS.render(id);
}
function normalizeNeoCardMarkers(content, available) {
  return normalizeMarkers(content, available, CARD_MARKERS);
}

// src/neo-board.ts
var NEO_BOARD_ITEMS_MAX = 40;
var NEO_SYSTEM_BOARD_ITEMS_MAX = 40;
var NEO_BOARDS_MAX = 12;
var NEO_BOARD_MAIN = "main";
var NEO_BOARD_MAIN_TITLE = "Board";
var NEO_BOARD_SLUG = /^[a-z0-9][a-z0-9-]{0,31}$/;
var NEO_BOARD_TITLE_MAX = 40;
var NEO_BOARD_ITEM_TITLE_MAX = 80;
var NEO_SYSTEM_PANE_BOARD_KEYS = [
  "wind",
  "depth",
  "engines",
  "energy",
  "tanks",
  "climate",
  "trends"
];
var NEO_SYSTEM_BOARD_KEYS = [
  ...OVERVIEW_MODE_KEYS,
  ...NEO_SYSTEM_PANE_BOARD_KEYS
];
var NEO_SYSTEM_BOARD_TITLES = {
  anchored: "At Anchor",
  underway: "Underway",
  // ADR 0306 §2, TOM-372.
  docked: "At Dock",
  wind: "Wind",
  depth: "Depth and Water",
  engines: "Engines",
  energy: "Energy",
  tanks: "Tanks",
  climate: "Climate",
  trends: "Trends"
};
function isNeoSystemBoardKey(value) {
  return typeof value === "string" && NEO_SYSTEM_BOARD_KEYS.includes(value);
}
var NEO_BOARD_ITEM_SIZES = ["s", "m", "l"];
function isNeoBoardItemSize(value) {
  return NEO_BOARD_ITEM_SIZES.includes(value);
}
var NEO_BOARD_ITEM_TITLE_ALIGNS = ["left", "center", "right"];
function isNeoBoardItemTitleAlign(value) {
  return NEO_BOARD_ITEM_TITLE_ALIGNS.includes(value);
}
var NEO_BOARD_GRID_COLUMNS = 12;
var NEO_BOARD_GRID_ROW_PX = 8;
var NEO_BOARD_GRID_GAP_PX = 12;
var NEO_BOARD_GRID_MIN_WIDTH_PX = 720;
var NEO_BOARD_GRID_MAX_H = 80;
var NEO_BOARD_GRID_MAX_Y = 2e3;
function isNeoBoardItemPlace(value) {
  if (typeof value !== "object" || value === null) return false;
  const { x, y, w, h } = value;
  const int = (n) => Number.isInteger(n);
  return int(x) && int(y) && int(w) && int(h) && x >= 0 && y >= 0 && y <= NEO_BOARD_GRID_MAX_Y && w >= 1 && x + w <= NEO_BOARD_GRID_COLUMNS && h >= 1 && h <= NEO_BOARD_GRID_MAX_H;
}
function neoBoardPlacesOverlap(a, b) {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}
function neoBoardSizeForWidth(w) {
  if (w >= 9) return "l";
  if (w >= 5) return "m";
  return "s";
}
function neoBoardWidthForSize(size) {
  return size === "l" ? NEO_BOARD_GRID_COLUMNS : size === "m" ? 6 : 3;
}
function neoBoardItemsInPlaceOrder(items) {
  return items.map((item, index) => ({ item, index })).sort((a, b) => {
    const pa = a.item.place;
    const pb = b.item.place;
    if (pa === void 0 || pb === void 0) {
      if (pa === void 0 && pb === void 0) return a.index - b.index;
      return pa === void 0 ? 1 : -1;
    }
    return pa.y - pb.y || pa.x - pb.x || a.index - b.index;
  }).map((e) => e.item);
}
var NEO_BOARD_EXTRA_KINDS = [
  "instrument",
  "module",
  "control",
  "page",
  "page2",
  "page3",
  "page-script"
];
function neoBoardItemIsCard(spec) {
  const kind = spec.kind;
  return kind !== "chat_page" && NEO_CARD_KINDS.includes(kind);
}
function neoBoardItemIsInstrument(spec) {
  return spec.kind === "instrument";
}
function neoBoardItemIsModule(spec) {
  return spec.kind === "module";
}
function neoBoardItemIsControl(spec) {
  return spec.kind === "control";
}
function neoBoardItemIsPage(spec) {
  return spec.kind === "page";
}
function neoBoardItemIsChart(spec) {
  return NEO_CHART_KINDS.includes(spec.kind);
}
function parseNeoBoardModuleSpec(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const spec = value;
  if (spec.v !== 1 || spec.kind !== "module") return null;
  if (typeof spec.boatId !== "string" || !isOverviewModuleKind(spec.module)) {
    return null;
  }
  const mode = OVERVIEW_MODE_KEYS.find((m) => m === spec.mode);
  const config = spec.config;
  if (mode === void 0 || typeof config !== "object" || config === null || Array.isArray(config)) {
    return null;
  }
  return {
    v: 1,
    kind: "module",
    boatId: spec.boatId,
    module: spec.module,
    mode,
    config: { ...config }
  };
}
function neoBoardItemSourceSpec(item) {
  const { spec } = item;
  if (!neoBoardItemIsPage(spec) || !neoPageIsV2(spec)) return spec;
  const unedited = neoPageUneditedSource(spec);
  if (unedited !== null) return unedited.spec;
  const module = neoPageModuleFeed(spec);
  if (module !== null) return module.spec;
  const area = spec.live[0];
  if (area !== void 0 && spec.template !== void 0 && neoPageTemplateIsHosted(spec.template.id)) {
    return area.spec;
  }
  return spec;
}
function neoBoardItemModule(item) {
  const { spec } = item;
  if (neoBoardItemIsModule(spec)) return spec;
  if (!neoBoardItemIsPage(spec) || !neoPageIsV2(spec)) return null;
  const feed = neoPageModuleFeed(spec);
  if (feed !== null) return feed.spec;
  for (const area of spec.live) {
    if (area.spec.kind === "module") return area.spec;
  }
  return null;
}
function neoBoardItemDefaultSize(spec) {
  if (spec.kind === "anchor" || spec.kind === "map" || spec.kind === "diagram") {
    return "l";
  }
  if (neoBoardItemIsControl(spec)) {
    return spec.control === "history" ? "l" : spec.control === "number" ? "s" : "m";
  }
  if (neoBoardItemIsPage(spec)) {
    if (neoPageIsV2(spec) && spec.template !== void 0) {
      return neoBoardItemDefaultSize(
        neoPageUneditedSource(spec)?.spec ?? spec.template.source.spec
      );
    }
    return "m";
  }
  if (neoBoardItemIsModule(spec)) {
    return spec.module === "anchor-watch" || spec.module === "route-map" ? "l" : "m";
  }
  return "m";
}
var NEO_BOARD_VERSION_ACTIONS = [
  "created",
  "pinned",
  "unpinned",
  "changed",
  "moved",
  "resized",
  "renamed",
  "reordered",
  "hidden",
  "shown",
  "regenerated",
  "undone",
  "restored",
  "removed",
  "unremoved",
  "baseline",
  // The conversion to version 2 pages (cloud ADR 0393, TOM-665).
  "converted",
  // Unedited template pages made again at their template's current version
  // (the refresh_template_pages job, TOM-675).
  "refreshed"
];
var NEO_BOARD_CHANGE_ACTIONS = [
  "pinned",
  "already_pinned",
  "unpinned",
  "created",
  "renamed",
  "changed",
  "moved",
  "undone",
  "resized"
];
function neoBoardItemKey(spec, rolling) {
  if (neoBoardItemIsInstrument(spec)) {
    return JSON.stringify(["instrument", spec.widget, spec.paths]);
  }
  if (neoBoardItemIsModule(spec)) {
    return JSON.stringify(["module", spec.module]);
  }
  if (neoBoardItemIsControl(spec)) {
    const bindings = Object.keys(spec.bindings).sort().map((role2) => [role2, spec.bindings[role2]]);
    return JSON.stringify([
      "control",
      spec.control,
      bindings,
      sortKeysDeep(spec.options)
    ]);
  }
  if (neoBoardItemIsPage(spec)) {
    const source = neoPageUneditedSource(spec);
    if (source !== null) return neoBoardItemKey(source.spec, source.rolling);
    const base = [
      "page",
      sortKeysDeep(spec.readings),
      spec.html,
      spec.css,
      spec.night ?? "tint"
    ];
    return JSON.stringify(
      neoPageIsV2(spec) && (spec.live.length > 0 || spec.feeds.length > 0) ? [...base, sortKeysDeep(spec.live), sortKeysDeep(spec.feeds)] : base
    );
  }
  if (neoBoardItemIsCard(spec)) {
    if (spec.kind === "friend") {
      return JSON.stringify(["card", spec.kind, spec.mmsi]);
    }
    if (spec.kind === "diagram") {
      const { title, nodes, edges } = spec;
      return JSON.stringify([
        "card",
        spec.kind,
        sortKeysDeep({ title, nodes, edges })
      ]);
    }
    if (spec.kind === "map") {
      const { window: track } = spec;
      const span2 = rolling !== null ? { rolling } : track === null ? null : { from: track.from, to: track.to };
      return JSON.stringify(["card", spec.kind, spec.layers, span2]);
    }
    return spec.kind === "how-to" ? JSON.stringify(["card", spec.kind, spec.procedureId]) : JSON.stringify(["card", spec.kind]);
  }
  if (!neoBoardItemIsChart(spec)) {
    return JSON.stringify(["other", sortKeysDeep(spec)]);
  }
  const { from, to } = spec.window;
  const span = rolling !== null ? { rolling } : { from, to };
  switch (spec.kind) {
    case "trend":
      return JSON.stringify(["trend", spec.paths, spec.chip, span]);
    case "readouts":
      return JSON.stringify(["readouts", spec.paths]);
    case "bars":
      return JSON.stringify(["bars", spec.metric, spec.engine, spec.by, span]);
    default:
      return JSON.stringify(["other", sortKeysDeep(spec)]);
  }
}
function neoBoardRolling(spec) {
  if (spec.kind === "readouts") return null;
  const { window: span } = spec;
  if (span === null) return null;
  const { from, to, live } = span;
  if (!live) return null;
  const ms = Date.parse(to) - Date.parse(from);
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.max(60, Math.round(ms / 6e4) * 60);
}

// src/neo-board-reflow.ts
var NEO_BOARD_REFLOW_TRACK_MIN_PX = 280;
var NEO_BOARD_REFLOW_MAX_ACROSS = 3;
var NEO_BOARD_REFLOW_WIDE_COLUMNS = 6;
var NEO_BOARD_PHONE_ROWS = {
  power: 16,
  // 308 px
  dial: 18,
  // 348 px: a dial needs a square
  tile: 9,
  // 168 px: one number
  chart: 13,
  // 248 px
  tanks: 14,
  // 268 px
  map: 19,
  // 368 px: a map, anchor watch, route map or helm
  other: 13
  // 248 px
};
var DIAL_CONTROLS = /* @__PURE__ */ new Set([
  "compass",
  "wind-rose",
  "wind-angle",
  "rudder",
  "arc-gauge",
  "needle-gauge"
]);
var TILE_CONTROLS = /* @__PURE__ */ new Set(["number", "trend-number"]);
var CHART_CONTROLS = /* @__PURE__ */ new Set(["history", "sparkline"]);
var MAP_MODULES = /* @__PURE__ */ new Set([
  "anchor-watch",
  "route-map",
  "helm"
]);
var CHART_MODULES = /* @__PURE__ */ new Set([
  "forecast",
  "tide",
  "pressure",
  "environment-outlook"
]);
var TANK_MODULES = /* @__PURE__ */ new Set(["tanks-budget", "reserves"]);
function neoBoardPhoneKind(item) {
  const module = neoBoardItemModule(item)?.module;
  if (module !== void 0) {
    if (module === "power-budget") return "power";
    if (TANK_MODULES.has(module)) return "tanks";
    if (MAP_MODULES.has(module)) return "map";
    if (CHART_MODULES.has(module)) return "chart";
    return "other";
  }
  const spec = neoBoardItemSourceSpec(item);
  if (neoBoardItemIsControl(spec)) {
    if (TILE_CONTROLS.has(spec.control)) return "tile";
    if (DIAL_CONTROLS.has(spec.control)) return "dial";
    if (CHART_CONTROLS.has(spec.control)) return "chart";
    return "other";
  }
  if (neoBoardItemIsChart(spec)) return "chart";
  if (neoBoardItemIsCard(spec) && (spec.kind === "anchor" || spec.kind === "map")) {
    return "map";
  }
  if (neoBoardItemIsPage(item.spec) && spec === item.spec) return "page";
  return "other";
}
function neoBoardRowsForPx(px) {
  const step = NEO_BOARD_GRID_ROW_PX + NEO_BOARD_GRID_GAP_PX;
  return Math.min(
    NEO_BOARD_GRID_MAX_H,
    Math.max(1, Math.ceil((px + NEO_BOARD_GRID_GAP_PX) / step))
  );
}
function neoBoardPhoneRows(kind, boardWidthPx) {
  if (kind !== "page") return NEO_BOARD_PHONE_ROWS[kind];
  return Math.min(
    NEO_BOARD_PHONE_ROWS.map,
    neoBoardRowsForPx(Math.max(0, boardWidthPx) * 3 / 4)
  );
}
var NEO_BOARD_MIN_COLUMNS = {
  power: 2,
  dial: 2,
  tile: 1,
  chart: 2,
  tanks: 2,
  map: 2,
  page: 2,
  other: 2
};
var NEO_BOARD_MIN_WIDTH_PX = {
  power: 140,
  dial: 140,
  tile: 60,
  chart: 140,
  tanks: 140,
  map: 140,
  page: 140,
  other: 140
};
function neoBoardMinColumns(kind) {
  return NEO_BOARD_MIN_COLUMNS[kind];
}
function neoBoardMinWidthPx(kind) {
  return NEO_BOARD_MIN_WIDTH_PX[kind];
}
function neoBoardColumnsPx(w, boardWidthPx) {
  const col = (boardWidthPx - NEO_BOARD_GRID_GAP_PX * (NEO_BOARD_GRID_COLUMNS - 1)) / NEO_BOARD_GRID_COLUMNS;
  return w * col + (w - 1) * NEO_BOARD_GRID_GAP_PX;
}
function neoBoardReflowAcross(boardWidthPx) {
  const n = Math.floor(
    (boardWidthPx + NEO_BOARD_GRID_GAP_PX) / (NEO_BOARD_REFLOW_TRACK_MIN_PX + NEO_BOARD_GRID_GAP_PX)
  );
  return Math.max(1, Math.min(NEO_BOARD_REFLOW_MAX_ACROSS, n));
}
function neoBoardReflow(items, boardWidthPx) {
  const fits = neoBoardReflowAcross(boardWidthPx) > 1 && items.every(
    (i) => neoBoardColumnsPx(i.place.w, boardWidthPx) >= neoBoardMinWidthPx(i.kind)
  );
  if (fits) {
    return {
      across: "arranged",
      items: items.map((i) => ({ id: i.id, place: { ...i.place } }))
    };
  }
  const across = neoBoardReflowAcross(boardWidthPx);
  const track = NEO_BOARD_GRID_COLUMNS / across;
  const order = items.map((item, index) => ({ item, index })).sort(
    (a, b) => a.item.place.y - b.item.place.y || a.item.place.x - b.item.place.x || a.index - b.index
  );
  const sky = new Array(across).fill(0);
  const boxes = /* @__PURE__ */ new Map();
  for (const { item } of order) {
    const h = across === 1 ? neoBoardPhoneRows(item.kind, boardWidthPx) : item.place.h;
    const span = item.place.w >= NEO_BOARD_REFLOW_WIDE_COLUMNS ? Math.min(2, across) : 1;
    let start = 0;
    let top = Number.POSITIVE_INFINITY;
    for (let s = 0; s + span <= across; s += 1) {
      const y = Math.max(...sky.slice(s, s + span));
      if (y < top) {
        top = y;
        start = s;
      }
    }
    boxes.set(item.id, { start, span, top, h });
    for (let s = start; s < start + span; s += 1) sky[s] = top + h;
  }
  const free = (id, track2, top, h) => [...boxes].every(
    ([other, b]) => other === id || track2 < b.start || track2 >= b.start + b.span || b.top + b.h <= top || top + h <= b.top
  );
  for (const { item } of order) {
    const b = boxes.get(item.id);
    while (b.start + b.span < across && free(item.id, b.start + b.span, b.top, b.h)) {
      b.span += 1;
    }
    while (b.start > 0 && free(item.id, b.start - 1, b.top, b.h)) {
      b.start -= 1;
      b.span += 1;
    }
  }
  const out = new Map(
    [...boxes].map(([id, b]) => [
      id,
      { x: b.start * track, y: b.top, w: b.span * track, h: b.h }
    ])
  );
  return {
    across,
    items: items.map((i) => ({ id: i.id, place: out.get(i.id) }))
  };
}

// src/neo-looks.ts
var NEO_BOARD_LOOK_SCOPES = ["item", "board", "boat"];
var NEO_BOARD_LOOKS_MAX = 60;
var NEO_NAMED_CONTROLS_MAX = 40;
var NEO_NAMED_CONTROL_NAME_MAX = 40;
var NEO_BOARD_LOOK_EXCEPT_MAX = 40;
var NEO_BOARD_LOOKS_BYTES_MAX = 1024 * 1024;
var NEO_BOARD_LOOK_CSS_BYTES_MAX = 4096;
var NEO_BOARD_LOOK_ACTIONS = [
  "look_added",
  "look_changed",
  "look_removed",
  "named_added",
  "named_changed",
  "named_removed",
  "changed",
  "undone"
];
var NEO_CONTROL_FAMILY = {
  number: "readout",
  "trend-number": "readout",
  "arc-gauge": "gauge",
  "needle-gauge": "gauge",
  "vertical-meter": "gauge",
  "horizontal-meter": "gauge",
  tank: "gauge",
  compass: "angular",
  "wind-rose": "angular",
  "wind-angle": "angular",
  rudder: "angular",
  sparkline: "graph",
  history: "graph",
  divider: "media"
};
var NEO_CONTROL_FAMILIES = [
  ...new Set(Object.values(NEO_CONTROL_FAMILY))
].sort();
function neoBoardLookRank(match) {
  if (match.item !== void 0) return 4;
  if (match.reading !== void 0) return 3;
  if (match.control !== void 0) return 2;
  return 1;
}
function neoBoardLookReadingHits(reading, path) {
  if (reading.endsWith(".*")) {
    return path.startsWith(reading.slice(0, -1));
  }
  return reading === path;
}
var NEO_BOARD_LOOK_SCOPE_ORDER = {
  boat: 0,
  board: 1,
  item: 2
};

// src/neo-notebook.ts
var NEO_NOTEBOOK_CHANGE_ACTIONS = [
  "added",
  "replaced",
  "corrected",
  "confirmed",
  "reaffirmed",
  "contradicted",
  "disputed",
  "forgotten",
  "set_aside"
];

// src/quick-readings.ts
var QUICK_READING_NEVER_DASH_MS = 100 * 365.25 * 24 * 36e5;
var QUICK_READING_FRESHNESS = {
  header: { dimAfterMs: 1e4, dashAfterMs: 12e4 },
  slow: { dimAfterMs: 5 * 6e4, dashAfterMs: 30 * 6e4 },
  battery: { dimAfterMs: 9e4, dashAfterMs: 10 * 6e4 },
  counter: { dimAfterMs: 5 * 6e4, dashAfterMs: QUICK_READING_NEVER_DASH_MS }
};
var QUICK_READING_FRESHNESS_PATTERNS = [{ path: "propulsion.*.runTime", freshness: "counter" }];
var QUICK_READING_FRESHNESS_PREFIXES = [{ prefix: "electrical.batteries.", freshness: "battery" }];
var WIND_SPEED_TRUE = "environment.wind.speedTrue";
var WIND_SPEED_TRUE_GROUND = "environment.wind.speedTrueGround";
var WIND_SPEED_OVER_GROUND = "environment.wind.speedOverGround";
var WIND_SPEED_APPARENT = "environment.wind.speedApparent";
var WIND_ANGLE_APPARENT = "environment.wind.angleApparent";
var WIND_ANGLE_TRUE_WATER = "environment.wind.angleTrueWater";
var WIND_DIRECTION_TRUE = "environment.wind.directionTrue";
var SPEED_OVER_GROUND = "navigation.speedOverGround";
var SHORT_LABELS = {
  [WIND_SPEED_TRUE_GROUND]: "True wind",
  [WIND_SPEED_OVER_GROUND]: "True wind",
  "environment.depth.belowKeel": "Below keel",
  "environment.depth.belowTransducer": "Below transducer",
  "environment.depth.belowSurface": "Below surface",
  [SPEED_OVER_GROUND]: "SOG",
  "navigation.speedThroughWater": "STW"
};
var header = (path, label2, unit, category, companion = null) => ({
  path,
  label: label2,
  shortLabel: SHORT_LABELS[path] ?? label2,
  unit,
  category,
  freshness: "header",
  companion
});
var slow = (path, unit, category) => ({
  path,
  label: null,
  shortLabel: null,
  unit,
  category,
  freshness: "slow",
  companion: null
});
var QUICK_READING_QUANTITIES = [
  // Wind.
  header(WIND_SPEED_TRUE, "True wind", "kn", "speed", WIND_ANGLE_TRUE_WATER),
  header(
    WIND_SPEED_TRUE_GROUND,
    "True wind over ground",
    "kn",
    "speed",
    WIND_DIRECTION_TRUE
  ),
  header(
    WIND_SPEED_OVER_GROUND,
    "True wind over ground",
    "kn",
    "speed",
    WIND_DIRECTION_TRUE
  ),
  header(
    WIND_SPEED_APPARENT,
    "Apparent wind",
    "kn",
    "speed",
    WIND_ANGLE_APPARENT
  ),
  header(WIND_ANGLE_APPARENT, "Apparent wind angle", "deg", null),
  header(WIND_ANGLE_TRUE_WATER, "True wind angle", "deg", null),
  header(
    "environment.wind.angleTrueGround",
    "True wind angle over ground",
    "deg",
    null
  ),
  header(WIND_DIRECTION_TRUE, "True wind direction", "deg", null),
  header(
    "environment.wind.directionMagnetic",
    "Magnetic wind direction",
    "deg",
    null
  ),
  // Depth, at each reference.
  header("environment.depth.belowKeel", "Depth below keel", "m", "depth"),
  header(
    "environment.depth.belowTransducer",
    "Depth below transducer",
    "m",
    "depth"
  ),
  header("environment.depth.belowSurface", "Depth below surface", "m", "depth"),
  // Motion and heading.
  header(SPEED_OVER_GROUND, "Speed over ground", "kn", "speed"),
  header("navigation.speedThroughWater", "Speed through water", "kn", "speed"),
  header(
    "navigation.courseOverGroundTrue",
    "True course over ground",
    "deg",
    null
  ),
  header(
    "navigation.courseOverGroundMagnetic",
    "Magnetic course over ground",
    "deg",
    null
  ),
  header("navigation.headingTrue", "True heading", "deg", null),
  header("navigation.headingMagnetic", "Magnetic heading", "deg", null),
  header("navigation.rateOfTurn", "Rate of turn", "deg/min", null),
  header("steering.rudderAngle", "Rudder angle", "deg", null),
  // Water and air.
  header(
    "environment.water.temperature",
    "Water temperature",
    "C",
    "temperature"
  ),
  header(
    "environment.outside.temperature",
    "Air temperature",
    "C",
    "temperature"
  ),
  header("environment.outside.pressure", "Barometer", "hPa", "pressure"),
  // Tanks: slow, labelled from the boat's path catalog ("Fuel tank 1 level").
  slow("tanks.*.*.currentLevel", "%", null),
  slow("tanks.*.*.currentVolume", "L", "volume"),
  slow("tanks.*.*.capacity", "L", "volume")
];
var INSTANCE = /^[A-Za-z0-9_-]{1,24}$/;
var EXACT = new Map(
  QUICK_READING_QUANTITIES.filter((q) => !q.path.includes("*")).map((q) => [
    q.path,
    q
  ])
);
var PATTERNS = QUICK_READING_QUANTITIES.filter(
  (q) => q.path.includes("*")
).map((q) => ({ quantity: q, segments: q.path.split(".") }));
function quickReadingQuantity(path) {
  const exact = EXACT.get(path);
  if (exact !== void 0) return exact;
  const segments = path.split(".");
  return PATTERNS.find(
    (p) => p.segments.length === segments.length && p.segments.every(
      (s, i) => s === "*" ? INSTANCE.test(segments[i]) : s === segments[i]
    )
  )?.quantity;
}
function quickReadingLabel(path) {
  return quickReadingQuantity(path)?.label ?? signalLabel(path);
}
function quickReadingShortLabel(path, label2) {
  return quickReadingQuantity(path)?.shortLabel ?? label2;
}
function fitsPattern(pattern, path) {
  const want = pattern.split(".");
  const got = path.split(".");
  return want.length === got.length && want.every((s, i) => s === "*" ? INSTANCE.test(got[i]) : s === got[i]);
}
function quickReadingFreshnessKind(path) {
  return quickReadingQuantity(path)?.freshness ?? QUICK_READING_FRESHNESS_PATTERNS.find((p) => fitsPattern(p.path, path))?.freshness ?? QUICK_READING_FRESHNESS_PREFIXES.find((p) => path.startsWith(p.prefix))?.freshness ?? "header";
}
function quickReadingFreshness(path) {
  return QUICK_READING_FRESHNESS[quickReadingFreshnessKind(path)];
}
function quickReadingCompanion(path) {
  return quickReadingQuantity(path)?.companion ?? null;
}
function quickReadingCategory(path, storeUnit) {
  const quantity = quickReadingQuantity(path);
  if (quantity !== void 0) return quantity.category;
  return categoryFor(path, storeUnit) ?? null;
}
var QUICK_READING_DEPTH_PATHS = [
  "environment.depth.belowKeel",
  "environment.depth.belowTransducer",
  "environment.depth.belowSurface"
];
var EXAMPLE_PATHS2 = [
  ["tanks.fuel.0.currentLevel", "%"],
  ["tanks.freshWater.port.currentVolume", "L"],
  ["tanks.blackWater.1.capacity", "L"],
  ["electrical.batteries.house.voltage", "V"],
  ["electrical.batteries.1.capacity.stateOfCharge", "%"],
  ["propulsion.port.revolutions", "rpm"],
  ["propulsion.port.runTime", "s"],
  ["propulsion.0.coolantTemperature", "C"],
  ["environment.inside.temperature", "C"],
  ["environment.outside.humidity", "%"]
];
function quickReadingsFixture() {
  const named = QUICK_READING_QUANTITIES.filter(
    (q) => !q.path.includes("*")
  ).map((q) => [q.path, q.unit]);
  const examples = [...named, ...EXAMPLE_PATHS2].map(
    ([path, unit]) => ({
      path,
      unit,
      label: quickReadingLabel(path),
      shortLabel: quickReadingShortLabel(path, quickReadingLabel(path)),
      category: quickReadingCategory(path, unit),
      freshness: quickReadingFreshnessKind(path),
      companion: quickReadingCompanion(path)
    })
  );
  return {
    version: 1,
    freshness: {
      header: { ...QUICK_READING_FRESHNESS.header },
      slow: { ...QUICK_READING_FRESHNESS.slow },
      battery: { ...QUICK_READING_FRESHNESS.battery },
      counter: { ...QUICK_READING_FRESHNESS.counter }
    },
    freshnessPrefixes: QUICK_READING_FRESHNESS_PREFIXES.map((p) => ({ ...p })),
    freshnessPatterns: QUICK_READING_FRESHNESS_PATTERNS.map((p) => ({ ...p })),
    depthReferences: [...QUICK_READING_DEPTH_PATHS],
    quantities: QUICK_READING_QUANTITIES.map((q) => ({ ...q })),
    examples
  };
}

// src/neo-page-freshness.ts
var NEO_FRESHNESS_STATES = [
  "live",
  "dim",
  "dash",
  "none"
];
var LEVEL = ["live", "dim", "dash"];
function neoCardFreshness(readings, nowMs) {
  let level = -1;
  let ageMs = null;
  let sinceMs = null;
  for (const { path, atMs } of readings) {
    if (atMs === null) continue;
    const age = Math.max(0, nowMs - atMs);
    const f = quickReadingFreshness(path);
    level = Math.max(
      level,
      age > f.dashAfterMs ? 2 : age > f.dimAfterMs ? 1 : 0
    );
    if (ageMs === null || age > ageMs) {
      ageMs = age;
      sinceMs = atMs;
    }
  }
  return { state: level < 0 ? "none" : LEVEL[level], ageMs, sinceMs };
}
function neoAgoLabel(ageMs) {
  const s = Math.round(ageMs / 1e3);
  if (s < 90) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
function dayOf(ms, timeZone) {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    ...timeZone !== void 0 ? { timeZone } : {}
  }).format(ms);
}
function neoNoReadingLine(sinceMs, nowMs, locale, timeZone) {
  const sameDay = dayOf(sinceMs, timeZone) === dayOf(nowMs, timeZone);
  const when = new Intl.DateTimeFormat(locale, {
    ...sameDay ? {} : { day: "numeric", month: "short" },
    hour: "numeric",
    minute: "2-digit",
    ...timeZone !== void 0 ? { timeZone } : {}
  }).format(sinceMs);
  return `No reading since ${when}`;
}
function neoCardFreshnessText(fresh, nowMs, options = {}) {
  if ((fresh.state === "dim" || options.record === true && fresh.state === "dash") && fresh.ageMs !== null)
    return `as of ${neoAgoLabel(fresh.ageMs)}`;
  if (fresh.state === "dash" && fresh.sinceMs !== null)
    return neoNoReadingLine(
      fresh.sinceMs,
      nowMs,
      options.locale,
      options.timeZone
    );
  if (fresh.state === "none") return "No reading yet";
  return "";
}
function neoPageFreshness(readings, nowMs, options = {}) {
  const seen = /* @__PURE__ */ new Set();
  const unique = readings.filter(
    (r) => seen.has(r.path) ? false : (seen.add(r.path), true)
  );
  if (unique.length === 0) return null;
  const fresh = neoCardFreshness(unique, nowMs);
  return {
    state: fresh.state,
    text: neoCardFreshnessText(fresh, nowMs, options)
  };
}
function neoPageHasFreshnessLine(html) {
  return /<[^>]*\sdata-tc-freshness(?:\s*=|[\s/>])/i.test(html);
}

// src/neo-page-modules.ts
var NEO_PAGE_MODULE_SERIES = {
  "depth-safety": ["depth"],
  "engine-vitals": [],
  "environment-outlook": ["headline", "pressure", "wind"],
  forecast: ["gusts", "rain", "wind"],
  helm: [],
  "power-budget": ["soc"],
  pressure: ["pressure"],
  reserves: [],
  "tanks-budget": [],
  tide: ["sea"],
  traffic: []
};
var NEO_PAGE_MODULE_FIELDS = {
  "depth-safety": [
    "depth",
    "depth-unit",
    "note",
    "stale",
    "shallow",
    "shallow-line",
    "trace"
  ],
  "engine-vitals": ["none", "dense"],
  "environment-outlook": [
    "wind-label",
    "wind",
    "wind-unit",
    "gust",
    "headline-label",
    "headline",
    "headline-unit",
    "headline-delta",
    "baro",
    "baro-now",
    "baro-glyph",
    "baro-rate",
    "baro-rate-unit",
    "baro-label"
  ],
  forecast: [
    "state",
    "message",
    "gust",
    "gust-time",
    "peak",
    "squall",
    "honesty",
    "summary"
  ],
  helm: [
    "rose",
    "ring",
    "cog-pointer",
    "awa-pointer",
    "true-pointer",
    "true-badge",
    "centre",
    "centre-unit",
    "centre-label",
    "heading",
    "heading-label",
    "cog",
    "sog",
    "sog-unit",
    "stw",
    "stw-unit",
    "delta",
    "reference"
  ],
  "power-budget": [
    "layout",
    "soc",
    "soc-now",
    "soc-delta",
    "flow-label",
    "flow",
    "flow-unit",
    "flow-sub",
    "flow-dir",
    "remaining",
    "voltage",
    "voltage-label",
    "health",
    "temperature",
    "temperature-unit",
    "projection"
  ],
  pressure: [
    "state",
    "message",
    "now",
    "now-unit",
    "trend-label",
    "glyph",
    "rate",
    "rate-unit"
  ],
  reserves: [
    "fuel-label",
    "fuel",
    "fuel-sub",
    "bank-label",
    "current",
    "charge",
    "flow"
  ],
  "tanks-budget": ["state", "message"],
  tide: ["state", "message", "next", "range", "summary"],
  traffic: ["state", "message"]
};
var NEO_PAGE_MODULE_ROWS = {
  "depth-safety": {},
  "engine-vitals": {
    engine: [
      "engine-name",
      "engine-rpm",
      "engine-coolant",
      "engine-coolant-unit",
      "engine-oil",
      "engine-oil-unit",
      "engine-load",
      "engine-fuel-rate",
      "engine-fuel-rate-unit",
      "engine-exhaust",
      "engine-exhaust-unit",
      "engine-alternator",
      "engine-hours"
    ],
    // `probe-note` is the probe's fault or "no signal" line (UnderwayModules'
    // NamedProbe sub), contract §4.8.1.
    probe: [
      "probe-label",
      "probe-value",
      "probe-unit",
      "probe-state",
      "probe-note"
    ],
    rest: ["rest-label", "rest-hours"]
  },
  "environment-outlook": { sub: ["sub-label", "sub-value", "sub-unit"] },
  forecast: { hour: ["hour-rain"] },
  helm: {},
  "power-budget": {
    source: ["source-label", "source-value", "source-unit", "source-state"]
  },
  pressure: {},
  reserves: {},
  "tanks-budget": {
    tank: [
      "tank-label-head",
      "tank-label-tail",
      "tank-glyph",
      "tank-value",
      "tank-unit",
      "tank-bar"
    ]
  },
  tide: { turn: ["turn-at", "turn-time", "turn-kind"] },
  traffic: {
    traffic: [
      "traffic-name",
      "traffic-range",
      "traffic-range-unit",
      "traffic-sog",
      "traffic-sog-unit",
      "traffic-age"
    ]
  }
};
var NEO_PAGE_MODULE_VALUE_FIELDS = {
  "depth-safety": ["depth"],
  "engine-vitals": [
    "engine-rpm",
    "engine-coolant",
    "engine-oil",
    "probe-value"
  ],
  "environment-outlook": ["wind", "headline"],
  forecast: ["gust"],
  helm: ["centre", "heading", "sog"],
  "power-budget": ["soc", "flow", "voltage"],
  pressure: ["now"],
  reserves: ["fuel", "current"],
  "tanks-budget": ["tank-value"],
  tide: ["next"],
  traffic: ["traffic-name", "traffic-range"]
};
var NEO_PAGE_MODULE_QUANTITIES = {
  "depth-safety": ["length", "time"],
  "engine-vitals": [
    "rotation",
    "temperature",
    "pressure",
    "ratio",
    "flow",
    "voltage",
    "time"
  ],
  "environment-outlook": ["speed", "temperature", "pressure", "time"],
  forecast: ["speed", "ratio", "time"],
  helm: ["angle", "speed"],
  "power-budget": [
    "ratio",
    "current",
    "power",
    "voltage",
    "time",
    "temperature"
  ],
  pressure: ["pressure", "time"],
  reserves: ["ratio", "current"],
  "tanks-budget": ["ratio"],
  tide: ["length", "time"],
  traffic: ["length", "speed", "time"]
};
var NEO_PAGE_STALE_AFTER_MS = 9e4;
function neoPageAgoLabel(ageMs) {
  const s = Math.round(ageMs / 1e3);
  if (s < 90) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
function trimZero(s) {
  const cleaned = s.includes(".") ? s.replace(/\.?0+$/, "") : s;
  return cleaned === "-0" ? "0" : cleaned;
}
function neoPageFormatValue(v) {
  if (!Number.isFinite(v)) return "\u2014";
  const abs = Math.abs(v);
  if (abs >= 1e4) {
    const [scaled, suffix] = abs >= 1e6 ? [v / 1e6, "M"] : [v / 1e3, "K"];
    return `${trimZero(scaled.toFixed(Math.abs(scaled) >= 100 ? 0 : 1))}${suffix}`;
  }
  return trimZero(v.toFixed(abs >= 100 ? 0 : 1));
}
function neoPageDeltaOverHours(points, hours, nowMs) {
  const valid = points.filter(
    (p) => p.v !== null
  );
  if (valid.length < 2) return null;
  const last = valid[valid.length - 1];
  const cutoff = nowMs - hours * 36e5;
  let ref = null;
  for (const p of valid) {
    if (p.t <= cutoff) ref = p;
    else break;
  }
  if (ref === null) {
    const first = valid[0];
    if (first.t - cutoff <= hours * 36e5 * 0.25 && first.t < last.t)
      ref = first;
  }
  if (ref === null || ref.t >= last.t) return null;
  return last.v - ref.v;
}
function neoPageMaxInWindow(points, windowMs, nowMs) {
  const cutoff = nowMs - windowMs;
  let max = null;
  for (const p of points) {
    if (p.v === null || p.t < cutoff) continue;
    if (max === null || p.v > max) max = p.v;
  }
  return max;
}
function neoPagePressureTendency(deltaHPa3h) {
  if (deltaHPa3h === null || !Number.isFinite(deltaHPa3h)) return null;
  const magnitude = Math.abs(deltaHPa3h);
  if (magnitude < 0.1) {
    return { direction: "steady", glyph: null, magnitude: 0, label: "steady" };
  }
  const direction = deltaHPa3h > 0 ? "rising" : "falling";
  const rate = magnitude > 6 ? " very rapidly" : magnitude > 3.5 ? " rapidly" : magnitude > 1.5 ? "" : " slowly";
  return {
    direction,
    glyph: direction === "rising" ? "\u25B2" : "\u25BC",
    magnitude: Math.round(magnitude * 10) / 10,
    label: `${direction}${rate}`
  };
}
function neoPageWindLabel(path, stationary) {
  if (path === null) return "Wind";
  if (stationary) return "Wind";
  return path.endsWith("speedApparent") ? "Apparent wind" : "True wind";
}
function temperatureDelta(deltaC, settings) {
  const zero = toDisplay(0, "C", "temperature", settings);
  const shifted = toDisplay(deltaC, "C", "temperature", settings);
  return { value: shifted.value - zero.value, unit: shifted.unit };
}
function pressureRate(deltaHpa, settings) {
  const d = toDisplay(deltaHpa, "hPa", "pressure", settings);
  return { text: d.value.toFixed(d.unit === "inHg" ? 2 : 1), unit: d.unit };
}
function clockFormat(locale, timeZone, options) {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    ...timeZone !== void 0 ? { timeZone } : {}
  });
}
function neoPageClock(t, timeZone, locale) {
  return clockFormat(locale, timeZone, {
    hour: "numeric",
    minute: "2-digit"
  }).format(t);
}
var NEO_PAGE_DEPTH_FRESHNESS = {
  ageingMs: 5e3,
  staleMs: 1e4
};
var NEO_PAGE_INSTRUMENT_FRESHNESS = {
  ageingMs: 8e3,
  staleMs: 2e4
};
var NEO_PAGE_SLOW_FRESHNESS = {
  ageingMs: 12e4,
  staleMs: 6e5
};
function neoPageCatalogFreshness(path) {
  const f = quickReadingFreshness(path);
  return { ageingMs: f.dimAfterMs, staleMs: f.dashAfterMs };
}
function neoPageUnderwayReading(raw, nowMs, thresholds) {
  const numeric = typeof raw.value === "number" && Number.isFinite(raw.value) ? raw.value : null;
  const ageMs = raw.atMs === null ? null : Math.max(0, nowMs - raw.atMs);
  const freshness = ageMs === null || ageMs >= thresholds.staleMs ? "stale" : ageMs >= thresholds.ageingMs ? "ageing" : "live";
  return {
    value: freshness === "stale" ? null : numeric,
    lastValue: numeric,
    ageMs,
    freshness,
    unit: raw.unit ?? null
  };
}
function neoPageLiveTone(reading, tone) {
  return reading.freshness === "live" ? tone : void 0;
}
function neoPageCounterReadout(reading) {
  if (reading.lastValue === null) return null;
  return {
    value: reading.lastValue,
    asOf: reading.freshness === "live" || reading.ageMs === null ? null : `as of ${neoPageAgoLabel(reading.ageMs)}`
  };
}
function neoPageTendency(points, noiseFloor, minSpanMinutes) {
  const known = points.filter(
    (p) => p.v !== null
  );
  if (known.length < 3) return null;
  const span = (known[known.length - 1].t - known[0].t) / 6e4;
  if (span < minSpanMinutes) return null;
  const delta = known[known.length - 1].v - known[0].v;
  if (Math.abs(delta) < noiseFloor) {
    return { direction: "steady", magnitude: 0, windowMinutes: span };
  }
  return {
    direction: delta > 0 ? "rising" : "falling",
    magnitude: Math.abs(delta),
    windowMinutes: span
  };
}
function neoPageShoalingNote(current, t, shallowM, units) {
  if (current === null || t === null || t.direction !== "falling" || t.magnitude <= 0)
    return null;
  const margin = current - shallowM;
  if (margin <= 0) return null;
  const perMinute = t.magnitude / t.windowMinutes;
  if (perMinute <= 0) return null;
  const minutes = margin / perMinute;
  if (minutes > 60) return null;
  const rounded = minutes < 1 ? "<1" : String(Math.round(minutes));
  const shallow = formatDisplay(shallowM, "m", "depth", units);
  return `shoaling \u2014 ${shallow.text} ${shallow.unit} in about ${rounded} min at this rate`;
}
var NEO_PAGE_COG_MIN_SOG_MS = 0.5 * 1852 / 3600;
function neoPageCourseWhileMoving(cog, sog) {
  return cog !== null && sog !== null && sog >= NEO_PAGE_COG_MIN_SOG_MS ? cog : null;
}
var NEO_PAGE_ENGINES_SHOWN_MAX = 4;
function neoPageHeardEngines(paths, readingAt, nowMs, staleMs = NEO_PAGE_SLOW_FRESHNESS.staleMs) {
  const newest2 = /* @__PURE__ */ new Map();
  for (const path of paths) {
    const id = /^propulsion\.([^.]+)\./.exec(path)?.[1];
    if (id === void 0) continue;
    const at = readingAt(path);
    const prev = newest2.get(id) ?? null;
    newest2.set(
      id,
      at === null ? prev : prev === null ? at : Math.max(prev, at)
    );
  }
  let lastHeardMs = null;
  const ids = [];
  for (const [id, at] of newest2) {
    if (at === null) continue;
    if (lastHeardMs === null || at > lastHeardMs) lastHeardMs = at;
    if (nowMs - at < staleMs) ids.push(id);
  }
  return {
    ids: ids.sort().slice(0, NEO_PAGE_ENGINES_SHOWN_MAX),
    lastHeardMs
  };
}
function neoPageNoEngineLine(lastHeardMs, locale, timeZone) {
  if (lastHeardMs === null) return "No engine reading yet";
  const when = clockFormat(locale, timeZone, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit"
  }).format(lastHeardMs);
  return `No engine reading since ${when}`;
}
function neoPageRestingEngines(engines) {
  const metered = (engines ?? []).filter((e) => e.hoursTotal !== null).slice(0, NEO_PAGE_ENGINES_SHOWN_MAX);
  return metered.map((e) => ({
    id: e.id,
    label: metered.length > 1 ? `${e.label} hours` : "Hours"
  }));
}
function neoPageCoolantLimitC(thresholds, engineId) {
  return thresholds.overTempByPath[`propulsion.${engineId}.coolantTemperature`] ?? thresholds.overTempC ?? null;
}
var NEO_PAGE_COOLANT_WARN_MARGIN_C = 10;
function neoPageCoolantTone(valueC, limitC) {
  if (limitC === null) return void 0;
  if (valueC >= limitC) return "bad";
  if (valueC >= limitC - NEO_PAGE_COOLANT_WARN_MARGIN_C) return "warn";
  return void 0;
}
var NEO_PAGE_ENGINE_TURNING_RPM = 50;
function neoPageOilPressureLostFor(oilHPa, rpm) {
  return oilHPa !== null && oilHPa <= 0 && typeof rpm === "number" && Number.isFinite(rpm) && rpm > NEO_PAGE_ENGINE_TURNING_RPM;
}
function neoPageProbeIsFaulted(status) {
  return typeof status === "string" && status.trim().length > 0 && status.trim().toLowerCase() !== "ok";
}
function neoPageWindReferenceFor(paths) {
  const has = (p) => paths.includes(p);
  if (has("environment.wind.directionTrue") || has("environment.wind.speedTrueGround")) {
    return "true-ground";
  }
  if (has("environment.wind.angleTrueWater") || has("environment.wind.speedTrue")) {
    return "true-water";
  }
  if (has("environment.wind.angleApparent") || has("environment.wind.speedApparent")) {
    return "apparent";
  }
  return null;
}
function firstOf(paths, ...candidates) {
  const set = new Set(paths);
  for (const c of candidates) if (set.has(c)) return c;
  return null;
}
var APPLIANCE_ZONE = /^(fridge|freezer|refrigerator|refrigeration|coolbox|iceb(ox)?|engine|engineroom|engine_room|exhaust|heating|heater|bilge|baitwell|livewell|oven)/i;
var INSIDE_ZONE_RE = /^environment\.inside\.([^.]+)\.temperature$/;
function titleCase(token) {
  const spaced = token.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
function neoPageInsideTemperature(paths) {
  if (paths.includes("environment.inside.temperature")) {
    return { path: "environment.inside.temperature", label: "Inside" };
  }
  const zones = [];
  for (const path of paths) {
    const zone = INSIDE_ZONE_RE.exec(path)?.[1];
    if (zone === void 0 || APPLIANCE_ZONE.test(zone)) continue;
    zones.push({ path, zone });
  }
  if (zones.length === 0) return null;
  const named = zones.filter((z) => !/^\d+$/.test(z.zone)).sort((a, b) => a.zone.localeCompare(b.zone));
  if (named.length > 0) {
    const pick = named[0];
    return { path: pick.path, label: titleCase(pick.zone) };
  }
  const indexed = [...zones].sort((a, b) => Number(a.zone) - Number(b.zone));
  return { path: indexed[0].path, label: "Inside" };
}
function neoPageWaterTemperature(paths) {
  return paths.includes("environment.water.temperature") ? { path: "environment.water.temperature", label: "Water" } : null;
}
function neoPageEnvironmentChoice(paths, showBarometer) {
  const wind = firstOf(
    paths,
    "environment.wind.speedApparent",
    "environment.wind.speedTrue"
  );
  const pressure = showBarometer ? firstOf(paths, "environment.outside.pressure") : null;
  const outside = firstOf(paths, "environment.outside.temperature");
  const inside = neoPageInsideTemperature(paths);
  const water = neoPageWaterTemperature(paths);
  const headline = outside !== null ? { path: outside, label: "Outside" } : inside ?? water;
  const sub = [inside, water].filter(
    (t) => t !== null && t.path !== headline?.path
  );
  return { wind, pressure, headline, sub };
}
function neoPageEnvironmentValue(paths, showBarometer) {
  const choice = neoPageEnvironmentChoice(paths, showBarometer);
  const series = [];
  if (choice.headline !== null) {
    series.push({
      field: "headline",
      path: choice.headline.path,
      window: "24h",
      points: 96
    });
  }
  if (choice.pressure !== null) {
    series.push({
      field: "pressure",
      path: choice.pressure,
      window: "24h",
      points: 96
    });
  }
  if (choice.wind !== null) {
    series.push({ field: "wind", path: choice.wind, window: "1h", points: 60 });
  }
  return { module: "environment-outlook", ...choice, series };
}
function neoPagePressureValue(paths) {
  const path = firstOf(paths, "environment.outside.pressure");
  return {
    module: "pressure",
    path,
    series: path === null ? [] : [{ field: "pressure", path, window: "24h", points: 96 }]
  };
}
var BANK_RE = /^electrical\.batteries\.([^.]+)\./;
var SOLAR_PANEL_RE = /^electrical\.solar\.([^.]+)\.panelPower$/;
var SOLAR_CURRENT_RE = /^electrical\.solar\.([^.]+)\.current$/;
var SEMANTIC_BANKS = {
  house: "House",
  starter: "Starter",
  engine: "Engine",
  generator: "Generator",
  genset: "Generator",
  port: "Port",
  starboard: "Starboard",
  bow: "Bow",
  stern: "Stern",
  aux: "Auxiliary",
  auxiliary: "Auxiliary",
  service: "Service",
  thruster: "Thruster",
  windlass: "Windlass"
};
function neoPageBankIdentity(bankKey, configuredHouseId) {
  if (bankKey === null) return null;
  if (configuredHouseId !== null && configuredHouseId === bankKey)
    return "House";
  const key = bankKey.toLowerCase();
  if (key === "house") return "House";
  return SEMANTIC_BANKS[key] ?? null;
}
function neoPageFormatTimeRemaining(seconds) {
  const h = seconds / 3600;
  if (h >= 10) return `about ${formatDurationSeconds(Math.round(h) * 3600)}`;
  if (h >= 3)
    return `about ${formatDurationSeconds(Math.round(h * 4) / 4 * 3600)}`;
  return formatDurationSeconds(Math.max(1, Math.round(seconds / 60)) * 60);
}
function neoPageSocSeverity(soc, warnBelow = 50, badBelow = 30) {
  if (soc === null) return "ok";
  if (soc < badBelow) return "bad";
  if (soc < warnBelow) return "warn";
  return "ok";
}
function neoPageSourceAmps(currentA, powerW, busV) {
  if (currentA !== null) return currentA;
  if (powerW !== null && busV !== null && busV > 0) return powerW / busV;
  return null;
}
function neoPageBestBatteryBank(paths, preferred) {
  const seen = /* @__PURE__ */ new Set();
  const banks = /* @__PURE__ */ new Set();
  for (const path of paths) {
    seen.add(path);
    const match = BANK_RE.exec(path);
    if (match !== null) banks.add(match[1]);
  }
  if (preferred !== null && banks.has(preferred)) return preferred;
  if (banks.has("house")) return "house";
  const score = (bank) => {
    const has = (suffix) => seen.has(`electrical.batteries.${bank}.${suffix}`);
    return (has("stateOfCharge") || has("capacity.stateOfCharge") ? 4 : 0) + (has("current") ? 2 : 0) + (has("voltage") ? 1 : 0);
  };
  return [...banks].sort(
    (a, b) => score(b) - score(a) || a.localeCompare(b, void 0, { numeric: true })
  )[0] ?? null;
}
function neoPageHouseBankFor(config, fallbacks, paths) {
  const nonEmpty = (v) => typeof v === "string" && v.length > 0 ? v : null;
  return nonEmpty(config?.houseBankId) ?? nonEmpty(fallbacks.powerBankId) ?? nonEmpty(fallbacks.profileBankId) ?? neoPageBestBatteryBank(paths, null);
}
function neoPagePowerChoice(paths, config) {
  const banks = [
    ...new Set(
      paths.map((p) => BANK_RE.exec(p)?.[1]).filter((b) => !!b)
    )
  ];
  const configured = typeof config?.houseBankId === "string" ? config.houseBankId : null;
  const socPathFor = (id) => firstOf(
    paths,
    `electrical.batteries.${id}.stateOfCharge`,
    `electrical.batteries.${id}.capacity.stateOfCharge`
  );
  const bankScore = (id) => {
    const has = (suffix) => paths.includes(`electrical.batteries.${id}.${suffix}`);
    return (socPathFor(id) !== null ? 4 : 0) + (has("current") ? 2 : 0) + (has("voltage") ? 1 : 0);
  };
  const bank = configured !== null && banks.includes(configured) ? configured : banks.includes("house") ? "house" : banks.length > 0 ? [...banks].sort((a, b) => bankScore(b) - bankScore(a))[0] : null;
  const held = (path) => path !== null && paths.includes(path) ? path : null;
  const prefix = bank === null ? null : `electrical.batteries.${bank}`;
  const worryPct = typeof config?.socWorryPct === "number" ? config.socWorryPct : 50;
  return {
    bank,
    configured,
    worryPct,
    paths: {
      soc: bank === null ? null : socPathFor(bank),
      voltage: held(prefix === null ? null : `${prefix}.voltage`),
      current: held(prefix === null ? null : `${prefix}.current`),
      timeRemaining: held(
        prefix === null ? null : `${prefix}.capacity.timeRemaining`
      ),
      health: held(prefix === null ? null : `${prefix}.capacity.stateOfHealth`),
      temperature: held(prefix === null ? null : `${prefix}.temperature`),
      solarPower: paths.filter((p) => SOLAR_PANEL_RE.test(p)),
      solarCurrent: paths.filter((p) => SOLAR_CURRENT_RE.test(p)),
      shore: held("electrical.shorePower.connected")
    }
  };
}
function neoPagePowerValue(paths, config, nameFor) {
  const choice = neoPagePowerChoice(paths, config);
  const bankName = (choice.bank === null ? void 0 : nameFor(`electrical.batteries.${choice.bank}`)) ?? neoPageBankIdentity(choice.bank, choice.configured);
  return {
    module: "power-budget",
    bank: choice.bank,
    bankName,
    worryPct: choice.worryPct,
    paths: choice.paths,
    series: choice.paths.soc === null ? [] : [{ field: "soc", path: choice.paths.soc, window: "24h", points: 96 }]
  };
}
var FLUIDS = {
  freshWater: {
    label: "Fresh water",
    kind: "water",
    goodHigh: true,
    warnAt: 0.25,
    badAt: 0.1
  },
  fuel: {
    label: "Fuel",
    kind: "fuel",
    goodHigh: true,
    warnAt: 0.3,
    badAt: 0.15
  },
  wasteWater: {
    label: "Waste water",
    kind: "waste",
    goodHigh: false,
    warnAt: 0.25,
    badAt: 0.1
  },
  blackWater: {
    label: "Black water",
    kind: "waste",
    goodHigh: false,
    warnAt: 0.2,
    badAt: 0.08
  }
};
function humanize(fluid) {
  const spaced = fluid.replace(/([a-z])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
function neoPageFluidMeta(fluid) {
  return FLUIDS[fluid] ?? {
    label: humanize(fluid),
    kind: "water",
    goodHigh: true,
    warnAt: 0.25,
    badAt: 0.1
  };
}
var TANK_RE = /^tanks\.([^.]+)\.([^.]+)\.currentLevel$/;
function neoPageTanksOf(paths) {
  return paths.map((p) => {
    const m = TANK_RE.exec(p);
    return m ? { path: p, fluid: m[1], instance: m[2] } : null;
  }).filter(
    (t) => t !== null
  );
}
function neoPageTankSeverity(fluid, pct, override) {
  if (pct === null) return "nosig";
  const m = neoPageFluidMeta(fluid);
  const warnAt = override?.warnAt ?? m.warnAt;
  const badAt = override?.badAt ?? m.badAt;
  const healthy = m.goodHigh ? pct / 100 : 1 - pct / 100;
  if (healthy <= badAt) return "bad";
  if (healthy <= warnAt) return "warn";
  return "ok";
}
var clamp01 = (n) => Math.max(0, Math.min(1, n));
function neoPageTankNameOverrides(config) {
  const raw = config?.["tankNames"];
  if (raw === null || typeof raw !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === "string" && v.trim() !== "") out[k] = v.trim();
  }
  return out;
}
function neoPageTankThresholdPct(config, fluid) {
  const raw = config?.["tankThresholds"];
  if (raw === null || typeof raw !== "object")
    return { warnPct: null, badPct: null };
  const t = raw[fluid];
  if (t === null || typeof t !== "object")
    return { warnPct: null, badPct: null };
  const rec = t;
  return {
    warnPct: typeof rec.warnPct === "number" ? rec.warnPct : null,
    badPct: typeof rec.badPct === "number" ? rec.badPct : null
  };
}
function neoPageTankLabel(name, fluid, siblingCount, instance) {
  const trimmed = typeof name === "string" ? name.trim() : "";
  if (trimmed !== "") return trimmed;
  const base = neoPageFluidMeta(fluid).label;
  if (siblingCount > 1) {
    const n = Number(instance);
    return `${base} ${Number.isNaN(n) ? instance : n + 1}`;
  }
  return base;
}
function subject(names, noun) {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.length} ${noun}`;
}
function neoPageTankAttentionLine(tanks) {
  const alarming = (goodHigh) => tanks.filter(
    (t) => (t.severity === "warn" || t.severity === "bad") && t.goodHigh === goodHigh
  ).map((t) => t.label);
  const low = alarming(true);
  const full = alarming(false);
  const parts = [];
  if (low.length > 0) {
    parts.push(
      `${subject(low, "tanks")} ${low.length === 1 ? "is" : "are"} running low`
    );
  }
  if (full.length > 0) {
    parts.push(
      `${subject(full, "tanks")} ${full.length === 1 ? "needs" : "need"} a pump-out`
    );
  }
  if (parts.length === 0) {
    const dead = tanks.filter((t) => t.severity === "nosig").map((t) => t.label);
    if (dead.length === 0) return null;
    parts.push(`no signal from ${subject(dead, "senders")}`);
  }
  const line = parts.join(" \xB7 ");
  return line.charAt(0).toUpperCase() + line.slice(1);
}
function neoPageTankAttentionTone(tanks) {
  if (tanks.some((t) => t.severity === "bad")) return "bad";
  if (tanks.some((t) => t.severity === "warn")) return "warn";
  return "nosig";
}
var PROMINENT_DEFAULT = ["freshWater", "wasteWater", "blackWater"];
function neoPageTanksValue(paths, config, names) {
  const tanks = neoPageTanksOf(paths);
  const prominent = Array.isArray(config?.prominent) ? config.prominent : PROMINENT_DEFAULT;
  const ordered = [...tanks].sort((a, b) => {
    const pa = prominent.includes(a.fluid) ? 0 : 1;
    const pb = prominent.includes(b.fluid) ? 0 : 1;
    return pa - pb || a.fluid.localeCompare(b.fluid) || a.instance.localeCompare(b.instance);
  });
  const counts = /* @__PURE__ */ new Map();
  for (const t of tanks) counts.set(t.fluid, (counts.get(t.fluid) ?? 0) + 1);
  const overrides = neoPageTankNameOverrides(config);
  return {
    module: "tanks-budget",
    rows: ordered.map((t) => {
      const threshold = neoPageTankThresholdPct(config, t.fluid);
      return {
        path: t.path,
        fluid: t.fluid,
        instance: t.instance,
        label: neoPageTankLabel(
          overrides[`${t.fluid}.${t.instance}`] ?? names.equipment(`tanks.${t.fluid}.${t.instance}`) ?? names.tankName?.(t.fluid, t.instance) ?? null,
          t.fluid,
          counts.get(t.fluid) ?? 1,
          t.instance
        ),
        warnPct: threshold.warnPct,
        badPct: threshold.badPct
      };
    }),
    series: []
  };
}
function neoPageHelmValue(paths) {
  return {
    module: "helm",
    heading: firstOf(
      paths,
      "navigation.headingTrue",
      "navigation.headingMagnetic"
    ),
    trueSpeed: firstOf(
      paths,
      "environment.wind.speedTrue",
      "environment.wind.speedTrueGround"
    ),
    reference: neoPageWindReferenceFor(paths),
    series: []
  };
}
function neoPageReservesValue(paths, houseBankId, nameFor, reservePct = 20) {
  const fuelPath = paths.find((p) => /^tanks\.fuel\.[^.]+\.currentLevel$/.test(p)) ?? null;
  const fuelKey = fuelPath === null ? null : equipmentInstanceOf(fuelPath)?.key ?? null;
  const bank = houseBankId ?? "house";
  return {
    module: "reserves",
    fuel: fuelPath === null ? null : {
      path: fuelPath,
      name: fuelKey === null ? null : nameFor(fuelKey) ?? null
    },
    bank: { id: bank, name: nameFor(`electrical.batteries.${bank}`) ?? null },
    socPath: firstOf(
      paths,
      `electrical.batteries.${bank}.stateOfCharge`,
      `electrical.batteries.${bank}.capacity.stateOfCharge`
    ),
    currentPath: `electrical.batteries.${bank}.current`,
    reservePct,
    series: []
  };
}
function neoPageEngineLabel(id, displayNumber) {
  if (/^\d+$/.test(id)) return `Engine ${displayNumber(id)}`;
  return id.charAt(0).toUpperCase() + id.slice(1);
}
function neoPageHourlySeries(block, key, fromMs, toMs) {
  const hourly = block?.hourly;
  if (hourly === void 0) return [];
  const times = hourly["time"];
  const values = hourly[key];
  if (!Array.isArray(times) || !Array.isArray(values)) return [];
  const out = [];
  for (let i = 0; i < times.length; i += 1) {
    const raw = times[i];
    if (typeof raw !== "string") continue;
    const t = Date.parse(
      raw.endsWith("Z") || raw.includes("+") ? raw : `${raw}Z`
    );
    if (!Number.isFinite(t) || t < fromMs || t > toMs) continue;
    const v = values[i];
    out.push({ t, v: typeof v === "number" && Number.isFinite(v) ? v : null });
  }
  return out;
}
function neoPageHourlyMax(block, key, fromMs, toMs) {
  let max = null;
  for (const p of neoPageHourlySeries(block, key, fromMs, toMs)) {
    if (p.v !== null && (max === null || p.v > max)) max = p.v;
  }
  return max;
}
var HOUR_MS = 36e5;
function neoPageHourlyAsSeries(points, fromMs, toMs) {
  const start = Math.floor(fromMs / HOUR_MS) * HOUR_MS;
  const count2 = Math.max(0, Math.ceil((toMs - start) / HOUR_MS) + 1);
  const slots = new Array(count2).fill(null);
  for (const p of points) {
    const i = Math.round((p.t - start) / HOUR_MS);
    if (i < 0 || i >= count2 || p.v === null) continue;
    slots[i] = [p.v, p.v, p.v, 1];
  }
  return { fromMs, toMs, bucketMs: HOUR_MS, angle: false, slots };
}
function neoPageSeriesPoints(series, pick = "avg") {
  if (series === null || series === void 0 || !(series.bucketMs > 0))
    return [];
  const start = Math.floor(series.fromMs / series.bucketMs) * series.bucketMs;
  const index = pick === "avg" ? 0 : pick === "min" ? 1 : 2;
  return series.slots.map((slot, i) => {
    const v = slot === null ? null : slot[index] ?? slot[0];
    return {
      t: start + i * series.bucketMs,
      v: typeof v === "number" && Number.isFinite(v) ? v : null
    };
  });
}
function modelState(response) {
  if (response === null) return "unavailable";
  if (response.status === "ready") return "ready";
  if (response.status === "pending") return "pending";
  return "unavailable";
}
function neoPageForecastValue(response, horizonHours, nowMs) {
  const state = modelState(response);
  const fromMs = nowMs - 2 * HOUR_MS;
  const toMs = nowMs + horizonHours * HOUR_MS;
  const block = response?.forecast;
  const series = state === "ready" ? [
    ["wind", "kn", "wind_speed_10m"],
    ["gusts", "kn", "wind_gusts_10m"],
    ["rain", "%", "precipitation_probability"],
    ["cape", "J/kg", "cape"]
  ].map(([field, unit, key]) => ({
    field,
    unit,
    series: neoPageHourlyAsSeries(
      neoPageHourlySeries(block, key, fromMs, toMs),
      fromMs,
      toMs
    )
  })) : [];
  return {
    module: "forecast",
    horizonHours,
    state,
    status: response?.status ?? null,
    updatedAt: response?.fetchedAt ?? null,
    series
  };
}
function neoPageTideValue(response, lookbackHours, minRangeM, nowMs) {
  const state = modelState(response);
  const fromMs = nowMs - lookbackHours * HOUR_MS;
  const toMs = nowMs + 12 * HOUR_MS;
  return {
    module: "tide",
    lookbackHours,
    minRangeM,
    state,
    status: response?.status ?? null,
    series: state === "ready" ? [
      {
        field: "sea",
        unit: "m",
        series: neoPageHourlyAsSeries(
          neoPageHourlySeries(
            response?.marine,
            "sea_level_height_msl",
            fromMs,
            toMs
          ),
          fromMs,
          toMs
        )
      }
    ] : []
  };
}
function neoPageTrafficValue(nearby, maxTargets) {
  if (nearby === null) {
    return {
      module: "traffic",
      maxTargets,
      state: "error",
      targets: [],
      series: []
    };
  }
  if (nearby.status !== "ready") {
    return {
      module: "traffic",
      maxTargets,
      state: "no-position",
      targets: [],
      series: []
    };
  }
  const vessels = Array.isArray(nearby.vessels) ? nearby.vessels : [];
  return {
    module: "traffic",
    maxTargets,
    state: "ready",
    targets: vessels.slice(0, maxTargets).map((v) => ({
      id: v.vesselId,
      name: v.name,
      mmsi: v.mmsi ?? 0,
      distanceM: v.distanceM,
      sogKn: v.sogKn,
      ageSeconds: v.ageSeconds
    })),
    series: []
  };
}
function neoPagePeakGust(points, fromMs, toMs) {
  let best = null;
  for (const p of points) {
    if (p.v === null || p.t < fromMs || p.t > toMs) continue;
    if (best === null || p.v > best.kn) best = { kn: p.v, t: p.t };
  }
  return best;
}
function neoPageSquallRisk(capeMax) {
  if (capeMax === null || !Number.isFinite(capeMax)) return null;
  if (capeMax > 1500) return "high";
  if (capeMax > 800) return "elevated";
  return null;
}
function neoPageModelUnavailableMessage(status, subject2) {
  if (subject2 === "tide") {
    if (status === "no_position") {
      return "Waiting for a position fix before the modelled tide can be requested.";
    }
    if (status === "stale_position") {
      return "Latest position is stale \u2014 the modelled tide is paused until a current fix arrives.";
    }
    return "Modelled tide unavailable right now.";
  }
  if (status === "no_position")
    return "Waiting for a position fix before requesting a forecast.";
  if (status === "stale_position") {
    return "Latest position is stale \u2014 forecast paused until a current fix arrives.";
  }
  return "Forecast unavailable \u2014 showing own-sensor readings only.";
}
var MAX_TIDE_SAMPLE_GAP_MS = 90 * 60 * 1e3;
function tideRuns(points) {
  const runs = [];
  let run = [];
  const flush = () => {
    if (run.length > 0) runs.push(run);
    run = [];
  };
  for (const point of points) {
    if (point.v === null || !Number.isFinite(point.t) || !Number.isFinite(point.v)) {
      flush();
      continue;
    }
    const previous = run[run.length - 1];
    if (previous !== void 0 && (point.t <= previous.t || point.t - previous.t > MAX_TIDE_SAMPLE_GAP_MS)) {
      flush();
    }
    run.push({ t: point.t, v: point.v });
  }
  flush();
  return runs;
}
function neoPageTideExtremes(points) {
  const RUN_MS = 90 * 60 * 1e3;
  const all = [];
  for (const valid of tideRuns(points)) {
    if (valid.length < 3) continue;
    const raw = [];
    for (let i = 1; i < valid.length - 1; i++) {
      const radius = Math.min(2, i, valid.length - 1 - i);
      const a = valid[i - radius].v;
      const b = valid[i].v;
      const c = valid[i + radius].v;
      if (b > a && b > c) raw.push({ t: valid[i].t, v: b, high: true });
      else if (b < a && b < c) raw.push({ t: valid[i].t, v: b, high: false });
    }
    const merged = [];
    for (const extreme of raw) {
      const previous = merged[merged.length - 1];
      if (previous !== void 0 && previous.high === extreme.high && extreme.t - previous.t <= RUN_MS) {
        const better = extreme.high ? extreme.v > previous.v : extreme.v < previous.v;
        if (better) merged[merged.length - 1] = extreme;
      } else {
        merged.push(extreme);
      }
    }
    all.push(...merged);
  }
  return all;
}
function neoPageNextTurn(points, nowMs) {
  const run = tideRuns(points).find((candidate) => {
    const first = candidate[0];
    const last = candidate[candidate.length - 1];
    return first !== void 0 && last !== void 0 && first.t <= nowMs && last.t > nowMs;
  });
  if (run === void 0) return null;
  const next = neoPageTideExtremes(run).find((extreme) => extreme.t > nowMs);
  if (next === void 0) return null;
  let state = null;
  for (let i = 0; i < run.length - 1; i++) {
    const a = run[i];
    const b = run[i + 1];
    if (nowMs >= a.t && nowMs < b.t) {
      state = b.v > a.v ? "Rising" : b.v < a.v ? "Falling" : "Steady";
      break;
    }
  }
  if (state === null) return null;
  return { high: next.high, t: next.t, msAway: next.t - nowMs, state };
}
function neoPageTideRangeM(points) {
  let widest = null;
  for (const run of tideRuns(points)) {
    if (run.length < 3) continue;
    const values = run.map((point) => point.v);
    const range = Math.max(...values) - Math.min(...values);
    if (widest === null || range > widest) widest = range;
  }
  return widest;
}
function neoPageFormatAway(msAway) {
  const total = Math.max(0, Math.round(msAway / 6e4));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  return `${h} h ${String(m).padStart(2, "0")} m`;
}
function neoPageTideAriaLabel(turn) {
  if (turn === null) return "Modelled sea level";
  return `Modelled sea level \u2014 ${turn.state.toLowerCase()}, ${turn.high ? "high" : "low"} water in ${neoPageFormatAway(turn.msAway)}`;
}
function neoPageLocalExtremes(points, opts = {}) {
  const vals = points.filter(
    (p) => p.v !== null && Number.isFinite(p.v)
  );
  if (vals.length < 3) return [];
  let min = vals[0];
  let max = vals[0];
  for (const p of vals) {
    if (p.v < min.v) min = p;
    if (p.v > max.v) max = p;
  }
  if (max.v === min.v) return [];
  const promAbs = (max.v - min.v) * (opts.prominence ?? 0.12);
  const sepMs = (vals[vals.length - 1].t - vals[0].t) * (opts.minSepFrac ?? 0.04);
  const candidates = [];
  let prevSlope = 0;
  for (let i = 1; i < vals.length; i += 1) {
    const dv = vals[i].v - vals[i - 1].v;
    const slope = dv > 0 ? 1 : dv < 0 ? -1 : 0;
    if (slope === 0) continue;
    if (prevSlope !== 0 && slope !== prevSlope) {
      candidates.push({ ...vals[i - 1], kind: prevSlope > 0 ? "max" : "min" });
    }
    prevSlope = slope;
  }
  const kept = [];
  for (const c of candidates) {
    const prev = kept[kept.length - 1];
    if (prev === void 0) {
      kept.push(c);
      continue;
    }
    if (c.kind === prev.kind) {
      const better = c.kind === "max" ? c.v > prev.v : c.v < prev.v;
      if (better) kept[kept.length - 1] = c;
      continue;
    }
    if (Math.abs(c.v - prev.v) < promAbs || c.t - prev.t < sepMs) continue;
    kept.push(c);
  }
  return kept;
}
function neoPagePadDomain(min, max, frac = 0.08, opts = {}) {
  let lo = Math.min(min, max);
  let hi = Math.max(min, max);
  if (lo === hi) {
    const bump = Math.max(Math.abs(lo) * 0.1, 1);
    lo -= bump;
    hi += bump;
  }
  const pad = (hi - lo) * frac;
  lo -= pad;
  hi += pad;
  if (opts.zero === true) {
    if (lo > 0) lo = 0;
    if (hi < 0) hi = 0;
  }
  return [lo, hi];
}
var DASH = "\u2014";
function good(text3, extra = {}) {
  return { text: text3, quality: "good", ...extra };
}
function missing(text3 = "", extra = {}) {
  return { text: text3, quality: "missing", ...extra };
}
function valueFig(text3, extra = {}) {
  return text3 === null ? missing(DASH, extra) : good(text3, extra);
}
function emptyCard(title) {
  return { title, chips: [], note: null, footer: null, freshness: null };
}
function numericOf(raw) {
  const v = raw?.value;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function rawOf(live, path) {
  if (path === null) return { value: void 0, unit: null, atMs: null };
  const r = live(path);
  return r === void 0 ? { value: void 0, unit: null, atMs: null } : { value: r.value, unit: r.unit ?? null, atMs: r.atMs };
}
function underway(live, path, nowMs, thresholds) {
  if (path === null) {
    return {
      value: null,
      lastValue: null,
      ageMs: null,
      freshness: "stale",
      unit: null
    };
  }
  return neoPageUnderwayReading(rawOf(live, path), nowMs, thresholds);
}
function qualityOf(freshness) {
  return freshness === "live" ? "good" : freshness === "ageing" ? "stale" : "missing";
}
function freshnessNote(live, paths, nowMs) {
  const ages = paths.filter((p) => p !== null).map((p) => live(p)?.atMs).filter((t) => typeof t === "number").map((t) => nowMs - t);
  if (ages.length === 0) return null;
  const stalest = Math.max(...ages);
  return stalest > NEO_PAGE_STALE_AFTER_MS ? `as of ${neoPageAgoLabel(stalest)}` : null;
}
function lastPoint(points) {
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const p = points[i];
    if (p.v !== null && Number.isFinite(p.v)) return { t: p.t, v: p.v };
  }
  return null;
}
function modelPoints(value, field) {
  return neoPageSeriesPoints(
    value.series.find((s) => s.field === field)?.series
  );
}
function within2(points, fromMs, toMs) {
  return points.filter((p) => p.t >= fromMs && p.t <= toMs);
}
function extentOf(lists) {
  let min = Infinity;
  let max = -Infinity;
  for (const points of lists) {
    for (const p of points) {
      if (p.v === null || !Number.isFinite(p.v)) continue;
      if (p.v < min) min = p.v;
      if (p.v > max) max = p.v;
    }
  }
  return min <= max ? { min, max } : null;
}
function forecastFigures(value, nowMs, units, timeZone, locale) {
  const horizon = value.horizonHours;
  const to = nowMs + horizon * HOUR_MS;
  const from = nowMs - 2 * HOUR_MS;
  const wind = within2(modelPoints(value, "wind"), from, to);
  const gustsAll = modelPoints(value, "gusts");
  const gusts = within2(gustsAll, from, to);
  const rain = within2(modelPoints(value, "rain"), from, to);
  const ready = value.state === "ready";
  const cape = ready ? within2(modelPoints(value, "cape"), nowMs, nowMs + 12 * HOUR_MS).reduce(
    (max, p) => p.v !== null && (max === null || p.v > max) ? p.v : max,
    null
  ) : null;
  const risk = neoPageSquallRisk(cape);
  const peak = ready ? neoPagePeakGust(gustsAll, nowMs, to) : null;
  const peakChip = peak === null ? null : toDisplay(peak.kn, "kn", "speed", units);
  const peakClock = peak === null ? null : clockFormat(locale, timeZone, {
    hour: "2-digit",
    minute: "2-digit"
  }).format(peak.t);
  const gust = peakChip === null ? null : `${Math.round(peakChip.value)} ${peakChip.unit}`;
  const updatedAt = value.updatedAt === null || !ready ? NaN : Date.parse(value.updatedAt);
  const age = Number.isFinite(updatedAt) ? neoPageAgoLabel(nowMs - updatedAt) : null;
  let state = value.state;
  let message = null;
  if (value.state === "unavailable") {
    message = neoPageModelUnavailableMessage(value.status, "forecast");
  } else if (value.state === "pending") {
    message = "Fetching the forecast\u2026";
  } else if (wind.length === 0 && gusts.length === 0) {
    state = "unavailable";
    message = "Forecast unavailable \u2014 showing own-sensor readings only.";
  }
  const peakSpoken = peak === null ? null : toDisplay(peak.kn, "kn", "speed", units);
  const summary = peak !== null && peakSpoken !== null ? `Wind and gust forecast \u2014 peak gust ${Math.round(peakSpoken.value)} ${peakSpoken.unit} around ${clockFormat(locale, timeZone, {
    hour: "2-digit",
    minute: "2-digit"
  }).format(peak.t)}` : "Wind and gust forecast";
  const chips = [];
  if (gust !== null) {
    chips.push({
      label: "Gust",
      value: `${gust}${risk === null && peakClock !== null ? ` \xB7 ${peakClock}` : ""}`,
      tone: null
    });
  }
  if (risk !== null) {
    chips.push({
      label: peak !== null ? "\u26A0" : "Squalls",
      value: peak !== null ? risk : `\u26A0 ${risk}`,
      tone: risk === "high" ? "bad" : "warn"
    });
  }
  const hours = rain.filter((p) => p.t >= nowMs).slice(0, 48);
  const scaleExt = extentOf([wind, gusts]);
  return {
    card: { ...emptyCard(`Forecast \xB7 ${horizon} h`), chips },
    fields: {
      state: good("", { state }),
      message: message === null ? missing() : good(message),
      gust: valueFig(gust),
      "gust-time": peakClock === null ? missing() : good(peakClock),
      peak: peak === null ? missing("", { point: null }) : good("", {
        point: { series: "gusts", atMs: peak.t, value: peak.kn }
      }),
      squall: risk === null ? missing("", { tone: null }) : good(risk, { tone: risk === "high" ? "bad" : "warn", state: risk }),
      honesty: good(
        age !== null ? `Open-Meteo model \xB7 updated ${age}` : "Open-Meteo model"
      ),
      summary: good(summary)
    },
    rows: {
      hour: hours.map(
        (p) => p.v === null ? { "hour-rain": missing("", { frac: null }) } : {
          "hour-rain": good("", {
            frac: clamp01(p.v / 100),
            point: { series: "rain", atMs: p.t, value: p.v }
          })
        }
      )
    },
    scales: {
      ...scaleExt === null ? {} : {
        wind: neoPagePadDomain(scaleExt.min, scaleExt.max, 0.09, {
          zero: true
        }),
        gusts: neoPagePadDomain(scaleExt.min, scaleExt.max, 0.09, {
          zero: true
        })
      },
      rain: [0, 100]
    }
  };
}
function tideFigures(value, nowMs, units, timeZone, locale) {
  const from = nowMs - value.lookbackHours * HOUR_MS;
  const to = nowMs + 12 * HOUR_MS;
  const points = within2(modelPoints(value, "sea"), from, to);
  const turn = neoPageNextTurn(points, nowMs);
  const turnText = turn !== null ? `${turn.high ? "\u25B2 high" : "\u25BC low"} \xB7 ${neoPageFormatAway(turn.msAway)}` : null;
  const card = emptyCard("Tide");
  let state;
  let message = null;
  if (value.state === "unavailable") {
    state = "unavailable";
    message = neoPageModelUnavailableMessage(value.status, "tide");
  } else if (value.state === "pending") {
    state = "pending";
    message = "Fetching the modelled tide\u2026";
  } else {
    const range2 = neoPageTideRangeM(points);
    if (range2 === null || range2 < value.minRangeM) {
      const few = points.filter((p) => p.v !== null).length < 3;
      state = few ? "none" : "negligible";
      message = few ? "No modelled tide available here." : "Negligible tide at this anchorage.";
    } else {
      state = "ready";
    }
  }
  const ready = state === "ready";
  if (ready && turnText !== null) {
    card.chips = [{ label: "Next", value: turnText, tone: null }];
  }
  const range = ready ? neoPageTideRangeM(points) : null;
  const rangeShown = range === null ? null : formatDisplay(range, "m", "shortDistance", units);
  const turns = ready ? neoPageLocalExtremes(points).slice(0, 6) : [];
  const ext = extentOf([points]);
  return {
    card,
    fields: {
      state: good("", { state }),
      message: message === null ? missing() : good(message),
      next: ready ? valueFig(turnText) : missing(DASH),
      range: rangeShown === null ? missing() : good(`\u2248${rangeShown.text} ${rangeShown.unit} range`),
      summary: good(neoPageTideAriaLabel(ready ? turn : null))
    },
    rows: {
      turn: turns.map((e) => ({
        "turn-at": good("", {
          point: { series: "sea", atMs: e.t, value: e.v }
        }),
        "turn-time": good(neoPageClock(e.t, timeZone, locale)),
        "turn-kind": good("", { state: e.kind === "max" ? "high" : "low" })
      }))
    },
    scales: ext === null || !ready ? {} : { sea: neoPagePadDomain(ext.min, ext.max, 0.17) }
  };
}
function powerFigures(value, live, series, nowMs, units) {
  const p = value.paths;
  const num2 = (path) => path === null ? null : numericOf(live(path));
  const hasSoc = p.soc !== null;
  const soc = hasSoc ? num2(p.soc) : null;
  const voltage = num2(p.voltage);
  const amps = num2(p.current);
  const netW = amps !== null && voltage !== null ? amps * voltage : null;
  const sumOf = (paths) => paths.reduce((sum, path) => {
    const v = num2(path);
    return v === null ? sum : (sum ?? 0) + v;
  }, null);
  const solarW = sumOf(p.solarPower);
  const solarA = sumOf(p.solarCurrent);
  const sources = [];
  if (solarW !== null || solarA !== null) {
    const a = solarA ?? neoPageSourceAmps(null, solarW, voltage);
    const text3 = a !== null ? `+${a.toFixed(1)} A${solarW !== null ? ` \xB7 ${Math.round(solarW)} W` : ""}` : solarW !== null ? `+${Math.round(solarW)} W` : null;
    if (text3 !== null) {
      sources.push({
        "source-label": good("Solar"),
        "source-value": good(text3),
        "source-unit": good(""),
        "source-state": good("", { state: "measured" })
      });
    }
  }
  if (p.shore !== null && live(p.shore)?.value === true) {
    sources.push({
      "source-label": good("Shore"),
      "source-value": good("Connected"),
      "source-unit": good(""),
      "source-state": good("", { state: "connected" })
    });
  }
  const secsLeft = num2(p.timeRemaining);
  const socPoints = neoPageSeriesPoints(series.soc);
  const validSoc = socPoints.filter(
    (pt) => pt.v !== null
  );
  const socTrend = hasSoc && validSoc.length >= 2;
  const socDelta = socTrend ? Math.round(validSoc[validSoc.length - 1].v - validSoc[0].v) : null;
  const socSev = neoPageSocSeverity(soc, value.worryPct);
  const socTone = socSev === "bad" ? "bad" : socSev === "warn" ? "warn" : null;
  const charging = amps !== null ? amps > 0.5 : netW !== null ? netW > 5 : null;
  const isFloat = soc !== null && soc >= 99 && charging === true;
  const flowValue = amps !== null ? Math.abs(amps).toFixed(1) : netW !== null ? String(Math.abs(Math.round(netW))) : null;
  const flowUnit = amps !== null ? "A" : netW !== null ? "W" : "";
  const flowLabel = charging === null ? "Net flow" : isFloat ? "Float" : charging ? "Charging" : "Discharging";
  const flowSub = amps !== null && netW !== null ? `${Math.abs(Math.round(netW))} W` : null;
  const health = num2(p.health);
  const tempC = num2(p.temperature);
  const tempDisplay = tempC !== null ? formatDisplay(tempC, "C", "temperature", units) : null;
  const layout = hasSoc ? "soc" : flowValue !== null ? "flow" : "voltage";
  const bankLabel = value.bankName ?? "Battery";
  const last = lastPoint(socPoints);
  return {
    card: {
      ...emptyCard("Power"),
      freshness: freshnessNote(live, [p.soc, p.voltage, p.current], nowMs)
    },
    fields: {
      layout: good("", { state: layout }),
      soc: soc === null ? missing(DASH, { frac: null, zone: null, tone: null }) : good(neoPageFormatValue(Math.round(soc)), {
        frac: clamp01(soc / 100),
        zone: socTone,
        tone: socTone
      }),
      "soc-now": !socTrend || last === null ? missing("", { point: null }) : good("", { point: { series: "soc", atMs: last.t, value: last.v } }),
      "soc-delta": socDelta === null ? missing() : good(`${socDelta >= 0 ? "\u25B2" : "\u25BC"} ${Math.abs(socDelta)}% / 24 h`),
      "flow-label": good(flowLabel),
      flow: valueFig(flowValue),
      "flow-unit": good(flowValue === null ? "" : flowUnit),
      "flow-sub": flowSub === null ? missing() : good(flowSub),
      "flow-dir": good(charging === null ? "" : charging ? "\u25B2" : "\u25BC", {
        state: charging === null ? "unknown" : charging ? "charging" : "discharging",
        tone: charging === true ? "good" : null
      }),
      remaining: soc !== null && secsLeft !== null ? good(neoPageFormatTimeRemaining(secsLeft)) : missing(),
      voltage: voltage === null ? missing(DASH) : good(
        layout === "voltage" ? neoPageFormatValue(voltage) : voltage.toFixed(2)
      ),
      "voltage-label": good(bankLabel),
      health: health === null ? missing() : good(neoPageFormatValue(Math.round(health))),
      temperature: tempDisplay === null ? missing() : good(tempDisplay.text),
      "temperature-unit": tempDisplay === null ? missing() : good(tempDisplay.unit),
      projection: layout === "flow" ? good(
        "No state-of-charge sensor on this bank \u2014 showing charge/discharge rate and voltage."
      ) : layout === "voltage" ? good(
        "No state-of-charge or current sensor \u2014 showing voltage only."
      ) : missing()
    },
    rows: { source: sources },
    scales: { soc: [0, 100] }
  };
}
function environmentFigures(value, live, series, nowMs, units) {
  const num2 = (path) => path === null ? null : numericOf(live(path));
  const windNow = num2(value.wind);
  const windDisplay = windNow !== null ? formatDisplay(windNow, "kn", "speed", units) : null;
  const gust1h = neoPageMaxInWindow(
    neoPageSeriesPoints(series.wind, "max"),
    HOUR_MS,
    nowMs
  );
  const gustDisplay = gust1h !== null ? toDisplay(gust1h, "kn", "speed", units) : null;
  const pressureNow = num2(value.pressure);
  const pressureDisplay = pressureNow !== null ? formatDisplay(pressureNow, "hPa", "pressure", units) : null;
  const tendency = neoPagePressureTendency(
    neoPageDeltaOverHours(neoPageSeriesPoints(series.pressure), 3, nowMs)
  );
  const baro = tendency === null ? null : { ...tendency, rate: pressureRate(tendency.magnitude, units) };
  const headline = value.headline;
  const headlineNow = num2(headline?.path ?? null);
  const headlineDisplay = headlineNow !== null ? formatDisplay(headlineNow, "C", "temperature", units) : null;
  const headlineDelta = neoPageDeltaOverHours(
    neoPageSeriesPoints(series.headline),
    3,
    nowMs
  );
  const deltaDisplay = headlineDelta === null ? null : temperatureDelta(headlineDelta, units);
  const fields = {
    "wind-label": value.wind === null ? missing() : good(neoPageWindLabel(value.wind, true)),
    wind: value.wind === null ? missing(DASH) : valueFig(windDisplay?.text ?? null),
    "wind-unit": windDisplay === null ? missing() : good(windDisplay.unit),
    gust: gustDisplay === null ? missing() : good(
      `${Math.round(gustDisplay.value)}\xA0${gustDisplay.unit} gust \xB7 1\xA0h`
    ),
    "headline-label": headline === null ? missing() : good(headline.label),
    headline: headline === null ? missing(DASH) : valueFig(headlineDisplay?.text ?? null),
    "headline-unit": headlineDisplay === null ? missing() : good(headlineDisplay.unit),
    "headline-delta": deltaDisplay === null || headline === null ? missing() : good(
      `${deltaDisplay.value > 0 ? "\u25B2" : "\u25BC"} ${Math.abs(deltaDisplay.value).toFixed(1)}\xA0${deltaDisplay.unit} / 3\xA0h`
    ),
    baro: good("", {
      state: value.pressure === null ? "off" : baro === null ? "building" : "on"
    }),
    "baro-now": value.pressure === null ? missing() : pressureDisplay === null ? missing(DASH) : good(
      baro === null ? `${pressureDisplay.text} ${pressureDisplay.unit}` : pressureDisplay.text
    ),
    "baro-glyph": baro?.glyph ? good(baro.glyph) : missing(),
    "baro-rate": baro === null ? missing() : good(baro.rate.text),
    "baro-rate-unit": baro === null ? missing() : good(`\xA0${baro.rate.unit} / 3\xA0h`),
    "baro-label": value.pressure === null ? missing() : good(baro === null ? "trend building" : baro.label)
  };
  const sub = value.sub.map((t, i) => {
    const v = num2(t.path);
    const d = v !== null ? formatDisplay(v, "C", "temperature", units) : null;
    return {
      "sub-label": good(t.label),
      "sub-value": d === null ? missing(DASH) : good(d.text),
      "sub-unit": i === value.sub.length - 1 && d !== null ? good(d.unit) : good("")
    };
  });
  return {
    card: {
      ...emptyCard("Environment"),
      freshness: freshnessNote(
        live,
        [value.wind, value.pressure, headline?.path ?? null],
        nowMs
      )
    },
    fields,
    rows: { sub },
    scales: {}
  };
}
function tanksFigures(value, live, nowMs) {
  const rows = value.rows.map((r) => {
    const level = numericOf(live(r.path));
    const meta = neoPageFluidMeta(r.fluid);
    const override = r.warnPct === null && r.badPct === null ? void 0 : {
      warnAt: r.warnPct === null ? void 0 : clamp01(r.warnPct / 100),
      badAt: r.badPct === null ? void 0 : clamp01(r.badPct / 100)
    };
    return {
      ...r,
      level,
      meta,
      severity: neoPageTankSeverity(r.fluid, level, override)
    };
  });
  const states = rows.map((r) => ({
    label: r.label,
    severity: r.severity,
    goodHigh: r.meta.goodHigh
  }));
  const attention = neoPageTankAttentionLine(states);
  const tone = neoPageTankAttentionTone(states);
  return {
    card: {
      ...emptyCard("Tanks"),
      footer: attention === null ? null : { text: attention, tone: tone === "nosig" ? "muted" : tone },
      freshness: freshnessNote(
        live,
        rows.map((r) => r.path),
        nowMs
      )
    },
    fields: {
      state: good("", { state: rows.length > 0 ? "rows" : "none" }),
      message: rows.length > 0 ? missing() : good("No tank levels reported.")
    },
    rows: {
      tank: rows.slice(0, 12).map((r) => {
        const m = /^(.*\S)\s+(\S{1,3})$/.exec(r.label);
        const zone = r.severity;
        const tone2 = r.severity === "bad" ? "bad" : r.severity === "warn" ? "warn" : null;
        const quality = r.level === null ? "missing" : "good";
        const row = {
          "tank-label-head": { text: m ? m[1] : r.label, quality, zone },
          "tank-label-tail": { text: m ? m[2] : "", quality, zone },
          "tank-glyph": { text: "", state: r.meta.kind, quality, zone },
          "tank-value": {
            text: r.level === null ? DASH : neoPageFormatValue(Math.round(r.level)),
            quality,
            zone,
            tone: tone2
          },
          "tank-unit": { text: r.level === null ? "" : "%", quality, zone },
          "tank-bar": {
            text: "",
            frac: r.level === null ? null : clamp01(r.level / 100),
            quality,
            zone,
            tone: tone2
          }
        };
        return row;
      })
    },
    scales: {}
  };
}
function pressureFigures(value, live, series, nowMs, units) {
  const card = {
    ...emptyCard("Pressure"),
    freshness: freshnessNote(live, [value.path], nowMs)
  };
  if (value.path === null) {
    return {
      card,
      fields: {
        state: good("", { state: "none" }),
        message: good("No barometer reported."),
        now: missing(DASH),
        "now-unit": missing(),
        "trend-label": missing(),
        glyph: missing(),
        rate: missing(DASH),
        "rate-unit": missing()
      },
      rows: {},
      scales: {}
    };
  }
  const now = numericOf(live(value.path));
  const nowDisplay = now !== null ? formatDisplay(now, "hPa", "pressure", units) : null;
  const tendency = neoPagePressureTendency(
    neoPageDeltaOverHours(neoPageSeriesPoints(series.pressure), 3, nowMs)
  );
  const baro = tendency === null ? null : { ...tendency, rate: pressureRate(tendency.magnitude, units) };
  return {
    card,
    fields: {
      state: good("", { state: "on" }),
      message: missing(),
      now: valueFig(nowDisplay?.text ?? null),
      "now-unit": nowDisplay === null ? missing() : good(nowDisplay.unit),
      "trend-label": good(baro === null ? "3 h trend" : baro.label),
      glyph: baro?.glyph ? good(baro.glyph) : missing(),
      rate: baro === null ? missing(DASH) : good(baro.rate.text),
      "rate-unit": baro === null ? missing() : good(`${baro.rate.unit} / 3 h`)
    },
    rows: {},
    scales: {}
  };
}
function ageChip(reading) {
  return reading.freshness === "ageing" && reading.ageMs !== null ? [{ label: "", value: neoPageAgoLabel(reading.ageMs), tone: null }] : [];
}
function depthFigures(value, live, series, nowMs, units) {
  const depthPath = value.depth?.path ?? null;
  const shallowM = value.depth?.shallowM ?? null;
  const depth = underway(live, depthPath, nowMs, NEO_PAGE_DEPTH_FRESHNESS);
  const history = neoPageSeriesPoints(series.depth);
  const known = history.filter((p) => p.v !== null);
  const trend = neoPageTendency(history, 0.3, 2);
  const note = shallowM === null ? null : neoPageShoalingNote(depth.value, trend, shallowM, units);
  const shallow = depth.value !== null && shallowM !== null && depth.value <= shallowM;
  const storeUnit = depth.unit ?? "m";
  const reading = depth.value === null ? null : formatDisplay(depth.value, storeUnit, "depth", units);
  const shallowLine = shallowM === null ? null : formatDisplay(shallowM, "m", "depth", units);
  const tone = neoPageLiveTone(depth, shallow ? "bad" : void 0);
  const domainMax = Math.max(
    ...known.map((p) => p.v ?? 0),
    (shallowM ?? 0) * 2,
    5
  );
  let stale = missing();
  if (depth.freshness === "stale") {
    if (depth.lastValue === null || depth.ageMs === null) {
      stale = good("no signal");
    } else {
      const last = formatDisplay(depth.lastValue, storeUnit, "depth", units);
      stale = good(
        `no signal \u2014 last: ${last.text} ${last.unit}, ${neoPageAgoLabel(depth.ageMs)}`
      );
    }
  }
  return {
    card: {
      ...emptyCard(value.depth?.label ?? "Depth"),
      chips: ageChip(depth)
    },
    fields: {
      depth: {
        text: reading?.text ?? DASH,
        quality: qualityOf(depth.freshness),
        tone: tone ?? null,
        zone: shallow ? "shallow" : null
      },
      "depth-unit": reading === null ? missing() : good(reading.unit),
      note: note === null ? missing() : good(note),
      stale,
      shallow: shallowLine !== null && depth.freshness !== "stale" ? good(`shallow below ${shallowLine.text} ${shallowLine.unit}`) : missing(),
      "shallow-line": shallowM === null ? missing("", { frac: null }) : good("", { frac: clamp01(shallowM / domainMax) }),
      trace: good("", { state: known.length >= 3 ? "shown" : "hidden" })
    },
    rows: {},
    scales: { depth: [0, domainMax] }
  };
}
function speedDisplay(reading, units) {
  if (reading.value === null) return null;
  return formatDisplay(reading.value, reading.unit ?? "kn", "speed", units);
}
function frac360(angle) {
  return (angle % 360 + 360) % 360 / 360;
}
function helmFigures(value, live, nowMs, units) {
  const r = (path) => underway(live, path, nowMs, NEO_PAGE_INSTRUMENT_FRESHNESS);
  const heading = r(value.heading);
  const cog = r("navigation.courseOverGroundTrue");
  const sog = r("navigation.speedOverGround");
  const stw = r("navigation.speedThroughWater");
  const awa = r("environment.wind.angleApparent");
  const aws = r("environment.wind.speedApparent");
  const twd = r("environment.wind.directionTrue");
  const twa = r("environment.wind.angleTrueWater");
  const tws = r(value.trueSpeed);
  const course = neoPageCourseWhileMoving(cog.value, sog.value);
  const courseUp = heading.value !== null;
  let truePointer = null;
  if (courseUp && value.reference === "true-ground" && twd.value !== null) {
    truePointer = twd.value;
  } else if (courseUp && value.reference === "true-water" && twa.value !== null) {
    truePointer = (heading.value + twa.value + 360) % 360;
  }
  const centreReading = tws.value !== null ? tws : aws.value !== null ? aws : sog;
  const centre = speedDisplay(centreReading, units);
  const centreLabel = tws.value !== null ? "TWS" : aws.value !== null ? "AWS" : "SOG";
  const delta = sog.value !== null && stw.value !== null ? sog.value - stw.value : null;
  const deltaDisplay = delta === null ? null : toDisplay(delta, sog.unit ?? stw.unit ?? "kn", "speed", units);
  const sogDisplay = speedDisplay(sog, units);
  const stwDisplay = speedDisplay(stw, units);
  const degrees = (v) => v === null ? missing(DASH) : good(`${neoPageFormatValue(Math.round(v))}\xB0`);
  return {
    card: {
      ...emptyCard("Helm"),
      chips: ageChip(heading.freshness === "live" ? sog : heading)
    },
    fields: {
      rose: good("", { state: courseUp ? "compass" : "relative" }),
      ring: good("", { frac: courseUp ? frac360(heading.value) : 0 }),
      "cog-pointer": courseUp && course !== null ? good("", { frac: frac360(course) }) : missing("", { frac: null }),
      "awa-pointer": awa.value !== null ? good("", {
        frac: frac360(
          courseUp ? heading.value + awa.value + 360 : awa.value
        )
      }) : missing("", { frac: null }),
      "true-pointer": truePointer === null ? missing("", { frac: null }) : good("", { frac: frac360(truePointer) }),
      "true-badge": truePointer === null ? missing() : good("T"),
      centre: valueFig(centre?.text ?? null),
      "centre-unit": centre === null ? missing() : good(centre.unit),
      "centre-label": good(centreLabel),
      heading: degrees(heading.value),
      "heading-label": good(
        value.heading === "navigation.headingTrue" ? "HDG true" : "HDG mag"
      ),
      cog: degrees(course),
      sog: valueFig(sogDisplay?.text ?? null),
      "sog-unit": sogDisplay === null ? missing() : good(sogDisplay.unit),
      stw: valueFig(stwDisplay?.text ?? null),
      "stw-unit": stwDisplay === null ? missing() : good(stwDisplay.unit),
      delta: deltaDisplay === null ? missing() : good(
        `SOG\u2212STW ${deltaDisplay.value >= 0 ? "+" : "\u2212"}${Math.abs(
          deltaDisplay.value
        ).toFixed(1)} ${deltaDisplay.unit}`
      ),
      reference: value.reference === "apparent" ? good("Apparent wind \u2014 this boat reports no true wind.") : missing()
    },
    rows: {},
    scales: {}
  };
}
var ENGINE_READING_SUFFIXES = [
  "revolutions",
  "coolantTemperature",
  "oilPressure",
  "engineLoad",
  "fuel.rate",
  "exhaustTemperature",
  "alternatorVoltage",
  "runTime"
];
function engineReading(live, path, nowMs, units, storeUnit, category, decimals, limitC = null) {
  const reading = neoPageUnderwayReading(
    rawOf(live, path),
    nowMs,
    neoPageCatalogFreshness(path)
  );
  if (reading.lastValue === null) return null;
  const display = category === void 0 || reading.value === null ? null : formatDisplay(
    reading.value,
    reading.unit ?? storeUnit,
    category,
    units
  );
  const tone = reading.value === null ? void 0 : neoPageLiveTone(reading, neoPageCoolantTone(reading.value, limitC));
  return {
    value: {
      text: reading.value === null ? DASH : display?.text ?? neoPageFormatValue(Number(reading.value.toFixed(decimals))),
      quality: qualityOf(reading.freshness),
      tone: tone ?? null,
      zone: tone ?? null
    },
    unit: good(display?.unit ?? storeUnit)
  };
}
function engineFigures(value, live, nowMs, units, timeZone, locale) {
  const allPaths = value.candidates.flatMap((c) => c.paths);
  const heard = neoPageHeardEngines(
    allPaths,
    (path) => live(path)?.atMs ?? null,
    nowMs
  );
  const lastHeardAt = value.lastHeardAt === null ? NaN : Date.parse(value.lastHeardAt);
  const lastHeardMs = heard.lastHeardMs ?? (Number.isFinite(lastHeardAt) ? lastHeardAt : null);
  const instances = heard.ids;
  const byId = new Map(value.candidates.map((c) => [c.id, c]));
  const engineReadings = allPaths.filter(
    (p) => ENGINE_READING_SUFFIXES.some(
      (suffix) => instances.some((id) => p === `propulsion.${id}.${suffix}`)
    )
  ).length;
  const probes = value.probes.slice(0, 8);
  const dense = engineReadings + probes.length > 6;
  const engines = instances.map((id) => {
    const candidate = byId.get(id);
    const at = (suffix) => `propulsion.${id}.${suffix}`;
    const row = {
      "engine-name": instances.length > 1 ? good(candidate?.label ?? id) : good("")
    };
    const put = (field, r, withUnit) => {
      if (r === null) {
        row[field] = missing();
        if (withUnit) row[`${field}-unit`] = missing();
        return;
      }
      row[field] = r.value;
      if (withUnit) row[`${field}-unit`] = r.unit;
    };
    put(
      "engine-rpm",
      engineReading(live, at("revolutions"), nowMs, units, "rpm", void 0, 0),
      false
    );
    put(
      "engine-coolant",
      engineReading(
        live,
        at("coolantTemperature"),
        nowMs,
        units,
        "C",
        "temperature",
        0,
        candidate?.coolantLimitC ?? null
      ),
      true
    );
    const oil = neoPageUnderwayReading(
      rawOf(live, at("oilPressure")),
      nowMs,
      neoPageCatalogFreshness(at("oilPressure"))
    );
    const rpm = neoPageUnderwayReading(
      rawOf(live, at("revolutions")),
      nowMs,
      neoPageCatalogFreshness(at("revolutions"))
    );
    if (oil.lastValue === null) {
      row["engine-oil"] = missing();
      row["engine-oil-unit"] = missing();
    } else {
      const lost = oil.value !== null && neoPageOilPressureLostFor(oil.value, rpm.value);
      const tone = oil.value === null ? void 0 : neoPageLiveTone(oil, lost ? "bad" : void 0);
      row["engine-oil"] = {
        text: oil.value === null ? DASH : neoPageFormatValue(Math.round(oil.value)),
        quality: qualityOf(oil.freshness),
        state: lost ? "lost" : "ok",
        tone: tone ?? null
      };
      row["engine-oil-unit"] = good("hPa");
    }
    put(
      "engine-load",
      engineReading(live, at("engineLoad"), nowMs, units, "%", void 0, 0),
      false
    );
    put(
      "engine-fuel-rate",
      engineReading(live, at("fuel.rate"), nowMs, units, "L/h", "volume", 1),
      true
    );
    put(
      "engine-exhaust",
      engineReading(
        live,
        at("exhaustTemperature"),
        nowMs,
        units,
        "C",
        "temperature",
        0
      ),
      true
    );
    put(
      "engine-alternator",
      engineReading(
        live,
        at("alternatorVoltage"),
        nowMs,
        units,
        "V",
        void 0,
        1
      ),
      false
    );
    const hours = neoPageCounterReadout(
      neoPageUnderwayReading(
        rawOf(live, at("runTime")),
        nowMs,
        neoPageCatalogFreshness(at("runTime"))
      )
    );
    row["engine-hours"] = hours === null ? missing() : good(neoPageFormatValue(Math.round(hours.value / 3600)));
    return row;
  });
  const probeRows = probes.map((probe) => {
    const reading = underway(live, probe.path, nowMs, NEO_PAGE_SLOW_FRESHNESS);
    const status = probe.statusPath === null ? null : live(probe.statusPath)?.value;
    const faulted = neoPageProbeIsFaulted(status);
    const display = faulted || reading.value === null ? null : formatDisplay(
      reading.value,
      reading.unit ?? "C",
      "temperature",
      units
    );
    const note = faulted ? typeof status === "string" && status.trim().length > 0 ? status.trim() : "sensor fault" : reading.freshness === "stale" ? "no signal" : null;
    return {
      "probe-label": good(probe.label),
      "probe-value": {
        text: display?.text ?? DASH,
        quality: display === null ? "missing" : qualityOf(reading.freshness),
        tone: faulted ? "warn" : null
      },
      "probe-unit": display === null ? missing() : good(display.unit),
      "probe-state": good("", { state: faulted ? "faulted" : "ok" }),
      "probe-note": note === null ? missing() : good(note)
    };
  });
  const resting = instances.length === 0 ? value.resting.slice(0, 4) : [];
  const restRows = resting.flatMap((e) => {
    const path = `propulsion.${e.id}.runTime`;
    const hours = neoPageCounterReadout(
      neoPageUnderwayReading(
        rawOf(live, path),
        nowMs,
        neoPageCatalogFreshness(path)
      )
    );
    return hours === null ? [] : [
      {
        "rest-label": good(e.label),
        "rest-hours": good(
          neoPageFormatValue(Math.round(hours.value / 3600))
        )
      }
    ];
  });
  return {
    card: emptyCard("Engine"),
    fields: {
      none: instances.length === 0 ? good(neoPageNoEngineLine(lastHeardMs, locale, timeZone)) : missing(),
      dense: good("", { state: dense ? "on" : "off" })
    },
    rows: { engine: engines, probe: probeRows, rest: restRows },
    scales: {}
  };
}
function trafficFigures(value, units) {
  const targets = value.targets.slice(0, Math.min(value.maxTargets, 20));
  let state = value.state;
  let message = null;
  if (value.state === "loading") message = "Listening\u2026";
  else if (value.state === "error")
    message = "Traffic unavailable \u2014 cannot reach the boat's receiver.";
  else if (value.state === "no-position")
    message = "Traffic unavailable \u2014 no current position fix, so nothing can be ranged.";
  else if (targets.length === 0) {
    state = "none";
    message = "No vessels heard.";
  }
  const shown = value.state === "ready" ? targets : [];
  return {
    card: {
      ...emptyCard("Traffic"),
      note: "What this boat's receiver hears, nearest first. It cannot see vessels that are not transmitting."
    },
    fields: {
      state: good("", { state }),
      message: message === null ? missing() : good(message)
    },
    rows: {
      traffic: shown.map((t) => {
        const range = t.distanceM === null ? null : formatTargetRange(t.distanceM, units);
        const sog = t.sogKn === null ? null : formatDisplay(t.sogKn, "kn", "speed", units);
        return {
          "traffic-name": good(t.name ?? `MMSI ${t.mmsi}`),
          "traffic-range": valueFig(range?.text ?? null),
          "traffic-range-unit": range === null ? missing() : good(range.unit),
          "traffic-sog": sog === null ? missing() : good(sog.text),
          "traffic-sog-unit": sog === null ? missing() : good(sog.unit),
          "traffic-age": t.ageSeconds > 120 ? good(neoPageAgoLabel(t.ageSeconds * 1e3)) : good("")
        };
      })
    },
    scales: {}
  };
}
function reservesFigures(value, live, nowMs) {
  const fuel = underway(
    live,
    value.fuel?.path ?? null,
    nowMs,
    NEO_PAGE_SLOW_FRESHNESS
  );
  const soc = underway(
    live,
    value.socPath,
    nowMs,
    value.socPath === null ? NEO_PAGE_SLOW_FRESHNESS : neoPageCatalogFreshness(value.socPath)
  );
  const current = underway(
    live,
    value.currentPath,
    nowMs,
    neoPageCatalogFreshness(value.currentPath)
  );
  const charging = current.value === null || Math.abs(current.value) <= 0.5 ? null : current.value > 0;
  const aboveReserve = fuel.value === null ? null : Math.max(0, fuel.value - value.reservePct);
  const flowTone = neoPageLiveTone(
    current,
    charging ? "good" : void 0
  );
  return {
    card: emptyCard("Reserves"),
    fields: {
      "fuel-label": good(value.fuel?.name ?? "Fuel"),
      fuel: {
        text: fuel.value === null ? DASH : neoPageFormatValue(fuel.value),
        quality: qualityOf(fuel.freshness)
      },
      "fuel-sub": aboveReserve === null ? missing() : good(`${aboveReserve.toFixed(0)}% above reserve`),
      "bank-label": good(value.bank.name ?? "House"),
      current: {
        text: current.value === null ? DASH : neoPageFormatValue(current.value),
        quality: qualityOf(current.freshness)
      },
      charge: soc.value === null ? missing() : good(`${soc.value.toFixed(0)}% charge`),
      flow: charging === null ? missing("", { tone: null }) : good(charging ? "charging" : "discharging", {
        state: charging ? "charging" : "discharging",
        tone: flowTone ?? null
      })
    },
    rows: {},
    scales: {}
  };
}
function neoPageModuleFigures(value, live, series, nowMs, units, timeZone, locale) {
  switch (value.module) {
    case "forecast":
      return forecastFigures(value, nowMs, units, timeZone, locale);
    case "tide":
      return tideFigures(value, nowMs, units, timeZone, locale);
    case "power-budget":
      return powerFigures(value, live, series, nowMs, units);
    case "environment-outlook":
      return environmentFigures(value, live, series, nowMs, units);
    case "tanks-budget":
      return tanksFigures(value, live, nowMs);
    case "pressure":
      return pressureFigures(value, live, series, nowMs, units);
    case "depth-safety":
      return depthFigures(value, live, series, nowMs, units);
    case "helm":
      return helmFigures(value, live, nowMs, units);
    case "engine-vitals":
      return engineFigures(value, live, nowMs, units, timeZone, locale);
    case "traffic":
      return trafficFigures(value, units);
    case "reserves":
      return reservesFigures(value, live, nowMs);
  }
}

// src/neo-page-status.ts
function dayKey(t, timeZone) {
  if (timeZone === void 0) return new Date(t).toDateString();
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(t);
}
function neoPageClockText(atMs, nowMs, timeZone, locale) {
  const sameDay = dayKey(nowMs, timeZone) === dayKey(atMs, timeZone);
  return new Date(atMs).toLocaleString(locale, {
    ...sameDay ? {} : { month: "short", day: "numeric" },
    hour: "numeric",
    minute: "2-digit",
    ...timeZone !== void 0 ? { timeZone } : {}
  });
}
function neoPageCardAgeText(atMs, nowMs) {
  const s = Math.max(0, Math.round((nowMs - atMs) / 1e3));
  if (s < 60) return `${s} s ago`;
  const min = Math.round(s / 60);
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}
var NEO_PAGE_SLOW_DIM_MS = 5 * 6e4;
var NEO_PAGE_SLOW_DASH_MS = 30 * 6e4;
function slowFreshness(atMs, nowMs) {
  if (atMs === null || !Number.isFinite(atMs)) return "dash";
  const age = nowMs - atMs;
  if (age >= NEO_PAGE_SLOW_DASH_MS) return "dash";
  if (age >= NEO_PAGE_SLOW_DIM_MS) return "dim";
  return "live";
}
function qualityOf2(f) {
  return f === "live" ? "good" : f === "dim" ? "stale" : "missing";
}
function neoPageFullnessWords(pct) {
  if (pct < 5) return "Nearly empty";
  if (pct < 20) return "Low";
  if (pct < 40) return "Less than half full";
  if (pct < 60) return "About half full";
  if (pct < 90) return "More than half full";
  if (pct < 98) return "Nearly full";
  return "Full";
}
function newest(card, live) {
  const cardAt = card === null ? NaN : Date.parse(card.sampledAt);
  const fromCard = card === null || !Number.isFinite(cardAt) ? null : { value: card.value, unit: card.unit, atMs: cardAt };
  if (live === void 0) return fromCard;
  if (fromCard === null || live.atMs >= fromCard.atMs) {
    return { value: live.value, unit: live.unit ?? null, atMs: live.atMs };
  }
  return fromCard;
}
function volumeText(value, unit, units) {
  return formatQuantity(value, unit ?? "L", "volume", units);
}
function neoPageWaterFigures(snapshot, live, nowMs, units, timeZone, locale) {
  const tanks = snapshot.tanks.map((tank) => {
    const base = `tanks.freshWater.${tank.instance}`;
    const level = newest(tank.level, live(`${base}.currentLevel`));
    const volume = newest(tank.volume, live(`${base}.currentVolume`));
    return {
      key: tank.instance,
      label: tank.label,
      level: level?.value ?? null,
      levelAt: level?.atMs ?? null,
      volume: volume === null ? null : { value: volume.value, unit: volume.unit },
      capacity: tank.capacity === null ? null : { value: tank.capacity.value, unit: tank.capacity.unit }
    };
  });
  if (tanks.length === 0) {
    return {
      hero: null,
      heroQuality: "missing",
      asOf: null,
      lastHeard: null,
      fullness: null,
      attention: null,
      levelsOnly: false,
      forecast: null,
      rows: []
    };
  }
  const rows = tanks.map((t) => {
    const freshness = slowFreshness(t.levelAt, nowMs);
    const pct = t.level === null || freshness === "dash" ? null : t.level;
    const severity = neoPageTankSeverity(
      "freshWater",
      pct
    );
    let value = "\u2014";
    let unit = "%";
    if (pct !== null) {
      if (t.capacity !== null && t.volume !== null) {
        const v = volumeText(t.volume.value, t.volume.unit, units);
        value = v.text;
        unit = v.unit;
      } else {
        value = Math.round(pct).toString();
      }
    }
    const age = freshness === "dim" && t.levelAt !== null ? neoPageCardAgeText(t.levelAt, nowMs) : null;
    return {
      label: t.label,
      value,
      unit,
      pct,
      severity,
      freshness,
      quality: qualityOf2(freshness),
      age
    };
  });
  const dashedAt = tanks.filter((t, i) => rows[i].freshness === "dash" && t.levelAt !== null).map((t) => t.levelAt);
  const lastHeard = dashedAt.length === 0 ? null : `No reading since ${neoPageClockText(Math.max(...dashedAt), nowMs, timeZone, locale)}.`;
  const reporting = tanks.filter((_, i) => rows[i].pct !== null);
  const oldestAt = Math.min(...tanks.map((t) => t.levelAt ?? -Infinity));
  const heroFreshness = slowFreshness(
    Number.isFinite(oldestAt) ? oldestAt : null,
    nowMs
  );
  let hero = null;
  let total = null;
  let levelsOnly = false;
  if (tanks.length === 1) {
    total = rows[0].pct;
    const t = tanks[0];
    if (total !== null && t.capacity !== null && t.volume !== null) {
      const v = volumeText(t.volume.value, t.volume.unit, units);
      const cap = volumeText(t.capacity.value, t.capacity.unit, units);
      hero = { value: v.text, unit: v.unit, of: `of ${cap.text} ${cap.unit}` };
    } else if (total !== null) {
      hero = { value: Math.round(total).toString(), unit: "%", of: null };
    }
  } else {
    const sameUnit = new Set(tanks.map((t) => t.capacity?.unit ?? null)).size === 1;
    const sized = reporting.length === tanks.length && tanks.every((t) => t.capacity !== null);
    if (sized && sameUnit) {
      const cap = tanks.reduce((sum, t) => sum + t.capacity.value, 0);
      const held = tanks.reduce(
        (sum, t) => sum + t.level / 100 * t.capacity.value,
        0
      );
      if (cap > 0) {
        total = held / cap * 100;
        const v = volumeText(held, tanks[0].capacity.unit, units);
        const c = volumeText(cap, tanks[0].capacity.unit, units);
        hero = { value: v.text, unit: v.unit, of: `of ${c.text} ${c.unit}` };
      }
    } else {
      levelsOnly = true;
    }
  }
  const states = tanks.map((t, i) => ({
    label: t.label,
    severity: rows[i].severity,
    goodHigh: true
  }));
  const attentionText = neoPageTankAttentionLine(states);
  const toneStates = states.map(
    (s, i) => rows[i].freshness === "live" || s.severity === "ok" ? s : { ...s, severity: "nosig" }
  );
  const days = snapshot.forecast.daysLeft;
  return {
    hero,
    heroQuality: qualityOf2(heroFreshness),
    asOf: heroFreshness === "dim" && Number.isFinite(oldestAt) ? `as of ${neoPageCardAgeText(oldestAt, nowMs)}` : null,
    lastHeard,
    fullness: total === null ? null : `${neoPageFullnessWords(total)}.`,
    attention: attentionText === null ? null : { text: attentionText, tone: neoPageTankAttentionTone(toneStates) },
    levelsOnly,
    forecast: days === null || !Number.isFinite(days) ? null : days < 1 ? "Less than a day left at this use." : `About ${Math.round(days)} ${Math.round(days) === 1 ? "day" : "days"} left at this use.`,
    rows: rows.map(({ freshness: _f, ...row }) => row)
  };
}
function stateFor(archetype) {
  if (archetype === "anchored") return "anchored";
  if (archetype === "underway") return "underway";
  if (archetype === "docked") return "docked";
  return "unknown";
}
function archetypeOf(mode) {
  const m = mode.toLowerCase();
  if (m === "anchored" || m === "moored") return "anchored";
  if (m === "underway" || m === "sailing" || m === "motoring")
    return "underway";
  if (m === "docked") return "docked";
  return null;
}
var CANT_TELL = "The boat hasn\u2019t said what it\u2019s doing, or can\u2019t tell right now.";
function staleCaption(lastSeenAt, nowMs, timeZone, locale) {
  const at = lastSeenAt === null ? NaN : Date.parse(lastSeenAt);
  return Number.isFinite(at) ? `The boat hasn\u2019t been heard from since ${neoPageClockText(at, nowMs, timeZone, locale)}.` : "The boat hasn\u2019t been heard from lately.";
}
function sinceCaption(since, nowMs, timeZone, locale) {
  const at = since === null ? NaN : Date.parse(since);
  return Number.isFinite(at) ? `Since ${neoPageClockText(at, nowMs, timeZone, locale)}` : "";
}
function neoPageModeStatus(reported, snapshot, nowMs, timeZone, locale) {
  const cantTell = {
    word: "Can\u2019t tell",
    caption: CANT_TELL,
    tag: null,
    state: "unknown",
    quality: "missing"
  };
  if (reported !== null) {
    switch (reported.kind) {
      case "reported":
        return {
          word: reported.mode,
          caption: sinceCaption(reported.since, nowMs, timeZone, locale),
          tag: reported.detectedBy === "manual" ? "You set this" : "Boat decides",
          state: stateFor(reported.archetype),
          quality: "good"
        };
      case "stale":
        return {
          word: reported.lastKnownMode === null ? "Not heard from" : `Last reported: ${reported.lastKnownMode}`,
          caption: staleCaption(
            reported.boatLastSeenAt,
            nowMs,
            timeZone,
            locale
          ),
          tag: null,
          state: stateFor(
            reported.lastKnownMode === null ? null : archetypeOf(reported.lastKnownMode)
          ),
          quality: "stale"
        };
      case "unknown":
        return cantTell;
    }
  }
  if (snapshot === null) return cantTell;
  if (snapshot.status === "ready" && snapshot.mode !== null) {
    return {
      word: snapshot.mode,
      caption: sinceCaption(snapshot.since, nowMs, timeZone, locale),
      tag: snapshot.detectedBy === "manual" ? "You set this" : "Boat decides",
      state: stateFor(snapshot.archetype),
      quality: "good"
    };
  }
  if (snapshot.status === "stale") {
    return {
      word: snapshot.mode === null ? "Not heard from" : `Last reported: ${snapshot.mode}`,
      caption: staleCaption(snapshot.boatLastSeenAt, nowMs, timeZone, locale),
      tag: null,
      state: stateFor(snapshot.archetype),
      quality: "stale"
    };
  }
  return cantTell;
}
function neoPageAnchorWord(status) {
  switch (status.status) {
    case "off":
      return { text: "Off", tone: "muted" };
    case "holding":
      return status.reasons.includes("near_radius") ? { text: "Near the edge", tone: "warn" } : { text: "Holding", tone: "good" };
    case "drag_suspected":
    case "outside":
    case "alarm":
      return { text: "Dragging", tone: "bad" };
    case "unknown":
      return { text: "Can\u2019t tell", tone: "muted" };
  }
}
var ANCHOR_NOTES = [
  ["boat_offline", "Neomaris hasn\u2019t heard from the boat lately."],
  ["distance_stale", "The boat\u2019s distance from the anchor is old."],
  ["no_distance", "The boat reports no distance from the anchor."],
  ["no_radius", "The boat reports no safe circle."]
];
function neoPageAnchorStatus(status, units) {
  const short = (m) => {
    if (m === null) {
      return {
        value: "\u2014",
        unit: formatQuantity(0, "m", "shortDistance", units).unit
      };
    }
    const f = formatQuantity(m, "m", "shortDistance", units);
    return { value: f.text, unit: f.unit };
  };
  const word = neoPageAnchorWord(status);
  const reason = ANCHOR_NOTES.find(([code]) => status.reasons.includes(code));
  if (!status.enabled) {
    const unsure = status.status !== "off";
    return {
      word: word.text,
      tone: word.tone,
      state: status.status,
      on: false,
      distance: short(null),
      circle: short(null),
      note: unsure ? `${reason === void 0 ? "" : `${reason[1]} `}It was off when the boat was last heard.` : "The anchor watch is off.",
      ratio: null,
      quality: unsure ? "stale" : "good"
    };
  }
  let note = reason?.[1] ?? null;
  if (note === null && status.depthBand !== null) {
    const low = formatQuantity(status.depthBand.lowM, "m", "depth", units);
    const high = formatQuantity(status.depthBand.highM, "m", "depth", units);
    note = `Depth here ${low.text}\u2013${high.text} ${high.unit}`;
  }
  const ratio = status.distanceM !== null && status.radiusM !== null && status.radiusM > 0 ? Math.max(0, Math.min(1, status.distanceM / status.radiusM)) : null;
  const muted = status.stale || status.status === "unknown";
  return {
    word: word.text,
    tone: word.tone,
    state: status.status,
    on: true,
    distance: short(status.distanceM),
    circle: short(status.radiusM),
    note,
    ratio,
    quality: status.status === "unknown" ? "missing" : muted ? "stale" : "good"
  };
}

// src/neo-page-data.ts
var SI_TO_STORE = {
  ratio: { unit: "%", factor: 100 },
  m3: { unit: "L", factor: 1e3 }
};
var ENGINE_HOURS_PATH = /^propulsion\.[^.]+\.runTime$/;
function neoPageIsEngineHours(path, unit) {
  return unit === "s" && ENGINE_HOURS_PATH.test(path);
}
function neoPageDisplayReading(value, unit, path, units) {
  if (neoPageIsEngineHours(path, unit))
    return { value: value / 3600, unit: "h" };
  const si = unit === null ? void 0 : SI_TO_STORE[unit];
  const storeValue = si === void 0 ? value : value * si.factor;
  const storeUnit = si === void 0 ? unit : si.unit;
  return toDisplay(storeValue, storeUnit, categoryFor(path, storeUnit), units);
}
function neoPageDisplayReadingText(value, unit, path, units) {
  if (neoPageIsEngineHours(path, unit)) {
    return { text: String(Math.round(value / 3600)), unit: "h" };
  }
  const si = unit === null ? void 0 : SI_TO_STORE[unit];
  const storeValue = si === void 0 ? value : value * si.factor;
  const storeUnit = si === void 0 ? unit : si.unit;
  return formatDisplay(
    storeValue,
    storeUnit,
    categoryFor(path, storeUnit),
    units
  );
}
function neoPageInStoreUnit(value, unit, storeUnit) {
  if (unit === null || unit === "" || storeUnit === null || storeUnit === "" || unit === storeUnit)
    return value;
  const si = SI_TO_STORE[unit];
  return si !== void 0 && si.unit === storeUnit ? value * si.factor : null;
}
var UNIT_CHOICES = {
  distance: DISTANCE_UNITS,
  shortDistance: SHORT_DISTANCE_UNITS,
  speed: SPEED_UNITS,
  depth: DEPTH_UNITS,
  temperature: TEMPERATURE_UNITS,
  pressure: PRESSURE_UNITS,
  volume: VOLUME_UNITS
};
function readingCategory(path, storeUnit) {
  if (neoPageIsEngineHours(path, storeUnit)) return void 0;
  const si = storeUnit === null ? void 0 : SI_TO_STORE[storeUnit];
  return categoryFor(path, si === void 0 ? storeUnit : si.unit);
}
function neoPageReadingUnitChoices(path, storeUnit) {
  const category = readingCategory(path, storeUnit);
  return category === void 0 ? [] : UNIT_CHOICES[category];
}
function neoPageReadingUnits(units, reading) {
  if (reading.unit === void 0) return units;
  const category = readingCategory(reading.path, reading.storeUnit);
  if (category === void 0) return units;
  if (!UNIT_CHOICES[category].includes(reading.unit)) return units;
  return { ...units, [category]: reading.unit };
}
function finite8(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function decimalsOf(text3) {
  return (text3.split(".")[1] ?? "").length;
}
function neoPageDisplayLine(path, storeUnit, units, sample = null) {
  if (isSecondsUnit(storeUnit) && !neoPageIsEngineHours(path, storeUnit)) {
    const scale = durationScale(sample ?? 0);
    const shown = Math.abs(sample ?? 0) / scale.seconds;
    return {
      a: 1 / scale.seconds,
      b: 0,
      decimals: shown > 0 && shown < 10 ? 1 : 0,
      duration: true
    };
  }
  const zero = neoPageDisplayReading(0, storeUnit, path, units).value;
  const one = neoPageDisplayReading(1, storeUnit, path, units).value;
  const decimals = sample === null ? decimalsOf(neoPageDisplayReadingText(0, storeUnit, path, units).text) : decimalsOf(
    neoPageDisplayReadingText(sample, storeUnit, path, units).text
  );
  return { a: one - zero, b: zero, decimals };
}
function neoPageReadingNow(reading, sample, nowMs, accountUnits) {
  const units = neoPageReadingUnits(accountUnits, reading);
  const durationText = isSecondsUnit(reading.storeUnit) && !neoPageIsEngineHours(reading.path, reading.storeUnit);
  const unitLabel2 = durationText ? "" : neoPageDisplayReading(0, reading.storeUnit, reading.path, units).unit;
  const n = finite8(sample?.value);
  const atMs = sample?.atMs ?? null;
  const measured = atMs !== null ? { measuredAt: atMs } : {};
  if (n === null || atMs === null) {
    return {
      value: null,
      storeValue: null,
      text: "",
      unit: unitLabel2,
      quality: "missing",
      ...measured
    };
  }
  const f = quickReadingFreshness(reading.path);
  const age = Math.max(0, nowMs - atMs);
  if (age > f.dashAfterMs) {
    return {
      value: null,
      storeValue: null,
      text: "",
      unit: unitLabel2,
      quality: "missing",
      ...measured
    };
  }
  const unit = sample?.unit === null || sample?.unit === "" || sample?.unit === void 0 ? reading.storeUnit : sample.unit;
  const shown = neoPageDisplayReading(n, unit, reading.path, units);
  return {
    value: shown.value,
    storeValue: neoPageInStoreUnit(n, unit, reading.storeUnit),
    text: neoPageDisplayReadingText(n, unit, reading.path, units).text,
    unit: durationText ? "" : shown.unit,
    quality: age > f.dimAfterMs ? "stale" : "good",
    ...measured
  };
}
var MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];
function neoPageBarLabel(key, by) {
  const [, m, d] = key.split("-").map(Number);
  if (by === "day") return String(d);
  return `${d} ${MONTHS[(m ?? 1) - 1]}`;
}
function neoPageBarValue(value, unit, units) {
  if (unit === "h") return { value, unit: "h" };
  return toDisplay(value * 1852, "m", "distance", units);
}
var RESULT_PLURALS = {
  person: "people",
  day: "days",
  week: "weeks"
};
function resultCategory(symbol, value, compound) {
  if (symbol === "L") return "volume";
  if (symbol === "kn") return "speed";
  if (symbol === "m")
    return compound || Math.abs(value) >= 1e3 ? "distance" : "shortDistance";
  return void 0;
}
function resultNumber(value) {
  const abs = Math.abs(value);
  const decimals = Number.isInteger(value) ? 0 : abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  const text3 = value.toFixed(decimals);
  return decimals === 0 ? text3 : text3.replace(/\.?0+$/, "");
}
function neoPageResultText(value, unit, units) {
  if (!Number.isFinite(value)) return "\u2014";
  if (unit === null || unit.trim() === "") return resultNumber(value);
  const [first = "", ...bottom] = unit.split("/");
  const top = first === "" ? [] : first.split("\xB7");
  const compound = top.length + bottom.length > 1;
  if (!compound && top.length === 1) {
    const symbol = top[0];
    const category = resultCategory(symbol, value, false);
    if (category !== void 0) {
      const shown2 = formatDisplay(value, symbol, category, units);
      return shown2.unit === "" ? shown2.text : `${shown2.text} ${shown2.unit}`;
    }
    if (symbol === "s") return formatDurationSeconds(value);
    if (symbol === "min") return formatDurationSeconds(value * 60);
    if (symbol === "%") return `${Math.round(value)} %`;
    if (symbol === "V") return `${value.toFixed(1)} V`;
    const plural = RESULT_PLURALS[symbol];
    const label2 = plural !== void 0 && value !== 1 ? plural : symbol;
    return `${resultNumber(value)} ${label2}`;
  }
  let shown = value;
  const over = [];
  const under = [];
  for (const [symbols, above] of [
    [top, true],
    [bottom, false]
  ]) {
    for (const symbol of symbols) {
      const category = resultCategory(symbol, value, true);
      let label2 = symbol;
      if (category !== void 0) {
        const one = toDisplay(1, symbol, category, units).value;
        shown = above ? shown * one : shown / one;
        label2 = unitLabel(category, units[category]);
      }
      (above ? over : under).push(label2);
    }
  }
  return `${resultNumber(shown)} ${over.join("\xB7")}${under.map((b) => `/${b}`).join("")}`;
}
function liveNumber(latest) {
  return (path) => {
    const sample = latest(path);
    const value = finite8(sample?.value);
    if (sample === void 0 || value === null || sample.atMs === null)
      return void 0;
    return { value, atMs: sample.atMs, unit: sample.unit };
  };
}
function neoPageData(input) {
  const { view, units, nowMs, latest } = input;
  const timeZone = input.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const readings = {};
  for (const [name, reading] of Object.entries(view.readings)) {
    const now = neoPageReadingNow(reading, latest(reading.path), nowMs, units);
    if (view.v === 2) {
      const named = view.readings[name];
      readings[name] = {
        ...now,
        label: named.label,
        ...named.equipment !== null ? { equipment: named.equipment } : {},
        ...named.device !== null ? { device: named.device } : {},
        display: neoPageDisplayLine(
          named.path,
          named.storeUnit,
          neoPageReadingUnits(units, named),
          now.storeValue
        )
      };
      if (!named.available) {
        readings[name] = {
          ...readings[name],
          value: null,
          storeValue: null,
          text: "",
          quality: "missing"
        };
      }
    } else {
      readings[name] = now;
    }
  }
  const data = {
    theme: input.theme,
    size: input.size,
    now: nowMs,
    readings,
    faces: [...input.faces ?? []],
    ...input.timeZone !== void 0 ? { timeZone: input.timeZone } : {},
    ...input.locale !== void 0 ? { locale: input.locale } : {},
    ...input.expanded === true ? { expanded: true } : {},
    ...input.slot !== void 0 ? { slot: input.slot } : {}
  };
  if (view.v !== 2) return data;
  const freshness = neoPageFreshness(
    Object.values(view.readings).map((r) => ({
      path: r.path,
      atMs: latest(r.path)?.atMs ?? null
    })),
    nowMs,
    {
      record: view.template?.id === "instrument.chart",
      timeZone,
      ...input.locale !== void 0 ? { locale: input.locale } : {}
    }
  );
  if (freshness !== null) data.freshness = freshness;
  data.series = { ...input.series ?? {} };
  const feeds = {};
  for (const [id, value] of Object.entries(view.feeds)) {
    if (value === null) {
      feeds[id] = null;
    } else if (value.kind === "bars") {
      const sample = neoPageBarValue(0, value.unit, units);
      const owner = (n) => n === null ? null : neoPageBarValue(n, value.unit, units).value;
      const total = owner(value.total);
      feeds[id] = {
        kind: "bars",
        unit: sample.unit,
        total: total === null ? "\u2014" : total.toFixed(1),
        rawTotal: total,
        engine: value.engineLabel ?? "",
        bars: value.bars.map((b) => {
          const raw = owner(b.value);
          return {
            value: raw === null ? "\u2014" : raw.toFixed(1),
            raw,
            label: neoPageBarLabel(b.key, value.by),
            partial: b.partial
          };
        })
      };
    } else if (value.kind === "water") {
      feeds[id] = {
        kind: "water",
        figures: neoPageWaterFigures(
          value.snapshot,
          liveNumber(latest),
          nowMs,
          units,
          timeZone,
          input.locale
        )
      };
    } else if (value.value === null || value.problem !== null) {
      feeds[id] = null;
    } else {
      const telemetry = input.moduleSeries?.[id] ?? {};
      const series = { ...telemetry };
      if (value.value.module === "forecast" || value.value.module === "tide") {
        for (const model of value.value.series)
          series[model.field] = model.series;
      }
      feeds[id] = {
        kind: "module",
        figures: neoPageModuleFigures(
          value.value,
          (path) => {
            const sample = latest(path);
            return sample === void 0 || sample.atMs === null ? void 0 : { value: sample.value, atMs: sample.atMs, unit: sample.unit };
          },
          telemetry,
          nowMs,
          units,
          timeZone,
          input.locale
        ),
        series
      };
    }
  }
  data.feeds = feeds;
  const statuses = {};
  if (view.statuses.includes("mode")) {
    const mode = input.statuses?.mode;
    statuses.mode = mode === void 0 || mode === null ? null : neoPageModeStatus(
      mode.reported,
      mode.snapshot,
      nowMs,
      timeZone,
      input.locale
    );
  }
  if (view.statuses.includes("anchor")) {
    const anchor = input.statuses?.anchor;
    statuses.anchor = anchor === void 0 || anchor === null ? null : neoPageAnchorStatus(anchor, units);
  }
  data.statuses = statuses;
  if (input.results !== void 0) {
    const results = {};
    for (const [id, result] of Object.entries(input.results)) {
      results[id] = {
        text: neoPageResultText(result.value, result.unit, units)
      };
    }
    data.results = results;
  }
  return data;
}

// src/operator-token.ts
var OPERATOR_TOKEN_PROTOCOL_VERSION = 1;
var OPERATOR_TOKEN_SET_COMMAND = "operator_token_set";
var OPERATOR_TOKEN_STATE_CLASS = "operator_token_state";
var OPERATOR_TOKEN_CLEAR_COMMAND = "operator_token_clear";
var CLEAR_REASONS = ["deregistered", "decommissioned", "no_owner"];
function parseOperatorTokenClearPayload(raw) {
  const value = asObject3(raw);
  if (!value) return rejected3("payload must be an object");
  if (value.v !== OPERATOR_TOKEN_PROTOCOL_VERSION) {
    return rejected3("unsupported operator token clear version");
  }
  if (typeof value.reason !== "string" || !CLEAR_REASONS.includes(value.reason)) {
    return rejected3("unknown clear reason");
  }
  return {
    status: "accepted",
    payload: {
      v: OPERATOR_TOKEN_PROTOCOL_VERSION,
      reason: value.reason
    }
  };
}
var OPERATOR_TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,128}$/;
var OPERATOR_TOKEN_DIGEST_PATTERN = /^[0-9a-f]{16}$/;
function operatorTokenDigest(token) {
  return sha256Hex(token).slice(0, 16);
}
function parseOperatorTokenSetPayload(raw) {
  const value = asObject3(raw);
  if (!value) return rejected3("payload must be an object");
  if (value.v !== OPERATOR_TOKEN_PROTOCOL_VERSION) {
    return rejected3(
      `unsupported operator token version (expected ${OPERATOR_TOKEN_PROTOCOL_VERSION})`
    );
  }
  if (!isVersion(value.version)) {
    return rejected3("version must be a positive integer");
  }
  if (typeof value.token !== "string" || !OPERATOR_TOKEN_PATTERN.test(value.token)) {
    return rejected3(
      "token must be 32 to 128 characters of letters, digits, - and _"
    );
  }
  if (!isTimestamp4(value.issuedAt)) {
    return rejected3("issuedAt must be an ISO-8601 timestamp");
  }
  return {
    status: "accepted",
    payload: {
      v: OPERATOR_TOKEN_PROTOCOL_VERSION,
      version: value.version,
      token: value.token,
      issuedAt: value.issuedAt
    }
  };
}
function parseOperatorTokenStatePayload(raw) {
  const value = asObject3(raw);
  if (!value) return rejected3("payload must be an object");
  if (value.v !== OPERATOR_TOKEN_PROTOCOL_VERSION) {
    return rejected3("unsupported operator token state version");
  }
  if (value.version !== null && !isVersion(value.version)) {
    return rejected3("version must be a positive integer or null");
  }
  const digest = value.digest ?? null;
  if (digest !== null && (typeof digest !== "string" || !OPERATOR_TOKEN_DIGEST_PATTERN.test(digest))) {
    return rejected3("digest must be 16 lowercase hex digits or null");
  }
  if (digest === null !== (value.version === null)) {
    return rejected3("digest and version go together");
  }
  if (value.unreadable !== void 0 && typeof value.unreadable !== "boolean") {
    return rejected3("unreadable must be a boolean");
  }
  if (value.unreadable === true && value.version !== null) {
    return rejected3("an unreadable record has no version");
  }
  if (!isTimestamp4(value.at)) {
    return rejected3("at must be an ISO-8601 timestamp");
  }
  return {
    status: "accepted",
    payload: {
      v: OPERATOR_TOKEN_PROTOCOL_VERSION,
      version: value.version,
      digest,
      ...value.unreadable === true ? { unreadable: true } : {},
      at: value.at
    }
  };
}
function isVersion(v) {
  return typeof v === "number" && Number.isSafeInteger(v) && v >= 1;
}
function asObject3(raw) {
  return raw !== null && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
}
function isTimestamp4(v) {
  return typeof v === "string" && v.length <= 64 && /^\d{4}-\d{2}-\d{2}T/.test(v) && Number.isFinite(Date.parse(v));
}
function rejected3(reason) {
  return { status: "rejected", reason };
}

// src/protect-config.ts
var PROTECT_CONFIG_VERSION = 1;
var PROTECT_CREDENTIALS_COMMAND = "protect_credentials";
var PROTECT_PIN_CONFIRM_COMMAND = "protect_pin_confirm";
var PROTECT_CONFIG_ACK_CLASS = "protect_config_ack";
var PROTECT_CONFIG_STATE_CLASS = "protect_config_state";
var PROTECT_API_KEY_MIN = 16;
var PROTECT_API_KEY_MAX = 256;
var PROTECT_API_KEY_PATTERN = /^[\x21-\x7e]{16,256}$/;
var PROTECT_HOST_MAX = 253;
var PROTECT_USER_ID_MAX = 200;
var PROTECT_COMMAND_KEY_MAX = 200;
var PROTECT_DETAIL_MAX = 500;
var SHA256_HEX3 = /^[0-9a-f]{64}$/;
var PROTECT_CREDENTIALS_ACTIONS = ["set", "clear"];
function parseProtectCredentialsPayload(raw) {
  const value = asObject4(raw);
  if (!value) return rejected4("payload must be an object");
  if (value.v !== PROTECT_CONFIG_VERSION) {
    return rejected4(
      `unsupported protect credentials version (expected ${PROTECT_CONFIG_VERSION})`
    );
  }
  if (!isOneOf7(PROTECT_CREDENTIALS_ACTIONS, value.action)) {
    return rejected4("missing or unknown action");
  }
  if (typeof value.version !== "number" || !Number.isSafeInteger(value.version) || value.version < 1) {
    return rejected4("version must be a positive integer");
  }
  if (!isTimestamp5(value.issuedAt)) {
    return rejected4("issuedAt must be an ISO-8601 timestamp");
  }
  if (!isBounded5(value.sourceUserId, PROTECT_USER_ID_MAX)) {
    return rejected4("sourceUserId must be a non-empty string");
  }
  const base = {
    v: PROTECT_CONFIG_VERSION,
    version: value.version,
    issuedAt: value.issuedAt,
    sourceUserId: value.sourceUserId
  };
  if (value.action === "clear") {
    if (value.apiKey !== void 0 || value.host !== void 0) {
      return rejected4("apiKey and host are not allowed for action=clear");
    }
    return { status: "accepted", payload: { ...base, action: "clear" } };
  }
  if (typeof value.apiKey !== "string" || !PROTECT_API_KEY_PATTERN.test(value.apiKey)) {
    return rejected4(
      `apiKey must be ${PROTECT_API_KEY_MIN}-${PROTECT_API_KEY_MAX} printable characters with no spaces`
    );
  }
  let host;
  if (value.host !== void 0) {
    if (typeof value.host !== "string" || value.host.trim().length === 0 || value.host.length > PROTECT_HOST_MAX || /[\s/]/.test(value.host.trim())) {
      return rejected4("host must be an address or name on the boat network");
    }
    host = value.host.trim();
  }
  return {
    status: "accepted",
    payload: {
      ...base,
      action: "set",
      apiKey: value.apiKey,
      ...host !== void 0 ? { host } : {}
    }
  };
}
function parseProtectPinConfirmPayload(raw) {
  const value = asObject4(raw);
  if (!value) return rejected4("payload must be an object");
  if (value.v !== PROTECT_CONFIG_VERSION) {
    return rejected4(
      `unsupported protect pin confirm version (expected ${PROTECT_CONFIG_VERSION})`
    );
  }
  const sha256 = typeof value.sha256 === "string" ? value.sha256.replace(/[\s:]/g, "").toLowerCase() : "";
  if (!SHA256_HEX3.test(sha256)) {
    return rejected4("sha256 must be a SHA-256 fingerprint");
  }
  if (!isBounded5(value.sourceUserId, PROTECT_USER_ID_MAX)) {
    return rejected4("sourceUserId must be a non-empty string");
  }
  return {
    status: "accepted",
    payload: {
      v: PROTECT_CONFIG_VERSION,
      sha256,
      sourceUserId: value.sourceUserId
    }
  };
}
var PROTECT_CONFIG_ACK_COMMANDS = [
  "credentials",
  "pin_confirm"
];
var PROTECT_CONFIG_REJECT_REASONS = [
  "invalid_command",
  "not_configured_host",
  "host_not_local",
  "superseded_local",
  "cert_changed",
  "secret_store_unavailable",
  // The console did not answer a pin confirm's fresh inspect: nothing was
  // pinned, and the owner can try again (TOM-834 core handler).
  "unreachable"
];
var PROTECT_KEY_SOURCES = ["cloud", "local"];
var PROTECT_CONNECTION_STATES = [
  "connected",
  "connecting",
  "unreachable",
  "unauthorized",
  "cert_unconfirmed",
  "cert_changed",
  "not_configured"
];
function parseProtectConfigAckPayload(raw) {
  const value = asObject4(raw);
  if (!value) return rejected4("payload must be an object");
  if (value.v !== PROTECT_CONFIG_VERSION) {
    return rejected4("unsupported protect config ack version");
  }
  if (!isBounded5(value.commandKey, PROTECT_COMMAND_KEY_MAX)) {
    return rejected4("commandKey must be a non-empty string");
  }
  if (!isOneOf7(PROTECT_CONFIG_ACK_COMMANDS, value.command)) {
    return rejected4("unknown command");
  }
  if (value.command === "credentials") {
    if (typeof value.version !== "number" || !Number.isSafeInteger(value.version) || value.version < 1) {
      return rejected4("a credentials ack needs its version");
    }
  } else if (value.version !== void 0) {
    return rejected4("version is only for a credentials ack");
  }
  if (value.phase !== "applied" && value.phase !== "rejected") {
    return rejected4("phase must be applied or rejected");
  }
  if (value.phase === "rejected") {
    if (!isOneOf7(PROTECT_CONFIG_REJECT_REASONS, value.reason)) {
      return rejected4("a rejected ack needs a known reason");
    }
  } else if (value.reason !== void 0) {
    return rejected4("reason is only for a rejected ack");
  }
  if (!isTimestamp5(value.at))
    return rejected4("at must be an ISO-8601 timestamp");
  if (value.detail !== void 0 && (typeof value.detail !== "string" || value.detail.length > PROTECT_DETAIL_MAX)) {
    return rejected4(`detail must be at most ${PROTECT_DETAIL_MAX} characters`);
  }
  return {
    status: "accepted",
    payload: {
      v: PROTECT_CONFIG_VERSION,
      commandKey: value.commandKey,
      command: value.command,
      ...typeof value.version === "number" ? { version: value.version } : {},
      phase: value.phase,
      ...value.reason !== void 0 ? { reason: value.reason } : {},
      at: value.at,
      ...typeof value.detail === "string" && value.detail.length > 0 ? { detail: value.detail } : {}
    }
  };
}
function parseProtectConfigStatePayload(raw) {
  const value = asObject4(raw);
  if (!value) return rejected4("payload must be an object");
  if (value.v !== PROTECT_CONFIG_VERSION) {
    return rejected4("unsupported protect config state version");
  }
  if (typeof value.configured !== "boolean") {
    return rejected4("configured must be a boolean");
  }
  if (!nullOr2(value.host, (h) => isBounded5(h, PROTECT_HOST_MAX))) {
    return rejected4("host must be a short string or null");
  }
  if (typeof value.keyStored !== "boolean") {
    return rejected4("keyStored must be a boolean");
  }
  if (!nullOr2(value.keyLast4, (k) => typeof k === "string" && k.length === 4)) {
    return rejected4("keyLast4 must be four characters or null");
  }
  if (!nullOr2(value.keySource, (s) => isOneOf7(PROTECT_KEY_SOURCES, s))) {
    return rejected4("keySource must be cloud, local or null");
  }
  if (!nullOr2(
    value.keyVersion,
    (n) => typeof n === "number" && Number.isSafeInteger(n) && n >= 1
  )) {
    return rejected4("keyVersion must be a positive integer or null");
  }
  if (!nullOr2(value.keySetAt, isTimestamp5)) {
    return rejected4("keySetAt must be an ISO-8601 timestamp or null");
  }
  if (!isOneOf7(PROTECT_CONNECTION_STATES, value.connection)) {
    return rejected4("unknown connection state");
  }
  const cert = asObject4(value.certificate);
  if (!cert || !nullOr2(cert.observedSha256, isSha2562) || !nullOr2(cert.pinnedSha256, isSha2562) || !nullOr2(cert.observedValidTo, isTimestamp5) || !nullOr2(cert.pinnedValidTo, isTimestamp5)) {
    return rejected4("certificate must carry SHA-256s and timestamps or nulls");
  }
  if (!isTimestamp5(value.at))
    return rejected4("at must be an ISO-8601 timestamp");
  return {
    status: "accepted",
    payload: {
      v: PROTECT_CONFIG_VERSION,
      configured: value.configured,
      host: value.host,
      keyStored: value.keyStored,
      keyLast4: value.keyLast4,
      keySource: value.keySource,
      keyVersion: value.keyVersion,
      keySetAt: value.keySetAt,
      connection: value.connection,
      certificate: {
        observedSha256: cert.observedSha256,
        observedValidTo: cert.observedValidTo,
        pinnedSha256: cert.pinnedSha256,
        pinnedValidTo: cert.pinnedValidTo
      },
      at: value.at
    }
  };
}
function asObject4(raw) {
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
}
function rejected4(reason) {
  return { status: "rejected", reason };
}
function isOneOf7(values, value) {
  return values.includes(value);
}
function isBounded5(value, max) {
  return typeof value === "string" && value.length > 0 && value.length <= max;
}
function isTimestamp5(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}
function isSha2562(value) {
  return typeof value === "string" && SHA256_HEX3.test(value);
}
function nullOr2(value, ok) {
  return value === null || ok(value);
}

// src/replay-clock.ts
var DAY_MS = 864e5;
var formatters = /* @__PURE__ */ new Map();
function formatterFor(timeZone) {
  let formatter = formatters.get(timeZone);
  if (formatter === void 0) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric"
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}
function isValidTimeZone(timeZone) {
  try {
    formatterFor(timeZone);
    return true;
  } catch {
    return false;
  }
}
function parseClockTime(text3) {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(text3);
  if (match === null) return void 0;
  return { hour: Number(match[1]), minute: Number(match[2]) };
}
function localParts(instantMs, timeZone) {
  const parts = {};
  for (const part of formatterFor(timeZone).formatToParts(
    new Date(instantMs)
  )) {
    if (part.type !== "literal") parts[part.type] = Number(part.value);
  }
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second
  };
}
function offsetAt(instantMs, timeZone) {
  const local = localParts(instantMs, timeZone);
  const localAsUtc = Date.UTC(
    local.year,
    local.month - 1,
    local.day,
    local.hour,
    local.minute,
    local.second
  );
  return localAsUtc - (instantMs - (instantMs % 1e3 + 1e3) % 1e3);
}
function zonedInstant(year, month, day, hour, minute, timeZone) {
  const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  const first = wallAsUtc - offsetAt(wallAsUtc, timeZone);
  return wallAsUtc - offsetAt(first, timeZone);
}
function addDays(date, days) {
  const t = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return {
    year: t.getUTCFullYear(),
    month: t.getUTCMonth() + 1,
    day: t.getUTCDate()
  };
}
function formatDate(date) {
  const mm = String(date.month).padStart(2, "0");
  const dd = String(date.day).padStart(2, "0");
  return `${date.year}-${mm}-${dd}`;
}
function replayDayAt(nowMs, timeZone, startsAt) {
  const local = localParts(nowMs, timeZone);
  let date = { year: local.year, month: local.month, day: local.day };
  let startMs = zonedInstant(
    date.year,
    date.month,
    date.day,
    startsAt.hour,
    startsAt.minute,
    timeZone
  );
  if (startMs > nowMs) {
    date = addDays(date, -1);
    startMs = zonedInstant(
      date.year,
      date.month,
      date.day,
      startsAt.hour,
      startsAt.minute,
      timeZone
    );
  }
  const next = addDays(date, 1);
  const nextStartMs = zonedInstant(
    next.year,
    next.month,
    next.day,
    startsAt.hour,
    startsAt.minute,
    timeZone
  );
  return { startMs, nextStartMs, localDate: formatDate(date) };
}
function daysBetween(from, to) {
  const parse = (text3) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text3);
    if (match === null) throw new Error(`Not a YYYY-MM-DD date: ${text3}`);
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  };
  return Math.round((parse(to) - parse(from)) / DAY_MS);
}
var CLOCK_MAX_DAYS = 31;
function fileDaysIn(spanMs) {
  return Math.floor(Math.max(0, spanMs) / DAY_MS) + 1;
}
function fileDayOn(localDate, days, anchor, epochDate) {
  const n = anchor !== void 0 ? anchor.day - 1 + daysBetween(anchor.date, localDate) : daysBetween(epochDate ?? "1970-01-01", localDate);
  const fileDay = (n % days + days) % days;
  return { fileDay, pass: Math.max(0, Math.floor(n / days)) };
}
function isLocalDate(text3) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text3);
  if (match === null) return false;
  const t = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  );
  return formatDate({
    year: t.getUTCFullYear(),
    month: t.getUTCMonth() + 1,
    day: t.getUTCDate()
  }) === text3;
}
var HOUR_MS2 = 36e5;
function changeInstant(loMs, hiMs, offset, timeZone) {
  let lo = loMs;
  let hi = hiMs;
  while (hi - lo > 1e3) {
    const mid = lo + Math.floor((hi - lo) / 2e3) * 1e3;
    if (offsetAt(mid, timeZone) === offset) lo = mid;
    else hi = mid;
  }
  return hi;
}
function replayDayMap(day, timeZone) {
  const { startMs, nextStartMs } = day;
  const span = nextStartMs - startMs;
  if (timeZone === null) {
    return {
      startMs,
      nextStartMs,
      segments: [{ fromOffsetMs: 0, toOffsetMs: span, shiftMs: 0 }]
    };
  }
  const o0 = offsetAt(startMs, timeZone);
  const pieces = [
    { from: startMs, offset: o0 }
  ];
  let prev = startMs;
  let prevOffset = o0;
  for (let at = startMs + HOUR_MS2; ; at += HOUR_MS2) {
    const probe = Math.min(at, nextStartMs - 1);
    const o = offsetAt(probe, timeZone);
    if (o !== prevOffset) {
      pieces.push({
        from: changeInstant(prev, probe, prevOffset, timeZone),
        offset: o
      });
      prevOffset = o;
    }
    prev = probe;
    if (probe >= nextStartMs - 1) break;
  }
  const segments = [];
  let covered = 0;
  for (let i = 0; i < pieces.length; i += 1) {
    const p = pieces[i];
    const to = pieces[i + 1]?.from ?? nextStartMs;
    const shiftMs = o0 - p.offset;
    const fromOffsetMs = Math.max(p.from - startMs - shiftMs, covered);
    const toOffsetMs = to - startMs - shiftMs;
    if (toOffsetMs <= fromOffsetMs) continue;
    segments.push({ fromOffsetMs, toOffsetMs, shiftMs });
    covered = toOffsetMs;
  }
  return { startMs, nextStartMs, segments };
}
function replayInstantOf(map, offsetMs) {
  for (const s of map.segments) {
    if (offsetMs >= s.fromOffsetMs && offsetMs < s.toOffsetMs) {
      return map.startMs + offsetMs + s.shiftMs;
    }
  }
  return null;
}
function replayOffsetOf(map, instantMs) {
  const at = instantMs - map.startMs;
  let last = 0;
  for (const s of map.segments) {
    if (at < s.fromOffsetMs + s.shiftMs) return s.fromOffsetMs;
    if (at < s.toOffsetMs + s.shiftMs) return at - s.shiftMs;
    last = s.toOffsetMs;
  }
  return last;
}

// src/sim-time.ts
var SIM_TIME_MAX_RATE = 10;
function simVirtualNowMs(sim, wallNowMs) {
  const virtualMs = Date.parse(sim.virtualNowIso);
  const reportedMs = Date.parse(sim.reportedAtIso);
  return virtualMs + (wallNowMs - reportedMs) * sim.rate;
}
function parseSimTimeStatus(value) {
  if (typeof value !== "object" || value === null) return null;
  const record2 = value;
  const rate = record2.rate;
  if (typeof rate !== "number" || !Number.isFinite(rate)) return null;
  if (rate < 1 || rate > SIM_TIME_MAX_RATE) return null;
  const anchorIso = isoOrNull(record2.anchorIso);
  const virtualNowIso = isoOrNull(record2.virtualNowIso);
  const reportedAtIso = isoOrNull(record2.reportedAtIso);
  if (anchorIso === null || virtualNowIso === null || reportedAtIso === null) {
    return null;
  }
  const status = {
    rate,
    anchorIso,
    virtualNowIso,
    reportedAtIso
  };
  const loop = record2.loop;
  if (typeof loop === "number" && Number.isInteger(loop) && loop >= 1) {
    status.loop = loop;
  }
  const spanMs = record2.spanMs;
  if (typeof spanMs === "number" && Number.isFinite(spanMs) && spanMs > 0) {
    status.spanMs = spanMs;
  }
  return status;
}
function isoOrNull(value) {
  if (typeof value !== "string" || value.length === 0) return null;
  return Number.isFinite(Date.parse(value)) ? value : null;
}

// src/source-analysis.ts
function gatewayRoleToSourcePlanRole(role2) {
  switch (role2) {
    case "primary_io":
      return "primary_io";
    case "standby":
      return "standby";
    case "provides":
      return "keep_unique";
    case "redundant":
    case "disabled":
      return "redundant_disable";
    default:
      return "keep_unique";
  }
}
function buildPrimaryReason(facts, opts) {
  const bits = [];
  if (facts.deviceCount > 0) {
    bits.push(
      `${facts.deviceCount} ${facts.deviceCount === 1 ? "device" : "devices"}`
    );
  }
  if (facts.msgRate > 0) {
    bits.push(`${Math.round(facts.msgRate)} msg/s`);
  }
  bits.push(
    facts.flapCount === 0 ? "a stable connection" : `${facts.flapCount} ${facts.flapCount === 1 ? "reconnect" : "reconnects"}`
  );
  const base = bits.length > 1 || facts.deviceCount > 0 || facts.msgRate > 0 ? `Chosen as your main connection \u2014 it sees ${joinReasonBits(bits)}.` : "Chosen as the most reliable read/write gateway for this bus.";
  if (opts?.suggestion && opts.suggestion.deviceCount > facts.deviceCount) {
    return `${base} Our suggestion saw ${opts.suggestion.deviceCount} ${opts.suggestion.deviceCount === 1 ? "device" : "devices"}.`;
  }
  return base;
}
function joinReasonBits(parts) {
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

// src/telemetry-series.ts
var NEO_SERIES_REQUEST_MAX = 48;
var NEO_SERIES_SLOTS_TOTAL_MAX = 6e3;
var NEO_SERIES_SPAN_DAYS_TOTAL_MAX = 400;
var MINUTE2 = 6e4;
var HOUR2 = 36e5;
var DAY2 = 864e5;
function neoSeriesCadenceMs(spanMs) {
  if (spanMs <= 6 * HOUR2) return MINUTE2;
  if (spanMs <= 48 * HOUR2) return 5 * MINUTE2;
  if (spanMs <= 14 * DAY2) return 15 * MINUTE2;
  return HOUR2;
}
function neoSeriesGrid(fromMs, toMs, points) {
  const spanS = Math.max(1, Math.round((toMs - fromMs) / 1e3));
  const bucketS = Math.max(1, Math.ceil(spanS / Math.max(1, points)));
  const bucketMs = bucketS * 1e3;
  const startMs = Math.floor(fromMs / bucketMs) * bucketMs;
  const count2 = Math.max(0, Math.ceil((toMs - startMs) / bucketMs));
  return { bucketMs, startMs, count: count2 };
}
function neoSeriesBatches(series) {
  const batches = [];
  let current = [];
  let slots = 0;
  let spanMs = 0;
  for (const entry of series) {
    const span = parseNeoPageWindow(entry.window);
    const points = entry.points ?? NEO_PAGE_SERIES_POINTS_DEFAULT;
    if (span === null || !Number.isInteger(points) || points < NEO_PAGE_SERIES_POINTS_MIN || points > NEO_PAGE_SERIES_POINTS_MAX) {
      continue;
    }
    const length = span.kind === "rolling" ? span.ms : span.toMs - span.fromMs;
    const count2 = points + 1;
    if (current.length > 0 && (current.length + 1 > NEO_SERIES_REQUEST_MAX || slots + count2 > NEO_SERIES_SLOTS_TOTAL_MAX || spanMs + length > NEO_SERIES_SPAN_DAYS_TOTAL_MAX * DAY2)) {
      batches.push(current);
      current = [];
      slots = 0;
      spanMs = 0;
    }
    current.push(entry);
    slots += count2;
    spanMs += length;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}
function isObj14(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function finite9(value) {
  return typeof value === "number" && Number.isFinite(value);
}
function isIso3(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}
function slotFrom2(value) {
  if (value === null) return null;
  if (!Array.isArray(value) || value.length !== 4) return void 0;
  const [avg, min, max, count2] = value;
  if (!finite9(avg) || !finite9(count2) || count2 < 0) return void 0;
  if (min !== null && !finite9(min)) return void 0;
  if (max !== null && !finite9(max)) return void 0;
  return [avg, min, max, count2];
}
function resultFrom2(value) {
  if (!isObj14(value)) return null;
  const { path, window, points, from, to, bucketSeconds, unit, angle } = value;
  if (typeof path !== "string" || typeof window !== "string") return null;
  if (parseNeoPageWindow(window) === null) return null;
  if (!finite9(points) || !Number.isInteger(points)) return null;
  if (!isIso3(from) || !isIso3(to)) return null;
  if (!finite9(bucketSeconds) || bucketSeconds <= 0) return null;
  if (unit !== null && typeof unit !== "string") return null;
  if (typeof angle !== "boolean") return null;
  const problem = value.problem;
  if (problem !== null && problem !== "unknown_path" && problem !== "not_numeric") {
    return null;
  }
  if (!Array.isArray(value.slots)) return null;
  const slots = [];
  for (const entry of value.slots) {
    const slot = slotFrom2(entry);
    if (slot === void 0) return null;
    slots.push(slot);
  }
  return {
    path,
    window,
    points,
    from,
    to,
    bucketSeconds,
    unit,
    angle,
    problem,
    slots
  };
}
function parseNeoSeriesResponse(raw) {
  if (!isObj14(raw) || !isIso3(raw.at) || !Array.isArray(raw.series)) return null;
  const series = [];
  for (const entry of raw.series) {
    const result = resultFrom2(entry);
    if (result === null) return null;
    series.push(result);
  }
  return { at: raw.at, series };
}

// src/update.ts
var UPDATE_MANIFEST_DOMAIN = "tombolo.core.update.v1\n";
function manifestPlatformKey(platform, arch) {
  if (platform === "darwin" && arch === "arm64") return "darwin-arm64";
  if (platform === "darwin" && arch === "x64") return "darwin-x64";
  if (platform === "linux" && arch === "x64") return "linux-amd64";
  if (platform === "linux" && arch === "arm64") return "linux-arm64";
  if (platform === "win32" && arch === "x64") return "win32-x64";
  return null;
}
function manifestHasArtifactForRuntime(manifest, platform, arch) {
  const key = manifestPlatformKey(platform, arch);
  return key !== null && manifest.platforms?.[key] !== void 0;
}
var PINNED_UPDATE_KEYS = [
  {
    keyId: "tombolo-core-2026a",
    publicJwk: {
      kty: "OKP",
      crv: "Ed25519",
      x: "tWsJVE4JoZt7p36Z7BV8GjGGjlwRwvMmyEOCzaXghuI"
    },
    notBefore: "2026-09-01T00:00:00Z",
    notAfter: "2028-01-01T00:00:00Z"
  },
  {
    keyId: "tombolo-core-2027a",
    publicJwk: {
      kty: "OKP",
      crv: "Ed25519",
      x: "n_N1d1rdHqLyTkfuUaZOStri3KDUKolfWtdjYkyzVAA"
    },
    notBefore: "2027-01-01T00:00:00Z",
    notAfter: "2029-01-01T00:00:00Z"
  }
];

// src/user-ws-close.ts
var USER_WS_CLOSE_TOKEN_EXPIRED = 4403;
var USER_WS_CLOSE_UNAUTHORIZED = 4401;

// src/vessel-type.ts
var VESSEL_SETTINGS_OUTBOX_CLASS = "vessel_settings";
var VESSEL_TYPES = ["power", "sail"];
var VESSEL_TYPE_ALIASES = {
  sail: "sail",
  sailboat: "sail",
  sailingboat: "sail",
  sailingvessel: "sail",
  sailingyacht: "sail",
  sailing: "sail",
  power: "power",
  powerboat: "power",
  motor: "power",
  motorboat: "power",
  motoryacht: "power",
  trawler: "power"
};
function isVesselType(raw) {
  return VESSEL_TYPES.includes(raw);
}
function normalizeVesselType(raw) {
  if (typeof raw !== "string") return null;
  const key = raw.trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (key.length === 0) return null;
  return VESSEL_TYPE_ALIASES[key] ?? null;
}
var VESSEL_TYPE_COMMAND_VERSION = 1;
var VESSEL_TYPE_COMMAND_ID_MAX = 200;
var VESSEL_TYPE_COMMAND_USER_LABEL_MAX = 64;
function parseVesselTypeCommandPayload(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { status: "rejected", reason: "payload must be an object" };
  }
  const value = raw;
  if (value.v !== VESSEL_TYPE_COMMAND_VERSION) {
    return {
      status: "rejected",
      reason: `unsupported vessel type command version (expected ${VESSEL_TYPE_COMMAND_VERSION})`
    };
  }
  if (!isVesselType(value.vesselType)) {
    return {
      status: "rejected",
      reason: `vesselType must be one of ${VESSEL_TYPES.join(", ")}`
    };
  }
  if (typeof value.sourceUserId !== "string" || value.sourceUserId.length === 0 || value.sourceUserId.length > VESSEL_TYPE_COMMAND_ID_MAX) {
    return {
      status: "rejected",
      reason: "sourceUserId must be a non-empty string"
    };
  }
  const label2 = typeof value.sourceUserLabel === "string" && value.sourceUserLabel.length > 0 ? value.sourceUserLabel.slice(0, VESSEL_TYPE_COMMAND_USER_LABEL_MAX) : void 0;
  return {
    status: "accepted",
    payload: {
      v: VESSEL_TYPE_COMMAND_VERSION,
      vesselType: value.vesselType,
      sourceUserId: value.sourceUserId,
      ...label2 !== void 0 ? { sourceUserLabel: label2 } : {}
    }
  };
}

// src/ws.ts
var NATIVE_CHANNELS = [
  "data",
  "ais",
  "connection",
  "priority",
  "parents",
  "admin",
  // First-time setup wizard channel (setup_api_router_and). Forwards `SetupSessionService`
  // phase/evidence/error events to subscribers per
  // `specs/api_contracts.md §Setup → WebSocket channel`.
  "setup",
  // Vessel settings changed on the boat — including display units pushed down
  // from a cloud account (`units_set`). Lets an open client re-render without
  // polling `GET /api/settings`. Adding a channel is additive for OLD clients
  // (they never subscribe) — but `parseClientMessage` rejects a subscribe
  // WHOLESALE when any one channel is unknown, so a NEW client talking to an
  // old core must send `settings` in its OWN subscribe frame, never batched
  // with the channels it cannot afford to lose. Batched, an old core answers
  // one `error` frame and subscribes to NOTHING — a blank live view, not a
  // missing settings refresh.
  "settings"
  // Bidirectional loopback IPC channel for the supervised Neomaris Monitor child:
  // (`hivemind/Monitor_Cloud_Egress.md`, ADR-031, T-IPC-01).
];
var PROXY_CHANNELS = ["alerts", "modes", "track", "sound"];
var ALL_CHANNELS = [...NATIVE_CHANNELS, ...PROXY_CHANNELS];
var KEEPALIVE_CHANNELS = ["data", "priority"];
var SETUP_SOURCE_ANALYSIS_EVENTS = [
  "source_analysis_started",
  "source_analysis_ready",
  "source_analysis_failed"
];
var SETUP_ALARM_ANALYSIS_EVENTS = [
  "alarm_analysis_started",
  "alarm_analysis_ready",
  "alarm_analysis_failed"
];
var SETUP_ALARM_RECOMMENDATION_EVENTS = [
  "alarm_recommendations_ready"
];
function isChannel(value) {
  return typeof value === "string" && ALL_CHANNELS.includes(value);
}
function parseClientMessage(raw) {
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { type: "invalid" };
  }
  if (!parsed || typeof parsed !== "object") return { type: "invalid" };
  const msg = parsed;
  if (typeof msg.type !== "string") return { type: "invalid" };
  if (msg.type !== "subscribe" && msg.type !== "unsubscribe") {
    return { type: "unknown", receivedType: msg.type };
  }
  if (!Array.isArray(msg.channels) || !msg.channels.every(isChannel)) {
    return { type: "invalid" };
  }
  if (msg.type === "subscribe") {
    const units = msg.units === "imperial" ? "imperial" : "metric";
    const sub = {
      type: "subscribe",
      channels: msg.channels,
      units
    };
    const sessionId = parsed.sessionId;
    if (typeof sessionId === "string" && sessionId.length > 0) {
      sub.sessionId = sessionId;
    }
    return sub;
  }
  return { type: "unsubscribe", channels: msg.channels };
}
export {
  ALARMS_PAUSED_RUNTIME_REASON,
  ALARMS_PAUSE_COMMAND_ID_MAX,
  ALARMS_PAUSE_COMMAND_USER_LABEL_MAX,
  ALARMS_PAUSE_COMMAND_VERSION,
  ALARM_REGENERATE_ACTIONS,
  ALARM_REGENERATE_COMMAND_VERSION,
  ALARM_REGENERATE_ID_MAX,
  ALARM_REGENERATE_MAX_MODES,
  ALARM_REGENERATE_MAX_TICKS,
  ALARM_REGENERATE_MODES,
  ALARM_REGENERATION_PREVIEW_CLASS,
  ALARM_SEVERITIES,
  ALERT_COMMAND_ACTIONS,
  ALERT_COMMAND_ID_MAX,
  ALERT_COMMAND_USER_LABEL_MAX,
  ALERT_COMMAND_VERSION,
  ALERT_STATE_META,
  ALL_CHANNELS,
  ANCHOR_AUTO_ARM_MODES,
  ANCHOR_COMMAND_STATUS_PHASES,
  ANCHOR_WATCH_COMMAND_ACTIONS,
  ANCHOR_WATCH_COMMAND_VERSION,
  ANCHOR_WATCH_ORIGINS,
  ANCHOR_WATCH_RADIUS_MAX_M,
  ANCHOR_WATCH_RADIUS_MIN_M,
  ANCHOR_WATCH_REASONS,
  ANCHOR_WATCH_RODE_MAX_M,
  ANCHOR_WATCH_RODE_MIN_M,
  ANCHOR_WATCH_STATUS_CODES,
  BATTERY_CHEMISTRIES,
  BATTERY_CHEMISTRY_BANK_NAME_MAX,
  BATTERY_CHEMISTRY_COMMAND_ID_MAX,
  BATTERY_CHEMISTRY_COMMAND_USER_LABEL_MAX,
  BATTERY_CHEMISTRY_COMMAND_VERSION,
  BATTERY_CHEMISTRY_SETTINGS_KEY,
  BATTERY_CHEMISTRY_SNAPSHOT_MAX_BANKS,
  BATTERY_CHEMISTRY_SNAPSHOT_VERSION,
  BATTERY_CHEMISTRY_SOURCES,
  BOAT_NAMES_MAX_ENTRIES,
  BOAT_NAMES_VERSION,
  BOAT_NAME_MAX,
  BOAT_NAME_SUBJECT_MAX,
  BOAT_SYSTEMS,
  BOAT_SYSTEM_PATH_RULES,
  CAMERA_AUDIO_KINDS,
  CAMERA_EVENTS_CLASS,
  CAMERA_EVENTS_MAX_PER_FRAME,
  CAMERA_EVENTS_VERSION,
  CAMERA_EVENT_SOURCES,
  CAMERA_EVENT_TYPES,
  CAMERA_ID_MAX,
  CAMERA_INVENTORY_CLASS,
  CAMERA_INVENTORY_MAX,
  CAMERA_LAN_ACK_CLASS,
  CAMERA_LAN_CHAIN_MAX,
  CAMERA_LAN_COMMAND_KEY_MAX,
  CAMERA_LAN_CONFIG_COMMAND,
  CAMERA_LAN_CSR_MAX,
  CAMERA_LAN_DEFAULT_PORT,
  CAMERA_LAN_DETAIL_MAX,
  CAMERA_LAN_HOSTNAME_MAX,
  CAMERA_LAN_LABEL_PATTERN,
  CAMERA_LAN_REJECT_REASONS,
  CAMERA_LAN_RENEW_BEFORE_DAYS,
  CAMERA_LAN_STATE_CLASS,
  CAMERA_LAN_TOKEN_GOLDEN,
  CAMERA_LAN_TOKEN_TYPE,
  CAMERA_LAN_VERSION,
  CAMERA_LAN_VIEW_KEY_PATTERN,
  CAMERA_MODEL_MAX,
  CAMERA_NAME_MAX,
  CAMERA_PTZ_ACK_CLASS,
  CAMERA_PTZ_ACK_RESULTS,
  CAMERA_PTZ_ACTIONS,
  CAMERA_PTZ_COMMAND,
  CAMERA_PTZ_COMMAND_KEY_MAX,
  CAMERA_PTZ_FAILURE_REASONS,
  CAMERA_PTZ_HOME_SLOT,
  CAMERA_PTZ_ID_MAX,
  CAMERA_PTZ_LIST_MAX,
  CAMERA_PTZ_SLOT_MAX,
  CAMERA_PTZ_USER_ID_MAX,
  CAMERA_PTZ_VERSION,
  CAMERA_SMART_DETECT_TYPES_MAX,
  CAMERA_SMART_DETECT_TYPE_MAX,
  CAMERA_SNAPSHOTS_CLASS,
  CAMERA_SNAPSHOT_ACK_CLASS,
  CAMERA_SNAPSHOT_ACK_RESULTS,
  CAMERA_SNAPSHOT_COMMAND_KEY_MAX,
  CAMERA_SNAPSHOT_FAILURE_REASONS,
  CAMERA_SNAPSHOT_ID_PATTERN,
  CAMERA_SNAPSHOT_MAX_BYTES,
  CAMERA_SNAPSHOT_REQUEST_COMMAND,
  CAMERA_SNAPSHOT_REQUEST_ID_MAX,
  CAMERA_SNAPSHOT_REQUEST_VERSION,
  CAMERA_SNAPSHOT_USER_ID_MAX,
  CAMERA_STATES,
  CAMERA_STREAM_ACTIONS,
  CAMERA_STREAM_FAILURE_REASONS,
  CAMERA_STREAM_KEY_MAX,
  CAMERA_STREAM_MAX_TTL_MS,
  CAMERA_STREAM_QUALITIES,
  CAMERA_STREAM_SESSION_ID_MAX,
  CAMERA_STREAM_STATUS_CLASS,
  CAMERA_STREAM_STATUS_DETAIL_MAX,
  CAMERA_STREAM_STATUS_PHASES,
  CAMERA_STREAM_TOKEN_MAX,
  CAMERA_STREAM_USER_ID_MAX,
  CAMERA_STREAM_VERSION,
  CAMERA_UPLOAD_CLOSE_NO_VIEWERS,
  CAMERA_UPLOAD_CLOSE_REPLACED,
  CAMERA_UPLOAD_CLOSE_TOKEN_REJECTED,
  CAMERA_UPLOAD_CLOSE_UNAVAILABLE,
  CAMERA_UPLOAD_FRAME_KINDS,
  CAMERA_UPLOAD_FRAME_VERSION,
  CAMERA_UPLOAD_HEADER_BYTES,
  CAMERA_UPLOAD_PATH,
  CANONICAL_MODES,
  CLOCK_MAX_DAYS,
  DATA_ANSWER_KINDS,
  DEFAULT_UNIT_SETTINGS,
  DEMO_REPLAY_COMMAND_VERSION,
  DEMO_REPLAY_JUMP_COMMAND_TYPE,
  DEMO_REPLAY_MAX_DAYS,
  DEMO_REPLAY_REFUSAL_REASONS,
  DEMO_REPLAY_REQUEST_ID_MAX,
  DEMO_REPLAY_STATUS_CLASS,
  DEMO_REPLAY_TIME_ZONE_MAX,
  DEPTH_UNITS,
  DEVICE_ALERTS_CLASS,
  DEVICE_ALERTS_MAX_PER_FRAME,
  DEVICE_ALERT_KEY_MAX,
  DEVICE_ALERT_MESSAGE_MAX,
  DEVICE_ALERT_PATH_PREFIX,
  DEVICE_ALERT_SEVERITIES,
  DEVICE_ALERT_SOURCES,
  DEVICE_ALERT_STATES,
  DISTANCE_UNITS,
  ENGINE_LOGBOOK_STATUSES,
  ENGINE_SUMMARY_WINDOW_DAYS,
  EQUIPMENT_FAMILIES,
  EQUIPMENT_FAMILY_ORDER,
  FIRMWARE_DOWNLOAD_DEADLINE_MS,
  FIRMWARE_MANIFEST_DOMAIN,
  FIRMWARE_OWNER_WAIT_LIMIT_MS,
  FIRMWARE_PENDING_STALE_MS,
  FLEET_ATTRIBUTION,
  FRIEND_RANGE_M,
  GEAR_FAMILY_ORDER,
  GEAR_MAKE_MODEL_MAX,
  KEEPALIVE_CHANNELS,
  MAP_CAPTION_NEAR,
  MAP_CAPTION_SEPARATOR,
  MAP_CONTEXT_MIN_REQUEST_INTERVAL_MS,
  MAP_CONTEXT_REFRESH_AGE_MS,
  MAP_CONTEXT_REUSE_RADIUS_M,
  MAP_LOCATION_ATTRIBUTIONS_MAX,
  MAP_LOCATION_ATTRIBUTION_TEXT_MAX,
  MAP_LOCATION_ATTRIBUTION_URL_MAX,
  MAP_LOCATION_LOOKUPS,
  MAP_LOCATION_SUBJECT_KINDS,
  MAP_PLACE_GROUPS,
  MAP_PLACE_GROUP_KINDS,
  MAP_PLACE_KIND_LABELS,
  MAP_PLACE_NAME_MAX,
  MODE_COMMAND_ACTIONS,
  MODE_COMMAND_ID_MAX,
  MODE_COMMAND_KEY_MAX,
  MODE_COMMAND_STATUS_CLASS,
  MODE_COMMAND_STATUS_DETAIL_MAX,
  MODE_COMMAND_STATUS_PHASES,
  MODE_COMMAND_USER_LABEL_MAX,
  MODE_COMMAND_VERSION,
  MODE_STATE_BECAUSE_MAX,
  MODE_STATE_BECAUSE_TEMPLATES,
  MODE_STATE_CONFIDENCES,
  MODE_STATE_EVIDENCE_MAX_JSON_CHARS,
  MODE_STATE_LABEL_MAX,
  MODE_STATE_MAX_CANDIDATES,
  MODE_STATE_SET_BY_MAX,
  MODE_STATE_SET_VIA_CLOUD,
  MODE_STATE_SET_VIA_LAN,
  MODE_STATE_SNAPSHOT_CLASS,
  MODE_STATE_SNAPSHOT_MAX_JSON_CHARS,
  MODE_STATE_SNAPSHOT_VERSION,
  MODE_STATE_STATUSES,
  N2K_DEVICE_CLASS_CODES,
  N2K_DEVICE_CLASS_SYSTEMS,
  N2K_OUTPUT_AUTHORITIES,
  N2K_OUTPUT_COMMAND_ID_MAX,
  N2K_OUTPUT_COMMAND_USER_LABEL_MAX,
  N2K_OUTPUT_CONFIG_COMMAND_VERSION,
  N2K_OUTPUT_CONFIG_VERSION,
  N2K_OUTPUT_INPUT_PATH_MAX,
  N2K_OUTPUT_INSTALL_ID_MAX,
  N2K_OUTPUT_INSTANCE_MAX,
  N2K_OUTPUT_MAX_INPUT_PATHS,
  N2K_OUTPUT_MAX_ROWS,
  N2K_OUTPUT_REJECTIONS,
  N2K_OUTPUT_SOURCE_ID_MAX,
  N2K_OUTPUT_STATE_CLASS,
  N2K_OUTPUT_STATE_VERSION,
  N2K_OUTPUT_TARGET_ID_MAX,
  NATIVE_CHANNELS,
  NAV_AID_ART,
  NAV_AID_ATTRIBUTE_MAX,
  NAV_AID_BODY_PAINTS,
  NAV_AID_CHUNKS_MAX,
  NAV_AID_CHUNK_ZOOM,
  NAV_AID_CLASSES,
  NAV_AID_CODEBOOK_VERSION,
  NAV_AID_COLOURS_MAX,
  NAV_AID_FEED_MIN_ZOOM,
  NAV_AID_FORMS,
  NAV_AID_GENERIC_ICON,
  NAV_AID_LIGHT_PAINTS,
  NAV_AID_LIMIT_MAX,
  NAV_AID_MIN_SEPARATION,
  NAV_AID_MIN_ZOOM,
  NAV_AID_OUTCOMES,
  NAV_AID_PRIORITY,
  NAV_AID_SOURCE_COLOURS,
  NAV_AID_TOPMARK_PAINTS,
  NAV_AID_TOPMARK_SHAPES,
  NEO_ALLOCATION_ROLES,
  NEO_ANSWER_ASSUMPTIONS_MAX,
  NEO_ANSWER_ASSUMPTION_ID,
  NEO_ANSWER_CALCULATION_ID,
  NEO_ANSWER_CHANGE_ABS_MAX,
  NEO_ANSWER_CHART_KINDS,
  NEO_ANSWER_EVIDENCE_ID,
  NEO_ANSWER_EVIDENCE_MAX,
  NEO_ANSWER_FOLLOWUPS_MAX,
  NEO_ANSWER_GROUPS_MAX,
  NEO_ANSWER_GROUP_ID,
  NEO_ANSWER_GROUP_VIEWS_MAX,
  NEO_ANSWER_GROUP_VIEWS_MIN,
  NEO_ANSWER_LABEL_MAX,
  NEO_ANSWER_LIMITATIONS_MAX,
  NEO_ANSWER_MISSING_MAX,
  NEO_ANSWER_RESULTS_MAX,
  NEO_ANSWER_RESULT_ID,
  NEO_ANSWER_TEXT_MAX,
  NEO_ANSWER_UNIT_MAX,
  NEO_ANSWER_VERSION,
  NEO_ASSUMPTION_ORIGINS,
  NEO_BOARDS_MAX,
  NEO_BOARD_CHANGE_ACTIONS,
  NEO_BOARD_EXTRA_KINDS,
  NEO_BOARD_GRID_COLUMNS,
  NEO_BOARD_GRID_GAP_PX,
  NEO_BOARD_GRID_MAX_H,
  NEO_BOARD_GRID_MAX_Y,
  NEO_BOARD_GRID_MIN_WIDTH_PX,
  NEO_BOARD_GRID_ROW_PX,
  NEO_BOARD_ITEMS_MAX,
  NEO_BOARD_ITEM_SIZES,
  NEO_BOARD_ITEM_TITLE_ALIGNS,
  NEO_BOARD_ITEM_TITLE_MAX,
  NEO_BOARD_LOOKS_BYTES_MAX,
  NEO_BOARD_LOOKS_MAX,
  NEO_BOARD_LOOK_ACTIONS,
  NEO_BOARD_LOOK_CSS_BYTES_MAX,
  NEO_BOARD_LOOK_EXCEPT_MAX,
  NEO_BOARD_LOOK_SCOPES,
  NEO_BOARD_LOOK_SCOPE_ORDER,
  NEO_BOARD_MAIN,
  NEO_BOARD_MAIN_TITLE,
  NEO_BOARD_MIN_COLUMNS,
  NEO_BOARD_MIN_WIDTH_PX,
  NEO_BOARD_PAGES_BYTES_MAX,
  NEO_BOARD_PHONE_ROWS,
  NEO_BOARD_REFLOW_MAX_ACROSS,
  NEO_BOARD_REFLOW_TRACK_MIN_PX,
  NEO_BOARD_REFLOW_WIDE_COLUMNS,
  NEO_BOARD_SLUG,
  NEO_BOARD_TITLE_MAX,
  NEO_BOARD_VERSION_ACTIONS,
  NEO_CAMERA_EVENT_TYPES,
  NEO_CAMERA_ID_MAX,
  NEO_CAMERA_NAME_MAX,
  NEO_CAMERA_SNAPSHOT_CAPTION,
  NEO_CAMERA_SNAPSHOT_DIMENSION_MAX,
  NEO_CARDS_PER_ANSWER_MAX,
  NEO_CARD_CONFIRM_KINDS,
  NEO_CARD_CONFIRM_TTL_MS,
  NEO_CARD_DRAWN_KINDS,
  NEO_CARD_ID,
  NEO_CARD_IDEMPOTENCY_KEY_MAX,
  NEO_CARD_KINDS,
  NEO_CARD_VERSION,
  NEO_CARD_WATER_TANKS_MAX,
  NEO_CHARTS_PER_ANSWER_MAX,
  NEO_CHARTS_PER_ANSWER_WITH_VIEWS_MAX,
  NEO_CHART_AGGREGATES,
  NEO_CHART_ALLOCATION_PARTS_MAX,
  NEO_CHART_ANSWER_BARS_METRICS,
  NEO_CHART_BARS_BY,
  NEO_CHART_BARS_MAX,
  NEO_CHART_BARS_METRICS,
  NEO_CHART_CHIPS,
  NEO_CHART_FORECAST_FIELDS_MAX,
  NEO_CHART_FORECAST_HOURS_MAX,
  NEO_CHART_HOUR_BARS_MAX,
  NEO_CHART_ID,
  NEO_CHART_KINDS,
  NEO_CHART_LABEL_MAX,
  NEO_CHART_PERIODS,
  NEO_CHART_READOUT_PATHS_MAX,
  NEO_CHART_SUM_UNITS,
  NEO_CHART_TITLE_MAX,
  NEO_CHART_TREND_PATHS_MAX,
  NEO_CHART_TREND_SLOTS,
  NEO_CHART_VERSION,
  NEO_CHART_VIEWS_MAX,
  NEO_CHART_VIEWS_MIN,
  NEO_CHART_VIEW_LABEL_MAX,
  NEO_CHAT_PAGE_BYTES_MAX,
  NEO_CHAT_PAGE_CARD,
  NEO_CHAT_PAGE_RESULTS_MAX,
  NEO_CHAT_PAGE_RESULT_ID,
  NEO_CHAT_PAGE_RESULT_LABEL_MAX,
  NEO_CHAT_PAGE_RESULT_ORIGINS,
  NEO_CHAT_PAGE_SERIES_POINTS_MAX,
  NEO_CHAT_PAGE_TITLE_MAX,
  NEO_CONTROL_FAMILIES,
  NEO_CONTROL_FAMILY,
  NEO_CONTROL_IDS,
  NEO_CONTROL_NAMES,
  NEO_CONTROL_PATHS_MAX,
  NEO_CONTROL_ROLES,
  NEO_CONTROL_STAND_IN_SUBTITLE,
  NEO_CONTROL_VALUE_FIELDS,
  NEO_DIAGRAM_ARROWS,
  NEO_DIAGRAM_DIRECTIONS,
  NEO_DIAGRAM_EDGES_MAX,
  NEO_DIAGRAM_EDGES_MIN,
  NEO_DIAGRAM_EDGE_LABEL_MAX,
  NEO_DIAGRAM_EDGE_POINTS_MAX,
  NEO_DIAGRAM_EDGE_POINTS_MIN,
  NEO_DIAGRAM_HEIGHT_MAX,
  NEO_DIAGRAM_LABEL_LINES_MAX,
  NEO_DIAGRAM_NODES_MAX,
  NEO_DIAGRAM_NODES_MIN,
  NEO_DIAGRAM_NODE_ID,
  NEO_DIAGRAM_NODE_LABEL_MAX,
  NEO_DIAGRAM_NODE_READINGS_MAX,
  NEO_DIAGRAM_PATHS_MAX,
  NEO_DIAGRAM_PATH_MAX,
  NEO_DIAGRAM_READING_LABEL_MAX,
  NEO_DIAGRAM_ROLES,
  NEO_DIAGRAM_TEXT,
  NEO_DIAGRAM_TITLE_MAX,
  NEO_DIAGRAM_UNIT_MAX,
  NEO_DIAGRAM_WIDTH_MAX,
  NEO_EVIDENCE_IDENTITY_MAX,
  NEO_EVIDENCE_KINDS,
  NEO_EVIDENCE_LIMITATIONS_MAX,
  NEO_FOLLOWUP_KINDS,
  NEO_FOLLOWUP_LABEL_MAX,
  NEO_FORECAST_FIELDS,
  NEO_FORECAST_FIELD_NAMES,
  NEO_FRESHNESS,
  NEO_FRESHNESS_STATES,
  NEO_MAP_AIS_VESSELS_MAX,
  NEO_MAP_FRIENDS_MAX,
  NEO_MAP_LAYERS,
  NEO_MAP_MISSING_REASONS,
  NEO_MAP_TEXT_MAX,
  NEO_MAP_TRACK_POINTS_MAX,
  NEO_MAP_TRACK_SPAN_MAX_MS,
  NEO_MAP_VESSEL_PICKS_MAX,
  NEO_MAP_VESSEL_PICK_NAME_MAX,
  NEO_NAMED_CONTROLS_MAX,
  NEO_NAMED_CONTROL_NAME_MAX,
  NEO_NOTEBOOK_CHANGE_ACTIONS,
  NEO_PAGE_BYTES_MAX,
  NEO_PAGE_COG_MIN_SOG_MS,
  NEO_PAGE_COOLANT_WARN_MARGIN_C,
  NEO_PAGE_DEPTH_FRESHNESS,
  NEO_PAGE_ENGINES_SHOWN_MAX,
  NEO_PAGE_ENGINE_TURNING_RPM,
  NEO_PAGE_FACES_MAX,
  NEO_PAGE_FACE_LOOK_BYTES_MAX,
  NEO_PAGE_FEEDS_MAX,
  NEO_PAGE_FEED_KINDS,
  NEO_PAGE_FEED_SLOTS_MAX,
  NEO_PAGE_HISTORY_MAX,
  NEO_PAGE_INSTRUMENT_FRESHNESS,
  NEO_PAGE_JS_BYTES_MAX,
  NEO_PAGE_LIVE_MAX,
  NEO_PAGE_LIVE_MIN_PX,
  NEO_PAGE_MODULE_FIELDS,
  NEO_PAGE_MODULE_PORTS,
  NEO_PAGE_MODULE_QUANTITIES,
  NEO_PAGE_MODULE_ROWS,
  NEO_PAGE_MODULE_SERIES,
  NEO_PAGE_MODULE_SERIES_MAX,
  NEO_PAGE_MODULE_SLOTS_MAX,
  NEO_PAGE_MODULE_VALUE_FIELDS,
  NEO_PAGE_NAME,
  NEO_PAGE_NEEDS,
  NEO_PAGE_NIGHT,
  NEO_PAGE_PART_ID,
  NEO_PAGE_POSITION_MODULES,
  NEO_PAGE_POSITION_PATH,
  NEO_PAGE_READINGS_MAX,
  NEO_PAGE_READING_UNITS,
  NEO_PAGE_SERIES_POINTS_DEFAULT,
  NEO_PAGE_SERIES_POINTS_MAX,
  NEO_PAGE_SERIES_POINTS_MIN,
  NEO_PAGE_SLOT_FRAMES,
  NEO_PAGE_SLOT_VIEWPORTS,
  NEO_PAGE_SLOW_DASH_MS,
  NEO_PAGE_SLOW_DIM_MS,
  NEO_PAGE_SLOW_FRESHNESS,
  NEO_PAGE_STALE_AFTER_MS,
  NEO_PAGE_STAND_IN_TEXT,
  NEO_PAGE_STATUS_KINDS,
  NEO_PAGE_TEMPLATE_ID,
  NEO_PAGE_TEMPLATE_SOURCE_BYTES_MAX,
  NEO_PAGE_TITLE,
  NEO_PAGE_V2_BYTES_MAX,
  NEO_PAGE_V2_READINGS_MAX,
  NEO_PAGE_WATER_ROWS_MAX,
  NEO_PAGE_WINDOW_MAX_MS,
  NEO_PAGE_WINDOW_MIN_MS,
  NEO_PAGE_ZONES_MAX,
  NEO_PICTURE_ASPECTS,
  NEO_PICTURE_CAPTION,
  NEO_PICTURE_DIMENSION_MAX,
  NEO_PICTURE_MIMES,
  NEO_PICTURE_SUBJECT_MAX,
  NEO_PICTURE_SUBJECT_MIN,
  NEO_SERIES_REQUEST_MAX,
  NEO_SERIES_SLOTS_TOTAL_MAX,
  NEO_SERIES_SPAN_DAYS_TOTAL_MAX,
  NEO_STATUS_ICONS,
  NEO_STATUS_SOURCES,
  NEO_STATUS_TONES,
  NEO_STATUS_WATCH_KEYS,
  NEO_STATUS_WATCH_TONES,
  NEO_SYSTEM_BOARD_ITEMS_MAX,
  NEO_SYSTEM_BOARD_KEYS,
  NEO_SYSTEM_BOARD_TITLES,
  NEO_SYSTEM_PANE_BOARD_KEYS,
  OPERATOR_TOKEN_CLEAR_COMMAND,
  OPERATOR_TOKEN_DIGEST_PATTERN,
  OPERATOR_TOKEN_PATTERN,
  OPERATOR_TOKEN_PROTOCOL_VERSION,
  OPERATOR_TOKEN_SET_COMMAND,
  OPERATOR_TOKEN_STATE_CLASS,
  ORDINAL_INSTANCE_LIMIT,
  OVERVIEW_MODE_KEYS,
  OVERVIEW_MODE_MODULE_LIMITS,
  OVERVIEW_MODULE_KINDS,
  OVERVIEW_MODULE_TITLES,
  PINNED_FIRMWARE_KEYS,
  PINNED_UPDATE_KEYS,
  PRESENCE_ANSWERS,
  PRESSURE_UNITS,
  PROCEDURES_PER_BOAT_MAX,
  PROCEDURE_CHECK_REASONS,
  PROCEDURE_CHECK_RESULTS,
  PROCEDURE_CITATIONS_MAX,
  PROCEDURE_PRECHECKS_MAX,
  PROCEDURE_PRECHECK_LABEL_MAX,
  PROCEDURE_PRECHECK_OPS,
  PROCEDURE_PRECHECK_TEXT_VALUE_MAX,
  PROCEDURE_SOURCES,
  PROCEDURE_STALE_AFTER_DEFAULT_S,
  PROCEDURE_STALE_AFTER_MAX_S,
  PROCEDURE_STEPS_MAX,
  PROCEDURE_STEP_TEXT_MAX,
  PROCEDURE_STEP_TIP_MAX,
  PROCEDURE_SYSTEM_MAX,
  PROCEDURE_TITLE_MAX,
  PROCEDURE_UNIT_MAX,
  PROTECT_API_KEY_MAX,
  PROTECT_API_KEY_MIN,
  PROTECT_API_KEY_PATTERN,
  PROTECT_COMMAND_KEY_MAX,
  PROTECT_CONFIG_ACK_CLASS,
  PROTECT_CONFIG_ACK_COMMANDS,
  PROTECT_CONFIG_REJECT_REASONS,
  PROTECT_CONFIG_STATE_CLASS,
  PROTECT_CONFIG_VERSION,
  PROTECT_CONNECTION_STATES,
  PROTECT_CREDENTIALS_ACTIONS,
  PROTECT_CREDENTIALS_COMMAND,
  PROTECT_DETAIL_MAX,
  PROTECT_HOST_MAX,
  PROTECT_KEY_SOURCES,
  PROTECT_PIN_CONFIRM_COMMAND,
  PROTECT_USER_ID_MAX,
  PROXY_CHANNELS,
  QUICK_READING_DEPTH_PATHS,
  QUICK_READING_FRESHNESS,
  QUICK_READING_FRESHNESS_PATTERNS,
  QUICK_READING_FRESHNESS_PREFIXES,
  QUICK_READING_NEVER_DASH_MS,
  QUICK_READING_QUANTITIES,
  RECIPE_SYSTEMS,
  SETUP_ALARM_ANALYSIS_EVENTS,
  SETUP_ALARM_RECOMMENDATION_EVENTS,
  SETUP_SOURCE_ANALYSIS_EVENTS,
  SHORT_DISTANCE_UNITS,
  SIM_TIME_MAX_RATE,
  SPEED_UNITS,
  TEMPERATURE_UNITS,
  UNITS_COMMAND_ID_MAX,
  UNITS_COMMAND_USER_LABEL_MAX,
  UNITS_COMMAND_VERSION,
  UPDATE_MANIFEST_DOMAIN,
  UPSTREAM_INBOUND_TYPES,
  UPSTREAM_OUTBOUND_TYPES,
  USER_WS_CLOSE_TOKEN_EXPIRED,
  USER_WS_CLOSE_UNAUTHORIZED,
  VESSEL_SETTINGS_OUTBOX_CLASS,
  VESSEL_TYPES,
  VESSEL_TYPE_COMMAND_ID_MAX,
  VESSEL_TYPE_COMMAND_USER_LABEL_MAX,
  VESSEL_TYPE_COMMAND_VERSION,
  VICTRON_ALERT_PATH_PREFIX,
  VOLUME_UNITS,
  WATCHED_VESSELS_MAX,
  WATCHED_VESSEL_LABEL_MAX,
  WATCHED_VESSEL_STATUSES,
  WIDGET_TYPES,
  acceptMapContext,
  alarmSeverityLabel,
  boatNameAnswerSubject,
  boatNameFrom,
  boatNameTargetFromAnswerSubject,
  boatNameTargetKey,
  boatNamesDigest,
  boatSystemPathRule,
  buildPrimaryReason,
  canAck,
  canSilence,
  canonicalJson,
  categoryFor,
  compareFirmwareVersions,
  compareNavAidIds,
  compareNavAids,
  convertQuantity,
  daysBetween,
  decodeCameraUploadFrame,
  describeRuntimeDisabledReason,
  deviceAlertDeviceKey,
  deviceAlertPathSource,
  deviceAlertsIdempotencyKey,
  displayInstanceNumber,
  displayUnitKnown,
  durationScale,
  effectiveBatteryChemistry,
  encodeCameraUploadHeader,
  equipmentFamilyOfKey,
  equipmentFixture,
  equipmentInstanceOf,
  fileDayOn,
  fileDaysIn,
  firmwareArtifactForCompatible,
  firmwareUpdateAvailable,
  formatCountdown,
  formatDisplay,
  formatDurationSeconds,
  formatQuantity,
  formatTargetRange,
  fromDisplay,
  gatewayRoleToSourcePlanRole,
  gearFamilyOfKey,
  gearMakeModelFrom,
  hourMeterText,
  instrumentAsPanel,
  isAlarmsPausedReason,
  isBatteryBankPath,
  isBatteryChemistry,
  isCameraLanAddress,
  isCanonicalMode,
  isChannel,
  isDeviceAlertPath,
  isFirmwareAcceptanceActive,
  isFirmwareManifest,
  isHourMeter,
  isLocalDate,
  isMapDisplayText,
  isMmsi,
  isNavAidIconKey,
  isNavAidTopmarkKey,
  isNeoAnswerExpression,
  isNeoBoardItemPlace,
  isNeoBoardItemSize,
  isNeoBoardItemTitleAlign,
  isNeoCameraId,
  isNeoCameraName,
  isNeoChatPageTitle,
  isNeoControlId,
  isNeoPageName,
  isNeoPagePartId,
  isNeoPageReadingUnit,
  isNeoPictureSubject,
  isNeoSystemBoardKey,
  isOverviewModuleKind,
  isParseFailure,
  isSecondsUnit,
  isSurfacedState,
  isUndefinedEquipmentKey,
  isValidTimeZone,
  isVesselType,
  localParts,
  manifestHasArtifactForRuntime,
  manifestPlatformKey,
  mapCaption,
  mapContextRefresh,
  mapDistanceM,
  mapMarineCaptionSpoken,
  mapMarineCaptionText,
  modeToOverviewArchetype,
  n2kOutputConfigCanonical,
  n2kOutputConfigDigest,
  n2kOutputInstanceLabel,
  n2kOutputShadowedRows,
  n2kOutputStateSupersedes,
  namedInstanceOf,
  navAidAccessibleLabel,
  navAidChunks,
  navAidDeclutter,
  navAidDrawnIconKey,
  navAidFamily,
  navAidIcon,
  navAidIconHeight,
  navAidPaintColours,
  navAidZoomForSpan,
  neoAgoLabel,
  neoAnswerExpressionIds,
  neoAnswerLabel,
  neoAnswerText,
  neoBoardColumnsPx,
  neoBoardItemDefaultSize,
  neoBoardItemIsCard,
  neoBoardItemIsChart,
  neoBoardItemIsControl,
  neoBoardItemIsInstrument,
  neoBoardItemIsModule,
  neoBoardItemIsPage,
  neoBoardItemKey,
  neoBoardItemModule,
  neoBoardItemSourceSpec,
  neoBoardItemsInPlaceOrder,
  neoBoardLookRank,
  neoBoardLookReadingHits,
  neoBoardMinColumns,
  neoBoardMinWidthPx,
  neoBoardPhoneKind,
  neoBoardPhoneRows,
  neoBoardPlacesOverlap,
  neoBoardReflow,
  neoBoardReflowAcross,
  neoBoardRolling,
  neoBoardRowsForPx,
  neoBoardSizeForWidth,
  neoBoardWidthForSize,
  neoCardConfirmOpen,
  neoCardConfirmRequest,
  neoCardFreshness,
  neoCardFreshnessText,
  neoCardMarker,
  neoCardTitle,
  neoChartIsBase,
  neoChartLabel,
  neoChartMarker,
  neoChartTitle,
  neoChatPageBindsResults,
  neoChatPageResultIds,
  neoChatPageSeriesKey,
  neoChatPageView,
  neoControlAsInstrument,
  neoControlForInstrument,
  neoControlIsStandIn,
  neoControlName,
  neoControlPaths,
  neoControlScale,
  neoDiagramPairKey,
  neoDiagramPaths,
  neoFollowupLabel,
  neoFollowupResolves,
  neoFreshnessFrom,
  neoNoReadingLine,
  neoPageAgoLabel,
  neoPageAnchorStatus,
  neoPageAnchorWord,
  neoPageAsControl,
  neoPageAsInstrument,
  neoPageBankIdentity,
  neoPageBarLabel,
  neoPageBarValue,
  neoPageBestBatteryBank,
  neoPageCardAgeText,
  neoPageCardOf,
  neoPageCatalogFreshness,
  neoPageClock,
  neoPageClockText,
  neoPageCoolantLimitC,
  neoPageCoolantTone,
  neoPageCounterReadout,
  neoPageCourseWhileMoving,
  neoPageData,
  neoPageDeltaOverHours,
  neoPageDisplayLine,
  neoPageDisplayReading,
  neoPageDisplayReadingText,
  neoPageDrawsDirect,
  neoPageEngineLabel,
  neoPageEnvironmentChoice,
  neoPageEnvironmentValue,
  neoPageFluidMeta,
  neoPageForecastValue,
  neoPageFormatAway,
  neoPageFormatTimeRemaining,
  neoPageFormatValue,
  neoPageFreshness,
  neoPageFullnessWords,
  neoPageHasFreshnessLine,
  neoPageHasScript,
  neoPageHeardEngines,
  neoPageHelmValue,
  neoPageHourlyAsSeries,
  neoPageHourlyMax,
  neoPageHourlySeries,
  neoPageHouseBankFor,
  neoPageInStoreUnit,
  neoPageInsideTemperature,
  neoPageIsEngineHours,
  neoPageIsUnedited,
  neoPageIsV2,
  neoPageLiveTone,
  neoPageLocalExtremes,
  neoPageMaxInWindow,
  neoPageModeStatus,
  neoPageModelUnavailableMessage,
  neoPageModuleFeed,
  neoPageModuleFigures,
  neoPageModuleIsPorted,
  neoPageNextTurn,
  neoPageNoEngineLine,
  neoPageOilPressureLostFor,
  neoPagePadDomain,
  neoPagePaths,
  neoPagePeakGust,
  neoPagePositionParts,
  neoPagePowerChoice,
  neoPagePowerValue,
  neoPagePressureTendency,
  neoPagePressureValue,
  neoPageProbeIsFaulted,
  neoPageReadingNow,
  neoPageReadingUnitChoices,
  neoPageReadingUnits,
  neoPageReservesValue,
  neoPageRestingEngines,
  neoPageResultText,
  neoPageSeriesPoints,
  neoPageShoalingNote,
  neoPageSocSeverity,
  neoPageSourceAmps,
  neoPageSquallRisk,
  neoPageStandIn,
  neoPageTankAttentionLine,
  neoPageTankAttentionTone,
  neoPageTankLabel,
  neoPageTankNameOverrides,
  neoPageTankSeverity,
  neoPageTankThresholdPct,
  neoPageTanksOf,
  neoPageTanksValue,
  neoPageTemplateIsHosted,
  neoPageTemplateIsPortedModule,
  neoPageTemplateOf,
  neoPageTemplateSourceAgrees,
  neoPageTendency,
  neoPageTideAriaLabel,
  neoPageTideExtremes,
  neoPageTideRangeM,
  neoPageTideValue,
  neoPageTrafficValue,
  neoPageUnderwayReading,
  neoPageUneditedSource,
  neoPageUtf8Bytes,
  neoPageWaterFigures,
  neoPageWaterTemperature,
  neoPageWindLabel,
  neoPageWindReferenceFor,
  neoPageWindowBounds,
  neoPageWindowFromRange,
  neoPageWindowFromRolling,
  neoPageWithoutScript,
  neoPictureAspectOf,
  neoSeriesBatches,
  neoSeriesCadenceMs,
  neoSeriesGrid,
  neomarisCameraEventId,
  normalizeNeoCardMarkers,
  normalizeNeoChartMarkers,
  normalizeVesselType,
  normalizeWatchedVesselLabel,
  numericThresholdFields,
  operatorTokenDigest,
  parseAlarmRegenerateCommand,
  parseAlarmsPauseCommandPayload,
  parseAlertCommandPayload,
  parseAnchorWatchCommandPayload,
  parseAnchorWatchStatus,
  parseBatteryChemistryCommandPayload,
  parseBatteryChemistrySnapshot,
  parseBoatNameTarget,
  parseBoatNamesSnapshot,
  parseCameraEventsPayload,
  parseCameraInventoryPayload,
  parseCameraLanAckPayload,
  parseCameraLanConfigPayload,
  parseCameraLanStatePayload,
  parseCameraPtzAckPayload,
  parseCameraPtzCommandPayload,
  parseCameraSnapshotAckPayload,
  parseCameraSnapshotPayload,
  parseCameraSnapshotRequestPayload,
  parseCameraStreamCommandPayload,
  parseCameraStreamStatusPayload,
  parseClientMessage,
  parseClockTime,
  parseDemoReplayJumpPayload,
  parseDemoReplayStatusPayload,
  parseInbound,
  parseMapLocationContext,
  parseModeCommandPayload,
  parseModeCommandStatusPayload,
  parseModeStateSnapshotPayload,
  parseN2kOutputConfig,
  parseN2kOutputConfigCommandPayload,
  parseN2kOutputStatePayload,
  parseNavAidAttributes,
  parseNavAidFeature,
  parseNavAidFeed,
  parseNeoAnswer,
  parseNeoAnswerChart,
  parseNeoBoardControlSpec,
  parseNeoBoardModuleSpec,
  parseNeoBoardPageSpec,
  parseNeoCameraSnapshotSnapshot,
  parseNeoCameraSnapshotSpecParts,
  parseNeoCard,
  parseNeoCards,
  parseNeoChatPageSnapshot,
  parseNeoChatPageSpec,
  parseNeoChatPageSpecParts,
  parseNeoControlOptions,
  parseNeoDiagramSnapshot,
  parseNeoDiagramSpecParts,
  parseNeoEvidenceRef,
  parseNeoFollowup,
  parseNeoFollowupIntent,
  parseNeoMapSnapshot,
  parseNeoMapSpecParts,
  parseNeoPageReading,
  parseNeoPageWindow,
  parseNeoPictureSnapshot,
  parseNeoPictureSpecParts,
  parseNeoSeriesResponse,
  parseNeoStatus,
  parseNeoWaterCardSnapshot,
  parseOperatorTokenClearPayload,
  parseOperatorTokenSetPayload,
  parseOperatorTokenStatePayload,
  parseProcedureCheck,
  parseProcedureCitation,
  parseProcedureList,
  parseProcedurePrecheck,
  parseProcedureSource,
  parseProcedureStep,
  parseProtectConfigAckPayload,
  parseProtectConfigStatePayload,
  parseProtectCredentialsPayload,
  parseProtectPinConfirmPayload,
  parseSimTimeStatus,
  parseUnitSettings,
  parseUnitsCommandPayload,
  parseVesselTypeCommandPayload,
  parseWatchedVessel,
  parseWatchedVesselPosition,
  pathBelongsToInstance,
  pathSystemIsOpen,
  quickReadingCategory,
  quickReadingCompanion,
  quickReadingFreshness,
  quickReadingFreshnessKind,
  quickReadingLabel,
  quickReadingQuantity,
  quickReadingShortLabel,
  quickReadingsFixture,
  renderModeStateBecause,
  replayDayAt,
  replayDayMap,
  replayInstantOf,
  replayOffsetOf,
  resolveUnitSettings,
  roundedReading,
  sameGearMakeModel,
  severityRank,
  sha256Hex,
  signalLabel,
  signalMeasurementLabel,
  simVirtualNowMs,
  sortActiveForDisplay,
  sortKeysDeep,
  splitNeoChartContent,
  stripNeoChartMarkers,
  systemOfRecipe,
  systemsOfDevice,
  systemsOfPath,
  toDisplay,
  unitConversionFacts,
  unitLabel,
  unitSettingsFromPreset,
  unitSettingsToVesselUnits,
  zonedInstant
};
